import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.quiz.runner3d');
import { AfterViewInit, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as THREE from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { PLYLoader } from 'three/addons/loaders/PLYLoader.js';
import { ColladaLoader } from 'three/addons/loaders/ColladaLoader.js';
import { TDSLoader } from 'three/addons/loaders/TDSLoader.js';
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';
import { Service } from "src/libs/portal/season/service";
import {
    AnswerRecord,
    ControlAction,
    EditorTab,
    GameMap,
    GameMode,
    GameScreen,
    MapGate,
    MapGroundImage,
    MapMountPlacement,
    MapObjectPlacement,
    MapSceneryPlacement,
    MapTheme,
    MapWaypoint,
    MountKind,
    ObstacleKind,
    PathPresetKind,
    QuizQuestion,
    RoadPieceKind,
    RuntimeGate,
    SceneryKind,
    TerrainGrid,
    TerrainKind,
    cloneMap,
    createBlankPlacement,
    createBlankQuestion,
    createYsPalaceCourseMap,
    createId,
    createPathPresetWaypoints,
    insertRoadPiece,
    normalizeMap
} from './game.types';
import { DecorationFormat, DecorationStorage, StoredDecorationAsset } from './decoration.storage';
import { SkinStorage, StoredSkin } from './skin.storage';
import {
    YS_DECORATION_CATEGORIES,
    YS_DECORATION_PRESETS,
    YsDecorationCategory,
    YsDecorationPreset
} from './ys-decoration.catalog';

interface CoinState {
    config: MapObjectPlacement;
    mesh: THREE.Group;
    collected: boolean;
    homePosition: THREE.Vector3;
}

interface MagnetState {
    config: MapObjectPlacement;
    mesh: THREE.Group;
    collected: boolean;
    homePosition: THREE.Vector3;
}

interface SupplementState {
    config: MapObjectPlacement;
    mesh: THREE.Group;
    collected: boolean;
    homePosition: THREE.Vector3;
}

interface MountState {
    config: MapMountPlacement;
    mesh: THREE.Group;
    collected: boolean;
    homePosition: THREE.Vector3;
}

interface RuntimePathSample {
    distance: number;
    worldDistance: number;
    point: THREE.Vector3;
}

interface RuntimeRibbonFrame {
    distance: number;
    point: THREE.Vector3;
    forward: THREE.Vector3;
    right: THREE.Vector3;
    surfaceUp: THREE.Vector3;
}

interface JumpPlatformSurface {
    kind: RoadPieceKind;
    startDistance: number;
    endDistance: number;
    centerX: number;
    centerZ: number;
    forwardX: number;
    forwardZ: number;
    rightX: number;
    rightZ: number;
    width: number;
    length: number;
    topY: number;
    rotationY: number;
}

interface MinimapGateMarker {
    id: string;
    x: number;
    z: number;
    gate: RuntimeGate;
}

interface SceneryCollider {
    id: string;
    centerX: number;
    centerZ: number;
    halfWidth: number;
    halfDepth: number;
    rotation: number;
    baseY: number;
    topY: number;
    scale: number;
    verticalScale: number;
    surface?: YsDecorationSurfaceMap;
    autoGate?: boolean;
}

interface AutoSceneryGateState {
    id: string;
    object: THREE.Object3D;
    closedY: number;
    openHeight: number;
    openRadius: number;
    openAmount: number;
}

interface YsDecorationSurfaceMap {
    version: number;
    id: string;
    targetHeight: number;
    cellSizeX: number;
    cellSizeZ: number;
    minX: number;
    minZ: number;
    columns: number;
    rows: number;
    maxY: number;
    heights: Array<number | null>;
}

interface ObstacleState {
    config: MapObjectPlacement;
    mesh: THREE.Group;
    hit: boolean;
}

interface HiddenNpcState {
    id: string;
    mesh: THREE.Group;
    resolved: boolean;
    question?: QuizQuestion;
}

interface HaechiGuideState {
    placement: MapSceneryPlacement;
    mesh: THREE.Group;
    shown: boolean;
}

interface HiddenNpcAnswerRecord {
    npcId: string;
    question: string;
    selected: boolean;
    answer: boolean;
    correct: boolean;
    reward: number;
}

interface LocalRankingEntry {
    id: string;
    runId: string;
    mapId: string;
    mapTitle: string;
    mode: GameMode;
    playerName: string;
    characterName: string;
    score: number;
    baseScore: number;
    hiddenBonus: number;
    coinScore: number;
    hiddenScore: number;
    timeScore: number;
    quizScore: number;
    obstacleCollisions: number;
    time: number;
    coins: number;
    accuracy: number;
    createdAt: string;
}

type BuiltInSceneryPaletteKind = Exclude<SceneryKind, 'ys' | 'custom'>;
type KimYeeunDecorationKind = 'kim-yeeun-old-shrine-naejaesil' | 'kim-yeeun-gyeonghoeru' | 'kim-yeeun-base' | 'kim-yeeun-stairs' | 'kim-yeeun-stone-path-small2' | 'kim-yeeun-old-shrine-south-haenggak' | 'kim-yeeun-gwollaegaksa-annex' | 'kim-yeeun-stone-block' | 'kim-yeeun-stone-floor';
type BundledNatureDecorationKind = 'pine-tree' | 'pine-tree-windswept' | 'pine-tree-young' | 'lotus-flower' | 'pond-rock' | 'stone-stairs' | 'stone-wall' | KimYeeunDecorationKind;
type BundledNatureCollider = 'trunk' | 'bounds' | 'steps' | 'none';
type BundledNaturePlaceholder = 'pine' | 'lotus' | 'rock' | 'stairs' | 'wall';
type EditorPaletteKind = 'item' | 'mount' | 'obstacle' | 'gate' | BuiltInSceneryPaletteKind | 'ys-decoration' | 'custom-decoration' | RoadPieceKind;
type ItemKind = 'coin' | 'magnet' | 'supplement';
type ItemCollection = 'coins' | 'magnets' | 'supplements';
type SkinFormat = 'glb' | 'gltf' | 'obj' | 'fbx' | 'stl' | 'ply' | 'dae' | '3ds';
type JoseonSkinId = 'joseon-yangban' | 'joseon-farmer' | 'joseon-king' | 'joseon-woman' | 'joseon-gisaeng' | 'joseon-guard' | 'joseon-general';
type BuiltInSkinId = 'neon-runner' | 'coral-bot' | 'forest-scout' | 'solar-droid' | 'joseon-king-2' | 'sejong-the-great' | 'haetae' | JoseonSkinId;
type EyeStyle = 'none' | 'calm' | 'bright' | 'dignified';
type NoseStyle = 'none' | 'soft' | 'straight' | 'round';
type MouthStyle = 'none' | 'gentle' | 'smile' | 'firm';
type HairStyle = 'none' | 'sangtu' | 'gat' | 'royal-crown' | 'headscarf' | 'binyeo' | 'jokduri' | 'warrior-helmet' | 'general-helmet';
type ExploreCameraMode = 'follow' | 'shoulder' | 'top';
type EditorViewMode = '2d' | '3d';
type GraphicsQuality = 'auto' | 'low' | 'balanced' | 'high';
type EffectiveGraphicsQuality = Exclude<GraphicsQuality, 'auto'>;
type RendererBackend = 'webgl' | 'webgpu';

interface BundledNatureDecorationSpec {
    label: string;
    url: string;
    format?: 'fbx' | 'glb';
    targetHeight: number;
    fallbackDimensions: readonly [number, number, number];
    ghostCenterOffset?: readonly [number, number];
    collider: BundledNatureCollider;
    trunkRadius?: number;
    stepProfile?: Readonly<{ count: number; width: number; depth: number; rise: number }>;
    placeholder: BundledNaturePlaceholder;
}

const BUNDLED_NATURE_DECORATION_SPECS: Record<BundledNatureDecorationKind, BundledNatureDecorationSpec> = {
    'pine-tree': {
        label: '곧은 소나무',
        url: '/assets/quiz-runner/decorations/pine-tree/pine-tree.fbx?v=20260827-3',
        targetHeight: 7.5,
        fallbackDimensions: [7.02, 7.5, 6.13],
        collider: 'trunk',
        trunkRadius: 0.58,
        placeholder: 'pine'
    },
    'pine-tree-windswept': {
        label: '바람소나무',
        url: '/assets/quiz-runner/decorations/pine-tree-windswept/pine-tree-windswept.fbx?v=20260827-1',
        targetHeight: 6.5,
        fallbackDimensions: [8.74, 6.5, 4.0],
        ghostCenterOffset: [1.03, -0.19],
        collider: 'trunk',
        trunkRadius: 0.62,
        placeholder: 'pine'
    },
    'pine-tree-young': {
        label: '어린 소나무',
        url: '/assets/quiz-runner/decorations/pine-tree-young/pine-tree-young.fbx?v=20260827-1',
        targetHeight: 4.5,
        fallbackDimensions: [3.68, 4.5, 3.71],
        collider: 'trunk',
        trunkRadius: 0.36,
        placeholder: 'pine'
    },
    'lotus-flower': {
        label: '연꽃',
        url: '/assets/quiz-runner/decorations/lotus-flower/lotus-flower.fbx?v=20260827-1',
        targetHeight: 0.8,
        fallbackDimensions: [3.3, 0.8, 2.53],
        collider: 'none',
        placeholder: 'lotus'
    },
    'pond-rock': {
        label: '연못 돌',
        url: '/assets/quiz-runner/decorations/pond-rock/pond-rock.fbx?v=20260827-1',
        targetHeight: 1.25,
        fallbackDimensions: [3.3, 1.25, 1.92],
        collider: 'bounds',
        placeholder: 'rock'
    },
    'stone-stairs': {
        label: '돌계단',
        url: '/assets/quiz-runner/decorations/stone-stairs/stone-stairs.fbx?v=20260827-1',
        targetHeight: 1.68,
        fallbackDimensions: [5.4, 1.68, 4.2],
        collider: 'steps',
        stepProfile: { count: 6, width: 5.4, depth: 0.7, rise: 0.28 },
        placeholder: 'stairs'
    },
    'stone-wall': {
        label: '돌담벽',
        url: '/assets/quiz-runner/decorations/stone-wall/stone-wall.fbx?v=20260827-1',
        targetHeight: 1.8,
        fallbackDimensions: [6.4, 1.8, 1.0],
        collider: 'bounds',
        placeholder: 'wall'
    },
    'kim-yeeun-old-shrine-naejaesil': {
        label: '구선원전 내재실',
        url: '/assets/quiz-runner/decorations/kim-yeeun/old-shrine-naejaesil.glb?v=20260902-server2',
        format: 'glb',
        targetHeight: 6,
        fallbackDimensions: [6.07, 6, 8.99],
        collider: 'bounds',
        placeholder: 'wall'
    },
    'kim-yeeun-gyeonghoeru': {
        label: '경회루',
        url: '/assets/quiz-runner/decorations/kim-yeeun/gyeonghoeru.glb?v=20260902-server2',
        format: 'glb',
        targetHeight: 6,
        fallbackDimensions: [11.43, 6, 9.95],
        collider: 'bounds',
        placeholder: 'wall'
    },
    'kim-yeeun-base': {
        label: '궁궐 석재 기단',
        url: '/assets/quiz-runner/decorations/kim-yeeun/base.glb?v=20260902-server2',
        format: 'glb',
        targetHeight: 6,
        fallbackDimensions: [89.61, 6, 50.27],
        collider: 'bounds',
        placeholder: 'wall'
    },
    'kim-yeeun-stairs': {
        label: '궁궐 석계단',
        url: '/assets/quiz-runner/decorations/kim-yeeun/stairs.glb?v=20260902-server2',
        format: 'glb',
        targetHeight: 6,
        fallbackDimensions: [14.54, 6, 16.84],
        collider: 'bounds',
        placeholder: 'stairs'
    },
    'kim-yeeun-stone-path-small2': {
        label: '궁궐 돌길',
        url: '/assets/quiz-runner/decorations/kim-yeeun/stone-path-small2.glb?v=20260902-server2',
        format: 'glb',
        targetHeight: 6,
        fallbackDimensions: [115.55, 6, 468.65],
        collider: 'bounds',
        placeholder: 'wall'
    },
    'kim-yeeun-old-shrine-south-haenggak': {
        label: '구선원전 남행각',
        url: '/assets/quiz-runner/decorations/kim-yeeun/old-shrine-south-haenggak.glb?v=20260902-server2',
        format: 'glb',
        targetHeight: 6,
        fallbackDimensions: [28.26, 6, 4.82],
        collider: 'bounds',
        placeholder: 'wall'
    },
    'kim-yeeun-gwollaegaksa-annex': {
        label: '궐내각사 검서청 부속채',
        url: '/assets/quiz-runner/decorations/kim-yeeun/gwollaegaksa-geomseocheong-annex.glb?v=20260902-server2',
        format: 'glb',
        targetHeight: 6,
        fallbackDimensions: [18.21, 6, 20.17],
        collider: 'bounds',
        placeholder: 'wall'
    },
    'kim-yeeun-stone-block': {
        label: '궁궐 돌석',
        url: '/assets/quiz-runner/decorations/kim-yeeun/stone-block.glb?v=20260902-server2',
        format: 'glb',
        targetHeight: 6,
        fallbackDimensions: [4.8, 6, 3.11],
        collider: 'bounds',
        placeholder: 'rock'
    },
    'kim-yeeun-stone-floor': {
        label: '궁궐 석재 바닥',
        url: '/assets/quiz-runner/decorations/kim-yeeun/stone-floor.glb?v=20260902-server2',
        format: 'glb',
        targetHeight: 6,
        fallbackDimensions: [198.8, 6, 980.37],
        collider: 'bounds',
        placeholder: 'wall'
    }
};

function isBundledNatureDecorationKind(kind: string): kind is BundledNatureDecorationKind {
    return Object.prototype.hasOwnProperty.call(BUNDLED_NATURE_DECORATION_SPECS, kind);
}

function isKimYeeunDecorationKind(kind: string): kind is KimYeeunDecorationKind {
    return kind.startsWith('kim-yeeun-')
        && Object.prototype.hasOwnProperty.call(BUNDLED_NATURE_DECORATION_SPECS, kind);
}

function bundledDecorationFormatLabel(spec: BundledNatureDecorationSpec): string {
    return (spec.format || 'fbx').toUpperCase();
}

interface CharacterCustomization {
    eyes: EyeStyle;
    nose: NoseStyle;
    mouth: MouthStyle;
    hair: HairStyle;
}

interface CharacterStyleOption<T extends string> {
    id: T;
    label: string;
}

interface CharacterStats {
    jump: number;
    steering: number;
}

interface TerrainPaletteItem {
    kind: TerrainKind;
    label: string;
    color: string;
    description: string;
}

interface ImportedModelSummary {
    meshCount: number;
    skinnedMeshCount: number;
    boneCount: number;
    animationCount: number;
}

interface PreparedSkinVisual {
    visual: THREE.Group;
    summary: ImportedModelSummary;
}

interface NfcCharacterAsset {
    name: string;
    fileName: string;
    format: SkinFormat;
    data: ArrayBuffer;
    participantId: string;
    assetId: string;
    checksum: string;
    fileSize: number;
    baseUrl: string;
    fromCache: boolean;
}

interface NfcRegistrationDraft {
    asset: NfcCharacterAsset;
    recordId: string;
    stats: CharacterStats;
}

interface CharDbAuthResponse {
    token: string;
    participantId: string;
    expiresAt?: string;
}

interface CharDbModelMetadata {
    id: string;
    asset_id: string;
    name?: string;
    original_name: string;
    format: 'GLB' | 'GLTF' | 'OBJ';
    file_size: number;
    checksum: string;
    status: 'AVAILABLE';
    downloadUrl: string;
}

interface ActiveNfcCharacterMetadata {
    participantId: string;
    assetId: string;
    checksum: string;
    fileSize: number;
    baseUrl: string;
}

interface CharDbRequestError extends Error {
    status?: number;
    code?: string;
}

interface ServerDecorationPreset {
    id: string;
    name: string;
    fileName: string;
    sourceFileName: string;
    sourceFormat: 'max' | 'fbx';
    url: string;
    version?: string;
}

interface BuiltInSkinPreset {
    id: BuiltInSkinId;
    name: string;
    icon: string;
    color: string;
    description: string;
    specialty: string;
    stats: CharacterStats;
    aiGenerated?: boolean;
}

interface EditorPaletteItem {
    kind: EditorPaletteKind;
    icon: string;
    label: string;
    hint: string;
}

interface EditorDropLocation {
    distance: number;
    lane: number;
    worldX: number;
    worldZ: number;
    pathWorldX: number;
    pathWorldZ: number;
}

interface EditorPlanBounds {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
}

interface EditorLoopMarker {
    id: string;
    left: number;
    top: number;
    label: string;
}

type PlanMoveType = 'coins' | 'magnets' | 'supplements' | 'mounts' | 'obstacles' | 'gates' | 'scenery' | 'waypoint';
type PlacementCollection = 'coins' | 'magnets' | 'supplements' | 'obstacles';
type Editor3dSelectableCollection = PlacementCollection | 'mounts' | 'gates' | 'scenery';

interface Editor3dObjectReference {
    collection: Editor3dSelectableCollection;
    id: string;
}

interface EditorItemReference {
    kind: ItemKind;
    collection: ItemCollection;
    index: number;
    placement: MapObjectPlacement;
}

interface ActivePlanMove {
    type: PlanMoveType;
    index: number;
    id: string;
    moved: boolean;
    originalX?: number;
    originalZ?: number;
}

interface SceneryPathAnchor {
    id: string;
    distance: number;
    lateralOffset: number;
    forwardOffset: number;
    relativeRotation: number;
    originalX: number;
    originalZ: number;
    originalRotation: number;
}

interface ActiveEditorPan {
    pointerId: number;
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
    moved: boolean;
}

interface EditorDropShadow {
    kind: EditorPaletteKind;
    icon: string;
    label: string;
    left: number;
    top: number;
    valid: boolean;
}

interface EditorHistoryEntry {
    map: GameMap;
    terrainDraft: TerrainGrid | null;
    terrainDraftDirty: boolean;
    label: string;
}

@Component({
    selector: 'wiz-page-quiz-runner3d',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.quiz.runner3d/view.scss */
:host {
  display: block;
  height: 100%;
  background: #020617;
}

.game-shell {
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  isolation: isolate;
}

.game-shell .editor-3d-host {
  left: 470px;
}

.editor-3d-viewport {
  cursor: grab;
  background: linear-gradient(to bottom, rgba(2, 6, 23, 0.04), rgba(2, 6, 23, 0.2));
}

.editor-3d-viewport:active {
  cursor: grabbing;
}

.game-ambient {
  background: radial-gradient(circle at 50% 28%, rgba(34, 211, 238, 0.09), transparent 35%), radial-gradient(circle at 12% 72%, rgba(124, 58, 237, 0.11), transparent 32%), linear-gradient(to bottom, rgba(2, 6, 23, 0.45), transparent 43%, rgba(2, 6, 23, 0.86));
}

.game-ambient::before,
.game-ambient::after {
  position: absolute;
  content: "";
  pointer-events: none;
}

.game-ambient::before {
  inset: 0;
  border: 1px solid rgba(255, 255, 255, 0.035);
  box-shadow: inset 0 0 110px rgba(2, 6, 23, 0.42);
}

.game-ambient::after {
  right: 8%;
  bottom: 12%;
  width: min(38vw, 540px);
  height: min(38vw, 540px);
  border-radius: 50%;
  background: rgba(34, 211, 238, 0.035);
  filter: blur(70px);
}

.game-header-action,
.map-title-pill,
.hud-card,
.runner-status,
.mobile-control-button,
.quiz-card {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 12px 34px rgba(2, 6, 23, 0.32);
}

.game-header-action {
  min-height: 40px;
  white-space: nowrap;
}

.game-header-action.icon-only {
  display: inline-flex;
  width: 40px;
  min-width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.pause-icon {
  display: inline-flex;
  width: 16px;
  height: 18px;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.pause-icon::before,
.pause-icon::after {
  display: block;
  width: 4px;
  height: 16px;
  border-radius: 1.5px;
  background: currentColor;
  content: "";
}

.hud-card {
  position: relative;
  overflow: hidden;
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.78), rgba(2, 6, 23, 0.56)), rgba(2, 6, 23, 0.58);
}

.hud-card::after {
  position: absolute;
  top: 0;
  right: 14%;
  left: 14%;
  height: 1px;
  content: "";
  background: linear-gradient(90deg, transparent, rgba(103, 232, 249, 0.55), transparent);
}

.mobile-controls {
  bottom: max(12px, env(safe-area-inset-bottom));
  padding-right: max(14px, env(safe-area-inset-right));
  padding-left: max(14px, env(safe-area-inset-left));
}

.desktop-control-hint {
  display: none;
}

.camera-adjust-active {
  cursor: grab;
}

.camera-adjust-dragging {
  cursor: grabbing;
}

.game-minimap {
  top: 220px;
  right: auto;
  left: max(18px, env(safe-area-inset-left));
  width: 184px;
  pointer-events: auto;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 18px 42px rgba(2, 6, 23, 0.48), 0 0 28px rgba(34, 211, 238, 0.08);
}

.minimap-viewport {
  height: 142px;
  overflow: hidden;
  background: radial-gradient(circle at 50% 48%, rgba(34, 211, 238, 0.1), transparent 62%), #07111f;
}

.minimap-viewport svg {
  display: block;
  width: 100%;
  height: 100%;
}

.minimap-control,
.minimap-zoom-label {
  display: inline-flex;
  min-width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  color: #cffafe;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
  transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;
}

.minimap-zoom-label {
  min-width: 34px;
  padding: 0 5px;
  font-size: 8px;
}

.minimap-control:hover,
.minimap-zoom-label:hover {
  border-color: rgba(103, 232, 249, 0.55);
  background: rgba(34, 211, 238, 0.16);
}

.minimap-control:active,
.minimap-zoom-label:active {
  transform: scale(0.94);
}

.minimap-control:disabled {
  opacity: 0.3;
}

.minimap-player-marker {
  filter: drop-shadow(0 0 3px rgba(34, 211, 238, 0.9));
}

.mobile-control-button {
  position: relative;
  overflow: hidden;
  touch-action: none;
  background: linear-gradient(145deg, rgba(30, 41, 59, 0.86), rgba(2, 6, 23, 0.72));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14), inset 0 -10px 24px rgba(2, 6, 23, 0.24), 0 10px 30px rgba(2, 6, 23, 0.42);
}

.mobile-control-button:active {
  transform: translateY(1px) scale(0.96);
  border-color: rgba(103, 232, 249, 0.65);
  background: linear-gradient(145deg, rgba(14, 116, 144, 0.72), rgba(15, 23, 42, 0.82));
}

.jump-control-button {
  background: radial-gradient(circle at 35% 28%, rgba(255, 255, 255, 0.18), transparent 28%), linear-gradient(145deg, rgba(8, 145, 178, 0.78), rgba(30, 64, 175, 0.7));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.24), 0 0 28px rgba(34, 211, 238, 0.2), 0 12px 34px rgba(2, 6, 23, 0.44);
}

.mode-choice-card {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.09), 0 20px 45px rgba(2, 6, 23, 0.28);
}

.explore-dpad {
  touch-action: none;
}

.quiz-overlay {
  padding-top: max(24px, env(safe-area-inset-top));
  padding-right: max(16px, env(safe-area-inset-right));
  padding-bottom: max(24px, env(safe-area-inset-bottom));
  padding-left: max(16px, env(safe-area-inset-left));
}

.quiz-card {
  background: radial-gradient(circle at 85% 0, rgba(124, 58, 237, 0.18), transparent 32%), linear-gradient(150deg, rgba(30, 41, 59, 0.98), rgba(9, 14, 29, 0.98));
}

button {
  touch-action: manipulation;
}

.plan-grid {
  background-color: #14241c;
  background-image: radial-gradient(circle at 18% 14%, rgba(74, 222, 128, 0.08), transparent 28%), linear-gradient(rgba(110, 231, 183, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(110, 231, 183, 0.08) 1px, transparent 1px), linear-gradient(rgba(148, 163, 184, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.035) 1px, transparent 1px);
  background-size: auto, 10% 10%, 10% 10%, 2% 2%, 2% 2%;
}

.plan-grid[data-map-theme=neon] {
  background-color: #07101f;
  background-image: radial-gradient(circle at 18% 14%, rgba(34, 211, 238, 0.1), transparent 28%), linear-gradient(rgba(56, 189, 248, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(56, 189, 248, 0.08) 1px, transparent 1px), linear-gradient(rgba(148, 163, 184, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.035) 1px, transparent 1px);
  background-size: auto, 10% 10%, 10% 10%, 2% 2%, 2% 2%;
}

.plan-grid[data-map-theme=beach] {
  background-color: #453c2d;
}

.map-viewport {
  overscroll-behavior: none;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.map-viewport::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.character-preview {
  position: relative;
  width: 220px;
  height: 330px;
  overflow: hidden;
  border: 1px solid rgba(167, 139, 250, 0.3);
  border-radius: 28px;
  background: radial-gradient(circle at 50% 38%, rgba(34, 211, 238, 0.16), transparent 42%), linear-gradient(160deg, #111827, #020617);
  box-shadow: inset 0 0 34px rgba(34, 211, 238, 0.08), 0 16px 36px rgba(2, 6, 23, 0.38);
  cursor: grab;
  perspective: 760px;
  touch-action: none;
}

.character-preview.is-dragging {
  cursor: grabbing;
}

.character-preview-floor {
  position: absolute;
  right: 24px;
  bottom: 10px;
  left: 24px;
  height: 30px;
  transform: rotateX(68deg);
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(34, 211, 238, 0.3), rgba(37, 99, 235, 0.08) 58%, transparent 72%);
  filter: blur(1px);
}

.character-preview-turntable {
  position: absolute;
  bottom: 13px;
  left: 50%;
  width: 160px;
  height: 292px;
  transform-style: preserve-3d;
  transform-origin: 50% 54%;
  transition: transform 150ms ease-out;
  will-change: transform;
}

.character-preview.is-dragging .character-preview-turntable {
  transition: none;
}

.character-preview-face {
  position: absolute;
  z-index: 4;
  top: 46px;
  left: 50%;
  width: 104px;
  height: 112px;
  transform: translateX(-50%) translateZ(13px);
  backface-visibility: hidden;
  border: 3px solid rgba(255, 255, 255, 0.32);
  border-radius: 46% 46% 43% 43%;
  background: linear-gradient(155deg, #fde68a, #fda4af);
  box-shadow: 0 0 28px rgba(251, 191, 36, 0.18);
  transition: opacity 120ms ease-out;
}

.character-preview-hair {
  position: absolute;
  z-index: 6;
  top: 26px;
  left: 50%;
  transform: translateX(-50%) translateZ(17px);
  backface-visibility: hidden;
  background: linear-gradient(150deg, #273034, #0f1416);
  filter: drop-shadow(0 5px 9px rgba(15, 20, 22, 0.45));
  transition: opacity 120ms ease-out;
}

.character-preview-head-back {
  position: absolute;
  z-index: 3;
  top: 46px;
  left: 50%;
  width: 104px;
  height: 112px;
  transform: translateX(-50%) translateZ(-8px);
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-radius: 46% 46% 43% 43%;
  background: linear-gradient(155deg, #eebf8e, #dc8f80);
  opacity: 0;
  transition: opacity 120ms ease-out;
}

.character-preview-head-back::before {
  position: absolute;
  top: -24px;
  left: -5px;
  width: 108px;
  height: 70px;
  border-radius: 58% 58% 24% 24%;
  background: linear-gradient(145deg, #273034, #0f1416);
  content: "";
}

.character-preview-turntable.outfit-joseon-general .character-preview-head-back::before {
  border: 3px solid rgba(251, 191, 36, 0.55);
  background: linear-gradient(145deg, #a63831, #581c18);
}

.character-preview-turntable.outfit-joseon-general .character-preview-head-back::after {
  position: absolute;
  top: -54px;
  left: 43px;
  width: 12px;
  height: 38px;
  border-radius: 8px 8px 0 0;
  background: #dc2626;
  content: "";
}

.character-preview-body {
  position: absolute;
  z-index: 2;
  top: 145px;
  left: 50%;
  width: 90px;
  height: 79px;
  transform: translateX(-50%) translateZ(2px);
  border: 2px solid rgba(255, 255, 255, 0.18);
  border-radius: 22px 22px 12px 12px;
  background: linear-gradient(145deg, #74b7c7, #31576e);
  box-shadow: inset 0 0 18px rgba(255, 255, 255, 0.08);
}

.character-preview-body::after {
  position: absolute;
  top: 4px;
  left: 50%;
  width: 18px;
  height: 58px;
  transform: translateX(-50%);
  border-right: 5px solid rgba(248, 250, 252, 0.78);
  border-left: 5px solid rgba(248, 250, 252, 0.78);
  content: "";
}

.character-preview-belt {
  position: absolute;
  z-index: 4;
  top: 216px;
  left: 50%;
  width: 104px;
  height: 13px;
  transform: translateX(-50%) translateZ(8px);
  border-radius: 6px;
  background: linear-gradient(90deg, #8b5e2e, #f3c969, #8b5e2e);
}

.character-preview-skirt {
  position: absolute;
  z-index: 1;
  top: 219px;
  left: 50%;
  width: 118px;
  height: 64px;
  transform: translateX(-50%);
  clip-path: polygon(14% 0, 86% 0, 100% 100%, 0 100%);
  background: linear-gradient(90deg, #233d50, #497b91 48%, #233d50);
  box-shadow: inset 0 -12px 20px rgba(2, 6, 23, 0.24);
}

.character-preview-arm,
.character-preview-leg,
.character-preview-shoe {
  position: absolute;
  display: block;
  backface-visibility: visible;
}

.character-preview-arm {
  z-index: 1;
  top: 151px;
  width: 27px;
  height: 87px;
  border-radius: 18px;
  background: linear-gradient(90deg, #294b62, #6da3b6);
}

.character-preview-arm.arm-left {
  left: 15px;
  transform: rotate(8deg);
}

.character-preview-arm.arm-right {
  right: 15px;
  transform: rotate(-8deg);
}

.character-preview-leg {
  z-index: 0;
  top: 265px;
  width: 25px;
  height: 23px;
  border-radius: 0 0 9px 9px;
  background: #17202b;
}

.character-preview-leg.leg-left {
  left: 48px;
}

.character-preview-leg.leg-right {
  right: 48px;
}

.character-preview-shoe {
  z-index: 3;
  top: 282px;
  width: 36px;
  height: 11px;
  border-radius: 55% 55% 30% 30%;
  background: #101517;
}

.character-preview-shoe.shoe-left {
  left: 40px;
}

.character-preview-shoe.shoe-right {
  right: 40px;
}

.character-preview-cape {
  position: absolute;
  z-index: 0;
  top: 143px;
  left: 50%;
  display: none;
  width: 108px;
  height: 121px;
  transform: translateX(-50%) translateZ(-9px);
  clip-path: polygon(12% 0, 88% 0, 100% 100%, 0 100%);
  background: linear-gradient(90deg, #4c1518, #991b1b, #4c1518);
}

.character-preview-armor {
  position: absolute;
  z-index: 5;
  top: 156px;
  left: 50%;
  display: none;
  grid-template-columns: repeat(5, 14px);
  gap: 3px;
  width: 82px;
  transform: translateX(-50%) translateZ(11px);
}

.character-preview-armor span {
  height: 13px;
  border: 1px solid rgba(251, 191, 36, 0.48);
  border-radius: 2px;
  background: linear-gradient(145deg, #9a3b32, #3f2529);
}

.character-preview-turntable.outfit-joseon-farmer .character-preview-body,
.character-preview-turntable.outfit-joseon-farmer .character-preview-arm {
  background: linear-gradient(145deg, #968361, #4d5e3e);
}

.character-preview-turntable.outfit-joseon-king .character-preview-body,
.character-preview-turntable.outfit-joseon-king .character-preview-arm,
.character-preview-turntable.outfit-joseon-king .character-preview-skirt {
  background: linear-gradient(145deg, #b52e35, #67181d);
}

.character-preview-turntable.outfit-joseon-woman .character-preview-body,
.character-preview-turntable.outfit-joseon-woman .character-preview-arm {
  background: linear-gradient(145deg, #f4eee3, #d5bda4);
}

.character-preview-turntable.outfit-joseon-woman .character-preview-skirt {
  width: 138px;
  background: linear-gradient(90deg, #518b71, #a1d7b9, #518b71);
}

.character-preview-turntable.outfit-joseon-gisaeng .character-preview-body,
.character-preview-turntable.outfit-joseon-gisaeng .character-preview-arm {
  background: linear-gradient(145deg, #f2d4b1, #d59a78);
}

.character-preview-turntable.outfit-joseon-gisaeng .character-preview-skirt {
  width: 138px;
  background: linear-gradient(90deg, #a42f50, #e76886, #a42f50);
}

.character-preview-turntable.outfit-joseon-guard .character-preview-body,
.character-preview-turntable.outfit-joseon-guard .character-preview-arm {
  background: linear-gradient(145deg, #526773, #263740);
}

.character-preview-turntable.outfit-joseon-general .character-preview-body,
.character-preview-turntable.outfit-joseon-general .character-preview-arm {
  background: linear-gradient(145deg, #6f2a27, #291c26);
}

.character-preview-turntable.outfit-joseon-general .character-preview-skirt {
  background: linear-gradient(90deg, #17293b, #314862, #17293b);
}

.character-preview-turntable.outfit-joseon-general .character-preview-cape {
  display: block;
}

.character-preview-turntable.outfit-joseon-general .character-preview-armor {
  display: grid;
}

.character-preview-turntable.is-back-facing .character-preview-face,
.character-preview-turntable.is-back-facing .character-preview-hair,
.character-preview-turntable.is-back-facing .character-preview-armor,
.character-preview-turntable.is-back-facing .character-preview-body::after {
  opacity: 0;
}

.character-preview-turntable.is-back-facing .character-preview-head-back {
  opacity: 1;
}

.character-preview-hair.hair-sangtu {
  top: 24px;
  width: 34px;
  height: 38px;
  border-radius: 52% 52% 42% 42%;
}

.character-preview-hair.hair-gat {
  top: 16px;
  width: 126px;
  height: 12px;
  border: 2px solid rgba(148, 163, 184, 0.3);
  border-radius: 50%;
  background: rgba(15, 20, 22, 0.92);
}

.character-preview-hair.hair-gat::after {
  position: absolute;
  bottom: 4px;
  left: 50%;
  width: 52px;
  height: 50px;
  transform: translateX(-50%);
  border: 2px solid rgba(148, 163, 184, 0.28);
  border-radius: 12px 12px 5px 5px;
  background: rgba(20, 27, 30, 0.88);
  content: "";
}

.character-preview-hair.hair-royal-crown {
  top: 17px;
  width: 58px;
  height: 47px;
  border: 2px solid rgba(251, 191, 36, 0.32);
  border-radius: 8px 8px 3px 3px;
}

.character-preview-hair.hair-royal-crown::before,
.character-preview-hair.hair-royal-crown::after {
  position: absolute;
  top: 9px;
  width: 52px;
  height: 11px;
  border-radius: 6px;
  background: #182023;
  content: "";
}

.character-preview-hair.hair-royal-crown::before {
  right: 44px;
  transform: rotate(8deg);
}

.character-preview-hair.hair-royal-crown::after {
  left: 44px;
  transform: rotate(-8deg);
}

.character-preview-hair.hair-headscarf {
  z-index: 1;
  top: 30px;
  width: 112px;
  height: 62px;
  border-radius: 55% 55% 28% 28%;
  background: linear-gradient(150deg, #786044, #4d3a29);
}

.character-preview-hair.hair-binyeo {
  top: 22px;
  width: 56px;
  height: 42px;
  border-radius: 52% 52% 45% 45%;
}

.character-preview-hair.hair-binyeo::after {
  position: absolute;
  top: 15px;
  left: -24px;
  width: 104px;
  height: 5px;
  border-radius: 5px;
  background: linear-gradient(90deg, #b7791f, #fde68a, #b7791f);
  content: "";
}

.character-preview-hair.hair-jokduri {
  top: 16px;
  width: 54px;
  height: 46px;
  border: 3px solid #d4a73c;
  border-radius: 45% 45% 16% 16%;
  background: linear-gradient(145deg, #262b2d, #111827);
}

.character-preview-hair.hair-jokduri::after {
  position: absolute;
  top: 9px;
  left: 50%;
  width: 10px;
  height: 10px;
  transform: translateX(-50%);
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: -15px 9px 0 #fb7185, 15px 9px 0 #34d399;
  content: "";
}

.character-preview-hair.hair-warrior-helmet,
.character-preview-hair.hair-general-helmet {
  z-index: 1;
  top: 28px;
  width: 112px;
  height: 68px;
  border: 3px solid rgba(226, 232, 240, 0.32);
  border-radius: 58% 58% 24% 24%;
  background: linear-gradient(145deg, #475569, #1f2937);
}

.character-preview-hair.hair-warrior-helmet::after,
.character-preview-hair.hair-general-helmet::after {
  position: absolute;
  bottom: 0;
  left: -10px;
  width: 132px;
  height: 10px;
  border-radius: 50%;
  background: #27343a;
  content: "";
}

.character-preview-hair.hair-general-helmet {
  border-color: rgba(251, 191, 36, 0.55);
  background: linear-gradient(145deg, #a63831, #581c18);
}

.character-preview-hair.hair-general-helmet::before {
  position: absolute;
  top: -28px;
  left: 50%;
  width: 12px;
  height: 38px;
  transform: translateX(-50%);
  border-radius: 8px 8px 0 0;
  background: #dc2626;
  content: "";
}

.character-preview-eyes {
  position: absolute;
  top: 39px;
  left: 18px;
  right: 18px;
  display: flex;
  justify-content: space-between;
}

.character-preview-eye {
  display: block;
  width: 18px;
  height: 12px;
  background: #0f172a;
}

.character-preview-eye.eyes-calm {
  height: 5px;
  margin-top: 5px;
  border-radius: 8px;
  box-shadow: 0 0 8px rgba(15, 23, 42, 0.35);
}

.character-preview-eye.eyes-bright {
  width: 17px;
  height: 17px;
  border: 4px solid #0f172a;
  border-radius: 50%;
  background: #f8fafc;
}

.character-preview-eye.eyes-dignified {
  height: 7px;
  margin-top: 4px;
  border-radius: 2px 8px 4px 4px;
  transform: rotate(-5deg);
  box-shadow: 0 -7px 0 -1px #334155;
}

.character-preview-nose {
  position: absolute;
  top: 61px;
  left: 50%;
  display: block;
  transform: translateX(-50%);
  background: #fb7185;
}

.character-preview-nose.nose-soft {
  width: 13px;
  height: 13px;
  border-radius: 50%;
}

.character-preview-nose.nose-straight {
  width: 8px;
  height: 21px;
  border-radius: 5px 5px 8px 8px;
}

.character-preview-nose.nose-round {
  width: 18px;
  height: 14px;
  border-radius: 50%;
}

.character-preview-mouth {
  position: absolute;
  top: 84px;
  left: 50%;
  display: block;
  transform: translateX(-50%);
}

.character-preview-mouth.mouth-gentle {
  width: 28px;
  height: 4px;
  border-radius: 4px;
  background: #9f1239;
}

.character-preview-mouth.mouth-smile {
  width: 32px;
  height: 16px;
  border-bottom: 5px solid #9f1239;
  border-radius: 0 0 24px 24px;
}

.character-preview-mouth.mouth-firm {
  width: 35px;
  height: 4px;
  border-radius: 2px;
  background: #881337;
}

@media (max-width: 767px) {
  .game-shell .editor-3d-host {
    top: 52%;
    left: 0;
  }
  .game-header {
    padding-top: max(10px, env(safe-area-inset-top));
    padding-right: max(10px, env(safe-area-inset-right));
    padding-left: max(10px, env(safe-area-inset-left));
  }
  .game-header-action {
    display: inline-flex;
    width: 40px;
    min-width: 40px;
    height: 40px;
    min-height: 40px;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
  .game-header .header-copy {
    display: none;
  }
  .game-header-tools {
    gap: 6px;
  }
  .map-title-pill {
    max-width: min(34vw, 132px);
    height: 40px;
    padding: 0 11px;
    line-height: 40px;
  }
  .game-hud {
    top: calc(max(10px, env(safe-area-inset-top)) + 49px);
  }
  .hud-card {
    min-height: 49px;
    padding: 8px 12px;
    border-radius: 14px;
  }
  .hud-card p:first-child {
    font-size: 8px;
    line-height: 10px;
  }
  .hud-card p.mt-1 {
    margin-top: 2px;
    font-size: 16px;
    line-height: 20px;
  }
  .runner-status {
    overflow: hidden;
    margin-top: 6px;
    padding: 6px 12px;
    font-size: 10px;
    line-height: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .game-minimap {
    top: calc(max(10px, env(safe-area-inset-top)) + 174px);
    right: auto;
    left: max(8px, env(safe-area-inset-left));
    width: 134px;
    border-radius: 15px;
  }
  .minimap-viewport {
    height: 98px;
  }
  .minimap-control,
  .minimap-zoom-label {
    min-width: 22px;
    height: 22px;
  }
  .minimap-zoom-label {
    min-width: 30px;
  }
  .quiz-card {
    max-height: calc(100dvh - max(48px, env(safe-area-inset-top) + env(safe-area-inset-bottom)));
    overflow-y: auto;
    border-radius: 24px;
  }
}
@media (max-width: 350px) {
  .map-title-pill {
    max-width: 84px;
  }
  .mobile-controls {
    padding-right: max(10px, env(safe-area-inset-right));
    padding-left: max(10px, env(safe-area-inset-left));
  }
  .mobile-controls > div {
    gap: 8px;
  }
}
@media (max-width: 767px) and (max-height: 620px) {
  .mobile-control-button {
    width: 56px;
    height: 56px;
    border-radius: 17px;
  }
  .jump-control-button {
    width: 68px;
    height: 68px;
    border-radius: 50%;
  }
  .hud-card {
    min-height: 44px;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  .runner-status {
    display: none;
  }
  .game-minimap {
    top: 154px;
  }
}
@media (max-height: 500px) and (orientation: landscape) {
  .game-hud {
    right: auto;
    width: min(48vw, 420px);
  }
  .hud-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
  .hud-card {
    min-height: 42px;
    padding: 6px 8px;
  }
  .mobile-controls {
    bottom: max(8px, env(safe-area-inset-bottom));
  }
  .mobile-control-button {
    width: 54px;
    height: 54px;
    border-radius: 16px;
  }
  .jump-control-button {
    width: 66px;
    height: 66px;
    border-radius: 50%;
  }
  .game-minimap {
    top: 60px;
    right: auto;
    left: max(8px, env(safe-area-inset-left));
    width: 132px;
  }
  .minimap-viewport {
    height: 86px;
  }
}
@media (min-width: 768px) and (hover: hover) and (pointer: fine) {
  .mobile-controls {
    display: none !important;
  }
  .desktop-control-hint {
    display: flex;
  }
}
@media (prefers-reduced-motion: reduce) {
  * {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}`],
})
export class PageQuizRunner3dComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('gameHost') public gameHost?: ElementRef<HTMLDivElement>;
    @ViewChild('mapCanvas') public mapCanvas?: ElementRef<HTMLDivElement>;
    @ViewChild('mapSurface') public mapSurface?: ElementRef<HTMLDivElement>;
    @ViewChild('editor3dViewport') public editor3dViewport?: ElementRef<HTMLDivElement>;
    @ViewChild('terrainCanvas') public terrainCanvas?: ElementRef<HTMLCanvasElement>;
    @ViewChild('customizationPreviewHost') public customizationPreviewHost?: ElementRef<HTMLDivElement>;
    @ViewChild('nfcPreviewHost') public nfcPreviewHost?: ElementRef<HTMLDivElement>;

    public screen: GameScreen = 'menu';
    public gameMode: GameMode = 'runner';
    public worldReady: boolean = false;
    public loadError: string = '';
    public coinCount: number = 0;
    public totalCoins: number = 0;
    public progressPercent: number = 0;
    public gatesPassed: number = 0;
    public correctCount: number = 0;
    public statusText: string = '게임 시작을 준비하고 있습니다.';
    public activeGateName: string = '';
    public activeQuestion: QuizQuestion | null = null;
    public quizSelectedAnswer: boolean | null = null;
    public lastAnswerCorrect: boolean = false;
    public quizFeedback: string = '';
    public answerRecords: AnswerRecord[] = [];
    public activeQuizKind: 'gate' | 'hiddenNpc' = 'gate';
    public hiddenNpcScore: number = 0;
    public hiddenNpcFound: number = 0;
    public hiddenNpcReward: number = 0;
    public hiddenNpcAnswerRecords: HiddenNpcAnswerRecord[] = [];
    public obstacleCollisionCount: number = 0;
    public runTime: number = 0;
    public exploreCameraMode: ExploreCameraMode = 'follow';
    public exploreCameraAdjustMode: boolean = false;
    public exploreCameraPitchOffset: number = 0;
    public minimapZoom: number = 4;
    public minimapPathPoints: string = '';
    public minimapGateMarkers: MinimapGateMarker[] = [];
    public minimapPlayerX: number = 0;
    public minimapPlayerZ: number = 0;
    public minimapPlayerRotation: number = 0;
    public minimapStartX: number = 0;
    public minimapStartZ: number = 0;
    public minimapFinishX: number = 0;
    public minimapFinishZ: number = 0;
    public harborEnvironmentShipCount: number = 0;
    public harborEnvironmentPierCount: number = 0;

    public readonly defaultMap: GameMap = createYsPalaceCourseMap();
    public savedMaps: GameMap[] = [];
    public serverMaps: GameMap[] = [];
    public serverMapStatus: string = '서버 맵은 맵 보관함을 열 때 동기화됩니다.';
    public serverSyncBusy: boolean = false;
    public activeMap: GameMap = cloneMap(this.defaultMap);
    public editorMap: GameMap | null = null;
    public editorTab: EditorTab = 'settings';
    public selectedWaypointIndex: number = 0;
    public selectedCoinIndex: number = 0;
    public selectedMagnetIndex: number = 0;
    public selectedSupplementIndex: number = 0;
    public selectedItemKind: ItemKind = 'coin';
    public selectedMountIndex: number = 0;
    public selectedObstacleIndex: number = 0;
    public selectedGateIndex: number = 0;
    public selectedSceneryIndex: number = 0;
    public selectedQuestionIndex: number = 0;
    public editorMessage: string = '';
    public importMessage: string = '';
    public isEditorTest: boolean = false;
    public editorViewMode: EditorViewMode = '2d';
    public editor3dGhostLabel: string = '';
    public editor3dGhostValid: boolean = false;
    public editorDragActive: boolean = false;
    public editorPanActive: boolean = false;
    public selectedPaletteKind: EditorPaletteKind | null = null;
    public editorDropShadow: EditorDropShadow | null = null;
    public editorZoom: number = 1;
    public editorPixelsPerDistance: number = 0.9;
    public selectedTerrainKind: TerrainKind = 'grass';
    public terrainBrushSize: number = 2;
    public terrainDraft: TerrainGrid | null = null;
    public terrainDraftDirty: boolean = false;
    public terrainUndoCells: TerrainKind[] | null = null;
    public groundImageBusy: boolean = false;
    public groundImageStatus: string = 'JPG, PNG, WEBP 이미지를 올리면 전체 맵 바닥에 맞춰 적용합니다.';
    public readonly terrainPalette: TerrainPaletteItem[] = [
        { kind: 'grass', label: '풀밭', color: '#3f6b45', description: '나무와 건물을 놓기 좋은 초지' },
        { kind: 'earth', label: '흙', color: '#75563c', description: '마당·농촌·비포장 지면' },
        { kind: 'stone', label: '돌바닥', color: '#77756f', description: '포구·마을·광장용 석재 바닥' },
        { kind: 'sand', label: '모래', color: '#d5b779', description: '해변과 모래사장' },
        { kind: 'water', label: '물', color: '#1887ae', description: '바다·강·연못 수면' },
        { kind: 'mountain', label: '산지', color: '#344b36', description: '능선과 바위가 있는 산 영역' }
    ];
    public circleLapCount: number = 1;
    public readonly circleLapOptions: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    public readonly objectPalette: EditorPaletteItem[] = [
        { kind: 'item', icon: '🎁', label: '아이템', hint: '배치 후 상세에서 코인·자석·영양제를 선택합니다.' },
        { kind: 'mount', icon: '🐎', label: '탈것', hint: '배치 후 상세에서 랜덤·가마·동물·전통 함선을 선택합니다.' },
        { kind: 'obstacle', icon: '▲', label: '장애물', hint: '충돌 장애물' },
        { kind: 'gate', icon: '◇', label: '게이트', hint: 'OX 퀴즈 게이트' }
    ];
    public readonly roadPalette: EditorPaletteItem[] = [
        { kind: 'straight', icon: '┃', label: '직선', hint: '곧은 길' },
        { kind: 'curve-left', icon: '╮', label: '왼쪽 곡선', hint: '왼쪽으로 휘는 길' },
        { kind: 'curve-right', icon: '╭', label: '오른쪽 곡선', hint: '오른쪽으로 휘는 길' },
        { kind: 's-left', icon: '∿', label: '왼쪽 S자', hint: '왼쪽부터 꺾이는 S길' },
        { kind: 's-right', icon: '∽', label: '오른쪽 S자', hint: '오른쪽부터 꺾이는 S길' },
        { kind: 'slope-up', icon: '↗', label: '오르막', hint: '높아지는 길' },
        { kind: 'slope-down', icon: '↘', label: '내리막', hint: '낮아지는 길' },
        { kind: 'loop-one', icon: '↻', label: '수직 1회전', hint: '롤러코스터처럼 위로 한 바퀴 회전하는 길' },
        { kind: 'loop-two', icon: '↻↻', label: '수직 2회전', hint: '수직 루프 두 개를 연속으로 도는 길' },
        { kind: 'jump-wide', icon: '▰ ▰', label: '넓은 징검발판', hint: '넓은 독립 발판 4개 · 짧은 3.2m 간격 · 발판 밖 추락' },
        { kind: 'jump-stairs', icon: '▰▰▰', label: '완만한 계단 발판', hint: '0.75m씩 오르내리는 넓은 독립 발판 5개 · 쉬움' },
        { kind: 'jump-zigzag', icon: '▰↝▰', label: '넓은 지그재그 발판', hint: '좌우 2.6m로 엇갈린 넓은 독립 발판 5개 · 쉬움' }
    ];
    public readonly sceneryPalette: EditorPaletteItem[] = [
        { kind: 'tree', icon: '🌲', label: '나무', hint: '코스 주변 장식 나무' },
        { kind: 'pine-tree', icon: '🌲', label: '곧은 소나무', hint: '곧게 자란 한국 소나무 FBX 장식' },
        { kind: 'pine-tree-windswept', icon: '🌲', label: '바람소나무', hint: '바람을 맞아 한쪽으로 굽은 소나무 FBX 장식' },
        { kind: 'pine-tree-young', icon: '🌱', label: '어린 소나무', hint: '낮고 풍성하게 자란 어린 소나무 FBX 장식' },
        { kind: 'building', icon: '🏢', label: '건물', hint: '코스 주변 네온 건물' },
        { kind: 'cone', icon: '🚧', label: '안전 원뿔', hint: '원하는 위치에 직접 놓는 코스 장식' },
        { kind: 'hanok', icon: '🏠', label: '기와집', hint: '기와와 목조 기둥을 갖춘 전통 가옥' },
        { kind: 'hanok-annex', icon: '🏡', label: '사랑채', hint: '툇마루와 방문이 긴 전통 사랑채' },
        { kind: 'hanok-pavilion', icon: '🏯', label: '팔각 정자', hint: '누마루와 기와지붕이 있는 전통 정자' },
        { kind: 'hanok-palace', icon: '🏰', label: '궁궐 전각', hint: '이중 월대·난간·석등을 갖춘 고급 전각' },
        { kind: 'hanok-courtyard', icon: '🏘️', label: '한옥 마당', hint: '본채·사랑채·담장·장독대를 묶은 한옥 단지' },
        { kind: 'hanok-library', icon: '🏯', label: '이층 누각', hint: '아래층 기둥과 위층 서고가 결합된 궁원 누각' },
        { kind: 'hanok-study', icon: '🛖', label: '선비 서재', hint: '쪽마루·책장·아궁이를 갖춘 작은 기와집' },
        { kind: 'lotus-pond', icon: '🪷', label: '연꽃 연못', hint: '자연석 호안과 수련·디딤돌을 갖춘 궁원 연못' },
        { kind: 'lotus-flower', icon: '🪷', label: '연꽃', hint: '연못 수면에 놓는 연잎·꽃송이 FBX 장식' },
        { kind: 'pond-rock', icon: '🪨', label: '연못 돌', hint: '연못 가장자리와 물가에 놓는 자연석 FBX 장식' },
        { kind: 'stone-bridge', icon: '🌉', label: '돌다리', hint: '연못과 계류에 놓는 낮은 홍예식 석교' },
        { kind: 'stone-stairs', icon: '🪨', label: '돌계단', hint: '단마다 직접 오를 수 있는 전통 석재 FBX 계단' },
        { kind: 'stone-wall', icon: '🧱', label: '돌담벽', hint: '엇갈린 줄눈과 갓돌을 갖춘 전통 석재 FBX 담장' },
        { kind: 'jangdok', icon: '🏺', label: '장독대', hint: '옹기 항아리가 모인 전통 생활 장식' },
        { kind: 'stone-lantern', icon: '🏮', label: '돌등', hint: '마당과 산길에 어울리는 석등' },
        { kind: 'wooden-fence', icon: '🪵', label: '전통 목책', hint: '기와집 주변 경계를 만드는 나무 울타리' },
        { kind: 'harbor-warehouse', icon: '🛖', label: '포구 창고', hint: '조선 나루터의 목조 물산 창고' },
        { kind: 'fishing-boat', icon: '⛵', label: '돛단 어선', hint: '항구 수면에 놓는 전통 어선' },
        { kind: 'kim-yeeun-old-shrine-naejaesil', icon: '🏯', label: '구선원전 내재실', hint: '김예은 원본을 서버 GLB로 등록한 기본 장식' },
        { kind: 'kim-yeeun-gyeonghoeru', icon: '🏯', label: '경회루', hint: '김예은 원본을 서버 GLB로 등록한 기본 장식' },
        { kind: 'kim-yeeun-base', icon: '🧱', label: '궁궐 석재 기단', hint: '김예은 원본을 서버 GLB로 등록한 기본 장식' },
        { kind: 'kim-yeeun-stairs', icon: '🪨', label: '궁궐 석계단', hint: '김예은 원본을 서버 GLB로 등록한 기본 장식' },
        { kind: 'kim-yeeun-stone-path-small2', icon: '🛤️', label: '궁궐 돌길', hint: '김예은 원본을 서버 GLB로 등록한 기본 장식' },
        { kind: 'kim-yeeun-old-shrine-south-haenggak', icon: '🏘️', label: '구선원전 남행각', hint: '김예은 원본을 서버 GLB로 등록한 기본 장식' },
        { kind: 'kim-yeeun-gwollaegaksa-annex', icon: '🏡', label: '궐내각사 검서청 부속채', hint: '김예은 원본을 서버 GLB로 등록한 기본 장식' },
        { kind: 'kim-yeeun-stone-block', icon: '🪨', label: '궁궐 돌석', hint: '김예은 원본을 서버 GLB로 등록한 기본 장식' },
        { kind: 'kim-yeeun-stone-floor', icon: '▦', label: '궁궐 석재 바닥', hint: '김예은 원본을 서버 GLB로 등록한 기본 장식' },
        { kind: 'haechi-guide', icon: '🦁', label: '서울 해치 안내자', hint: '건물 앞에 놓고 설명을 연결하는 서울 상징 캐릭터' }
    ];
    public readonly ysDecorationCategories = YS_DECORATION_CATEGORIES;
    public readonly ysDecorationPresets = YS_DECORATION_PRESETS;
    public selectedYsDecorationCategory: YsDecorationCategory | 'all' = 'all';
    public selectedYsDecorationId: string = YS_DECORATION_PRESETS[0]?.id || '';
    public ysDecorationStatus: string = `Meshopt 압축 GLB ${YS_DECORATION_PRESETS.length}개를 필요할 때만 불러옵니다.`;
    public readonly themeOptions: Array<{ id: MapTheme; label: string; icon: string; description: string }> = [
        { id: 'neon', label: '네온 시티', icon: '✦', description: '야간 도시와 네온 조명' },
        { id: 'mountain', label: '산길', icon: '▲', description: '소나무와 능선이 보이는 숲길' },
        { id: 'coast', label: '바다길', icon: '≈', description: '해안 절벽과 바다를 따라가는 길' },
        { id: 'beach', label: '해수욕장', icon: '☀', description: '모래사장과 야자수, 파라솔이 있는 길' },
        { id: 'joseon-harbor', label: '조선 나루터', icon: '⚓', description: '돌길·객주·목조 선착장과 어선이 있는 포구' },
        { id: 'ys-palace', label: 'YS 궁궐 담장', icon: '🏯', description: '원거리 산·높은 담장·사방 자동문 배경' }
    ];

    public skinName: string = '조선 임금';
    public skinFileName: string = '';
    public skinStatus: string = '기본 캐릭터를 사용 중입니다.';
    public skinBusy: boolean = false;
    public hasCustomSkin: boolean = false;
    public localSkins: StoredSkin[] = [];
    public localSkinStatus: string = '업로드한 캐릭터를 이 브라우저에 등록할 수 있습니다.';
    public localSkinSearchQuery: string = '';
    public customSkinRotationY: number = 180;
    public activeLocalSkinId: string = '';
    public characterCustomizationEnabled: boolean = false;
    public characterNameDraft: string = '조선 임금';
    public customCharacterStats: CharacterStats = { jump: 5, steering: 5 };
    public selectedBuiltInSkinId: BuiltInSkinId | null = 'joseon-king-2';
    public readonly builtInSkins: BuiltInSkinPreset[] = [
        { id: 'joseon-king-2', name: '조선 임금', icon: '👑', color: '#ef4444', description: 'AI로 제작한 웹 최적화 GLB와 2K 텍스처 기반 고정밀 조선 임금', specialty: '균형형', stats: { jump: 5, steering: 5 }, aiGenerated: true },
        { id: 'haetae', name: '해태', icon: '🦁', color: '#338a80', description: 'AI로 제작해 공용파일로 등록한 웹 최적화 GLB 기반 해태', specialty: '좌우 특화', stats: { jump: 4, steering: 6 }, aiGenerated: true }
    ];
    public characterCustomization: CharacterCustomization = { eyes: 'calm', nose: 'soft', mouth: 'gentle', hair: 'royal-crown' };
    public customizationPreviewRotation: number = 0;
    public customizationPreviewDragging: boolean = false;
    public readonly eyeStyleOptions: CharacterStyleOption<EyeStyle>[] = [
        { id: 'none', label: '눈 없음' },
        { id: 'calm', label: '선비 눈매' },
        { id: 'bright', label: '총명한 눈' },
        { id: 'dignified', label: '위엄 눈썹' }
    ];
    public readonly noseStyleOptions: CharacterStyleOption<NoseStyle>[] = [
        { id: 'none', label: '코 없음' },
        { id: 'soft', label: '부드러운 코' },
        { id: 'straight', label: '곧은 코' },
        { id: 'round', label: '복코' }
    ];
    public readonly mouthStyleOptions: CharacterStyleOption<MouthStyle>[] = [
        { id: 'none', label: '입 없음' },
        { id: 'gentle', label: '온화한 입' },
        { id: 'smile', label: '미소' },
        { id: 'firm', label: '굳센 입' }
    ];
    public readonly hairStyleOptions: CharacterStyleOption<HairStyle>[] = [
        { id: 'none', label: '관모 없음' },
        { id: 'sangtu', label: '상투' },
        { id: 'gat', label: '갓' },
        { id: 'royal-crown', label: '익선관' },
        { id: 'headscarf', label: '농민 두건' },
        { id: 'binyeo', label: '비녀 쪽머리' },
        { id: 'jokduri', label: '족두리' },
        { id: 'warrior-helmet', label: '호위 투구' },
        { id: 'general-helmet', label: '장군 투구' }
    ];
    public customizationStatus: string = '조선시대 눈매·얼굴·표정·관모를 골라 캐릭터에 적용하세요.';
    public decorationAssets: StoredDecorationAsset[] = [];
    public selectedDecorationAssetId: string = '';
    public decorationStatus: string = '나무·소나무 3D·건물·안전 원뿔을 배치하거나 3D 장식 파일을 추가하세요.';
    public decorationBusy: boolean = false;
    public graphicsQuality: GraphicsQuality = 'auto';
    public effectiveGraphicsQuality: EffectiveGraphicsQuality = 'balanced';
    public rendererBackendPreference: RendererBackend = 'webgl';
    public activeRendererBackend: RendererBackend = 'webgl';
    public webGpuSupported: boolean = false;
    public rendererStatus: string = 'WebGL2 안정 모드';
    public averageFps: number = 0;
    public renderDrawCalls: number = 0;
    public renderTriangles: number = 0;
    public utilitySidebarOpen: boolean = false;
    public adminPasswordPromptOpen: boolean = false;
    public adminSettingsOpen: boolean = false;
    public adminPasswordInput: string = '';
    public adminLoginStatus: string = '';
    public adminNewPasswordInput: string = '';
    public adminPasswordConfirmInput: string = '';
    public adminSettingsStatus: string = '';
    public nfcApiUrl: string = '';
    public nfcApiUrlDraft: string = '';
    public nfcRegistrationOpen: boolean = false;
    public nfcIdentifierDraft: string = '';
    public nfcRegistrationStatus: string = '';
    public nfcRegistrationBusy: boolean = false;
    public nfcWaitingForModel: boolean = false;
    public nfcScanning: boolean = false;
    public nfcCharacterNameDraft: string = '';
    public nfcPreviewRotationY: number = 180;
    public nfcPreviewInitialRotationY: number = 180;
    public nfcPreviewDragging: boolean = false;
    public nfcPreviewReady: boolean = false;
    public nfcDraftSaved: boolean = false;
    public sidebarRankingMode: GameMode = 'runner';
    public localRankings: LocalRankingEntry[] = [];
    public rankingNameInput: string = '';
    public rankingStatus: string = '완주 이름을 입력하면 이 브라우저의 맵별 랭킹에 등록됩니다.';
    public rankingSubmitted: boolean = false;
    public readonly graphicsQualityOptions: Array<{ id: GraphicsQuality; label: string; icon: string; description: string }> = [
        { id: 'auto', label: '자동', icon: '✦', description: '기기 판단 + 동적 해상도' },
        { id: 'low', label: '저사양', icon: '◔', description: '30FPS·그림자 제거' },
        { id: 'balanced', label: '표준', icon: '◑', description: '60FPS·균형 품질' },
        { id: 'high', label: '고품질', icon: '●', description: '고해상도·부드러운 그림자' }
    ];
    private readonly hiddenNpcQuestionBank: QuizQuestion[] = [
        { id: 'hidden-history-1', question: '훈민정음은 세종대왕 때 창제되었다.', answer: true, explanation: '세종대왕은 백성이 쉽게 익힐 수 있도록 훈민정음을 창제했습니다.', correctMessage: '숨은 지식을 찾았습니다!', wrongMessage: '조선의 기록을 다시 살펴보세요.' },
        { id: 'hidden-history-2', question: '거북선은 임진왜란에서 사용된 조선 수군의 군선이다.', answer: true, explanation: '거북선은 조선 수군이 운용한 전투선으로 알려져 있습니다.', correctMessage: '바다의 비밀을 풀었습니다!', wrongMessage: '조선 수군의 배를 떠올려보세요.' },
        { id: 'hidden-culture-1', question: '한옥의 온돌은 바닥을 데우는 난방 방식이다.', answer: true, explanation: '온돌은 아궁이의 열과 연기를 바닥 아래로 통과시켜 방을 덥힙니다.', correctMessage: '생활문화 비밀을 찾았습니다!', wrongMessage: '한옥의 겨울 난방을 생각해보세요.' },
        { id: 'hidden-culture-2', question: '갓은 조선시대에 주로 발에 신던 물건이다.', answer: false, explanation: '갓은 주로 성인 남성이 머리에 쓰던 관모입니다.', correctMessage: '관모의 비밀을 풀었습니다!', wrongMessage: '갓을 쓰는 위치를 생각해보세요.' },
        { id: 'hidden-science-1', question: '해시계는 해의 그림자를 이용해 시간을 측정한다.', answer: true, explanation: '해시계는 태양의 위치에 따라 달라지는 그림자로 시간을 읽습니다.', correctMessage: '과학의 비밀을 찾았습니다!', wrongMessage: '그림자와 시간의 관계를 생각해보세요.' }
    ];

    private readonly baseSpeed: number = 29;
    private readonly exploreBaseSpeed: number = 13;
    private readonly exploreBaseSpeedMultiplier: number = 1.3;
    private readonly exploreSprintMultiplier: number = 1.5;
    private readonly supplementSpeedMultiplier: number = 1.5;
    private readonly itemPickupRangeMultiplier: number = 1.3;
    private readonly baseJumpVelocity: number = 11.8;
    private readonly baseJumpVelocityMultiplier: number = 1.3;
    private readonly maxJumpCount: number = 2;
    private readonly mapStorageKey: string = 'quiz-rush-3d.maps.v1';
    private readonly maxMapFileSize: number = 1024 * 1024;
    private readonly maxGroundImageFileSize: number = 10 * 1024 * 1024;
    private readonly maxGroundImageDataUrlLength: number = 460 * 1024;
    private readonly maxGroundImageDimension: number = 2048;
    private readonly maxSkinFileSize: number = 8 * 1024 * 1024;
    private readonly maxNfcModelFileSize: number = 128 * 1024 * 1024;
    private readonly maxDecorationFileSize: number = 200 * 1024 * 1024;
    private readonly maxDecorationSourceFileSize: number = 200 * 1024 * 1024;
    private readonly maxConvertedDecorationFileSize: number = 24 * 1024 * 1024;
    private readonly editorDragMime: string = 'application/x-quiz-rush-editor-item';
    private readonly editorLaneDisplayDirection: 1 = 1;
    private readonly skinPresetStorageKey: string = 'quiz-rush-3d.skin-preset.v1';
    private readonly characterCustomizationStorageKey: string = 'quiz-rush-3d.character-customization.v1';
    private readonly legacyCharacterCleanupStorageKey: string = 'quiz-rush-3d.legacy-character-cleanup.preview-only.v1';
    private readonly characterProfilesStorageKey: string = 'quiz-rush-3d.character-profiles.v2';
    private readonly characterBalanceStorageKey: string = 'quiz-rush-3d.character-balance.steering-haetae.v1';
    private readonly rankingStorageKey: string = 'quiz-rush-3d.rankings.v1';
    private readonly rankingNameStorageKey: string = 'quiz-rush-3d.ranking-name.v1';
    private readonly graphicsQualityStorageKey: string = 'quiz-rush-3d.graphics-quality.v1';
    private readonly rendererBackendStorageKey: string = 'quiz-rush-3d.renderer-backend.v1';
    private readonly adminPasswordStorageKey: string = 'quiz-rush-3d.admin-password.v1';
    private readonly nfcApiUrlStorageKey: string = 'quiz-rush-3d.nfc-api-url.v1';
    private readonly cameraFollowDistance: number = 15;
    private readonly cameraMaxDrift: number = 0.9;
    private readonly skinStorage = new SkinStorage();
    private readonly decorationStorage = new DecorationStorage();
    private nfcScanAbortController?: AbortController;
    private nfcDownloadRequest?: XMLHttpRequest;
    private nfcSessionSequence: number = 0;
    private nfcUserToken: string = '';
    private nfcParticipantId: string = '';
    private nfcCurrentUid: string = '';
    private nfcKeyboardBuffer: string = '';
    private nfcKeyboardLastInputAt: number = 0;
    private activeNfcCharacterMetadata: ActiveNfcCharacterMetadata | null = null;
    private readonly king2GlbPath: string = '/assets/quiz-runner/characters/king-2/king-2.glb';
    private readonly haetaeGlbPath: string = '/assets/quiz-runner/characters/haetae/haetae.glb';
    private readonly serverDecorationPresets: ServerDecorationPreset[] = [
        {
            id: 'max-decoration-sm-paldalmun',
            name: '팔달문',
            fileName: 'SM_Paldalmun.web.glb',
            sourceFileName: 'SM_Paldalmun.max',
            sourceFormat: 'max',
            url: '/assets/quiz-runner/decorations/max-imported/SM_Paldalmun.web.glb'
        },
        {
            id: 'max-decoration-sm-uhwagan',
            name: '우화관',
            fileName: 'SM_Uhwagan.web.glb',
            sourceFileName: 'SM_Uhwagan.max',
            sourceFormat: 'max',
            url: '/assets/quiz-runner/decorations/max-imported/SM_Uhwagan.web.glb'
        },
        {
            id: 'decoration-asset-mtawfrqx-ws1oqn6',
            name: 'old-shrine-naejaesil',
            fileName: 'old-shrine-naejaesil.glb',
            sourceFileName: 'old-shrine-naejaesil.fbx',
            sourceFormat: 'fbx',
            url: '/assets/quiz-runner/decorations/kim-yeeun/old-shrine-naejaesil.glb?v=20260902-server2',
            version: '20260902-server2'
        },
        {
            id: 'decoration-asset-mtawgpq3-cwdjrtr',
            name: 'gyeonghoeru',
            fileName: 'gyeonghoeru.glb',
            sourceFileName: 'gyeonghoeru.fbx',
            sourceFormat: 'fbx',
            url: '/assets/quiz-runner/decorations/kim-yeeun/gyeonghoeru.glb?v=20260902-server2',
            version: '20260902-server2'
        },
        {
            id: 'decoration-asset-mtcbnocx-uvenirm',
            name: 'base',
            fileName: 'base.glb',
            sourceFileName: 'base.fbx',
            sourceFormat: 'fbx',
            url: '/assets/quiz-runner/decorations/kim-yeeun/base.glb?v=20260902-server2',
            version: '20260902-server2'
        },
        {
            id: 'decoration-asset-mtccivj1-7xdmy7l',
            name: 'stairs',
            fileName: 'stairs.glb',
            sourceFileName: 'stairs.fbx',
            sourceFormat: 'fbx',
            url: '/assets/quiz-runner/decorations/kim-yeeun/stairs.glb?v=20260902-server2',
            version: '20260902-server2'
        },
        {
            id: 'decoration-asset-mtci15oz-m8z2ygj',
            name: '돌길__small2',
            fileName: 'stone-path-small2.glb',
            sourceFileName: '돌길__small2.fbx',
            sourceFormat: 'fbx',
            url: '/assets/quiz-runner/decorations/kim-yeeun/stone-path-small2.glb?v=20260902-server2',
            version: '20260902-server2'
        },
        {
            id: 'decoration-asset-mtawfw2l-q89wu86',
            name: 'old-shrine-south-haenggak',
            fileName: 'old-shrine-south-haenggak.glb',
            sourceFileName: 'old-shrine-south-haenggak.fbx',
            sourceFormat: 'fbx',
            url: '/assets/quiz-runner/decorations/kim-yeeun/old-shrine-south-haenggak.glb?v=20260902-server2',
            version: '20260902-server2'
        },
        {
            id: 'decoration-asset-mtawaip1-xhwsgfk',
            name: 'gwollaegaksa-geomseocheong-annex',
            fileName: 'gwollaegaksa-geomseocheong-annex.glb',
            sourceFileName: 'gwollaegaksa-geomseocheong-annex.fbx',
            sourceFormat: 'fbx',
            url: '/assets/quiz-runner/decorations/kim-yeeun/gwollaegaksa-geomseocheong-annex.glb?v=20260902-server2',
            version: '20260902-server2'
        },
        {
            id: 'decoration-asset-mtikpav8-tv7hfa6',
            name: '돌석',
            fileName: 'stone-block.glb',
            sourceFileName: '돌석.fbx',
            sourceFormat: 'fbx',
            url: '/assets/quiz-runner/decorations/kim-yeeun/stone-block.glb?v=20260902-server2',
            version: '20260902-server2'
        },
        {
            id: 'decoration-asset-mtimau44-7e4zxfz',
            name: 'stone_floor',
            fileName: 'stone-floor.glb',
            sourceFileName: 'stone_floor.fbx',
            sourceFormat: 'fbx',
            url: '/assets/quiz-runner/decorations/kim-yeeun/stone-floor.glb?v=20260902-server2',
            version: '20260902-server2'
        }
    ];

    private scene?: THREE.Scene;
    private camera?: THREE.PerspectiveCamera;
    private readonly cameraLookTarget = new THREE.Vector3();
    private readonly editor3dRaycaster = new THREE.Raycaster();
    private readonly editor3dGroundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    private editor3dGhost?: THREE.Group;
    private editor3dGhostKey: string = '';
    private renderer?: THREE.WebGLRenderer;
    private player?: THREE.Group;
    private playerVisual?: THREE.Group;
    private playerAnimationMixer?: THREE.AnimationMixer;
    private playerMountVisual?: THREE.Group;
    private animationFrameId: number | null = null;
    private lastFrameTime: number = 0;
    private lowPowerRendering: boolean = false;
    private maxRendererPixelRatio: number = 1;
    private currentRendererPixelRatio: number = 1;
    private frameBudgetElapsed: number = 0;
    private frameBudgetFrames: number = 0;
    private decorationCullElapsed: number = 0;
    private uiRefreshElapsed: number = 0;
    private renderPending: boolean = false;
    private customizationPreviewPointerId: number | null = null;
    private customizationPreviewLastX: number = 0;
    private customizationPreviewScene?: THREE.Scene;
    private customizationPreviewCamera?: THREE.PerspectiveCamera;
    private customizationPreviewRenderer?: THREE.WebGLRenderer;
    private customizationPreviewModel?: THREE.Group;
    private nfcPreviewPointerId: number | null = null;
    private nfcPreviewLastX: number = 0;
    private nfcPreviewScene?: THREE.Scene;
    private nfcPreviewCamera?: THREE.PerspectiveCamera;
    private nfcPreviewRenderer?: THREE.WebGLRenderer;
    private nfcPreviewModel?: THREE.Group;
    private nfcRegistrationDraft: NfcRegistrationDraft | null = null;
    private exploreCameraPointerId: number | null = null;
    private exploreCameraPointerLastX: number = 0;
    private exploreCameraPointerLastY: number = 0;
    private statusExpiresAt: number = 0;
    private sceneryCollisionStatusAt: number = 0;
    private readonly heritageBaseTextureCache = new Map<string, THREE.Texture>();
    private readonly heritageTextureCache = new Map<string, THREE.Texture>();
    private readonly heritageTextureLoadPromises = new Map<string, Promise<void>>();
    private readonly decorationTemplateCache = new Map<string, THREE.Group>();
    private readonly decorationTemplateLoadPromises = new Map<string, Promise<void>>();
    private decorationAssetsReady: boolean = false;
    private decorationAssetsLoadPromise?: Promise<void>;
    private readonly builtInDecorationTemplateCache = new Map<SceneryKind, THREE.Group>();
    private readonly ysDecorationTemplateCache = new Map<string, THREE.Group>();
    private readonly ysDecorationLoadPromises = new Map<string, Promise<THREE.Group>>();
    private readonly ysDecorationLoadQueue: Array<() => void> = [];
    private ysDecorationActiveLoadCount: number = 0;
    private readonly ysDecorationLoadConcurrency: number = 2;
    private readonly ysDecorationSurfaceCache = new Map<string, YsDecorationSurfaceMap>();
    private readonly ysDecorationSurfaceLoadPromises = new Map<string, Promise<YsDecorationSurfaceMap>>();
    private readonly bundledNatureTemplateCache = new Map<BundledNatureDecorationKind, THREE.Group>();
    private readonly bundledNatureLoadPromises = new Map<BundledNatureDecorationKind, Promise<THREE.Group>>();
    private readonly autoSceneryGates = new Map<string, AutoSceneryGateState>();
    private mapAssetRequestSequence: number = 0;
    private destroyed: boolean = false;
    private previousMap: GameMap = cloneMap(this.defaultMap);
    private customSkinData: ArrayBuffer | null = null;
    private customSkinFormat: SkinFormat = 'glb';
    private currentRunId: string = '';
    private activePlanMove: ActivePlanMove | null = null;
    private activeSceneryPathAnchors: SceneryPathAnchor[] = [];
    private activeEditorPan: ActiveEditorPan | null = null;
    private suppressPlanClick: boolean = false;
    private draggingPaletteKind: EditorPaletteKind | null = null;
    private readonly editorHistoryLimit: number = 80;
    private editorUndoStack: EditorHistoryEntry[] = [];
    private editorRedoStack: EditorHistoryEntry[] = [];
    private editorHistoryCurrent: EditorHistoryEntry | null = null;
    private editorHistoryFingerprint: string = '';
    private editorHistoryRestoring: boolean = false;
    private editorKnownTrackLength: number = this.defaultMap.trackLength;
    private editorPlanBounds: EditorPlanBounds = { minX: -50, maxX: 50, minZ: -100, maxZ: 0 };
    private editorWheelAccumulator: number = 0;
    private editorWheelUpdatedAt: number = 0;
    private terrainPainting: boolean = false;
    private terrainPointerId: number | null = null;
    private terrainRenderFrame: number | null = null;

    private progress: number = 0;
    private lateralOffset: number = 0;
    private coinSteerBonus: number = 0;
    public magnetRemaining: number = 0;
    public supplementRemaining: number = 0;
    public mountRemaining: number = 0;
    public activeMountKind: MountKind | null = null;
    private jumpHeight: number = 0;
    private jumpVelocity: number = 0;
    private jumpsUsed: number = 0;
    private grounded: boolean = true;
    private falling: boolean = false;
    private fallOffset: number = 0;
    private fallVelocity: number = 0;
    private checkpoint: number = 0;
    private controls = { left: false, right: false, forward: false, backward: false };
    public exploreSprintActive: boolean = false;
    private readonly explorePosition = new THREE.Vector3();
    private exploreHeading: number = 0;
    private exploreCameraHeading: number = 0;
    private explorePathSamples: RuntimePathSample[] = [];
    private runtimePathWorldLength: number = 0;
    private runtimeRibbonFrames: RuntimeRibbonFrame[] = [];
    private jumpPlatformSurfaces: JumpPlatformSurface[] = [];
    private minimapBounds: EditorPlanBounds = { minX: -50, maxX: 50, minZ: -50, maxZ: 50 };
    private minimapBaseSize: number = 100;
    private serverMapsLoaded: boolean = false;
    private exploreBounds: EditorPlanBounds = { minX: -70, maxX: 70, minZ: -170, maxZ: 70 };
    private coins: CoinState[] = [];
    private magnets: MagnetState[] = [];
    private supplements: SupplementState[] = [];
    private mounts: MountState[] = [];
    private obstacles: ObstacleState[] = [];
    private hiddenNpcs: HiddenNpcState[] = [];
    private haechiGuides: HaechiGuideState[] = [];
    private sceneryColliders: SceneryCollider[] = [];
    private activeHiddenNpc?: HiddenNpcState;
    public activeHaechiGuide?: HaechiGuideState;
    private gates: RuntimeGate[] = [];
    private performanceDecorations: THREE.Object3D[] = [];
    private menuHeritageShowcase?: THREE.Group;
    private king2AssetSource?: THREE.Group;
    private king2AssetLoadPromise?: Promise<void>;
    private haetaeAssetSource?: THREE.Group;
    private haetaeAssetLoadPromise?: Promise<void>;
    private readonly builtInCharacterTemplateCache = new Map<'joseon-king-2' | 'sejong-the-great' | 'haetae', THREE.Group>();

    constructor(@Inject( Service) public service: Service) { }

    public get allMaps(): GameMap[] {
        const maps = [this.defaultMap, ...this.savedMaps]
            .filter((map) => map.id !== 'builtin-neon-learning-run');
        this.serverMaps.forEach((map) => {
            if (map.id === 'builtin-neon-learning-run') return;
            if (!maps.some((item) => item.id === map.id)) maps.push(map);
        });
        return maps;
    }

    public isLocalMap(map: GameMap): boolean {
        return this.savedMaps.some((item) => item.id === map.id);
    }

    public isServerMap(map: GameMap): boolean {
        return this.serverMaps.some((item) => item.id === map.id);
    }

    public get totalGates(): number {
        return this.gates.length;
    }

    public get accuracy(): number {
        if (this.answerRecords.length === 0) return 0;
        return Math.round((this.correctCount / this.answerRecords.length) * 100);
    }

    public get formattedTime(): string {
        const total = Math.max(0, Math.floor(this.runTime));
        return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
    }

    public get exploreXLabel(): string {
        return this.explorePosition.x.toFixed(3);
    }

    public get exploreZLabel(): string {
        return this.explorePosition.z.toFixed(3);
    }

    public get exploreYLabel(): string {
        return (this.explorePosition.y + this.jumpHeight).toFixed(3);
    }

    public get exploreSupportYLabel(): string {
        return this.explorePosition.y.toFixed(3);
    }

    public get exploreCameraHeadingLabel(): string {
        return THREE.MathUtils.radToDeg(this.exploreCameraHeading).toFixed(1);
    }

    public get exploreCameraModeLabel(): string {
        if (this.exploreCameraMode === 'shoulder') return '어깨';
        if (this.exploreCameraMode === 'top') return '탑뷰';
        return '후방';
    }

    public get exploreCameraDragging(): boolean {
        return this.exploreCameraPointerId !== null;
    }

    public get exploreCameraPitchLabel(): string {
        return `${Math.round(THREE.MathUtils.radToDeg(this.exploreCameraPitchOffset))}°`;
    }

    public get minimapZoomLabel(): string {
        return `${Number.isInteger(this.minimapZoom) ? this.minimapZoom.toFixed(0) : this.minimapZoom.toFixed(1)}×`;
    }

    public get minimapViewBox(): string {
        const size = this.minimapBaseSize / this.minimapZoom;
        const half = size / 2;
        const centerX = THREE.MathUtils.clamp(
            this.minimapPlayerX,
            this.minimapBounds.minX + half,
            this.minimapBounds.maxX - half
        );
        const centerZ = THREE.MathUtils.clamp(
            this.minimapPlayerZ,
            this.minimapBounds.minZ + half,
            this.minimapBounds.maxZ - half
        );
        return `${(centerX - half).toFixed(2)} ${(centerZ - half).toFixed(2)} ${size.toFixed(2)} ${size.toFixed(2)}`;
    }

    public get minimapMarkerScale(): number {
        return Math.max(0.18, (this.minimapBaseSize / this.minimapZoom) / 100);
    }

    public get minimapTrackStrokeWidth(): number {
        return Math.max(2, this.activeMap.trackWidth);
    }

    public get exploreSpeedMultiplier(): number {
        if (this.gameMode !== 'explore') return 1;
        return this.exploreBaseSpeedMultiplier
            * (this.exploreSprintActive ? this.exploreSprintMultiplier : 1)
            * (this.supplementRemaining > 0 ? this.supplementSpeedMultiplier : 1)
            * (this.activeMountKind ? 2 : 1);
    }

    public get modeLabel(): string {
        return this.gameMode === 'runner' ? '달리기 모드' : '자유 탐색 모드';
    }

    public get coinScore(): number {
        return this.coinCount * 5;
    }

    public get coinScoreMaximum(): number {
        return this.totalCoins * 5;
    }

    public get quizScore(): number {
        return this.correctCount * 150;
    }

    public get quizScoreMaximum(): number {
        return this.gates.filter((gate) => gate.quizEnabled).length * 150;
    }

    public get timeScoreReferenceSeconds(): number {
        const worldLength = this.runtimePathWorldLength > 0 ? this.runtimePathWorldLength : this.activeMap.trackLength;
        const movementSpeed = this.gameMode === 'runner'
            ? this.baseSpeed
            : this.exploreBaseSpeed * this.exploreBaseSpeedMultiplier;
        const quizAllowance = this.gates.filter((gate) => gate.quizEnabled).length * 8;
        return Math.max(30, Math.ceil(worldLength / Math.max(1, movementSpeed) + quizAllowance));
    }

    public get timeScore(): number {
        const scoreRatio = 1 - this.runTime / (this.timeScoreReferenceSeconds * 2);
        return Math.round(300 * THREE.MathUtils.clamp(scoreRatio, 0, 1));
    }

    public get baseScore(): number {
        return this.coinScore + this.timeScore + this.quizScore;
    }

    public get totalScore(): number {
        return this.coinScore + this.hiddenNpcScore + this.timeScore + this.quizScore;
    }

    public get sidebarCharacterValue(): string {
        if (this.activeNfcCharacterMetadata) return 'current-nfc';
        if (this.activeLocalSkinId) return `local:${this.activeLocalSkinId}`;
        if (this.hasCustomSkin) return 'current-upload';
        return this.selectedBuiltInSkinId ? `builtin:${this.selectedBuiltInSkinId}` : '';
    }

    public get hasActiveNfcCharacter(): boolean {
        return Boolean(this.activeNfcCharacterMetadata);
    }

    public get selectableLocalSkins(): StoredSkin[] {
        return this.localSkins.filter((skin) => !skin.nfcAssetId && !skin.nfcIdentifier);
    }

    public get filteredLocalSkins(): StoredSkin[] {
        const query = this.normalizeLocalSkinSearch(this.localSkinSearchQuery);
        if (!query) return this.selectableLocalSkins;
        return this.selectableLocalSkins.filter((skin) => this.normalizeLocalSkinSearch(skin.name).includes(query));
    }

    public setLocalSkinSearchQuery(value: string) {
        this.localSkinSearchQuery = String(value || '').slice(0, 30);
        this.requestUiRender();
    }

    public clearLocalSkinSearch() {
        if (!this.localSkinSearchQuery) return;
        this.localSkinSearchQuery = '';
        this.requestUiRender();
    }

    public get canSaveNfcCharacter(): boolean {
        return this.nfcPreviewReady
            && Boolean(this.nfcRegistrationDraft)
            && Boolean(this.nfcCharacterNameDraft.trim())
            && !this.nfcRegistrationBusy
            && !this.nfcDraftSaved;
    }

    public get sidebarRankings(): LocalRankingEntry[] {
        return this.rankingsFor(this.activeMap.id, this.sidebarRankingMode).slice(0, 10);
    }

    public get currentModeRankings(): LocalRankingEntry[] {
        return this.rankingsFor(this.activeMap.id, this.gameMode).slice(0, 10);
    }

    public formatRankingTime(seconds: number): string {
        const total = Math.max(0, Math.floor(seconds));
        return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
    }

    public themeLabel(theme: MapTheme): string {
        return this.themeOptions.find((item) => item.id === theme)?.label || '네온 시티';
    }

    public graphicsQualityLabel(quality: GraphicsQuality | EffectiveGraphicsQuality): string {
        return this.graphicsQualityOptions.find((item) => item.id === quality)?.label || '표준';
    }

    public get activeGraphicsQualityLabel(): string {
        const selected = this.graphicsQualityLabel(this.graphicsQuality);
        if (this.graphicsQuality !== 'auto') return selected;
        return `${selected} · ${this.graphicsQualityLabel(this.effectiveGraphicsQuality)}`;
    }

    public get activeRendererBackendLabel(): string {
        return this.activeRendererBackend === 'webgpu' ? 'WebGPU 실험' : 'WebGL2 안정';
    }

    public get localSkinRegisterLabel(): string {
        return this.activeLocalSkinId ? '로컬 캐릭터 업데이트' : '로컬 캐릭터 등록';
    }

    public isActiveLocalSkin(skin: StoredSkin): boolean {
        return this.activeLocalSkinId === skin.id;
    }

    public localSkinRotationLabel(skin: StoredSkin): string {
        return `${this.normalizeSkinRotation(skin.rotationY)}°`;
    }

    public get renderTrianglesLabel(): string {
        if (this.renderTriangles >= 1000000) return `${(this.renderTriangles / 1000000).toFixed(1)}M`;
        if (this.renderTriangles >= 1000) return `${Math.round(this.renderTriangles / 1000)}K`;
        return String(this.renderTriangles);
    }

    public async setGraphicsQuality(quality: GraphicsQuality) {
        if (this.screen === 'playing' || this.screen === 'paused' || this.screen === 'quiz' || this.screen === 'quizFeedback') {
            this.statusText = '화질 변경은 진행 중인 게임을 마친 뒤 적용할 수 있습니다.';
            this.requestUiRender();
            return;
        }
        if (!this.graphicsQualityOptions.some((item) => item.id === quality)) return;
        this.graphicsQuality = quality;
        localStorage.setItem(this.graphicsQualityStorageKey, quality);
        this.resolveEffectiveGraphicsQuality();
        this.applyRendererQuality();
        this.disposeCustomizationPreview();
        this.disposeNfcPreview();
        if (this.worldReady) this.rebuildWorld();
        this.statusText = `${this.activeGraphicsQualityLabel} 그래픽 모드를 적용했습니다.`;
        await this.service.render();
    }

    public toggleUtilitySidebar() {
        this.utilitySidebarOpen = !this.utilitySidebarOpen;
        this.requestUiRender();
        if (this.utilitySidebarOpen && !this.serverMapsLoaded) void this.refreshServerMaps();
    }

    public openAdminLogin() {
        this.adminPasswordInput = '';
        this.adminLoginStatus = '';
        this.adminPasswordPromptOpen = true;
        this.adminSettingsOpen = false;
        this.requestUiRender();
    }

    public closeAdminPasswordPrompt() {
        this.adminPasswordPromptOpen = false;
        this.adminPasswordInput = '';
        this.adminLoginStatus = '';
        this.requestUiRender();
    }

    public setAdminPinInput(field: 'login' | 'new' | 'confirm', value: string) {
        const pin = String(value || '').replace(/\D/g, '').slice(0, 4);
        if (field === 'login') this.adminPasswordInput = pin;
        if (field === 'new') this.adminNewPasswordInput = pin;
        if (field === 'confirm') this.adminPasswordConfirmInput = pin;
    }

    public confirmAdminLogin() {
        if (this.adminPasswordInput.length !== 4) {
            this.adminLoginStatus = '4자리 비밀번호를 입력해주세요.';
            this.requestUiRender();
            return;
        }
        if (this.adminPasswordInput !== this.readAdminPassword()) {
            this.adminLoginStatus = '비밀번호가 올바르지 않습니다.';
            this.adminPasswordInput = '';
            this.requestUiRender();
            return;
        }
        this.adminPasswordPromptOpen = false;
        this.adminSettingsOpen = true;
        this.adminPasswordInput = '';
        this.adminLoginStatus = '';
        this.adminNewPasswordInput = '';
        this.adminPasswordConfirmInput = '';
        this.adminSettingsStatus = '';
        this.nfcApiUrlDraft = this.nfcApiUrl;
        this.requestUiRender();
    }

    public closeAdminSettings() {
        this.adminSettingsOpen = false;
        this.adminNewPasswordInput = '';
        this.adminPasswordConfirmInput = '';
        this.adminSettingsStatus = '';
        this.requestUiRender();
    }

    public async saveNfcApiUrl() {
        try {
            const normalized = this.normalizeNfcApiBaseUrl(this.nfcApiUrlDraft);
            const changed = normalized !== this.nfcApiUrl;
            this.nfcApiUrl = normalized;
            this.nfcApiUrlDraft = normalized;
            localStorage.setItem(this.nfcApiUrlStorageKey, normalized);
            if (changed) await this.endNfcCharacterSession(false, true);
            this.adminSettingsStatus = 'CharDB 서버 주소를 저장했습니다. NFC 인증부터 모델 다운로드까지 이 주소를 사용합니다.';
        } catch (error: any) {
            this.adminSettingsStatus = error?.message || 'CharDB 서버 주소를 저장하지 못했습니다.';
        }
        this.requestUiRender();
    }

    public changeAdminPassword() {
        if (this.adminNewPasswordInput.length !== 4) {
            this.adminSettingsStatus = '새 비밀번호는 숫자 4자리로 입력해주세요.';
            this.requestUiRender();
            return;
        }
        if (this.adminNewPasswordInput !== this.adminPasswordConfirmInput) {
            this.adminSettingsStatus = '새 비밀번호와 확인 값이 일치하지 않습니다.';
            this.requestUiRender();
            return;
        }
        localStorage.setItem(this.adminPasswordStorageKey, this.adminNewPasswordInput);
        this.adminNewPasswordInput = '';
        this.adminPasswordConfirmInput = '';
        this.adminSettingsStatus = 'ADMIN 접속 비밀번호를 변경했습니다.';
        this.requestUiRender();
    }

    public openNfcRegistration() {
        this.clearNfcRegistrationDraft();
        this.nfcRegistrationOpen = true;
        this.nfcIdentifierDraft = '';
        this.nfcRegistrationStatus = this.nfcApiUrl
            ? '7바이트 UID를 입력하거나 이 모달에서 NFC 스캔을 시작하세요.'
            : '먼저 관리 사이드바의 ADMIN에서 CharDB 서버 주소를 설정해주세요.';
        this.requestUiRender();
        window.setTimeout(() => this.initializeNfcPreview(), 0);
    }

    public closeNfcRegistration() {
        this.abortNfcScan();
        if (this.nfcRegistrationBusy || this.nfcUserToken || this.nfcParticipantId) {
            void this.endNfcCharacterSession(false, false, true);
        }
        this.clearNfcRegistrationDraft();
        this.disposeNfcPreview();
        this.nfcRegistrationOpen = false;
        this.nfcIdentifierDraft = '';
        this.nfcRegistrationStatus = '';
        this.requestUiRender();
    }

    public setNfcIdentifier(value: string) {
        this.nfcIdentifierDraft = this.formatNfcUidInput(value);
    }

    public setNfcCharacterName(value: string) {
        this.nfcCharacterNameDraft = String(value || '').slice(0, 30);
        this.nfcDraftSaved = false;
        this.requestUiRender();
    }

    public async startNfcScan() {
        if (!this.nfcRegistrationOpen || this.nfcRegistrationBusy || this.nfcScanning) return;
        if (!this.nfcApiUrl) {
            this.nfcRegistrationStatus = 'ADMIN에서 CharDB 서버 주소를 먼저 설정해주세요.';
            this.requestUiRender();
            return;
        }
        const NdefReader = (window as any).NDEFReader;
        if (typeof NdefReader !== 'function') {
            this.nfcRegistrationStatus = '이 브라우저는 Web NFC를 지원하지 않습니다. 식별값을 직접 입력하거나 키보드형 NFC 스캐너를 사용해주세요.';
            this.requestUiRender();
            return;
        }
        this.abortNfcScan();
        const controller = new AbortController();
        this.nfcScanAbortController = controller;
        try {
            const reader = new NdefReader();
            reader.addEventListener('readingerror', () => {
                this.nfcRegistrationStatus = 'NFC 태그를 읽지 못했습니다. 다시 스캔해주세요.';
                this.requestUiRender();
            });
            reader.addEventListener('reading', (event: any) => void this.handleNfcReading(event));
            await reader.scan({ signal: controller.signal });
            this.nfcScanning = true;
            this.nfcRegistrationStatus = 'NFC 태그를 스캐너에 가까이 대주세요.';
        } catch (error: any) {
            this.nfcScanAbortController = undefined;
            this.nfcScanning = false;
            this.nfcRegistrationStatus = error?.name === 'NotAllowedError'
                ? 'NFC 사용 권한이 허용되지 않았습니다.'
                : (error?.message || 'NFC 스캔을 시작하지 못했습니다.');
        }
        this.requestUiRender();
    }

    public stopNfcScan() {
        this.abortNfcScan();
        this.nfcRegistrationStatus = 'Web NFC 스캔을 중지했습니다. 키보드형 리더 UID는 계속 감지합니다.';
        this.requestUiRender();
    }

    public async cancelNfcRegistration() {
        if (!this.nfcRegistrationBusy) return;
        await this.endNfcCharacterSession(false, false, true);
        this.clearNfcRegistrationDraft();
        this.nfcRegistrationStatus = '현재 NFC 요청을 취소했습니다. 태그를 다시 스캔할 수 있습니다.';
        this.requestUiRender();
    }

    public async registerNfcCharacter() {
        if (!this.nfcRegistrationOpen) return;
        const uid = this.normalizeNfcUid(this.nfcIdentifierDraft);
        this.nfcIdentifierDraft = uid;
        if (!uid) {
            this.nfcRegistrationStatus = 'NFC UID를 04:2F:1C:49:C6:2A:81 형식의 7바이트 값으로 입력해주세요.';
            this.requestUiRender();
            return;
        }
        if (!this.nfcApiUrl) {
            this.nfcRegistrationStatus = 'ADMIN에서 CharDB 서버 주소를 먼저 설정해주세요.';
            this.requestUiRender();
            return;
        }
        if (this.nfcRegistrationBusy || this.skinBusy) return;
        await this.loadNfcCharacter(uid, false);
    }

    public async saveNfcCharacter() {
        const draft = this.nfcRegistrationDraft;
        const name = this.nfcCharacterNameDraft.trim().slice(0, 30);
        if (!draft || !this.nfcPreviewReady) {
            this.nfcRegistrationStatus = '먼저 NFC를 스캔해 캐릭터 미리보기를 준비해주세요.';
            this.requestUiRender();
            return;
        }
        if (!name) {
            this.nfcRegistrationStatus = '저장할 캐릭터 이름을 입력해주세요.';
            this.requestUiRender();
            return;
        }
        if (this.nfcRegistrationBusy || this.skinBusy || this.nfcDraftSaved) return;

        const asset = draft.asset;
        const rotationY = this.normalizeSkinRotation(this.nfcPreviewRotationY);
        this.nfcRegistrationBusy = true;
        this.skinBusy = true;
        this.nfcRegistrationStatus = '캐릭터 이름과 정면 방향을 저장하고 있습니다.';
        this.requestUiRender();
        try {
            const existing = this.localSkins.find((skin) => skin.id === draft.recordId);
            const record: StoredSkin = {
                id: draft.recordId,
                name,
                fileName: asset.fileName,
                format: asset.format,
                data: asset.data.slice(0),
                scale: 1,
                yOffset: 0,
                rotationY,
                nfcParticipantId: asset.participantId,
                nfcAssetId: asset.assetId,
                nfcChecksum: asset.checksum,
                nfcFileSize: asset.fileSize,
                nfcBaseUrl: asset.baseUrl,
                customizationEnabled: false,
                stats: { ...draft.stats },
                updatedAt: new Date().toISOString()
            };
            await this.skinStorage.put(record);
            const summary = await this.applyCustomSkin(record.data, record.format, rotationY);
            this.localSkins = this.sortLocalSkins(existing
                ? this.localSkins.map((skin) => skin.id === existing.id ? record : skin)
                : [record, ...this.localSkins]);
            this.activeLocalSkinId = record.id;
            this.activeNfcCharacterMetadata = {
                participantId: asset.participantId,
                assetId: asset.assetId,
                checksum: asset.checksum,
                fileSize: asset.fileSize,
                baseUrl: asset.baseUrl
            };
            this.customSkinData = record.data.slice(0);
            this.customSkinFormat = record.format;
            this.customSkinRotationY = rotationY;
            this.skinName = record.name;
            this.characterNameDraft = record.name;
            this.skinFileName = record.fileName;
            this.characterCustomizationEnabled = false;
            this.customCharacterStats = this.normalizeCharacterStats(record.stats);
            this.selectedBuiltInSkinId = null;
            this.hasCustomSkin = true;
            localStorage.removeItem(this.skinPresetStorageKey);
            await this.persistSkin();
            await this.removeObsoleteNfcCache(record);
            this.refreshCustomizationPreviewModel();
            this.nfcCharacterNameDraft = record.name;
            this.nfcPreviewRotationY = rotationY;
            this.nfcPreviewInitialRotationY = rotationY;
            this.nfcDraftSaved = true;
            const cacheStatus = asset.fromCache ? '검증된 로컬 캐시 사용' : '다운로드·검증·캐시 완료';
            this.nfcRegistrationStatus = `${record.name}을(를) ${rotationY.toFixed(1).replace('.0', '')}° 정면 방향으로 저장하고 적용했습니다.`;
            this.localSkinStatus = `CharDB ${asset.format.toUpperCase()} · 메시 ${summary.meshCount}개 · ${cacheStatus}`;
            this.skinStatus = `${record.name} NFC 캐릭터 적용 완료 · ${asset.format.toUpperCase()}`;
        } catch (error: any) {
            this.nfcRegistrationStatus = error?.message || 'NFC 캐릭터를 저장하지 못했습니다.';
        } finally {
            this.nfcRegistrationBusy = false;
            this.skinBusy = false;
            this.requestUiRender();
        }
    }

    public async selectSidebarMap(mapId: string) {
        if (this.screen === 'playing') return;
        const requestId = ++this.mapAssetRequestSequence;
        const requestScreen = this.screen;
        const selected = this.allMaps.find((map) => map.id === mapId);
        if (!selected || selected.id === this.activeMap.id) return;
        await this.prepareMapDecorationAssets(selected);
        if (this.destroyed || requestId !== this.mapAssetRequestSequence || this.screen !== requestScreen) return;
        this.isEditorTest = false;
        this.activeMap = cloneMap(selected);
        this.rebuildWorld();
        if (this.screen === 'menu') this.setMenuHeritageShowcaseVisible(true);
        this.statusText = `“${this.activeMap.title}” 맵을 선택했습니다.`;
        this.requestUiRender();
    }

    public setSidebarRankingMode(mode: GameMode) {
        this.sidebarRankingMode = mode === 'explore' ? 'explore' : 'runner';
        this.requestUiRender();
    }

    public async selectSidebarCharacter(value: string) {
        if (!value || value === 'current-upload' || value === 'current-nfc' || this.skinBusy) return;
        if (value.startsWith('builtin:')) {
            const id = value.slice('builtin:'.length) as BuiltInSkinId;
            await this.selectBuiltInSkin(id);
            return;
        }
        if (value.startsWith('local:')) {
            const id = value.slice('local:'.length);
            const skin = this.localSkins.find((item) => item.id === id);
            if (skin) await this.applyLocalSkin(skin);
        }
    }

    public async setRendererBackend(backend: RendererBackend) {
        if (backend === 'webgpu' && !this.webGpuSupported) {
            this.rendererStatus = '이 브라우저는 WebGPU를 지원하지 않아 WebGL2를 유지합니다.';
            await this.service.render();
            return;
        }
        if (this.rendererBackendPreference === backend) return;
        this.rendererBackendPreference = backend;
        localStorage.setItem(this.rendererBackendStorageKey, backend);
        this.rendererStatus = `${backend === 'webgpu' ? 'WebGPU 실험' : 'WebGL2 안정'} 렌더러로 재시작합니다.`;
        await this.service.render();
        window.setTimeout(() => window.location.reload(), 80);
    }

    public async setEditorTheme(theme: MapTheme, event?: PointerEvent) {
        event?.preventDefault();
        if (!this.editorMap) return;
        const previousTheme = this.editorMap.theme;
        if (previousTheme === theme && theme !== 'ys-palace') return;
        const backgroundPrefix = 'ys-palace-background-';
        if (previousTheme === 'ys-palace' || theme === 'ys-palace') {
            const removedIds = new Set(this.editorMap.scenery
                .filter((item) => item.id.startsWith(backgroundPrefix))
                .map((item) => item.id));
            this.editorMap.scenery = this.editorMap.scenery.filter((item) => !item.id.startsWith(backgroundPrefix));
            if (theme !== 'ys-palace') this.clearHaechiGuideTargetReferences(removedIds);
        }
        if (theme === 'ys-palace') {
            const presetScenery = cloneMap(this.defaultMap).scenery
                .filter((item) => item.id.startsWith(backgroundPrefix));
            this.editorMap.scenery.push(...presetScenery);
            this.editorMap.mapWidth = Math.max(300, this.editorMap.mapWidth);
            this.editorMap.mapDepth = Math.max(420, this.editorMap.mapDepth);
            this.selectedSceneryIndex = 0;
        } else if (this.editorMap.scenery.length > 0) {
            this.selectedSceneryIndex = Math.min(this.selectedSceneryIndex, this.editorMap.scenery.length - 1);
        } else {
            this.selectedSceneryIndex = 0;
        }
        this.editorMap.theme = theme;
        const message = theme === 'ys-palace'
            ? 'YS 궁궐 담장 배경을 적용했습니다. 높은 담장 52개와 사방 자동문 4개가 포함됩니다.'
            : `${this.themeLabel(theme)} 배경을 선택했습니다.`;
        this.markEditorChanged(message);
        await this.service.render();
    }

    public async uploadGroundImage(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file || !this.editorMap || this.groundImageBusy) return;
        const editorMapId = this.editorMap.id;

        this.groundImageBusy = true;
        this.groundImageStatus = '바닥 이미지를 최적화하는 중입니다.';
        await this.service.render();
        try {
            if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
                && !/\.(?:jpe?g|png|webp)$/i.test(file.name)) {
                throw new Error('JPG, PNG, WEBP 이미지만 올릴 수 있습니다.');
            }
            if (file.size > this.maxGroundImageFileSize) {
                throw new Error('바닥 원본 이미지는 10MB 이하만 올릴 수 있습니다.');
            }
            const groundImage = await this.optimizeGroundImage(file);
            if (!this.editorMap || this.editorMap.id !== editorMapId) {
                throw new Error('이미지 처리 중 편집기가 닫혀 적용하지 않았습니다.');
            }
            this.editorMap.groundImage = groundImage;
            this.editorMap.terrain = undefined;
            this.terrainDraft = null;
            this.terrainDraftDirty = false;
            this.terrainUndoCells = null;
            this.groundImageStatus = `${groundImage.fileName} · ${groundImage.width} × ${groundImage.height}px · 저장용으로 자동 최적화됨`;
            this.markEditorChanged('업로드한 이미지를 전체 맵 바닥에 적용했습니다.');
        } catch (error: any) {
            this.groundImageStatus = error?.message || '바닥 이미지를 적용하지 못했습니다.';
        } finally {
            this.groundImageBusy = false;
            await this.service.render();
        }
    }

    public removeGroundImage() {
        if (!this.editorMap?.groundImage) return;
        this.editorMap.groundImage = undefined;
        this.terrainDraft = this.createTerrainDraft();
        this.terrainDraftDirty = false;
        this.terrainUndoCells = null;
        this.groundImageStatus = '업로드 이미지를 제거했습니다. 바닥을 직접 칠하거나 테마 자동 배경을 사용할 수 있습니다.';
        this.markEditorChanged('업로드한 바닥 이미지를 제거했습니다.');
        setTimeout(() => this.scheduleTerrainCanvasRender(), 0);
    }

    public get activeCharacterStats(): CharacterStats {
        if (this.hasCustomSkin || !this.selectedBuiltInSkinId) return this.customCharacterStats;
        return this.builtInSkins.find((item) => item.id === this.selectedBuiltInSkinId)?.stats || { jump: 5, steering: 5 };
    }

    public get activeCharacterSpecialty(): string {
        return this.characterStatsSpecialty(this.activeCharacterStats);
    }

    public characterStatTotal(preset: BuiltInSkinPreset): number {
        return preset.stats.jump + preset.stats.steering;
    }

    public characterStatsSpecialty(stats: CharacterStats): string {
        if (stats.jump > stats.steering) return '점프 특화';
        if (stats.steering > stats.jump) return '좌우 특화';
        return '균형형';
    }

    public setEyeStyle(style: EyeStyle) {
        this.characterCustomization.eyes = style;
        this.commitCharacterCustomization('눈 스타일을 적용했습니다.');
    }

    public setNoseStyle(style: NoseStyle) {
        this.characterCustomization.nose = style;
        this.commitCharacterCustomization('코 스타일을 적용했습니다.');
    }

    public setMouthStyle(style: MouthStyle) {
        this.characterCustomization.mouth = style;
        this.commitCharacterCustomization('입 스타일을 적용했습니다.');
    }

    public setHairStyle(style: HairStyle) {
        this.characterCustomization.hair = style;
        this.commitCharacterCustomization('관모·머리 스타일을 적용했습니다.');
    }

    public resetCharacterCustomization() {
        this.characterCustomization = { eyes: 'calm', nose: 'soft', mouth: 'gentle', hair: 'gat' };
        this.commitCharacterCustomization('조선시대 기본 꾸미기로 되돌렸습니다.');
    }

    public setCharacterCustomizationEnabled(enabled: boolean) {
        this.characterCustomizationEnabled = enabled;
        this.commitCharacterCustomization(enabled ? '얼굴·관모 꾸미기를 사용합니다.' : '원본 캐릭터 모습만 사용합니다.');
    }

    public async saveCharacterName() {
        const name = this.characterNameDraft.trim().slice(0, 30);
        if (!name) {
            this.skinStatus = '캐릭터 이름을 한 글자 이상 입력해주세요.';
            this.requestUiRender();
            return;
        }
        this.skinName = name;
        if (this.hasCustomSkin) {
            await this.persistSkin();
            await this.syncActiveLocalSkin();
        } else if (this.selectedBuiltInSkinId) {
            const preset = this.builtInSkins.find((item) => item.id === this.selectedBuiltInSkinId);
            if (preset) preset.name = name;
            this.persistBuiltInCharacterProfiles();
        }
        this.skinStatus = `캐릭터 이름을 “${name}”(으)로 저장했습니다.`;
        this.requestUiRender();
    }

    public async rollCharacterStats() {
        const jump = 3 + Math.floor(Math.random() * 5);
        const stats: CharacterStats = { jump, steering: 10 - jump };
        if (this.hasCustomSkin || !this.selectedBuiltInSkinId) {
            this.customCharacterStats = stats;
            await this.persistSkin();
            await this.syncActiveLocalSkin();
        } else {
            const preset = this.builtInSkins.find((item) => item.id === this.selectedBuiltInSkinId);
            if (preset) preset.stats = stats;
            this.persistBuiltInCharacterProfiles();
        }
        this.skinStatus = `🎲 능력치: 점프 ${stats.jump} · 좌우 ${stats.steering} · 합계 10`;
        this.requestUiRender();
    }

    public registerRanking() {
        if (this.screen !== 'result' || this.rankingSubmitted) return;
        const playerName = this.rankingNameInput.trim().slice(0, 20);
        if (!playerName) {
            this.rankingStatus = '랭킹에 표시할 이름을 입력해주세요.';
            this.requestUiRender();
            return;
        }
        const entry: LocalRankingEntry = {
            id: createId('ranking'),
            runId: this.currentRunId || createId('run'),
            mapId: this.activeMap.id,
            mapTitle: this.activeMap.title,
            mode: this.gameMode,
            playerName,
            characterName: this.skinName,
            score: this.totalScore,
            baseScore: this.baseScore,
            hiddenBonus: this.hiddenNpcScore,
            coinScore: this.coinScore,
            hiddenScore: this.hiddenNpcScore,
            timeScore: this.timeScore,
            quizScore: this.quizScore,
            obstacleCollisions: this.obstacleCollisionCount,
            time: this.runTime,
            coins: this.coinCount,
            accuracy: this.accuracy,
            createdAt: new Date().toISOString()
        };
        this.localRankings = [entry, ...this.localRankings]
            .sort((a, b) => b.score - a.score || a.time - b.time || b.createdAt.localeCompare(a.createdAt))
            .slice(0, 500);
        localStorage.setItem(this.rankingStorageKey, JSON.stringify(this.localRankings));
        localStorage.setItem(this.rankingNameStorageKey, playerName);
        this.rankingSubmitted = true;
        const rank = this.rankingsFor(this.activeMap.id, this.gameMode).findIndex((item) => item.id === entry.id) + 1;
        this.rankingStatus = `${playerName} 기록을 ${this.modeLabel} ${rank}위로 등록했습니다.`;
        this.requestUiRender();
    }

    public beginCustomizationPreviewRotate(event: PointerEvent) {
        if (event.button !== 0) return;
        this.customizationPreviewPointerId = event.pointerId;
        this.customizationPreviewLastX = event.clientX;
        this.customizationPreviewDragging = true;
        (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
        event.preventDefault();
        this.requestUiRender();
    }

    public rotateCustomizationPreview(event: PointerEvent) {
        if (!this.customizationPreviewDragging || this.customizationPreviewPointerId !== event.pointerId) return;
        const deltaX = event.clientX - this.customizationPreviewLastX;
        this.customizationPreviewLastX = event.clientX;
        const nextRotation = this.customizationPreviewRotation + deltaX * 0.9;
        this.customizationPreviewRotation = Math.round((((nextRotation % 360) + 360) % 360) * 10) / 10;
        this.applyCustomizationPreviewRotation();
        event.preventDefault();
        this.requestUiRender();
    }

    public endCustomizationPreviewRotate(event?: PointerEvent) {
        if (event && this.customizationPreviewPointerId !== null && event.pointerId !== this.customizationPreviewPointerId) return;
        const target = event?.currentTarget as HTMLElement | undefined;
        if (event && target?.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);
        this.customizationPreviewPointerId = null;
        this.customizationPreviewDragging = false;
        this.requestUiRender();
    }

    public resetCustomizationPreviewRotation() {
        this.customizationPreviewRotation = 0;
        this.applyCustomizationPreviewRotation();
        this.requestUiRender();
    }

    public beginNfcPreviewRotate(event: PointerEvent) {
        if (!this.nfcPreviewReady || event.button !== 0) return;
        this.nfcPreviewPointerId = event.pointerId;
        this.nfcPreviewLastX = event.clientX;
        this.nfcPreviewDragging = true;
        (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
        event.preventDefault();
        this.requestUiRender();
    }

    public rotateNfcPreview(event: PointerEvent) {
        if (!this.nfcPreviewDragging || this.nfcPreviewPointerId !== event.pointerId) return;
        const deltaX = event.clientX - this.nfcPreviewLastX;
        this.nfcPreviewLastX = event.clientX;
        this.nfcPreviewRotationY = this.normalizeSkinRotation(this.nfcPreviewRotationY + deltaX * 0.9);
        this.nfcDraftSaved = false;
        this.applyNfcPreviewRotation();
        event.preventDefault();
        this.requestUiRender();
    }

    public endNfcPreviewRotate(event?: PointerEvent) {
        if (event && this.nfcPreviewPointerId !== null && event.pointerId !== this.nfcPreviewPointerId) return;
        const target = event?.currentTarget as HTMLElement | undefined;
        if (event && target?.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);
        this.nfcPreviewPointerId = null;
        this.nfcPreviewDragging = false;
        this.requestUiRender();
    }

    public resetNfcPreviewRotation() {
        if (!this.nfcPreviewReady) return;
        this.nfcPreviewRotationY = this.nfcPreviewInitialRotationY;
        this.nfcDraftSaved = false;
        this.applyNfcPreviewRotation();
        this.requestUiRender();
    }

    public get magnetTimeLabel(): string {
        return Math.max(0, this.magnetRemaining).toFixed(1);
    }

    public get supplementTimeLabel(): string {
        return Math.max(0, this.supplementRemaining).toFixed(1);
    }

    public get mountTimeLabel(): string {
        return Math.max(0, this.mountRemaining).toFixed(1);
    }

    public get mountDurationLabel(): string {
        return this.gameMode === 'explore' && this.activeMountKind ? '제한 없음' : `${this.mountTimeLabel}초`;
    }

    public get hasActiveMount(): boolean {
        return !!this.activeMountKind;
    }

    public get activeMountLabel(): string {
        return this.mountLabel(this.activeMountKind);
    }

    public get selectedWaypoint(): MapWaypoint | null {
        return this.editorMap?.waypoints[this.selectedWaypointIndex] || null;
    }

    public get selectedCoin(): MapObjectPlacement | null {
        return this.editorMap?.coins[this.selectedCoinIndex] || null;
    }

    public get selectedMagnet(): MapObjectPlacement | null {
        return this.editorMap?.magnets[this.selectedMagnetIndex] || null;
    }

    public get selectedSupplement(): MapObjectPlacement | null {
        return this.editorMap?.supplements[this.selectedSupplementIndex] || null;
    }

    public get editorItems(): EditorItemReference[] {
        if (!this.editorMap) return [];
        return [
            ...this.editorMap.coins.map((placement, index) => ({ kind: 'coin' as ItemKind, collection: 'coins' as ItemCollection, index, placement })),
            ...this.editorMap.magnets.map((placement, index) => ({ kind: 'magnet' as ItemKind, collection: 'magnets' as ItemCollection, index, placement })),
            ...this.editorMap.supplements.map((placement, index) => ({ kind: 'supplement' as ItemKind, collection: 'supplements' as ItemCollection, index, placement }))
        ].sort((left, right) => left.placement.distance - right.placement.distance);
    }

    public get selectedItemCollection(): ItemCollection {
        return this.itemCollection(this.selectedItemKind);
    }

    public get selectedItem(): MapObjectPlacement | null {
        if (!this.editorMap) return null;
        return this.editorMap[this.selectedItemCollection][this.selectedPlacementIndex(this.selectedItemCollection)] || null;
    }

    public get selectedMount(): MapMountPlacement | null {
        return this.editorMap?.mounts[this.selectedMountIndex] || null;
    }

    public get selectedObstacle(): MapObjectPlacement | null {
        return this.editorMap?.obstacles[this.selectedObstacleIndex] || null;
    }

    public get selectedGate(): MapGate | null {
        return this.editorMap?.gates[this.selectedGateIndex] || null;
    }

    public get selectedScenery(): MapSceneryPlacement | null {
        return this.editorMap?.scenery[this.selectedSceneryIndex] || null;
    }

    public get haechiGuideTargetOptions(): MapSceneryPlacement[] {
        return (this.editorMap?.scenery || []).filter((item) => this.isHaechiGuideBuildingKind(item.kind));
    }

    public get activeHaechiGuideTargetName(): string {
        const targetId = this.activeHaechiGuide?.placement.guideTargetId;
        const target = targetId ? this.activeMap.scenery.find((item) => item.id === targetId) : undefined;
        return target?.name || '건물 이야기';
    }

    public get activeHaechiGuideText(): string {
        const placement = this.activeHaechiGuide?.placement;
        if (!placement) return '';
        const target = placement.guideTargetId
            ? this.activeMap.scenery.find((item) => item.id === placement.guideTargetId)
            : undefined;
        return placement.guideText?.trim() || this.defaultHaechiGuideText(target);
    }

    public sceneryIcon(kind: SceneryKind): string {
        if (kind === 'tree') return '🌲';
        if (kind === 'pine-tree' || kind === 'pine-tree-windswept') return '🌲';
        if (kind === 'pine-tree-young') return '🌱';
        if (kind === 'building') return '🏢';
        if (kind === 'cone') return '🚧';
        if (kind === 'hanok') return '🏠';
        if (kind === 'hanok-annex') return '🏡';
        if (kind === 'hanok-pavilion') return '🏯';
        if (kind === 'hanok-palace') return '🏰';
        if (kind === 'hanok-courtyard') return '🏘️';
        if (kind === 'hanok-library') return '🏯';
        if (kind === 'hanok-study') return '🛖';
        if (kind === 'lotus-pond') return '🪷';
        if (kind === 'lotus-flower') return '🪷';
        if (kind === 'pond-rock') return '🪨';
        if (kind === 'stone-bridge') return '🌉';
        if (kind === 'stone-stairs') return '🪨';
        if (kind === 'stone-wall') return '🧱';
        if (kind === 'jangdok') return '🏺';
        if (kind === 'stone-lantern') return '🏮';
        if (kind === 'wooden-fence') return '🪵';
        if (kind === 'harbor-warehouse') return '🛖';
        if (kind === 'fishing-boat') return '⛵';
        if (kind === 'haechi-guide') return '🦁';
        if (isKimYeeunDecorationKind(kind)) return '🏛️';
        if (kind === 'ys') return '🏛️';
        return '📦';
    }

    public mountIcon(kind: MountKind | null): string {
        if (kind === 'random') return '🐎';
        if (kind === 'horse') return '🐎';
        if (kind === 'ox') return '🐂';
        if (kind === 'tiger') return '🐅';
        if (kind === 'panokseon') return '⛵';
        if (kind === 'turtle-ship') return '🐢';
        return '🪑';
    }

    public mountLabel(kind: MountKind | null): string {
        if (kind === 'random') return '랜덤 탈것';
        if (kind === 'horse') return '말';
        if (kind === 'ox') return '소';
        if (kind === 'tiger') return '호랑이';
        if (kind === 'panokseon') return '판옥선';
        if (kind === 'turtle-ship') return '거북선';
        if (kind === 'palanquin') return '가마';
        return '';
    }

    public itemIcon(kind: ItemKind): string {
        if (kind === 'magnet') return '🧲';
        if (kind === 'supplement') return '💊';
        return '●';
    }

    public itemLabel(kind: ItemKind): string {
        if (kind === 'magnet') return '자석';
        if (kind === 'supplement') return '영양제';
        return '코인';
    }

    public obstacleIcon(kind: ObstacleKind | undefined): string {
        if (kind === 'fence') return '🪵';
        if (kind === 'scarecrow') return '🌾';
        if (kind === 'fortress-wall') return '🏯';
        return '▲';
    }

    public obstacleLabel(kind: ObstacleKind | undefined): string {
        if (kind === 'fence') return '울타리';
        if (kind === 'scarecrow') return '허수아비';
        if (kind === 'fortress-wall') return '성벽';
        return '기본 장애물';
    }

    public get selectedQuestion(): QuizQuestion | null {
        return this.selectedGate?.questions[this.selectedQuestionIndex] || null;
    }

    public get editorPathPoints(): string {
        if (!this.editorMap) return '';
        const points: string[] = [];
        const samples = Math.min(320, Math.max(96, this.editorMap.waypoints.length * 14));
        for (let index = 0; index <= samples; index++) {
            const progress = index / samples;
            const point = this.getEditorPlanPoint(progress * this.editorMap.trackLength);
            points.push(`${this.planWorldXCoordinate(point.x)},${this.planWorldZCoordinate(point.z)}`);
        }
        return points.join(' ');
    }

    public get editorLeftLaneGuidePoints(): string {
        return this.buildEditorLaneGuidePoints(-1);
    }

    public get editorRightLaneGuidePoints(): string {
        return this.buildEditorLaneGuidePoints(1);
    }

    public get editorLoopMarkers(): EditorLoopMarker[] {
        if (!this.editorMap) return [];
        return this.editorMap.waypoints
            .filter((waypoint) => (waypoint.roadPiece === 'loop-one' || waypoint.roadPiece === 'loop-two')
                && waypoint.name.endsWith('· 진입'))
            .map((waypoint) => ({
                id: waypoint.id,
                left: this.planWorldXPercent(waypoint.x),
                top: this.planWorldZPercent(waypoint.z),
                label: waypoint.roadPiece === 'loop-two' ? '수직 2회전' : '수직 1회전'
            }));
    }

    public get editorJumpMarkers(): EditorLoopMarker[] {
        if (!this.editorMap) return [];
        return this.editorMap.waypoints
            .filter((waypoint) => this.isJumpRoadPiece(waypoint.roadPiece || '') && waypoint.name.endsWith('· 시작'))
            .map((waypoint) => ({
                id: waypoint.id,
                left: this.planWorldXPercent(waypoint.x),
                top: this.planWorldZPercent(waypoint.z),
                label: waypoint.roadPiece === 'jump-wide'
                    ? '넓은 징검발판'
                    : (waypoint.roadPiece === 'jump-stairs' ? '완만한 계단 발판' : '넓은 지그재그 발판')
            }));
    }

    public get editorRoadWidth(): number {
        const naturalWidth = this.editorNaturalRoadWidth;
        const requestedWidth = THREE.MathUtils.clamp(naturalWidth * 1.12, 18, 96);
        return Math.round(requestedWidth * 10) / 10;
    }

    public get editorBackgroundWidth(): number {
        return Math.min(220, this.editorRoadWidth + 72);
    }

    public get editorHasCustomTerrain(): boolean {
        if (this.editorMap?.groundImage) return true;
        const terrain = this.terrainDraftDirty ? this.terrainDraft : this.editorMap?.terrain;
        return !!terrain
            && terrain.columns > 0
            && terrain.rows > 0
            && terrain.cells.length === terrain.columns * terrain.rows;
    }

    public get editorRoadSurfaceColor(): string {
        if (this.editorMap?.theme === 'beach') return '#c7a16c';
        if (this.editorMap?.theme === 'neon') return '#263957';
        return '#6b655c';
    }

    public get editorRoadShoulderColor(): string {
        if (this.editorMap?.theme === 'beach') return '#8f744f';
        if (this.editorMap?.theme === 'neon') return '#12243d';
        return '#3f392f';
    }

    public get editorBackgroundZoneColor(): string {
        if (this.editorMap?.theme === 'beach') return '#66573d';
        if (this.editorMap?.theme === 'neon') return '#11243a';
        if (this.editorMap?.theme === 'joseon-harbor') return '#31483b';
        if (this.editorMap?.theme === 'ys-palace') return '#5b4430';
        return '#294435';
    }

    public get editorLateralVisualScale(): number {
        return Math.round((this.editorRoadWidth / Math.max(0.1, this.editorNaturalRoadWidth)) * 100) / 100;
    }

    public get editorRoadDisplayPercent(): number {
        return Math.round((this.editorRoadWidth / Math.max(0.1, this.editorNaturalRoadWidth)) * 100);
    }

    public get editorZoomPercent(): number {
        return Math.round(this.editorZoom * 100);
    }

    public get editorCanvasHeight(): number {
        const courseLength = this.editorMap?.trackLength || this.editorKnownTrackLength || 900;
        const mapDepth = this.editorMap?.mapDepth || courseLength;
        return Math.round(THREE.MathUtils.clamp(Math.max(courseLength, mapDepth) * this.editorPixelsPerDistance, 680, 24000));
    }

    public get editorScaledCanvasHeight(): number {
        return Math.round(this.editorCanvasHeight * this.editorZoom);
    }

    public get editorDensityLabel(): string {
        return `${this.editorPixelsPerDistance.toFixed(1)} px/m`;
    }

    public get editorMapAreaX(): number {
        const centerX = (this.editorPlanBounds.minX + this.editorPlanBounds.maxX) / 2;
        return this.planWorldXCoordinate(centerX + (this.editorMap?.mapWidth || 600) / 2);
    }

    public get editorMapAreaY(): number {
        const centerZ = (this.editorPlanBounds.minZ + this.editorPlanBounds.maxZ) / 2;
        return this.planWorldZCoordinate(centerZ + (this.editorMap?.mapDepth || 1200) / 2);
    }

    public get editorMapAreaWidth(): number {
        const span = Math.max(1, this.editorPlanBounds.maxX - this.editorPlanBounds.minX);
        return Math.min(920, ((this.editorMap?.mapWidth || 600) / span) * 920);
    }

    public get editorMapAreaHeight(): number {
        const span = Math.max(1, this.editorPlanBounds.maxZ - this.editorPlanBounds.minZ);
        return Math.min(920, ((this.editorMap?.mapDepth || 1200) / span) * 920);
    }

    public planPlacementLeft(item: MapObjectPlacement): number {
        const point = this.getMapObjectPlanPoint(item);
        return this.planWorldXPercent(point.x);
    }

    public planPlacementTop(item: MapObjectPlacement): number {
        const point = this.getMapObjectPlanPoint(item);
        return this.planWorldZPercent(point.z);
    }

    public planWaypointLeft(waypoint: MapWaypoint): number {
        return this.planWorldXPercent(waypoint.x);
    }

    public planWaypointTop(index: number): number {
        const waypoint = this.editorMap?.waypoints[index];
        return this.planWorldZPercent(waypoint?.z || 0);
    }

    public planSceneryLeft(item: MapSceneryPlacement): number {
        return this.planWorldXPercent(this.getSceneryPlanPoint(item).x);
    }

    public planSceneryTop(item: MapSceneryPlacement): number {
        return this.planWorldZPercent(this.getSceneryPlanPoint(item).z);
    }

    public planSceneryRotation(item: MapSceneryPlacement): number {
        return this.normalizeEditorRotation(-(Number(item.rotation) || 0));
    }

    public async ngOnInit() {
        this.loadSavedMaps();
        this.characterCustomizationEnabled = false;
        localStorage.removeItem(this.characterCustomizationStorageKey);
        this.restoreBuiltInCharacterProfiles();
        this.applyCharacterBalanceMigration();
        this.restoreRankings();
        this.restorePerformancePreferences();
        this.restoreAdminSettings();
        await this.service.init();
        await this.service.render();
    }

    public async ngAfterViewInit() {
        try {
            await this.initializeRenderer();
            this.statusText = '기본 3D 월드를 구성하고 있습니다.';
            this.requestUiRender();
            this.rebuildWorld();
            this.worldReady = true;
            this.statusText = '준비 완료. 코스에 도전해보세요.';
            this.requestUiRender();
            this.scheduleDeferredStartupWork();
        } catch (error: any) {
            this.loadError = error?.message || 'WebGL 3D 월드를 초기화하지 못했습니다.';
            this.requestUiRender();
        }
    }

    public ngOnDestroy() {
        this.destroyed = true;
        this.nfcSessionSequence += 1;
        this.abortNfcScan();
        this.nfcDownloadRequest?.abort();
        this.nfcDownloadRequest = undefined;
        if (this.animationFrameId !== null) cancelAnimationFrame(this.animationFrameId);
        if (this.terrainRenderFrame !== null) cancelAnimationFrame(this.terrainRenderFrame);
        this.disposeScene();
        this.disposeCustomizationPreview();
        this.disposeNfcPreview();
        this.builtInCharacterTemplateCache.forEach((template) => this.disposeObjectTree(template, true));
        this.builtInCharacterTemplateCache.clear();
        this.decorationTemplateCache.forEach((template) => this.disposeObjectTree(template, true));
        this.decorationTemplateCache.clear();
        this.decorationTemplateLoadPromises.clear();
        this.builtInDecorationTemplateCache.forEach((template) => this.disposeObjectTree(template, true));
        this.builtInDecorationTemplateCache.clear();
        this.ysDecorationTemplateCache.forEach((template) => this.disposeObjectTree(template, true));
        this.ysDecorationTemplateCache.clear();
        this.ysDecorationLoadPromises.clear();
        this.ysDecorationSurfaceCache.clear();
        this.ysDecorationSurfaceLoadPromises.clear();
        this.bundledNatureTemplateCache.forEach((template) => this.disposeObjectTree(template, true));
        this.bundledNatureTemplateCache.clear();
        this.bundledNatureLoadPromises.clear();
        if (this.king2AssetSource) this.disposeObjectTree(this.king2AssetSource, true);
        this.king2AssetSource = undefined;
        if (this.haetaeAssetSource) this.disposeObjectTree(this.haetaeAssetSource, true);
        this.haetaeAssetSource = undefined;
        this.heritageTextureCache.forEach((texture) => texture.dispose());
        this.heritageTextureCache.clear();
        this.heritageBaseTextureCache.forEach((texture) => texture.dispose());
        this.heritageBaseTextureCache.clear();
        this.heritageTextureLoadPromises.clear();
        if (this.renderer) {
            this.renderer.dispose();
            this.renderer.domElement.remove();
        }
    }

    public startGame(mode: GameMode = this.gameMode) {
        if (!this.worldReady) return;
        this.mapAssetRequestSequence += 1;
        const modeChanged = this.gameMode !== mode;
        this.gameMode = mode;
        if (modeChanged) this.rebuildWorld();
        else this.resetRun();
        this.currentRunId = createId('run');
        this.rankingSubmitted = false;
        this.rankingStatus = '완주 후 이름을 입력하면 이 브라우저의 맵별 랭킹에 등록됩니다.';
        this.screen = 'playing';
        this.setMenuHeritageShowcaseVisible(false);
        this.statusText = mode === 'runner'
            ? `“${this.activeMap.title}” 달리기를 시작합니다.`
            : `“${this.activeMap.title}” 자유 탐색을 시작합니다. 방향키로 원하는 곳을 살펴보세요.`;
        this.requestUiRender();
    }

    public restartGame() {
        this.startGame();
    }

    public pauseGame() {
        if (this.screen !== 'playing') return;
        this.screen = 'paused';
        this.clearControls();
        this.statusText = '게임이 일시정지되었습니다.';
        this.requestUiRender();
    }

    public resumeGame() {
        if (this.screen !== 'paused') return;
        this.screen = 'playing';
        this.statusText = '게임을 다시 시작합니다.';
        this.requestUiRender();
    }

    public closeHaechiGuide() {
        if (!this.activeHaechiGuide) return;
        const targetName = this.activeHaechiGuideTargetName;
        this.activeHaechiGuide = undefined;
        this.statusText = `${targetName} 설명을 확인했습니다. 자유롭게 탐색을 계속하세요.`;
        this.requestUiRender();
    }

    public goHub() {
        this.service.href('/games/frog');
    }

    public showMenu() {
        this.mapAssetRequestSequence += 1;
        this.isEditorTest = false;
        this.activeHaechiGuide = undefined;
        this.screen = 'menu';
        this.clearControls();
        this.setMenuHeritageShowcaseVisible(true);
        this.snapCamera();
        this.requestUiRender();
    }

    public showMaps() {
        this.mapAssetRequestSequence += 1;
        this.activeHaechiGuide = undefined;
        this.screen = 'maps';
        this.setMenuHeritageShowcaseVisible(false);
        this.importMessage = '';
        this.clearControls();
        this.requestUiRender();
        if (!this.serverMapsLoaded) void this.refreshServerMaps();
    }

    public showSkins() {
        this.mapAssetRequestSequence += 1;
        this.activeHaechiGuide = undefined;
        this.screen = 'skins';
        this.setMenuHeritageShowcaseVisible(false);
        this.clearControls();
        this.requestUiRender();
        setTimeout(() => this.initializeCustomizationPreview(), 0);
    }

    public async playMap(map: GameMap, mode: GameMode = 'runner') {
        const requestId = ++this.mapAssetRequestSequence;
        const requestScreen = this.screen;
        const needsDecorationAssets = map.scenery.some((item) => item.kind === 'custom'
            || item.kind === 'ys'
            || item.kind === 'haechi-guide'
            || isBundledNatureDecorationKind(item.kind));
        if (needsDecorationAssets) {
            this.statusText = '선택한 맵의 3D 장식을 준비하고 있습니다.';
            this.requestUiRender();
            await this.prepareMapDecorationAssets(map);
            if (this.destroyed || requestId !== this.mapAssetRequestSequence || this.screen !== requestScreen) return;
        }
        this.isEditorTest = false;
        this.activeMap = cloneMap(map);
        this.gameMode = mode;
        this.rebuildWorld();
        this.startGame(mode);
    }

    public createMap() {
        this.mapAssetRequestSequence += 1;
        const source = cloneMap(this.defaultMap);
        const now = new Date().toISOString();
        source.id = createId('map');
        source.title = `${source.title} 새 맵`;
        source.description = '직접 제작한 Quiz Rush 3D 코스입니다.';
        source.author = '로컬 제작자';
        source.visibility = 'private';
        source.createdAt = now;
        source.updatedAt = now;
        source.builtIn = false;
        source.hiddenNpcCount = 0;
        source.coins = [];
        source.magnets = [];
        source.supplements = [];
        source.mounts = [];
        source.obstacles = [];
        source.gates = [];
        source.scenery = [];
        source.theme = 'neon';
        source.terrain = undefined;
        source.groundImage = undefined;
        this.openEditor(source);
        void this.ensureDecorationAssetsReady();
    }

    public async editMap(map: GameMap) {
        const requestId = ++this.mapAssetRequestSequence;
        const requestScreen = this.screen;
        const source = cloneMap(map);
        await this.prepareMapDecorationAssets(source);
        if (this.destroyed || requestId !== this.mapAssetRequestSequence || this.screen !== requestScreen) return;
        if (source.builtIn) {
            source.id = createId('map');
            source.title = `${source.title} 편집본`;
            source.builtIn = false;
            source.createdAt = new Date().toISOString();
        }
        this.openEditor(source);
        void this.ensureDecorationAssetsReady();
    }

    public duplicateMap(map: GameMap) {
        const duplicate = cloneMap(map);
        duplicate.id = createId('map');
        duplicate.title = `${map.title} 복제본`;
        duplicate.builtIn = false;
        duplicate.createdAt = new Date().toISOString();
        duplicate.updatedAt = duplicate.createdAt;
        this.savedMaps.unshift(normalizeMap(duplicate));
        this.persistMaps();
        this.importMessage = '맵을 복제했습니다.';
        this.requestUiRender();
    }

    public deleteMap(map: GameMap) {
        if (map.builtIn) return;
        this.mapAssetRequestSequence += 1;
        this.savedMaps = this.savedMaps.filter((item) => item.id !== map.id);
        this.persistMaps();
        if (this.activeMap.id === map.id) {
            this.activeMap = cloneMap(this.defaultMap);
            this.rebuildWorld();
        }
        this.importMessage = '로컬 맵을 삭제했습니다.';
        this.requestUiRender();
    }

    public async refreshServerMaps() {
        if (this.serverSyncBusy) return;
        this.serverSyncBusy = true;
        this.serverMapStatus = '서버 맵을 불러오는 중입니다.';
        this.requestUiRender();
        try {
            const { code, data } = await wiz.call('server_list');
            if (code !== 200) throw new Error(typeof data === 'string' ? data : data?.message || '서버 맵을 불러오지 못했습니다.');
            const rawMaps = Array.isArray(data?.maps) ? data.maps : [];
            this.serverMaps = rawMaps.map((item: any) => normalizeMap(item)).filter((item: GameMap) => !item.builtIn);
            this.serverMapsLoaded = true;
            this.serverMapStatus = this.serverMaps.length > 0
                ? `서버에 저장된 맵 ${this.serverMaps.length}개를 동기화했습니다.`
                : '서버에 저장된 맵이 없습니다.';
        } catch (error: any) {
            this.serverMapStatus = `${error?.message || '서버 맵을 불러오지 못했습니다.'} 로컬 맵은 계속 사용할 수 있습니다.`;
        } finally {
            this.serverSyncBusy = false;
            this.requestUiRender();
        }
    }

    public async uploadMapToServer(map: GameMap) {
        if (map.builtIn || this.serverSyncBusy) return;
        this.serverSyncBusy = true;
        this.serverMapStatus = `“${map.title}” 맵을 서버에 저장하는 중입니다.`;
        this.requestUiRender();
        try {
            const payload = cloneMap(map);
            const { code, data } = await wiz.call('server_save', { map: JSON.stringify(payload) });
            if (code !== 200) throw new Error(typeof data === 'string' ? data : data?.message || '서버 저장에 실패했습니다.');
            const stored = normalizeMap(data?.map || payload);
            const index = this.serverMaps.findIndex((item) => item.id === stored.id);
            if (index === -1) this.serverMaps.unshift(stored);
            else this.serverMaps[index] = stored;
            this.serverMapStatus = `“${stored.title}” 맵을 서버에 저장했습니다.`;
        } catch (error: any) {
            this.serverMapStatus = `${error?.message || '서버 저장에 실패했습니다.'} 브라우저 저장본은 유지됩니다.`;
        } finally {
            this.serverSyncBusy = false;
            this.requestUiRender();
        }
    }

    public async deleteServerMap(map: GameMap) {
        if (!this.isServerMap(map) || this.serverSyncBusy) return;
        this.serverSyncBusy = true;
        this.serverMapStatus = `“${map.title}” 서버 맵을 삭제하는 중입니다.`;
        this.requestUiRender();
        try {
            const { code, data } = await wiz.call('server_remove', { id: map.id });
            if (code !== 200) throw new Error(typeof data === 'string' ? data : data?.message || '서버 맵 삭제에 실패했습니다.');
            this.serverMaps = this.serverMaps.filter((item) => item.id !== map.id);
            this.serverMapStatus = `“${map.title}” 서버 저장본을 삭제했습니다.`;
        } catch (error: any) {
            this.serverMapStatus = error?.message || '서버 맵 삭제에 실패했습니다.';
        } finally {
            this.serverSyncBusy = false;
            this.requestUiRender();
        }
    }

    public exportMap(map: GameMap) {
        const safeName = map.title.replace(/[^a-zA-Z0-9가-힣_-]+/g, '-').replace(/^-+|-+$/g, '') || 'quiz-rush-map';
        this.service.file.download(cloneMap(map), `${safeName}.json`);
        this.importMessage = '공유용 JSON 파일을 내려받았습니다.';
        this.requestUiRender();
    }

    public async importMap(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) return;

        try {
            if (file.size > this.maxMapFileSize) throw new Error('맵 파일은 1MB 이하만 가져올 수 있습니다.');
            const parsed = JSON.parse(await file.text());
            const map = normalizeMap(parsed);
            map.id = createId('map');
            map.title = `${map.title} 가져옴`;
            map.builtIn = false;
            map.createdAt = new Date().toISOString();
            map.updatedAt = map.createdAt;
            this.savedMaps.unshift(map);
            this.persistMaps();
            this.importMessage = '맵을 가져왔습니다. 바로 플레이하거나 편집할 수 있습니다.';
        } catch (error: any) {
            this.importMessage = error?.message || '맵 파일을 가져오지 못했습니다.';
        }
        this.requestUiRender();
    }

    public setEditorTab(tab: EditorTab) {
        this.editorTab = tab;
        if (tab === 'terrain') {
            this.selectedPaletteKind = null;
            this.editorDropShadow = null;
            if (!this.editorMap?.groundImage && !this.terrainDraft) {
                this.terrainDraft = this.createTerrainDraft();
                this.terrainDraftDirty = false;
                this.terrainUndoCells = null;
            }
            this.editorMessage = this.editorMap?.groundImage
                ? '업로드한 이미지가 전체 맵 바닥으로 적용되어 있습니다.'
                : '바닥 종류를 선택하고 캔버스에 그린 뒤 “배경 저장”을 누르세요.';
        } else {
            this.terrainPainting = false;
            this.terrainPointerId = null;
        }
        this.requestUiRender();
        setTimeout(() => this.scheduleTerrainCanvasRender(), 0);
    }

    public setEditorViewMode(mode: EditorViewMode) {
        if (!this.editorMap || this.editorViewMode === mode) return;
        this.clearControls();
        this.clearEditor3dGhost();
        this.editorDropShadow = null;
        this.editorDragActive = false;
        this.draggingPaletteKind = null;
        this.editorViewMode = mode;
        if (mode === '3d') {
            this.gameMode = 'explore';
            this.exploreCameraMode = 'shoulder';
            this.exploreCameraPitchOffset = 0;
            this.previewEditor(false);
            this.editorMessage = '3D 캐릭터 시점입니다. 좌클릭으로 오브젝트를 선택해 옵션을 조절하거나 Delete 키로 삭제할 수 있습니다.';
        } else {
            this.focusEditorDistance(this.selectedEditorDistance());
            this.editorMessage = '2D 탑뷰로 전환했습니다. 팔레트 항목을 원하는 좌표에 놓으세요.';
            this.scheduleTerrainCanvasRender();
        }
        this.requestUiRender();
        setTimeout(() => this.handleResize(), 0);
    }

    public get canUndoEditorChange(): boolean {
        return !!this.editorMap && this.editorUndoStack.length > 0;
    }

    public get canRedoEditorChange(): boolean {
        return !!this.editorMap && this.editorRedoStack.length > 0;
    }

    public get editorUndoCount(): number {
        return this.editorUndoStack.length;
    }

    public get editorRedoCount(): number {
        return this.editorRedoStack.length;
    }

    public get editorUndoTitle(): string {
        return this.canUndoEditorChange
            ? `되돌리기 · ${this.editorHistoryCurrent?.label || '최근 변경'}`
            : '편집을 연 시점보다 이전으로는 돌아갈 수 없습니다.';
    }

    public get editorRedoTitle(): string {
        const entry = this.editorRedoStack[this.editorRedoStack.length - 1];
        return entry ? `다시 실행 · ${entry.label}` : '다시 실행할 변경이 없습니다.';
    }

    public undoEditorChange() {
        if (!this.canUndoEditorChange || !this.editorHistoryCurrent) return;
        const actionLabel = this.editorHistoryCurrent.label;
        const target = this.editorUndoStack.pop();
        if (!target) return;
        this.editorRedoStack.push(this.editorHistoryCurrent);
        this.editorHistoryCurrent = target;
        this.editorHistoryFingerprint = this.createEditorHistoryFingerprint(target.map, target.terrainDraft, target.terrainDraftDirty);
        this.restoreEditorHistoryEntry(target, `“${actionLabel}” 변경을 되돌렸습니다.`);
    }

    public redoEditorChange() {
        if (!this.canRedoEditorChange || !this.editorHistoryCurrent) return;
        const target = this.editorRedoStack.pop();
        if (!target) return;
        this.editorUndoStack.push(this.editorHistoryCurrent);
        this.editorHistoryCurrent = target;
        this.editorHistoryFingerprint = this.createEditorHistoryFingerprint(target.map, target.terrainDraft, target.terrainDraftDirty);
        this.restoreEditorHistoryEntry(target, `“${target.label}” 변경을 다시 실행했습니다.`);
    }

    public syncEditorFormChange(message: string = '편집 내용을 변경했습니다.') {
        this.recordEditorHistory(message);
        this.editorMessage = message;
        this.requestUiRender();
    }

    public selectTerrainBrush(kind: TerrainKind) {
        if (!this.terrainPalette.some((item) => item.kind === kind)) return;
        this.selectedTerrainKind = kind;
        this.editorMessage = `${this.terrainLabel(kind)} 브러시를 선택했습니다.`;
        this.requestUiRender();
    }

    public setTerrainBrushSize(value: number) {
        this.terrainBrushSize = Math.round(THREE.MathUtils.clamp(Number(value) || 1, 1, 5));
        this.requestUiRender();
    }

    public beginTerrainPaint(event: PointerEvent) {
        if (this.editorTab !== 'terrain' || !this.terrainDraft) return;
        event.preventDefault();
        event.stopPropagation();
        this.terrainPainting = true;
        this.terrainPointerId = event.pointerId;
        this.terrainUndoCells = [...this.terrainDraft.cells];
        (event.currentTarget as HTMLCanvasElement).setPointerCapture?.(event.pointerId);
        this.paintTerrainAtPointer(event);
    }

    public moveTerrainPaint(event: PointerEvent) {
        if (!this.terrainPainting || event.pointerId !== this.terrainPointerId) return;
        event.preventDefault();
        event.stopPropagation();
        this.paintTerrainAtPointer(event);
    }

    public finishTerrainPaint(event: PointerEvent) {
        if (event.pointerId !== this.terrainPointerId) return;
        event.preventDefault();
        event.stopPropagation();
        this.terrainPainting = false;
        this.terrainPointerId = null;
        (event.currentTarget as HTMLCanvasElement).releasePointerCapture?.(event.pointerId);
        this.editorMessage = `${this.terrainLabel(this.selectedTerrainKind)} 영역을 그렸습니다. 배경 저장 전까지는 임시 상태입니다.`;
        this.recordEditorHistory(`${this.terrainLabel(this.selectedTerrainKind)} 바닥 그리기`);
        this.requestUiRender();
    }

    public undoTerrainPaint() {
        if (!this.terrainDraft || !this.terrainUndoCells) return;
        const current = [...this.terrainDraft.cells];
        this.terrainDraft.cells = [...this.terrainUndoCells];
        this.terrainUndoCells = current;
        this.terrainDraftDirty = true;
        this.editorMessage = '마지막 바닥 그리기를 되돌렸습니다.';
        this.recordEditorHistory('바닥 그리기 취소');
        this.renderTerrainCanvas();
        this.requestUiRender();
    }

    public fillTerrainBackground() {
        if (!this.terrainDraft) return;
        this.terrainUndoCells = [...this.terrainDraft.cells];
        this.terrainDraft.cells.fill(this.selectedTerrainKind);
        if (this.editorMap) this.editorMap.groundImage = undefined;
        this.terrainDraftDirty = true;
        this.editorMessage = `전체 바닥을 ${this.terrainLabel(this.selectedTerrainKind)}으로 채웠습니다.`;
        this.recordEditorHistory(`${this.terrainLabel(this.selectedTerrainKind)}으로 전체 바닥 채우기`);
        this.renderTerrainCanvas();
        this.requestUiRender();
    }

    public saveTerrainBackground() {
        if (!this.applyTerrainDraftToEditor()) return;
        this.terrainDraftDirty = false;
        this.editorTab = 'path';
        this.markEditorChanged('바닥 배경을 저장했습니다. 이제 길과 오브젝트를 배치할 수 있습니다.');
        setTimeout(() => this.scheduleTerrainCanvasRender(), 0);
    }

    public cancelTerrainBackground() {
        this.terrainDraft = null;
        this.terrainDraftDirty = false;
        this.terrainUndoCells = null;
        this.editorTab = 'settings';
        this.editorMessage = '배경 캔버스의 임시 변경을 취소했습니다.';
        this.recordEditorHistory('임시 바닥 변경 취소');
        this.requestUiRender();
    }

    public restoreAutomaticBackground() {
        if (!this.editorMap) return;
        this.editorMap.terrain = undefined;
        this.editorMap.groundImage = undefined;
        this.terrainDraft = null;
        this.terrainDraftDirty = false;
        this.terrainUndoCells = null;
        this.editorTab = 'settings';
        this.groundImageStatus = 'JPG, PNG, WEBP 이미지를 올리면 전체 맵 바닥에 맞춰 적용합니다.';
        this.markEditorChanged('직접 만든 바닥을 제거하고 테마 자동 배경으로 복원했습니다.');
    }

    public terrainLabel(kind: TerrainKind): string {
        return this.terrainPalette.find((item) => item.kind === kind)?.label || kind;
    }

    private applyTerrainDraftToEditor(): boolean {
        if (!this.editorMap || !this.terrainDraft) return false;
        this.editorMap.groundImage = undefined;
        this.editorMap.terrain = {
            columns: this.terrainDraft.columns,
            rows: this.terrainDraft.rows,
            cells: [...this.terrainDraft.cells]
        };
        return true;
    }

    private async optimizeGroundImage(file: File): Promise<MapGroundImage> {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();
        try {
            await new Promise<void>((resolve, reject) => {
                image.onload = () => resolve();
                image.onerror = () => reject(new Error('이미지 파일을 읽을 수 없습니다.'));
                image.src = objectUrl;
            });

            const sourceWidth = Math.max(1, image.naturalWidth);
            const sourceHeight = Math.max(1, image.naturalHeight);
            const initialScale = Math.min(1, this.maxGroundImageDimension / Math.max(sourceWidth, sourceHeight));
            let width = Math.max(1, Math.round(sourceWidth * initialScale));
            let height = Math.max(1, Math.round(sourceHeight * initialScale));
            let quality = 0.88;
            let dataUrl = '';

            for (let attempt = 0; attempt < 12; attempt++) {
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const context = canvas.getContext('2d');
                if (!context) throw new Error('이미지 변환을 지원하지 않는 브라우저입니다.');
                context.drawImage(image, 0, 0, width, height);
                dataUrl = canvas.toDataURL('image/webp', quality);
                if (dataUrl.length <= this.maxGroundImageDataUrlLength) {
                    return { dataUrl, fileName: file.name.slice(0, 120), width, height };
                }
                if (quality > 0.58) {
                    quality -= 0.1;
                } else {
                    width = Math.max(1, Math.round(width * 0.82));
                    height = Math.max(1, Math.round(height * 0.82));
                    quality = 0.8;
                }
            }
            throw new Error('이미지를 저장 가능한 크기로 줄이지 못했습니다. 더 작은 이미지를 사용해 주세요.');
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    }

    public startPaletteDrag(kind: EditorPaletteKind, event: DragEvent) {
        if (!event.dataTransfer) return;
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData(this.editorDragMime, kind);
        event.dataTransfer.setData('text/plain', kind);
        this.draggingPaletteKind = kind;
        this.editorDragActive = true;
        this.editorMessage = this.editorViewMode === '3d'
            ? '3D 화면에서 반투명 섀도우를 확인한 뒤 원하는 위치에 놓으세요.'
            : '2D 탑뷰에서 실제 배치할 좌표에 놓으세요.';
        this.requestUiRender();
    }

    public get filteredYsDecorationPresets(): YsDecorationPreset[] {
        if (this.selectedYsDecorationCategory === 'all') return this.ysDecorationPresets;
        return this.ysDecorationPresets.filter((preset) => preset.category === this.selectedYsDecorationCategory);
    }

    public get selectedYsDecorationPreset(): YsDecorationPreset | undefined {
        return this.ysDecorationPresets.find((preset) => preset.id === this.selectedYsDecorationId);
    }

    public selectYsDecorationCategory(category: YsDecorationCategory | 'all') {
        this.selectedYsDecorationCategory = category;
        const visible = this.filteredYsDecorationPresets;
        if (!visible.some((preset) => preset.id === this.selectedYsDecorationId)) {
            this.selectedYsDecorationId = visible[0]?.id || '';
        }
        this.requestUiRender();
    }

    public chooseYsDecoration(preset: YsDecorationPreset) {
        this.selectedYsDecorationId = preset.id;
        this.selectedPaletteKind = 'ys-decoration';
        this.editorDropShadow = null;
        this.clearEditor3dGhost();
        this.editorMessage = `${preset.name} 배치 도구를 선택했습니다. ${this.editorViewMode === '3d' ? '3D 화면' : '2D 탑뷰'}에서 원하는 좌표를 누르세요.`;
        this.ysDecorationStatus = `${preset.name}을(를) 선택했습니다. 처음 배치할 때 모델을 불러옵니다.`;
        this.requestUiRender();
        void this.ensureYsDecorationSurfaceLoaded(preset)
            .then(() => {
                if (this.selectedYsDecorationId !== preset.id) return;
                this.clearEditor3dGhost();
                this.requestUiRender();
            })
            .catch(() => undefined);
    }

    public startYsDecorationDrag(preset: YsDecorationPreset, event: DragEvent) {
        this.chooseYsDecoration(preset);
        this.startPaletteDrag('ys-decoration', event);
    }

    public finishPaletteDrag() {
        this.draggingPaletteKind = null;
        this.editorDragActive = false;
        this.editorDropShadow = null;
        this.clearEditor3dGhost();
        this.requestUiRender();
    }

    public onEditorDragOver(event: DragEvent) {
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        const location = this.resolveEditorDrop(event.clientX, event.clientY);
        if (location && this.draggingPaletteKind) this.updateEditorDropShadow(this.draggingPaletteKind, location);
        if (!this.editorDragActive) {
            this.editorDragActive = true;
            this.requestUiRender();
        }
    }

    public onEditorDragLeave(event: DragEvent) {
        const target = event.currentTarget as HTMLElement | null;
        const related = event.relatedTarget as Node | null;
        if (target && related && target.contains(related)) return;
        this.editorDragActive = false;
        this.editorDropShadow = null;
        this.requestUiRender();
    }

    public onEditorDrop(event: DragEvent) {
        event.preventDefault();
        this.editorDragActive = false;
        this.editorDropShadow = null;
        this.draggingPaletteKind = null;
        const raw = event.dataTransfer?.getData(this.editorDragMime) || event.dataTransfer?.getData('text/plain') || '';
        if (!this.isPaletteKind(raw)) {
            this.editorMessage = '지원하는 편집 팔레트 항목만 놓을 수 있습니다.';
            this.requestUiRender();
            return;
        }
        const location = this.resolveEditorDrop(event.clientX, event.clientY);
        if (!location) {
            this.editorMessage = '2D 탑뷰 안의 원하는 좌표에 항목을 놓아주세요.';
            this.requestUiRender();
            return;
        }
        this.placeEditorPaletteItem(raw, location);
    }

    public selectPaletteItem(kind: EditorPaletteKind) {
        this.selectedPaletteKind = this.selectedPaletteKind === kind ? null : kind;
        this.editorDropShadow = null;
        const label = [...this.objectPalette, ...this.roadPalette, ...this.sceneryPalette].find((item) => item.kind === kind)?.label
            || (kind === 'custom-decoration' ? '업로드 장식' : undefined)
            || (kind === 'ys-decoration' ? this.selectedYsDecorationPreset?.name : undefined)
            || kind;
        this.editorMessage = this.selectedPaletteKind
            ? `${label} 배치 도구를 선택했습니다. ${this.editorViewMode === '3d' ? '3D 화면' : '2D 탑뷰'}에서 원하는 좌표를 누르세요.`
            : '배치 도구 선택을 해제했습니다.';
        if (!this.selectedPaletteKind) this.clearEditor3dGhost();
        this.requestUiRender();
        const selectedNatureKind = this.selectedPaletteKind;
        const hasPendingNatureStatus = Object.values(BUNDLED_NATURE_DECORATION_SPECS)
            .some((spec) => this.decorationStatus === `${spec.label} ${bundledDecorationFormatLabel(spec)} 모델을 준비하고 있습니다.`)
            || this.decorationStatus === '서울 해치 안내자 GLB 모델을 준비하고 있습니다.';
        if (hasPendingNatureStatus && (!selectedNatureKind
            || (!isBundledNatureDecorationKind(selectedNatureKind) && selectedNatureKind !== 'haechi-guide'))) {
            this.decorationStatus = '다른 배치 도구를 선택했습니다. 자연 장식 모델은 백그라운드에서 준비됩니다.';
            this.requestUiRender();
        }
        if (selectedNatureKind && isBundledNatureDecorationKind(selectedNatureKind)) {
            const spec = BUNDLED_NATURE_DECORATION_SPECS[selectedNatureKind];
            const formatLabel = bundledDecorationFormatLabel(spec);
            this.decorationStatus = `${spec.label} ${formatLabel} 모델을 준비하고 있습니다.`;
            void this.ensureBundledNatureTemplateLoaded(selectedNatureKind)
                .then(() => {
                    if (this.destroyed || this.selectedPaletteKind !== selectedNatureKind) return;
                    this.decorationStatus = `${spec.label} ${formatLabel} 모델이 준비되었습니다.`;
                    this.clearEditor3dGhost();
                    this.requestUiRender();
                })
                .catch((error: any) => {
                    if (this.destroyed || this.selectedPaletteKind !== selectedNatureKind) return;
                    this.decorationStatus = error?.message || `${spec.label} ${formatLabel} 모델을 불러오지 못했습니다.`;
                    this.requestUiRender();
                });
        }
        if (selectedNatureKind === 'haechi-guide') {
            this.decorationStatus = '서울 해치 안내자 GLB 모델을 준비하고 있습니다.';
            void this.ensureHaetaeAssetLoaded()
                .then(() => {
                    if (this.destroyed || this.selectedPaletteKind !== 'haechi-guide') return;
                    this.decorationStatus = '서울 해치 안내자 GLB 모델이 준비되었습니다.';
                    this.clearEditor3dGhost();
                    this.requestUiRender();
                })
                .catch((error: any) => {
                    if (this.destroyed || this.selectedPaletteKind !== 'haechi-guide') return;
                    this.decorationStatus = error?.message || '서울 해치 안내자 GLB 모델을 불러오지 못했습니다.';
                    this.requestUiRender();
                });
        }
    }

    public placeSelectedPalette(event: MouseEvent) {
        if (this.suppressPlanClick) return;
        if (!this.selectedPaletteKind) return;
        const location = this.resolveEditorDrop(event.clientX, event.clientY);
        if (location) this.placeEditorPaletteItem(this.selectedPaletteKind, location);
    }

    public onEditor3dDragOver(event: DragEvent) {
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        const kind = this.draggingPaletteKind;
        const location = this.resolveEditor3dDrop(event.clientX, event.clientY);
        if (kind && location) this.updateEditor3dGhost(kind, location);
        this.editorDragActive = true;
    }

    public onEditor3dDragLeave(event: DragEvent) {
        const target = event.currentTarget as HTMLElement | null;
        const related = event.relatedTarget as Node | null;
        if (target && related && target.contains(related)) return;
        this.editorDragActive = false;
        this.clearEditor3dGhost();
        this.requestUiRender();
    }

    public onEditor3dDrop(event: DragEvent) {
        event.preventDefault();
        const raw = event.dataTransfer?.getData(this.editorDragMime) || event.dataTransfer?.getData('text/plain') || '';
        const location = this.resolveEditor3dDrop(event.clientX, event.clientY);
        this.editorDragActive = false;
        this.draggingPaletteKind = null;
        this.clearEditor3dGhost();
        if (!this.isPaletteKind(raw) || !location) {
            this.editorMessage = '3D 맵 바닥 안의 원하는 위치에 편집 팔레트 항목을 놓아주세요.';
            this.requestUiRender();
            return;
        }
        this.placeEditorPaletteItem(raw, location);
    }

    public onEditor3dPointerMove(event: PointerEvent) {
        if (this.exploreCameraPointerId === event.pointerId) {
            this.moveExploreCameraDrag(event);
            return;
        }
        if (!this.selectedPaletteKind) return;
        const location = this.resolveEditor3dDrop(event.clientX, event.clientY);
        if (location) this.updateEditor3dGhost(this.selectedPaletteKind, location);
        else {
            this.clearEditor3dGhost();
            this.requestUiRender();
        }
    }

    public placeSelectedPalette3d(event: MouseEvent) {
        if (!this.selectedPaletteKind || event.button !== 0) return;
        const location = this.resolveEditor3dDrop(event.clientX, event.clientY);
        if (!location) return;
        this.clearEditor3dGhost();
        this.placeEditorPaletteItem(this.selectedPaletteKind, location);
    }

    public onEditor3dClick(event: MouseEvent) {
        if (event.button !== 0) return;
        if (this.selectedPaletteKind) {
            this.placeSelectedPalette3d(event);
            return;
        }
        const pointerType = (event as PointerEvent).pointerType;
        if (pointerType && pointerType !== 'mouse') return;
        this.selectEditor3dObjectAt(event.clientX, event.clientY);
    }

    public beginEditorPan(event: PointerEvent) {
        if (!this.editorMap || !this.mapCanvas || this.editorDragActive) return;
        const isMiddleButton = event.button === 1;
        if (event.button !== 0 && !isMiddleButton) return;
        if (this.selectedPaletteKind && !isMiddleButton) return;
        event.preventDefault();
        const viewport = this.mapCanvas.nativeElement;
        this.activeEditorPan = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            scrollLeft: viewport.scrollLeft,
            scrollTop: viewport.scrollTop,
            moved: false
        };
        this.editorPanActive = true;
        (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
        this.requestUiRender();
    }

    public beginPlanMove(type: PlanMoveType, index: number, event: PointerEvent) {
        if (!this.editorMap) return;
        event.preventDefault();
        event.stopPropagation();
        const item = this.getPlanMoveItem(type, index);
        if (!item) return;
        const positionItem = type === 'waypoint' || type === 'scenery'
            ? item as MapWaypoint | MapSceneryPlacement
            : null;
        this.activePlanMove = {
            type,
            index,
            id: item.id,
            moved: false,
            originalX: positionItem?.x,
            originalZ: positionItem?.z
        };
        this.activeSceneryPathAnchors = type === 'waypoint'
            ? this.captureSceneryPathAnchors(index)
            : [];
        this.selectPlanMoveItem(type, index);
        (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
        this.requestUiRender();
    }

    public movePlanItem(event: PointerEvent) {
        if (!this.editorMap) return;
        if (this.activeEditorPan) {
            const viewport = this.mapCanvas?.nativeElement;
            if (!viewport || event.pointerId !== this.activeEditorPan.pointerId) return;
            event.preventDefault();
            const deltaX = event.clientX - this.activeEditorPan.startX;
            const deltaY = event.clientY - this.activeEditorPan.startY;
            viewport.scrollLeft = this.activeEditorPan.scrollLeft - deltaX;
            viewport.scrollTop = this.activeEditorPan.scrollTop - deltaY;
            if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) this.activeEditorPan.moved = true;
            return;
        }
        const location = this.resolveEditorDrop(event.clientX, event.clientY);
        if (!location) return;
        const shadowKind = this.draggingPaletteKind || this.selectedPaletteKind;
        if (!this.activePlanMove && shadowKind) {
            this.updateEditorDropShadow(shadowKind, location);
            return;
        }
        if (!this.activePlanMove) return;
        event.preventDefault();
        this.editorDropShadow = null;
        const item = this.getPlanMoveItem(this.activePlanMove.type, this.activePlanMove.index);
        if (!item) return;
        if (this.activePlanMove.type === 'waypoint') {
            const waypoint = item as MapWaypoint;
            waypoint.x = location.pathWorldX;
            waypoint.z = location.pathWorldZ;
            this.applySceneryPathAnchors(this.activeSceneryPathAnchors);
        } else if (this.activePlanMove.type === 'scenery') {
            const scenery = item as MapSceneryPlacement;
            scenery.x = location.worldX;
            scenery.z = location.worldZ;
        } else {
            const laneLimit = this.editorMap.trackWidth / 2 - 0.8;
            if (Math.abs(location.lane) > laneLimit) return;
            const placement = item as MapObjectPlacement;
            placement.distance = location.distance;
            placement.lane = location.lane;
        }
        this.activePlanMove.moved = true;
        this.requestUiRender();
    }

    public clearEditorDropShadow() {
        if (this.editorDragActive) return;
        this.editorDropShadow = null;
        this.requestUiRender();
    }

    public zoomEditor(delta: number) {
        this.setEditorZoom(this.editorZoom + delta);
    }

    public resetEditorZoom() {
        this.setEditorZoom(1);
    }

    public changeEditorDensity(delta: number) {
        this.editorPixelsPerDistance = Math.round(THREE.MathUtils.clamp(this.editorPixelsPerDistance + delta, 0.4, 2.4) * 10) / 10;
        this.editorDropShadow = null;
        this.refreshEditorPlanBounds();
        this.requestUiRender();
    }

    public fitEditorPath(showMessage: boolean = true) {
        this.refreshEditorPlanBounds();
        const viewport = this.mapCanvas?.nativeElement;
        const fitZoom = viewport
            ? Math.min(1, (viewport.clientHeight - 24) / Math.max(1, this.editorCanvasHeight))
            : 1;
        this.editorZoom = Math.round(THREE.MathUtils.clamp(fitZoom, 0.1, 1) * 20) / 20;
        this.editorDropShadow = null;
        setTimeout(() => {
            const currentViewport = this.mapCanvas?.nativeElement;
            if (!currentViewport) return;
            currentViewport.scrollLeft = 0;
            currentViewport.scrollTop = 0;
        }, 30);
        if (showMessage) this.editorMessage = '현재 자유형 경로 전체가 탑뷰에 들어오도록 맞췄습니다.';
        this.requestUiRender();
    }

    public setEditorTrackLength(value: number) {
        if (!this.editorMap) return;
        const nextLength = Math.round(THREE.MathUtils.clamp(Number(value) || 300, 300, 10000));
        const previousLength = Math.max(1, this.editorKnownTrackLength || this.editorMap.trackLength);
        const ratio = nextLength / previousLength;
        const originX = this.editorMap.waypoints[0]?.x || 0;
        const originZ = this.editorMap.waypoints[0]?.z || 0;
        this.editorMap.trackLength = nextLength;
        this.editorMap.waypoints.forEach((waypoint, index, list) => {
            waypoint.distance = index === list.length - 1 ? nextLength : waypoint.distance * ratio;
            waypoint.x = originX + (waypoint.x - originX) * ratio;
            waypoint.z = originZ + (waypoint.z - originZ) * ratio;
        });
        [...this.editorMap.coins, ...this.editorMap.magnets, ...this.editorMap.supplements, ...this.editorMap.mounts, ...this.editorMap.obstacles, ...this.editorMap.gates].forEach((item) => {
            item.distance = THREE.MathUtils.clamp(item.distance * ratio, 10, nextLength - 10);
        });
        this.editorKnownTrackLength = nextLength;
        this.refreshEditorPlanBounds();
        this.markEditorChanged(`코스 길이를 ${nextLength}m로 변경하고 경로 축척과 캔버스 높이를 함께 조정했습니다.`);
    }

    public setEditorTrackWidth(value: number) {
        if (!this.editorMap) return;
        const nextWidth = Math.round(THREE.MathUtils.clamp(Number(value) || 16, 10, 60));
        if (this.editorMap.trackWidth === nextWidth) return;
        this.editorMap.trackWidth = nextWidth;
        this.refreshEditorPlanBounds();
        this.markEditorChanged(`길 너비를 ${nextWidth}m로 변경했습니다.`);
    }

    public setEditorMapSize(axis: 'width' | 'depth', value: number) {
        if (!this.editorMap) return;
        const minimum = axis === 'width' ? 200 : 300;
        const fallback = axis === 'width' ? 600 : Math.max(1200, this.editorMap.trackLength + 400);
        const nextSize = Math.round(THREE.MathUtils.clamp(Number(value) || fallback, minimum, 20000));
        if (axis === 'width') this.editorMap.mapWidth = nextSize;
        else this.editorMap.mapDepth = nextSize;
        const resize = this.expandEditorMapToFitContent();
        this.refreshEditorPlanBounds();
        const appliedSize = axis === 'width' ? this.editorMap.mapWidth : this.editorMap.mapDepth;
        let message = appliedSize === nextSize
            ? `전체 맵 ${axis === 'width' ? '가로' : '세로'} 범위를 ${nextSize}m로 변경했습니다. 코스 길이는 유지됩니다.`
            : `현재 경로와 장식을 포함하려면 ${appliedSize}m가 필요해 그 크기로 자동 보정했습니다.`;
        if (!resize.accepted) message = '현재 콘텐츠가 최대 맵 범위 20,000m를 초과합니다. 경로나 장식 좌표를 안쪽으로 이동해 주세요.';
        this.markEditorChanged(message);
    }

    public setEditorHiddenNpcCount(value: number) {
        if (!this.editorMap) return;
        this.editorMap.hiddenNpcCount = Math.round(THREE.MathUtils.clamp(Number(value) || 0, 0, 20));
        this.markEditorChanged(`자유 탐색 히든 NPC를 ${this.editorMap.hiddenNpcCount}명으로 설정했습니다. NPC는 실행할 때마다 임의 위치에 배치됩니다.`);
    }

    public applyPathPreset(kind: PathPresetKind) {
        if (!this.editorMap) return;
        this.editorMap.waypoints = createPathPresetWaypoints(this.editorMap, kind, this.circleLapCount);
        this.selectedWaypointIndex = 0;
        this.editorTab = 'path';
        this.refreshEditorPlanBounds();
        this.previewEditor();
        this.fitEditorPath(false);
        this.editorMessage = kind === 'circle'
            ? `원형 자유 경로를 ${this.circleLapCount}바퀴로 적용했습니다. 시작과 골인은 같은 위치입니다.`
            : '가로 방향 S 자유 경로를 적용했습니다. 웨이포인트를 어느 방향으로든 다시 이동할 수 있습니다.';
        this.recordEditorHistory(kind === 'circle' ? `원형 코스 ${this.circleLapCount}바퀴 적용` : '가로 S 코스 적용');
        this.requestUiRender();
    }

    public setCircleLapCount(value: number) {
        this.circleLapCount = Math.round(THREE.MathUtils.clamp(Number(value) || 1, 1, 10));
        this.requestUiRender();
    }

    public onEditorWheel(event: WheelEvent) {
        event.preventDefault();
        event.stopPropagation();
        const now = Date.now();
        const modeScale = event.deltaMode === WheelEvent.DOM_DELTA_LINE
            ? 16
            : (event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? 100 : 1);
        const normalizedDelta = THREE.MathUtils.clamp(event.deltaY * modeScale, -180, 180);
        if (now - this.editorWheelUpdatedAt > 180 || this.editorWheelAccumulator * normalizedDelta < 0) {
            this.editorWheelAccumulator = 0;
        }
        this.editorWheelUpdatedAt = now;
        this.editorWheelAccumulator += normalizedDelta;
        const wheelThreshold = 64;
        const availableSteps = Math.floor(Math.abs(this.editorWheelAccumulator) / wheelThreshold);
        if (availableSteps === 0) return;
        const appliedSteps = Math.min(2, availableSteps);
        const direction = Math.sign(this.editorWheelAccumulator);
        this.editorWheelAccumulator -= direction * appliedSteps * wheelThreshold;
        this.zoomEditor(-direction * appliedSteps * 0.05);
    }

    public finishPlanMove(event: PointerEvent) {
        if (this.activeEditorPan && event.pointerId === this.activeEditorPan.pointerId) {
            event.preventDefault();
            event.stopPropagation();
            const moved = this.activeEditorPan.moved;
            this.activeEditorPan = null;
            this.editorPanActive = false;
            if (moved) {
                this.suppressPlanClick = true;
                setTimeout(() => { this.suppressPlanClick = false; }, 0);
            }
            const target = event.currentTarget as HTMLElement | null;
            if (target?.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);
            this.requestUiRender();
            return;
        }
        if (!this.activePlanMove) return;
        event.preventDefault();
        event.stopPropagation();
        const move = this.activePlanMove;
        this.activePlanMove = null;
        this.suppressPlanClick = true;
        setTimeout(() => { this.suppressPlanClick = false; }, 0);
        if (!move.moved || !this.editorMap) {
            this.activeSceneryPathAnchors = [];
            this.requestUiRender();
            return;
        }
        if (move.type === 'gates') {
            this.editorMap.gates.sort((a, b) => a.distance - b.distance);
            this.selectedGateIndex = this.editorMap.gates.findIndex((item) => item.id === move.id);
        }
        let resizeMessage = '';
        if (move.type === 'waypoint' || move.type === 'scenery') {
            const resize = this.expandEditorMapToFitContent();
            if (!resize.accepted) {
                const item = this.getPlanMoveItem(move.type, move.index) as MapWaypoint | MapSceneryPlacement | null;
                if (item && Number.isFinite(move.originalX) && Number.isFinite(move.originalZ)) {
                    item.x = move.originalX as number;
                    item.z = move.originalZ as number;
                }
                this.restoreSceneryPathAnchors(this.activeSceneryPathAnchors);
                this.activeSceneryPathAnchors = [];
                this.refreshEditorPlanBounds();
                this.previewEditor();
                this.editorMessage = '최대 맵 범위 20,000m를 벗어나는 이동은 적용하지 않았습니다.';
                this.requestUiRender();
                return;
            }
            this.refreshEditorPlanBounds();
            if (resize.expanded) resizeMessage = ` 전체 맵을 ${this.editorMap.mapWidth} × ${this.editorMap.mapDepth}m로 자동 확장했습니다.`;
        }
        const followedSceneryCount = move.type === 'waypoint' ? this.activeSceneryPathAnchors.length : 0;
        this.activeSceneryPathAnchors = [];
        this.previewEditor();
        this.editorMessage = move.type === 'waypoint'
            ? `웨이포인트의 자유 좌표를 이동했습니다. 주변 장식 ${followedSceneryCount}개의 길 간격과 방향을 유지했습니다.${resizeMessage}`
            : (move.type === 'scenery' ? `장식을 도로 제약 없이 선택한 좌표로 이동했습니다.${resizeMessage}` : '선택한 요소를 놓은 좌표로 이동했습니다.');
        this.recordEditorHistory(move.type === 'waypoint' ? '웨이포인트 이동' : (move.type === 'scenery' ? '장식 이동' : '맵 요소 이동'));
        this.requestUiRender();
    }

    public selectWaypoint(index: number) {
        this.selectedWaypointIndex = index;
        this.focusEditorDistance(this.editorMap?.waypoints[index]?.distance || 0);
        this.requestUiRender();
    }

    public addWaypoint() {
        if (!this.editorMap) return;
        const list = this.editorMap.waypoints;
        const goalIndex = Math.max(1, list.length - 1);
        const previous = list[goalIndex - 1];
        const goal = list[goalIndex];
        list.splice(goalIndex, 0, {
            id: createId('waypoint'),
            name: `웨이포인트 ${list.length + 1}`,
            distance: (previous.distance + goal.distance) / 2,
            x: (previous.x + goal.x) / 2,
            y: (previous.y + goal.y) / 2,
            z: (previous.z + goal.z) / 2
        });
        this.selectedWaypointIndex = goalIndex;
        this.markEditorChanged('웨이포인트를 추가했습니다.');
    }

    public duplicateWaypoint() {
        if (!this.editorMap || !this.selectedWaypoint) return;
        const list = this.editorMap.waypoints;
        const next = list[this.selectedWaypointIndex + 1];
        const previous = list[this.selectedWaypointIndex - 1];
        const insertIndex = next ? this.selectedWaypointIndex + 1 : this.selectedWaypointIndex;
        const distance = next
            ? (this.selectedWaypoint.distance + next.distance) / 2
            : ((previous?.distance || 0) + this.selectedWaypoint.distance) / 2;
        const copy = { ...this.selectedWaypoint, id: createId('waypoint'), name: `${this.selectedWaypoint.name} 복제`, distance };
        list.splice(insertIndex, 0, copy);
        this.selectedWaypointIndex = insertIndex;
        this.markEditorChanged('웨이포인트를 복제했습니다.');
    }

    public moveWaypoint(direction: number) {
        if (!this.editorMap) return;
        const target = this.selectedWaypointIndex + direction;
        if (target < 0 || target >= this.editorMap.waypoints.length) return;
        const current = this.editorMap.waypoints[this.selectedWaypointIndex];
        const targetItem = this.editorMap.waypoints[target];
        const currentDistance = current.distance;
        current.distance = targetItem.distance;
        targetItem.distance = currentDistance;
        this.editorMap.waypoints.sort((a, b) => a.distance - b.distance);
        this.selectedWaypointIndex = this.editorMap.waypoints.findIndex((item) => item.id === current.id);
        this.markEditorChanged('웨이포인트 순서를 변경했습니다.');
    }

    public deleteWaypoint() {
        if (!this.editorMap || this.editorMap.waypoints.length <= 4) {
            this.editorMessage = '웨이포인트는 최소 4개가 필요합니다.';
            this.requestUiRender();
            return;
        }
        this.editorMap.waypoints.splice(this.selectedWaypointIndex, 1);
        this.selectedWaypointIndex = Math.max(0, this.selectedWaypointIndex - 1);
        this.markEditorChanged('웨이포인트를 삭제했습니다.');
    }

    public selectPlacement(type: PlacementCollection, index: number) {
        this.setSelectedPlacementIndex(type, index);
        if (type !== 'obstacles') {
            this.selectedItemKind = this.itemKind(type);
            this.editorTab = 'items';
        }
        const item = this.editorMap?.[type][index] || null;
        if (item) this.focusEditorDistance(item.distance);
        this.requestUiRender();
    }

    public selectEditorItem(reference: EditorItemReference) {
        this.selectedItemKind = reference.kind;
        this.setSelectedPlacementIndex(reference.collection, reference.index);
        this.editorTab = 'items';
        this.focusEditorDistance(reference.placement.distance);
        this.requestUiRender();
    }

    public isSelectedEditorItem(reference: EditorItemReference): boolean {
        return this.editorTab === 'items'
            && this.selectedItemKind === reference.kind
            && this.selectedPlacementIndex(reference.collection) === reference.index;
    }

    public changeSelectedItemKind(value: ItemKind) {
        if (!this.editorMap || !this.isItemKind(value) || value === this.selectedItemKind) return;
        const sourceCollection = this.selectedItemCollection;
        const sourceIndex = this.selectedPlacementIndex(sourceCollection);
        const source = this.editorMap[sourceCollection][sourceIndex];
        if (!source) return;
        this.editorMap[sourceCollection].splice(sourceIndex, 1);
        this.setSelectedPlacementIndex(sourceCollection, Math.max(0, sourceIndex - 1));
        const targetCollection = this.itemCollection(value);
        this.editorMap[targetCollection].push({ ...source, id: createId(value) });
        this.selectedItemKind = value;
        this.setSelectedPlacementIndex(targetCollection, this.editorMap[targetCollection].length - 1);
        this.markEditorChanged(`${this.itemLabel(value)} 아이템으로 변경했습니다.`);
    }

    public addPlacement(type: PlacementCollection) {
        if (!this.editorMap) return;
        const list = this.editorMap[type];
        const prefix = this.placementPrefix(type);
        const placement = createBlankPlacement(prefix, Math.min(this.editorMap.trackLength - 20, 80 + list.length * 24));
        if (type === 'obstacles') placement.obstacleKind = 'basic';
        list.push(placement);
        this.setSelectedPlacementIndex(type, list.length - 1);
        this.markEditorChanged(`${this.placementLabel(type)}을(를) 추가했습니다.`);
    }

    public duplicatePlacement(type: PlacementCollection) {
        if (!this.editorMap) return;
        const list = this.editorMap[type];
        const index = this.selectedPlacementIndex(type);
        const source = list[index];
        if (!source) return;
        list.splice(index + 1, 0, {
            ...source,
            id: createId(this.placementPrefix(type)),
            distance: Math.min(this.editorMap.trackLength - 10, source.distance + 8)
        });
        this.setSelectedPlacementIndex(type, index + 1);
        this.markEditorChanged('오브젝트를 복제했습니다.');
    }

    public deletePlacement(type: PlacementCollection) {
        if (!this.editorMap) return;
        const list = this.editorMap[type];
        const index = this.selectedPlacementIndex(type);
        if (!list[index]) return;
        list.splice(index, 1);
        this.setSelectedPlacementIndex(type, Math.max(0, index - 1));
        this.markEditorChanged('오브젝트를 삭제했습니다.');
    }

    public selectMount(index: number) {
        this.selectedMountIndex = index;
        const mount = this.selectedMount;
        if (mount) this.focusEditorDistance(mount.distance);
        this.requestUiRender();
    }

    public addMount(kind: MountKind = 'random') {
        if (!this.editorMap) return;
        const placement: MapMountPlacement = {
            ...createBlankPlacement('mount', Math.min(this.editorMap.trackLength - 20, 120 + this.editorMap.mounts.length * 160)),
            kind
        };
        this.editorMap.mounts.push(placement);
        this.selectedMountIndex = this.editorMap.mounts.length - 1;
        this.markEditorChanged(`${this.mountLabel(kind)} 탑승 아이템을 추가했습니다.`);
    }

    public duplicateMount() {
        if (!this.editorMap || !this.selectedMount) return;
        const copy: MapMountPlacement = {
            ...this.selectedMount,
            id: createId('mount'),
            distance: Math.min(this.editorMap.trackLength - 10, this.selectedMount.distance + 12)
        };
        this.editorMap.mounts.splice(this.selectedMountIndex + 1, 0, copy);
        this.selectedMountIndex += 1;
        this.markEditorChanged('탈것 아이템을 복제했습니다.');
    }

    public deleteMount() {
        if (!this.editorMap || !this.selectedMount) return;
        this.editorMap.mounts.splice(this.selectedMountIndex, 1);
        this.selectedMountIndex = Math.max(0, this.selectedMountIndex - 1);
        this.markEditorChanged('탈것 아이템을 삭제했습니다.');
    }

    private selectedPlacementIndex(type: PlacementCollection): number {
        if (type === 'coins') return this.selectedCoinIndex;
        if (type === 'magnets') return this.selectedMagnetIndex;
        if (type === 'supplements') return this.selectedSupplementIndex;
        return this.selectedObstacleIndex;
    }

    private isItemKind(value: string): value is ItemKind {
        return value === 'coin' || value === 'magnet' || value === 'supplement';
    }

    private itemCollection(kind: ItemKind): ItemCollection {
        if (kind === 'magnet') return 'magnets';
        if (kind === 'supplement') return 'supplements';
        return 'coins';
    }

    private itemKind(type: ItemCollection): ItemKind {
        if (type === 'magnets') return 'magnet';
        if (type === 'supplements') return 'supplement';
        return 'coin';
    }

    private setSelectedPlacementIndex(type: PlacementCollection, index: number) {
        if (type === 'coins') this.selectedCoinIndex = index;
        else if (type === 'magnets') this.selectedMagnetIndex = index;
        else if (type === 'supplements') this.selectedSupplementIndex = index;
        else this.selectedObstacleIndex = index;
    }

    private placementPrefix(type: PlacementCollection): 'coin' | 'magnet' | 'supplement' | 'obstacle' {
        if (type === 'coins') return 'coin';
        if (type === 'magnets') return 'magnet';
        if (type === 'supplements') return 'supplement';
        return 'obstacle';
    }

    private placementLabel(type: PlacementCollection): string {
        if (type === 'coins') return '동전';
        if (type === 'magnets') return '자석';
        if (type === 'supplements') return '영양제';
        return '장애물';
    }

    public selectGate(index: number) {
        this.selectedGateIndex = index;
        this.selectedQuestionIndex = 0;
        const gate = this.selectedGate;
        if (gate) this.focusEditorDistance(gate.distance);
        this.requestUiRender();
    }

    public selectQuestion(index: number) {
        this.selectedQuestionIndex = index;
        this.requestUiRender();
    }

    public addGate() {
        if (!this.editorMap) return;
        const distance = Math.min(this.editorMap.trackLength - 25, 180 + this.editorMap.gates.length * 220);
        this.editorMap.gates.push({
            ...createBlankPlacement('gate', distance),
            name: `게이트 ${this.editorMap.gates.length + 1}`,
            design: this.editorMap.theme === 'neon' ? 'neon' : 'hanok',
            quizEnabled: true,
            questions: [createBlankQuestion()]
        });
        this.editorMap.gates.sort((a, b) => a.distance - b.distance);
        this.selectedGateIndex = this.editorMap.gates.findIndex((gate) => gate.distance === distance);
        this.selectedQuestionIndex = 0;
        this.markEditorChanged('게이트를 추가했습니다.');
    }

    public duplicateGate() {
        if (!this.editorMap || !this.selectedGate) return;
        const copy = JSON.parse(JSON.stringify(this.selectedGate)) as MapGate;
        copy.id = createId('gate');
        copy.name = `${copy.name} 복제`;
        copy.distance = Math.min(this.editorMap.trackLength - 10, copy.distance + 15);
        copy.questions = copy.questions.map((question) => ({ ...question, id: createId('quiz') }));
        this.editorMap.gates.push(copy);
        this.editorMap.gates.sort((a, b) => a.distance - b.distance);
        this.selectedGateIndex = this.editorMap.gates.findIndex((gate) => gate.id === copy.id);
        this.markEditorChanged('게이트를 복제했습니다.');
    }

    public deleteGate() {
        if (!this.editorMap || !this.selectedGate) return;
        this.editorMap.gates.splice(this.selectedGateIndex, 1);
        this.selectedGateIndex = Math.max(0, this.selectedGateIndex - 1);
        this.selectedQuestionIndex = 0;
        this.markEditorChanged('게이트를 삭제했습니다.');
    }

    public selectScenery(index: number) {
        this.selectedSceneryIndex = index;
        this.editorTab = 'scenery';
        this.requestUiRender();
    }

    public setSelectedSceneryRotation(value: number) {
        if (!this.selectedScenery) return;
        const numeric = Number(value);
        const normalized = Number.isFinite(numeric)
            ? Math.round(((((numeric + 180) % 360) + 360) % 360 - 180) * 10) / 10
            : 0;
        this.selectedScenery.rotation = normalized;
        this.markEditorChanged(`장식 정면을 ${normalized}°로 회전했습니다.`);
    }

    public syncEditor3dChange(message: string = '오브젝트 변경사항을 적용했습니다.') {
        this.markEditorChanged(message);
    }

    public rotateSelectedScenery(delta: number) {
        if (!this.selectedScenery) return;
        this.setSelectedSceneryRotation((this.selectedScenery.rotation || 0) + delta);
    }

    public setSelectedHaechiGuideTarget(targetId: string) {
        const guide = this.selectedScenery;
        if (!guide || guide.kind !== 'haechi-guide') return;
        const target = this.haechiGuideTargetOptions.find((item) => item.id === targetId);
        guide.guideTargetId = target?.id;
        guide.guideText = this.defaultHaechiGuideText(target);
        if (target) guide.rotation = target.rotation;
        this.markEditorChanged(target
            ? `${target.name} 건물 설명을 해치 안내자에 연결했습니다.`
            : '해치 안내자의 대상 건물 연결을 해제했습니다.');
    }

    public setSelectedHaechiGuideRadius(value: number) {
        const guide = this.selectedScenery;
        if (!guide || guide.kind !== 'haechi-guide') return;
        guide.interactionRadius = Math.round(THREE.MathUtils.clamp(Number(value) || 4.5, 2, 12) * 10) / 10;
        this.syncEditorFormChange('해치 안내 시작 거리를 변경했습니다.');
    }

    public placeSelectedHaechiGuideInFront() {
        const guide = this.selectedScenery;
        if (!guide || guide.kind !== 'haechi-guide' || !guide.guideTargetId) return;
        const target = this.haechiGuideTargetOptions.find((item) => item.id === guide.guideTargetId);
        if (!target) return;
        const angle = THREE.MathUtils.degToRad(Number(target.rotation) || 0);
        const offset = this.haechiGuideBuildingFrontOffset(target);
        guide.x = target.x - Math.sin(angle) * offset;
        guide.z = target.z - Math.cos(angle) * offset;
        guide.y = Math.max(0, Number(target.y) || 0);
        guide.rotation = target.rotation;
        this.refreshEditorPlanBounds();
        this.markEditorChanged(`${target.name} 앞에 해치 안내자를 자동 배치했습니다.`);
    }

    public duplicateScenery() {
        if (!this.editorMap || !this.selectedScenery) return;
        const source = this.selectedScenery;
        const copy: MapSceneryPlacement = {
            ...source,
            id: createId('scenery'),
            name: `${source.name} 복제`,
            x: source.x + 5,
            z: source.z + 5
        };
        this.editorMap.scenery.splice(this.selectedSceneryIndex + 1, 0, copy);
        this.selectedSceneryIndex += 1;
        this.refreshEditorPlanBounds();
        this.markEditorChanged('장식을 복제했습니다.');
    }

    public deleteScenery() {
        if (!this.editorMap || !this.selectedScenery) return;
        const removedId = this.selectedScenery.id;
        this.editorMap.scenery.splice(this.selectedSceneryIndex, 1);
        const detachedGuideCount = this.clearHaechiGuideTargetReferences(new Set([removedId]));
        this.selectedSceneryIndex = Math.max(0, this.selectedSceneryIndex - 1);
        this.refreshEditorPlanBounds();
        this.markEditorChanged(detachedGuideCount > 0
            ? `장식을 삭제하고 연결된 해치 안내자 ${detachedGuideCount}개의 대상과 설명을 초기화했습니다.`
            : '장식을 삭제했습니다.');
    }

    private isHaechiGuideBuildingKind(kind: SceneryKind): boolean {
        return kind === 'building'
            || kind === 'hanok'
            || kind === 'hanok-annex'
            || kind === 'hanok-pavilion'
            || kind === 'hanok-palace'
            || kind === 'hanok-courtyard'
            || kind === 'hanok-library'
            || kind === 'hanok-study'
            || kind === 'harbor-warehouse'
            || kind === 'ys'
            || kind === 'custom';
    }

    private nearestHaechiGuideBuilding(x: number, z: number): MapSceneryPlacement | undefined {
        return this.haechiGuideTargetOptions.reduce<MapSceneryPlacement | undefined>((nearest, item) => {
            if (!nearest) return item;
            const distance = (item.x - x) ** 2 + (item.z - z) ** 2;
            const nearestDistance = (nearest.x - x) ** 2 + (nearest.z - z) ** 2;
            return distance < nearestDistance ? item : nearest;
        }, undefined);
    }

    private defaultHaechiGuideText(building?: MapSceneryPlacement): string {
        if (!building) {
            return '안녕하세요! 저는 서울을 지키는 상징 해치예요. 에디터에서 설명할 건물을 연결하면 이곳의 이야기를 들려드릴게요.';
        }
        const descriptions: Partial<Record<SceneryKind, string>> = {
            building: '도시의 생활과 활동이 모이는 현대 건축물이에요.',
            hanok: '기와지붕과 목조 구조가 어우러진 한국의 전통 가옥이에요.',
            'hanok-annex': '손님을 맞고 학문과 교류를 나누던 사랑채 형식의 건물이에요.',
            'hanok-pavilion': '주변 풍경을 바라보며 쉬거나 모임을 열던 전통 정자예요.',
            'hanok-palace': '월대와 기와지붕, 단청 구조가 어우러진 궁궐 전각이에요.',
            'hanok-courtyard': '여러 채의 한옥과 마당이 생활 공간을 이루는 전통 건축 배치예요.',
            'hanok-library': '아래층 기둥과 위층 공간을 함께 활용한 전통 누각이에요.',
            'hanok-study': '책을 읽고 학문을 닦던 아담한 전통 서재예요.',
            'harbor-warehouse': '포구에서 들어온 물산을 보관하고 나르던 목조 창고예요.',
            ys: '우리 문화유산의 건축 형식과 장식을 살펴볼 수 있는 궁궐 건물이에요.',
            custom: '맵 제작자가 이 장소의 이야기를 위해 직접 배치한 건물이에요.'
        };
        return `${building.name}에 대해 알려드릴게요. ${descriptions[building.kind] || '이 장소를 대표하는 건축 장식이에요.'}`;
    }

    private haechiGuideBuildingFrontOffset(building: MapSceneryPlacement): number {
        const baseOffsets: Partial<Record<SceneryKind, number>> = {
            building: 7,
            hanok: 6,
            'hanok-annex': 7,
            'hanok-pavilion': 6,
            'hanok-palace': 11,
            'hanok-courtyard': 16,
            'hanok-library': 8,
            'hanok-study': 5,
            'harbor-warehouse': 8,
            ys: 10,
            custom: 7
        };
        return Math.max(4, (baseOffsets[building.kind] || 7) * THREE.MathUtils.clamp(Number(building.scale) || 1, 0.1, 10));
    }

    private clearHaechiGuideTargetReferences(removedIds: Set<string>): number {
        if (!this.editorMap || removedIds.size === 0) return 0;
        let cleared = 0;
        this.editorMap.scenery.forEach((item) => {
            if (item.kind !== 'haechi-guide' || !item.guideTargetId || !removedIds.has(item.guideTargetId)) return;
            item.guideTargetId = undefined;
            item.guideText = undefined;
            cleared += 1;
        });
        return cleared;
    }

    private deleteSelectedEditorObject(): boolean {
        if (!this.editorMap) return false;
        if (this.editorTab === 'path' && this.selectedWaypoint) {
            this.deleteWaypoint();
            return true;
        }
        if (this.editorTab === 'items' && this.selectedItem) {
            this.deletePlacement(this.selectedItemCollection);
            return true;
        }
        if (this.editorTab === 'coins' && this.selectedCoin) {
            this.deletePlacement('coins');
            return true;
        }
        if (this.editorTab === 'magnets' && this.selectedMagnet) {
            this.deletePlacement('magnets');
            return true;
        }
        if (this.editorTab === 'supplements' && this.selectedSupplement) {
            this.deletePlacement('supplements');
            return true;
        }
        if (this.editorTab === 'mounts' && this.selectedMount) {
            this.deleteMount();
            return true;
        }
        if (this.editorTab === 'obstacles' && this.selectedObstacle) {
            this.deletePlacement('obstacles');
            return true;
        }
        if (this.editorTab === 'gates' && this.selectedGate) {
            this.deleteGate();
            return true;
        }
        if (this.editorTab === 'scenery' && this.selectedScenery) {
            this.deleteScenery();
            return true;
        }
        return false;
    }

    public chooseDecorationAsset(id: string) {
        const asset = this.decorationAssets.find((item) => item.id === id);
        if (!asset) return;
        if (!asset.format || !asset.data) {
            this.selectedDecorationAssetId = asset.id;
            if (this.selectedPaletteKind === 'custom-decoration') this.selectedPaletteKind = null;
            this.editorDropShadow = null;
            this.decorationStatus = `${asset.name}은(는) 이전 방식으로 저장된 MAX 원본입니다. MAX 파일을 다시 추가하면 자동 변환됩니다.`;
            this.requestUiRender();
            return;
        }
        this.selectedDecorationAssetId = asset.id;
        this.selectedPaletteKind = 'custom-decoration';
        this.editorDropShadow = null;
        this.clearEditor3dGhost();
        this.decorationStatus = `${asset.name} 배치 도구를 선택했습니다. 탑뷰의 원하는 위치를 누르거나 드래그하세요.`;
        this.requestUiRender();
        void this.ensureDecorationTemplateLoaded(asset)
            .then(() => {
                if (this.selectedDecorationAssetId !== asset.id) return;
                this.clearEditor3dGhost();
                this.requestUiRender();
            })
            .catch((error: any) => {
                if (this.selectedDecorationAssetId !== asset.id) return;
                this.decorationStatus = error?.message || `${asset.name} 장식을 준비하지 못했습니다.`;
                this.requestUiRender();
            });
    }

    public startDecorationAssetDrag(asset: StoredDecorationAsset, event: DragEvent) {
        if (!asset.format || !asset.data) {
            event.preventDefault();
            this.decorationStatus = `${asset.name} MAX 파일을 다시 추가해 자동 변환해주세요.`;
            this.requestUiRender();
            return;
        }
        this.chooseDecorationAsset(asset.id);
        this.startPaletteDrag('custom-decoration', event);
    }

    public async uploadDecoration(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files || []);
        input.value = '';
        if (files.length === 0) return;

        this.decorationBusy = true;
        this.decorationStatus = '3D 장식 파일을 검사하고 있습니다.';
        this.requestUiRender();
        try {
            const maxFiles = files.filter((item) => /\.max$/i.test(item.name));
            const placementFiles = files.filter((item) => !/\.max$/i.test(item.name));
            if (maxFiles.length > 0 && placementFiles.length > 0) {
                throw new Error('MAX 파일은 서버에서 자동 변환되므로 다른 형식과 나누어 선택해주세요.');
            }
            if (maxFiles.length > 0) {
                const registered: StoredDecorationAsset[] = [];
                let autoPlacedCount = 0;
                for (let index = 0; index < maxFiles.length; index += 1) {
                    const maxFile = maxFiles[index];
                    if (maxFile.size > this.maxDecorationSourceFileSize) {
                        throw new Error(`${maxFile.name}: MAX 원본 파일은 200MB 이하만 자동 변환할 수 있습니다.`);
                    }
                    await this.validateMaxDecorationSource(maxFile);
                    this.decorationStatus = `${index + 1}/${maxFiles.length} ${maxFile.name}을(를) 서버에서 GLB로 자동 변환하고 있습니다.`;
                    this.requestUiRender();
                    const asset = await this.convertAndStoreMaxDecoration(maxFile);
                    registered.push(asset);
                    if (this.autoPlaceDecorationAsset(asset)) autoPlacedCount += 1;
                }
                this.selectedDecorationAssetId = registered[0]?.id || '';
                if (registered[0]) this.chooseDecorationAsset(registered[0].id);
                this.editorDropShadow = null;
                this.decorationStatus = `${registered.length}개의 MAX를 웹용 GLB로 자동 변환·등록${autoPlacedCount > 0 ? `하고 ${autoPlacedCount}개를 배치된 장식에 추가` : ''}했습니다.`;
                return;
            }
            if (placementFiles.length > 1) {
                throw new Error('MAX 이외의 3D 장식 파일은 한 번에 1개씩 추가해주세요.');
            }
            const file = placementFiles[0];
            if (!file) throw new Error('추가할 3D 장식 파일을 선택해주세요.');

            const format = this.detectSkinFormat(file.name) as DecorationFormat;
            if (file.size > this.maxDecorationFileSize) throw new Error('맵 배치용 3D 장식 파일은 200MB 이하만 업로드할 수 있습니다.');
            const data = await file.arrayBuffer();
            this.validateSkinData(data, format);
            const parsed = await this.parseSkinModel(data, format);
            const box = new THREE.Box3().setFromObject(parsed);
            const size = box.getSize(new THREE.Vector3());
            if (!Number.isFinite(size.y) || size.y <= 0.001) {
                this.disposeObjectTree(parsed, true);
                throw new Error('장식 모델의 크기를 계산할 수 없습니다.');
            }

            const existing = this.decorationAssets.find((item) => item.fileName.toLowerCase() === file.name.toLowerCase());
            const asset: StoredDecorationAsset = {
                ...(existing || {} as StoredDecorationAsset),
                id: existing?.id || createId('decoration-asset'),
                name: this.decorationFileStem(file.name),
                fileName: file.name,
                format,
                data: data.slice(0),
                sourceFileName: undefined,
                sourceFormat: undefined,
                sourceData: undefined,
                updatedAt: new Date().toISOString()
            };
            await this.decorationStorage.put(asset);
            this.storeDecorationTemplate(asset.id, parsed);
            this.decorationAssets = existing
                ? this.decorationAssets.map((item) => item.id === asset.id ? asset : item)
                : [asset, ...this.decorationAssets];
            this.chooseDecorationAsset(asset.id);
            const autoPlaced = this.autoPlaceDecorationAsset(asset);
            this.decorationStatus = `${asset.name}을(를) 저장${autoPlaced ? '하고 배치된 장식에 추가' : ''}했습니다.`;
        } catch (error: any) {
            this.decorationStatus = error?.message || '3D 장식 파일을 추가하지 못했습니다.';
        } finally {
            this.decorationBusy = false;
            this.requestUiRender();
        }
    }

    private decorationFileStem(fileName: string): string {
        return fileName.replace(/\.[^.]+$/, '');
    }

    private async convertAndStoreMaxDecoration(sourceFile: File): Promise<StoredDecorationAsset> {
        const data = await this.convertMaxDecoration(sourceFile);
        if (data.byteLength > this.maxConvertedDecorationFileSize) {
            throw new Error(`${sourceFile.name}: 변환 결과가 24MB를 초과합니다. 원본 메시를 단순화해주세요.`);
        }
        this.validateSkinData(data, 'glb');
        const parsed = await this.parseSkinModel(data, 'glb');
        const box = new THREE.Box3().setFromObject(parsed);
        const size = box.getSize(new THREE.Vector3());
        if (!Number.isFinite(size.y) || size.y <= 0.001) {
            this.disposeObjectTree(parsed, true);
            throw new Error(`${sourceFile.name}: 변환한 장식의 크기를 계산할 수 없습니다.`);
        }
        const existing = this.decorationAssets.find((item) =>
            item.sourceFileName?.toLowerCase() === sourceFile.name.toLowerCase()
        );
        const asset: StoredDecorationAsset = {
            ...(existing || {} as StoredDecorationAsset),
            id: existing?.id || createId('decoration-asset'),
            name: this.decorationFileStem(sourceFile.name),
            fileName: `${this.decorationFileStem(sourceFile.name)}.web.glb`,
            format: 'glb',
            data: data.slice(0),
            sourceFileName: sourceFile.name,
            sourceFormat: 'max',
            sourceData: undefined,
            updatedAt: new Date().toISOString()
        };
        await this.decorationStorage.put(asset);
        this.storeDecorationTemplate(asset.id, parsed as THREE.Group);
        this.decorationAssets = existing
            ? this.decorationAssets.map((item) => item.id === asset.id ? asset : item)
            : [asset, ...this.decorationAssets];
        return asset;
    }

    private convertMaxDecoration(file: File): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const form = new FormData();
            form.append('file[]', file, file.name);
            const request = new XMLHttpRequest();
            let lastUploadPercent = -1;
            request.open('POST', wiz.url('max_convert'), true);
            request.responseType = 'arraybuffer';
            request.timeout = 360000;
            request.upload.onprogress = (progress) => {
                if (!progress.lengthComputable) return;
                const percent = Math.min(100, Math.round(progress.loaded / progress.total * 100));
                if (percent === lastUploadPercent) return;
                lastUploadPercent = percent;
                this.decorationStatus = `${file.name} 업로드 ${percent}%${percent === 100 ? ' · 서버에서 형상 변환 중' : ''}`;
                this.requestUiRender();
            };
            request.onload = () => {
                const response = request.response as ArrayBuffer | null;
                if (request.status >= 200 && request.status < 300 && response && response.byteLength > 0) {
                    resolve(response);
                    return;
                }
                reject(new Error(this.readMaxConversionError(response, request.status)));
            };
            request.onerror = () => reject(new Error('MAX 자동 변환 서버에 연결하지 못했습니다.'));
            request.ontimeout = () => reject(new Error('MAX 자동 변환 시간이 6분을 초과했습니다.'));
            request.send(form);
        });
    }

    private readMaxConversionError(response: ArrayBuffer | null, status: number): string {
        if (response && response.byteLength > 0) {
            try {
                const payload = JSON.parse(new TextDecoder().decode(response));
                const message = payload?.data?.message || payload?.message;
                if (message) return String(message);
            } catch {
                const message = new TextDecoder().decode(response).trim();
                if (message.length > 0 && message.length < 300) return message;
            }
        }
        return status === 429
            ? '다른 MAX 파일을 변환 중입니다. 잠시 후 다시 시도해주세요.'
            : `MAX 자동 변환에 실패했습니다. (HTTP ${status || 0})`;
    }

    private async validateMaxDecorationSource(file: File) {
        if (file.size < 64) throw new Error('유효한 MAX 원본 파일이 아닙니다.');
        const signature = new Uint8Array(await file.slice(0, 8).arrayBuffer());
        const expected = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
        if (!expected.every((value, index) => signature[index] === value)) {
            throw new Error('3ds Max 원본 헤더를 확인할 수 없습니다.');
        }
    }

    private autoPlaceDecorationAsset(asset: StoredDecorationAsset): boolean {
        if (!this.editorMap || !asset.format || !asset.data) return false;
        const existingIndex = this.editorMap.scenery.findIndex((item) => item.kind === 'custom' && item.assetId === asset.id);
        if (existingIndex >= 0) {
            this.selectedSceneryIndex = existingIndex;
            this.editorTab = 'scenery';
            this.selectedPaletteKind = null;
            this.editorDropShadow = null;
            return false;
        }

        const customCount = this.editorMap.scenery.filter((item) => item.kind === 'custom').length;
        const distance = THREE.MathUtils.clamp(this.editorMap.trackLength * 0.12, 8, Math.max(8, this.editorMap.trackLength));
        const frame = this.getPathFrame(this.editorMap, distance);
        const side = customCount % 2 === 0 ? 1 : -1;
        const lateralOffset = this.editorMap.trackWidth / 2 + 6 + Math.floor(customCount / 2) * 3;
        const point = frame.point.clone().addScaledVector(frame.right, side * lateralOffset);
        const scenery: MapSceneryPlacement = {
            id: createId('scenery'),
            name: asset.name,
            kind: 'custom',
            x: point.x,
            y: 0,
            z: point.z,
            rotation: 0,
            scale: 1,
            assetId: asset.id,
            fileName: asset.fileName
        };
        this.editorMap.scenery.push(scenery);
        this.selectedSceneryIndex = this.editorMap.scenery.length - 1;
        this.editorTab = 'scenery';
        this.selectedPaletteKind = null;
        this.editorDropShadow = null;
        this.refreshEditorPlanBounds();
        this.markEditorChanged(`${asset.name} 장식을 길 옆 기본 위치에 자동 배치했습니다.`);
        return true;
    }

    public async deleteDecorationAsset(id: string) {
        const used = this.editorMap?.scenery.some((item) => item.assetId === id);
        if (used) {
            this.decorationStatus = '먼저 맵에 배치된 해당 장식을 삭제해주세요.';
            this.requestUiRender();
            return;
        }
        try {
            await this.decorationStorage.delete(id);
            const cachedTemplate = this.decorationTemplateCache.get(id);
            if (cachedTemplate) this.disposeObjectTree(cachedTemplate, true);
            this.decorationTemplateCache.delete(id);
            this.decorationAssets = this.decorationAssets.filter((item) => item.id !== id);
            if (this.selectedDecorationAssetId === id) {
                this.selectedDecorationAssetId = '';
                if (this.selectedPaletteKind === 'custom-decoration') this.selectedPaletteKind = null;
            }
            this.decorationStatus = '업로드 장식을 브라우저에서 삭제했습니다.';
        } catch (error: any) {
            this.decorationStatus = error?.message || '업로드 장식을 삭제하지 못했습니다.';
        }
        this.requestUiRender();
    }

    public addQuestion() {
        if (!this.selectedGate) return;
        this.selectedGate.questions.push(createBlankQuestion());
        this.selectedQuestionIndex = this.selectedGate.questions.length - 1;
        this.markEditorChanged('OX 문제를 추가했습니다.');
    }

    public duplicateQuestion() {
        if (!this.selectedGate || !this.selectedQuestion) return;
        const copy = { ...this.selectedQuestion, id: createId('quiz') };
        this.selectedGate.questions.splice(this.selectedQuestionIndex + 1, 0, copy);
        this.selectedQuestionIndex += 1;
        this.markEditorChanged('문제를 복제했습니다.');
    }

    public deleteQuestion() {
        if (!this.selectedGate) return;
        if (this.selectedGate.questions.length <= 1) {
            this.editorMessage = '퀴즈 사용 게이트에는 문제가 최소 1개 필요합니다.';
            this.requestUiRender();
            return;
        }
        this.selectedGate.questions.splice(this.selectedQuestionIndex, 1);
        this.selectedQuestionIndex = Math.max(0, this.selectedQuestionIndex - 1);
        this.markEditorChanged('문제를 삭제했습니다.');
    }

    public previewEditor(preserve3dPose: boolean = true): boolean {
        if (!this.editorMap) return false;
        try {
            const editor3dPosition = this.editorViewMode === '3d' && preserve3dPose
                ? this.explorePosition.clone()
                : null;
            const editor3dHeading = this.exploreHeading;
            const editor3dCameraHeading = this.exploreCameraHeading;
            const editor3dCameraPitch = this.exploreCameraPitchOffset;
            if (this.terrainDraftDirty) this.applyTerrainDraftToEditor();
            const normalized = normalizeMap(this.editorMap);
            normalized.id = this.editorMap.id;
            normalized.createdAt = this.editorMap.createdAt;
            normalized.builtIn = false;
            this.editorMap = normalized;
            this.editorKnownTrackLength = normalized.trackLength;
            this.refreshEditorPlanBounds();
            this.activeMap = this.editorMap;
            this.rebuildWorld();
            this.screen = 'editor';
            if (this.editorViewMode === '3d') {
                if (editor3dPosition) {
                    const bounds = this.getRuntimeMapBounds(this.activeMap);
                    this.explorePosition.set(
                        THREE.MathUtils.clamp(editor3dPosition.x, bounds.minX, bounds.maxX),
                        0,
                        THREE.MathUtils.clamp(editor3dPosition.z, bounds.minZ, bounds.maxZ)
                    );
                    this.explorePosition.y = this.sceneryGroundHeightAt(this.explorePosition.x, this.explorePosition.z);
                    this.exploreHeading = editor3dHeading;
                    this.exploreCameraHeading = editor3dCameraHeading;
                    this.exploreCameraPitchOffset = editor3dCameraPitch;
                    this.progress = this.findNearestExploreDistance(this.explorePosition.x, this.explorePosition.z);
                    this.updatePlayerTransform(0);
                }
                this.snapCamera();
            } else {
                this.focusEditorDistance(this.selectedEditorDistance());
            }
            this.editorMessage = '3D 미리보기를 갱신했습니다.';
            this.requestUiRender();
            return true;
        } catch (error: any) {
            this.editorMessage = error?.message || '미리보기를 갱신하지 못했습니다.';
            this.requestUiRender();
            return false;
        }
    }

    public playEditorMap(mode: GameMode = 'runner') {
        if (!this.editorMap) return;
        try {
            if (this.terrainDraftDirty) this.applyTerrainDraftToEditor();
            this.activeMap = normalizeMap(this.editorMap);
            this.activeMap.id = this.editorMap.id;
            this.activeMap.builtIn = false;
            this.editorMap = this.activeMap;
            this.editorKnownTrackLength = this.activeMap.trackLength;
            this.refreshEditorPlanBounds();
            this.gameMode = mode;
            this.rebuildWorld();
            this.isEditorTest = true;
            this.startGame(mode);
        } catch (error: any) {
            this.editorMessage = error?.message || '테스트 플레이를 시작하지 못했습니다.';
            this.requestUiRender();
        }
    }

    public returnToEditor() {
        if (!this.editorMap) return;
        this.activeMap = this.editorMap;
        if (this.editorViewMode === '3d') this.gameMode = 'explore';
        this.rebuildWorld();
        this.screen = 'editor';
        this.clearControls();
        if (this.editorViewMode === '3d') this.snapCamera();
        else this.focusEditorDistance(this.selectedEditorDistance());
        this.editorMessage = '테스트 플레이에서 에디터로 돌아왔습니다.';
        this.requestUiRender();
    }

    public saveEditorMap() {
        if (!this.editorMap) return;
        try {
            if (this.terrainDraftDirty) this.applyTerrainDraftToEditor();
            const map = normalizeMap(this.editorMap);
            map.id = this.editorMap.id;
            map.createdAt = this.editorMap.createdAt;
            map.builtIn = false;
            const index = this.savedMaps.findIndex((item) => item.id === map.id);
            if (index === -1) this.savedMaps.unshift(map);
            else this.savedMaps[index] = map;
            this.persistMaps();
            this.activeMap = cloneMap(map);
            this.clearEditorHistory();
            this.editorMap = null;
            this.editorViewMode = '2d';
            this.clearControls();
            this.rebuildWorld();
            this.screen = 'maps';
            this.importMessage = '맵을 브라우저에 저장했습니다.';
        } catch (error: any) {
            this.editorMessage = error?.message || '맵을 저장하지 못했습니다.';
        }
        this.requestUiRender();
    }

    public cancelEditor() {
        this.clearEditorHistory();
        this.editorMap = null;
        this.editorViewMode = '2d';
        this.clearControls();
        this.activeMap = cloneMap(this.previousMap);
        this.rebuildWorld();
        this.screen = 'maps';
        this.requestUiRender();
    }

    public async selectBuiltInSkin(id: BuiltInSkinId) {
        const preset = this.builtInSkins.find((item) => item.id === id);
        if (!preset || this.skinBusy) return;
        if (id === 'joseon-king-2' || id === 'haetae') {
            this.skinBusy = true;
            this.skinStatus = `${preset.name}의 고해상도 모델과 텍스처를 불러오고 있습니다.`;
            this.requestUiRender();
            try {
                if (id === 'joseon-king-2') await this.ensureKing2AssetLoaded();
                else await this.ensureHaetaeAssetLoaded();
            } catch (error: any) {
                this.skinStatus = error?.message || `${preset.name} 자산을 불러오지 못했습니다.`;
                this.skinBusy = false;
                this.requestUiRender();
                return;
            }
        }
        this.customSkinData = null;
        this.customSkinFormat = 'glb';
        this.activeNfcCharacterMetadata = null;
        this.nfcUserToken = '';
        this.nfcParticipantId = '';
        this.nfcCurrentUid = '';
        this.hasCustomSkin = false;
        this.activeLocalSkinId = '';
        this.customSkinRotationY = 180;
        this.selectedBuiltInSkinId = id;
        this.skinName = preset.name;
        this.characterNameDraft = preset.name;
        this.skinFileName = '';
        this.characterCustomizationEnabled = false;
        this.replacePlayerVisual(this.createBuiltInPlayerVisual(id));
        this.refreshCustomizationPreviewModel();
        this.persistBuiltInSkinSettings(id);
        try {
            await this.skinStorage.clear();
            this.skinStatus = `${preset.name} 기본 캐릭터를 적용했습니다.`;
        } catch {
            this.skinStatus = `${preset.name}을 적용했지만 현재 적용 스킨 기록은 정리하지 못했습니다.`;
        }
        this.skinBusy = false;
        this.requestUiRender();
    }

    public async downloadBuiltInSkin(id: BuiltInSkinId) {
        const preset = this.builtInSkins.find((item) => item.id === id);
        if (!preset || this.skinBusy) return;
        this.skinBusy = true;
        this.skinStatus = `${preset.name} GLB 샘플을 만들고 있습니다.`;
        this.requestUiRender();
        let model: THREE.Group | undefined;
        try {
            if (id === 'joseon-king-2') await this.ensureKing2AssetLoaded();
            if (id === 'haetae') await this.ensureHaetaeAssetLoaded();
            model = this.createBuiltInPlayerVisual(id);
            model.updateMatrixWorld(true);
            const data = await new Promise<ArrayBuffer>((resolve, reject) => {
                new GLTFExporter().parse(
                    model,
                    (result) => result instanceof ArrayBuffer
                        ? resolve(result)
                        : reject(new Error('GLB 바이너리 생성 결과가 올바르지 않습니다.')),
                    (error) => reject(error instanceof Error ? error : new Error('GLB 샘플을 만들지 못했습니다.')),
                    { binary: true, onlyVisible: true }
                );
            });
            this.downloadBlob(data, `quiz-rush-${id}.glb`, 'model/gltf-binary');
            this.skinStatus = `${preset.name} GLB 샘플을 다운로드했습니다. 이 화면에서 다시 업로드할 수 있습니다.`;
        } catch (error: any) {
            this.skinStatus = error?.message || '캐릭터 샘플을 다운로드하지 못했습니다.';
        } finally {
            if (model) this.disposeObjectTree(model);
            this.skinBusy = false;
            this.requestUiRender();
        }
    }

    public async uploadSkin(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) return;

        this.skinBusy = true;
        this.skinStatus = '3D 캐릭터 파일을 검사하고 있습니다.';
        this.requestUiRender();

        try {
            const format = this.detectSkinFormat(file.name);
            if (file.size > this.maxSkinFileSize) throw new Error('3D 스킨 파일은 8MB 이하만 업로드할 수 있습니다.');
            const data = await file.arrayBuffer();
            this.validateSkinData(data, format);
            this.customSkinRotationY = 180;
            this.activeLocalSkinId = '';
            this.activeNfcCharacterMetadata = null;
            this.nfcUserToken = '';
            this.nfcParticipantId = '';
            this.nfcCurrentUid = '';
            const summary = await this.applyCustomSkin(data, format, this.customSkinRotationY);
            this.customSkinData = data.slice(0);
            this.customSkinFormat = format;
            this.selectedBuiltInSkinId = null;
            localStorage.removeItem(this.skinPresetStorageKey);
            this.skinName = file.name.replace(/\.(glb|gltf|obj|fbx|stl|ply|dae|3ds)$/i, '');
            this.characterNameDraft = this.skinName;
            this.skinFileName = file.name;
            this.characterCustomizationEnabled = false;
            this.customCharacterStats = { jump: 5, steering: 5 };
            await this.persistSkin();
            this.hasCustomSkin = true;
            this.refreshCustomizationPreviewModel();
            const animationStatus = summary.animationCount > 0
                ? `애니메이션 ${summary.animationCount}개 중 첫 동작 재생`
                : '정적 포즈';
            this.skinStatus = `${format.toUpperCase()} 스킨 적용 완료 · 메시 ${summary.meshCount}개 · ${animationStatus}`;
            this.localSkinStatus = '현재 캐릭터는 아직 로컬 캐릭터 공간에 등록되지 않았습니다.';
        } catch (error: any) {
            this.skinStatus = error?.message || '3D 스킨을 적용하지 못했습니다.';
        } finally {
            this.skinBusy = false;
            this.requestUiRender();
        }
    }

    public async resetSkin() {
        await this.selectBuiltInSkin('joseon-king-2');
    }

    public async rotateCustomSkin180() {
        if (!this.hasCustomSkin || this.skinBusy) return;
        this.skinBusy = true;
        try {
            this.customSkinRotationY = this.customSkinRotationY === 0 ? 180 : 0;
            const orientation = this.playerVisual?.getObjectByName('uploaded-skin-orientation');
            if (!orientation) throw new Error('업로드 캐릭터의 방향 기준을 찾지 못했습니다.');
            orientation.rotation.y = THREE.MathUtils.degToRad(this.customSkinRotationY);
            orientation.updateMatrixWorld(true);
            await this.persistSkin();
            if (this.activeLocalSkinId) {
                const current = this.localSkins.find((skin) => skin.id === this.activeLocalSkinId);
                if (current) {
                    const updated: StoredSkin = { ...current, rotationY: this.customSkinRotationY, updatedAt: new Date().toISOString() };
                    await this.skinStorage.put(updated);
                    this.localSkins = this.sortLocalSkins(this.localSkins.map((skin) => skin.id === updated.id ? updated : skin));
                }
            }
            this.refreshCustomizationPreviewModel();
            this.skinStatus = `업로드 캐릭터를 ${this.customSkinRotationY}° 방향으로 적용했습니다.`;
        } catch (error: any) {
            this.skinStatus = error?.message || '캐릭터 방향을 전환하지 못했습니다.';
        } finally {
            this.skinBusy = false;
            this.requestUiRender();
        }
    }

    public async registerCurrentSkin() {
        if (!this.hasCustomSkin || !this.customSkinData || this.skinBusy) return;
        this.skinBusy = true;
        try {
            const now = new Date().toISOString();
            const id = this.activeLocalSkinId || createId('local-character');
            const record: StoredSkin = {
                id,
                name: this.skinName,
                fileName: this.skinFileName,
                format: this.customSkinFormat,
                data: this.customSkinData.slice(0),
                scale: 1,
                yOffset: 0,
                rotationY: this.customSkinRotationY,
                ...this.activeNfcStorageFields(),
                customizationEnabled: this.characterCustomizationEnabled,
                stats: { ...this.customCharacterStats },
                updatedAt: now
            };
            await this.skinStorage.put(record);
            this.activeLocalSkinId = id;
            const exists = this.localSkins.some((skin) => skin.id === id);
            this.localSkins = this.sortLocalSkins(exists
                ? this.localSkins.map((skin) => skin.id === id ? record : skin)
                : [record, ...this.localSkins]);
            await this.persistSkin();
            this.localSkinStatus = `${record.name}을(를) 로컬 캐릭터 공간에 ${exists ? '업데이트' : '등록'}했습니다.`;
        } catch (error: any) {
            this.localSkinStatus = error?.message || '로컬 캐릭터를 등록하지 못했습니다.';
        } finally {
            this.skinBusy = false;
            this.requestUiRender();
        }
    }

    public async applyLocalSkin(skin: StoredSkin) {
        if (this.skinBusy) return;
        if (skin.nfcAssetId || skin.nfcIdentifier) {
            this.localSkinStatus = 'CharDB 캐릭터는 NFC 인증 후에만 적용할 수 있습니다.';
            this.requestUiRender();
            return;
        }
        this.skinBusy = true;
        this.skinStatus = `${skin.name} 로컬 캐릭터를 적용하고 있습니다.`;
        this.requestUiRender();
        try {
            const format = skin.format || this.detectSkinFormat(skin.fileName);
            this.validateSkinData(skin.data, format);
            this.customSkinRotationY = this.normalizeSkinRotation(skin.rotationY);
            const summary = await this.applyCustomSkin(skin.data, format, this.customSkinRotationY);
            this.customSkinData = skin.data.slice(0);
            this.customSkinFormat = format;
            this.skinName = skin.name;
            this.characterNameDraft = skin.name;
            this.skinFileName = skin.fileName;
            this.characterCustomizationEnabled = false;
            this.customCharacterStats = this.normalizeCharacterStats(skin.stats);
            this.selectedBuiltInSkinId = null;
            this.hasCustomSkin = true;
            this.activeLocalSkinId = skin.id;
            this.activeNfcCharacterMetadata = null;
            this.nfcUserToken = '';
            this.nfcParticipantId = '';
            this.nfcCurrentUid = '';
            localStorage.removeItem(this.skinPresetStorageKey);
            await this.persistSkin();
            this.refreshCustomizationPreviewModel();
            this.skinStatus = `${skin.name} 적용 완료 · ${this.customSkinRotationY}° · 메시 ${summary.meshCount}개`;
            this.localSkinStatus = `${skin.name}을(를) 현재 캐릭터로 적용했습니다.`;
        } catch (error: any) {
            this.skinStatus = error?.message || '로컬 캐릭터를 적용하지 못했습니다.';
        } finally {
            this.skinBusy = false;
            this.requestUiRender();
        }
    }

    public async deleteLocalSkin(skin: StoredSkin) {
        if (this.skinBusy) return;
        this.skinBusy = true;
        try {
            const wasActive = this.activeLocalSkinId === skin.id;
            await this.skinStorage.deleteLocal(skin.id);
            this.localSkins = this.localSkins.filter((item) => item.id !== skin.id);
            if (wasActive) {
                this.customSkinData = null;
                this.customSkinFormat = 'glb';
                this.customSkinRotationY = 180;
                this.activeLocalSkinId = '';
                this.activeNfcCharacterMetadata = null;
                this.hasCustomSkin = false;
                this.selectedBuiltInSkinId = 'joseon-king-2';
                const fallback = this.builtInSkins.find((item) => item.id === 'joseon-king-2')!;
                this.skinName = fallback.name;
                this.characterNameDraft = fallback.name;
                this.skinFileName = '';
                this.characterCustomizationEnabled = false;
                this.persistBuiltInSkinSettings('joseon-king-2');
                await this.ensureKing2AssetLoaded();
                await this.skinStorage.clear();
                this.replacePlayerVisual(this.createBuiltInPlayerVisual('joseon-king-2'));
                this.refreshCustomizationPreviewModel();
                this.skinStatus = '적용 중이던 로컬 캐릭터를 삭제해 조선 임금으로 복원했습니다.';
            }
            this.localSkinStatus = `${skin.name}을(를) 로컬 캐릭터 공간에서 삭제했습니다.`;
        } catch (error: any) {
            this.localSkinStatus = error?.message || '로컬 캐릭터를 삭제하지 못했습니다.';
        } finally {
            this.skinBusy = false;
            this.requestUiRender();
        }
    }

    public selectQuizAnswer(selected: boolean) {
        if (this.screen !== 'quiz') return;
        this.quizSelectedAnswer = selected;
        this.requestUiRender();
    }

    public submitQuizAnswer() {
        if (this.quizSelectedAnswer === null) return;
        this.answerQuiz(this.quizSelectedAnswer);
    }

    private answerQuiz(selected: boolean) {
        if (this.screen !== 'quiz' || !this.activeQuestion) return;
        if (this.activeQuizKind === 'hiddenNpc' && this.activeHiddenNpc) {
            const npc = this.activeHiddenNpc;
            const correct = selected === this.activeQuestion.answer;
            const reward = correct ? 10 + Math.floor(Math.random() * 191) : 0;
            this.lastAnswerCorrect = correct;
            this.hiddenNpcReward = reward;
            this.hiddenNpcScore += reward;
            this.quizFeedback = correct
                ? `${this.activeQuestion.correctMessage} ${this.activeQuestion.explanation}`
                : `${this.activeQuestion.wrongMessage} ${this.activeQuestion.explanation}`;
            this.hiddenNpcAnswerRecords.push({
                npcId: npc.id,
                question: this.activeQuestion.question,
                selected,
                answer: this.activeQuestion.answer,
                correct,
                reward
            });
            npc.resolved = true;
            this.screen = 'quizFeedback';
            this.requestUiRender();
            return;
        }
        const gate = this.gates.find((item) => item.selectedQuestion === this.activeQuestion);
        if (!gate) return;

        const correct = selected === this.activeQuestion.answer;
        this.lastAnswerCorrect = correct;
        this.quizFeedback = correct
            ? `${this.activeQuestion.correctMessage} ${this.activeQuestion.explanation}`
            : `${this.activeQuestion.wrongMessage} ${this.activeQuestion.explanation}`;

        this.answerRecords.push({
            gateId: gate.id,
            gateName: gate.name,
            question: this.activeQuestion.question,
            selected,
            answer: this.activeQuestion.answer,
            correct,
            explanation: this.activeQuestion.explanation
        });

        gate.resolved = true;
        this.gatesPassed += 1;
        if (correct) this.correctCount += 1;
        this.screen = 'quizFeedback';
        this.updateGateAppearance(gate);
        this.requestUiRender();
    }

    public continueAfterQuiz() {
        if (this.screen !== 'quizFeedback') return;
        if (this.activeQuizKind === 'hiddenNpc' && this.activeHiddenNpc) {
            this.activeHiddenNpc.mesh.visible = false;
            this.hiddenNpcFound += 1;
            const message = this.lastAnswerCorrect
                ? `히든 퀴즈 정답! 히든 점수 +${this.hiddenNpcReward}점이 최종 점수에 합산됩니다.`
                : '히든 NPC를 발견했습니다. 이번 히든 점수는 없습니다.';
            this.activeHiddenNpc = undefined;
            this.activeQuestion = null;
            this.quizSelectedAnswer = null;
            this.activeGateName = '';
            this.activeQuizKind = 'gate';
            this.screen = 'playing';
            this.showTemporaryStatus(message, 3);
            return;
        }
        const gate = this.gates.find((item) => item.selectedQuestion === this.activeQuestion);
        if (gate) {
            this.checkpoint = gate.distance + 6;
            if (this.gameMode === 'runner') this.progress = Math.max(0, gate.distance - 3);
        }
        this.activeQuestion = null;
        this.quizSelectedAnswer = null;
        this.activeGateName = '';
        this.screen = 'playing';
        this.showTemporaryStatus('게이트가 열렸습니다. 다음 구간으로 출발하세요!', 3);
    }

    public pressControl(action: ControlAction, event?: PointerEvent) {
        if (event) event.preventDefault();
        const editor3dActive = this.screen === 'editor' && this.editorViewMode === '3d';
        if (this.screen !== 'playing' && !editor3dActive) return;
        if (action === 'jump') this.jump();
        else this.controls[action] = true;
    }

    public releaseControl(action: 'left' | 'right' | 'forward' | 'backward') {
        this.controls[action] = false;
    }

    public cycleExploreCameraMode() {
        const modes: ExploreCameraMode[] = ['follow', 'shoulder', 'top'];
        const nextIndex = (modes.indexOf(this.exploreCameraMode) + 1) % modes.length;
        this.exploreCameraMode = modes[nextIndex];
        this.exploreCameraPitchOffset = 0;
        this.snapCamera();
        if (this.screen === 'editor' && this.editorViewMode === '3d') {
            this.editorMessage = `3D 카메라 시점: ${this.exploreCameraModeLabel}`;
            this.requestUiRender();
        } else {
            this.showTemporaryStatus(`카메라 시점: ${this.exploreCameraModeLabel}`, 3);
        }
    }

    public adjustMinimapZoom(delta: number) {
        if (this.screen !== 'playing') return;
        const next = THREE.MathUtils.clamp(Math.round((this.minimapZoom + delta) * 2) / 2, 1, 4);
        if (next === this.minimapZoom) return;
        this.minimapZoom = next;
        this.requestUiRender();
    }

    public resetMinimapZoom() {
        if (this.minimapZoom === 4) return;
        this.minimapZoom = 4;
        this.requestUiRender();
    }

    public beginExploreCameraDrag(event: PointerEvent) {
        const editor3dActive = this.screen === 'editor' && this.editorViewMode === '3d';
        const isMouseRightDrag = event.pointerType === 'mouse' && event.button === 2;
        const isTouchDrag = event.pointerType !== 'mouse' && event.isPrimary && event.button === 0
            && (!editor3dActive || !this.selectedPaletteKind);
        if ((!editor3dActive && (this.screen !== 'playing' || this.gameMode !== 'explore'))
            || (!isMouseRightDrag && !isTouchDrag)) return;
        this.exploreCameraAdjustMode = true;
        this.exploreCameraPointerId = event.pointerId;
        this.exploreCameraPointerLastX = event.clientX;
        this.exploreCameraPointerLastY = event.clientY;
        (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
        event.preventDefault();
        this.requestUiRender();
    }

    public moveExploreCameraDrag(event: PointerEvent) {
        const editor3dActive = this.screen === 'editor' && this.editorViewMode === '3d';
        if (this.exploreCameraPointerId !== event.pointerId
            || (!editor3dActive && (this.screen !== 'playing' || this.gameMode !== 'explore'))) return;
        const deltaX = event.clientX - this.exploreCameraPointerLastX;
        const deltaY = event.clientY - this.exploreCameraPointerLastY;
        this.exploreCameraPointerLastX = event.clientX;
        this.exploreCameraPointerLastY = event.clientY;
        this.exploreCameraHeading -= deltaX * 0.009;
        this.exploreCameraPitchOffset = THREE.MathUtils.clamp(this.exploreCameraPitchOffset + deltaY * 0.006, -0.35, 0.7);
        this.requestUiRender();
        event.preventDefault();
    }

    public suppressExploreCameraContextMenu(event: MouseEvent) {
        const editor3dActive = this.screen === 'editor' && this.editorViewMode === '3d';
        if (!editor3dActive && (this.screen !== 'playing' || this.gameMode !== 'explore')) return;
        event.preventDefault();
    }

    public endExploreCameraDrag(event: PointerEvent) {
        if (this.exploreCameraPointerId !== event.pointerId) return;
        (event.currentTarget as HTMLElement | null)?.releasePointerCapture?.(event.pointerId);
        this.exploreCameraPointerId = null;
        this.requestUiRender();
    }

    @HostListener('window:keydown', ['$event'])
    public handleKeydown(event: KeyboardEvent) {
        if (this.captureKeyboardNfcInput(event)) return;
        if (this.adminPasswordPromptOpen || this.adminSettingsOpen || this.nfcRegistrationOpen) {
            if (event.key === 'Escape') {
                event.preventDefault();
                if (this.adminPasswordPromptOpen) this.closeAdminPasswordPrompt();
                else if (this.adminSettingsOpen) this.closeAdminSettings();
                else this.closeNfcRegistration();
            }
            return;
        }
        if (this.activeHaechiGuide) {
            const dismissKey = event.key === 'Escape' || event.key === 'Enter'
                || event.key === ' ' || event.code === 'Space';
            if (dismissKey) {
                event.preventDefault();
                if (!event.repeat) this.closeHaechiGuide();
            }
            return;
        }
        const historyShortcutKey = event.key.toLowerCase();
        if (this.screen === 'editor' && (event.ctrlKey || event.metaKey) && !event.altKey
            && (historyShortcutKey === 'z' || historyShortcutKey === 'y')) {
            event.preventDefault();
            if (!event.repeat) {
                const redo = historyShortcutKey === 'y' || (historyShortcutKey === 'z' && event.shiftKey);
                if (redo) this.redoEditorChange();
                else this.undoEditorChange();
            }
            return;
        }
        const target = event.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
        const key = event.key.toLowerCase();
        const code = event.code;
        const isSpace = key === ' ' || event.code === 'Space';
        if (event.key === 'Escape') {
            if (this.screen === 'playing') {
                event.preventDefault();
                this.pauseGame();
                return;
            }
            if (this.screen === 'paused') {
                event.preventDefault();
                this.resumeGame();
                return;
            }
        }
        if (this.screen === 'quiz') {
            if (key === 'arrowleft' || key === 'a' || code === 'KeyA') {
                event.preventDefault();
                this.selectQuizAnswer(true);
            } else if (key === 'arrowright' || key === 'd' || code === 'KeyD') {
                event.preventDefault();
                this.selectQuizAnswer(false);
            } else if (isSpace) {
                event.preventDefault();
                if (!event.repeat) this.submitQuizAnswer();
            }
            return;
        }
        if (this.screen === 'quizFeedback') {
            if (isSpace) {
                event.preventDefault();
                if (!event.repeat) this.continueAfterQuiz();
            }
            return;
        }
        if (event.key === 'Escape' && this.screen === 'editor' && this.selectedPaletteKind) {
            this.selectedPaletteKind = null;
            this.clearEditor3dGhost();
            this.editorMessage = '배치 도구 선택을 해제했습니다.';
            this.requestUiRender();
            return;
        }
        if (this.screen === 'editor' && (key === 'delete' || key === 'backspace')) {
            event.preventDefault();
            if (!event.repeat) this.deleteSelectedEditorObject();
            return;
        }
        if (this.screen === 'editor' && this.editorViewMode === '3d') {
            if (key === 'shift' || code === 'ShiftLeft' || code === 'ShiftRight') {
                this.exploreSprintActive = true;
                this.requestUiRender();
            }
            if (key === 'arrowleft' || key === 'a' || code === 'KeyA') {
                event.preventDefault();
                this.controls.left = true;
            }
            if (key === 'arrowright' || key === 'd' || code === 'KeyD') {
                event.preventDefault();
                this.controls.right = true;
            }
            if (key === 'arrowup' || key === 'w' || code === 'KeyW') {
                event.preventDefault();
                this.controls.forward = true;
            }
            if (key === 'arrowdown' || key === 's' || code === 'KeyS') {
                event.preventDefault();
                this.controls.backward = true;
            }
            if (code === 'KeyC' && !event.repeat) {
                event.preventDefault();
                this.cycleExploreCameraMode();
            }
            if (isSpace) {
                event.preventDefault();
                if (!event.repeat) this.jump();
            }
            return;
        }
        if (event.key === 'Enter' && (this.screen === 'menu' || this.screen === 'result')) {
            event.preventDefault();
            this.startGame();
            return;
        }
        if (this.screen !== 'playing') return;
        const minimapZoomIn = key === '+' || key === '=' || code === 'NumpadAdd';
        const minimapZoomOut = key === '-' || key === '_' || code === 'NumpadSubtract';
        if (!event.ctrlKey && !event.metaKey && !event.altKey && (minimapZoomIn || minimapZoomOut)) {
            event.preventDefault();
            if (!event.repeat) this.adjustMinimapZoom(minimapZoomIn ? 0.5 : -0.5);
            return;
        }
        if (this.gameMode === 'explore') {
            if (key === 'shift' || code === 'ShiftLeft' || code === 'ShiftRight') {
                this.exploreSprintActive = true;
                this.requestUiRender();
            }
            if (key === 'arrowleft' || key === 'a' || code === 'KeyA') {
                event.preventDefault();
                this.controls.left = true;
            }
            if (key === 'arrowright' || key === 'd' || code === 'KeyD') {
                event.preventDefault();
                this.controls.right = true;
            }
            if (key === 'arrowup' || key === 'w' || code === 'KeyW') {
                event.preventDefault();
                this.controls.forward = true;
            }
            if (key === 'arrowdown' || key === 's' || code === 'KeyS') {
                event.preventDefault();
                this.controls.backward = true;
            }
            if (code === 'KeyC' && !event.repeat) {
                event.preventDefault();
                this.cycleExploreCameraMode();
            }
            if (key === ' ' || event.code === 'Space') {
                event.preventDefault();
                if (!event.repeat) this.jump();
            }
            return;
        }
        if (key === 'arrowleft' || key === 'a' || code === 'KeyA') {
            event.preventDefault();
            this.controls.left = true;
        }
        if (key === 'arrowright' || key === 'd' || code === 'KeyD') {
            event.preventDefault();
            this.controls.right = true;
        }
        if (key === 'arrowup' || key === 'w' || code === 'KeyW' || key === ' ' || event.code === 'Space') {
            event.preventDefault();
            if (!event.repeat) this.jump();
        }
    }

    @HostListener('window:keyup', ['$event'])
    public handleKeyup(event: KeyboardEvent) {
        const key = event.key.toLowerCase();
        const code = event.code;
        if (key === 'shift' || code === 'ShiftLeft' || code === 'ShiftRight') {
            this.exploreSprintActive = false;
            this.requestUiRender();
        }
        if (key === 'arrowleft' || key === 'a' || code === 'KeyA') this.controls.left = false;
        if (key === 'arrowright' || key === 'd' || code === 'KeyD') this.controls.right = false;
        if (key === 'arrowup' || key === 'w' || code === 'KeyW') this.controls.forward = false;
        if (key === 'arrowdown' || key === 's' || code === 'KeyS') this.controls.backward = false;
    }

    @HostListener('window:blur')
    public handleWindowBlur() {
        this.clearControls();
    }

    @HostListener('window:resize')
    public handleResize() {
        if (this.screen === 'editor' && this.editorMap) {
            this.refreshEditorPlanBounds();
            this.scheduleTerrainCanvasRender();
        }
        if (this.screen === 'skins') {
            this.resizeCustomizationPreview();
            this.resizeNfcPreview();
        }
        const host = this.gameHost?.nativeElement;
        if (!host || !this.renderer || !this.camera) return;
        const width = Math.max(1, host.clientWidth);
        const height = Math.max(1, host.clientHeight);
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height, false);
    }

    private isJumpRoadPiece(kind: string): boolean {
        return kind === 'jump-wide' || kind === 'jump-stairs' || kind === 'jump-zigzag';
    }

    private isRoadPiece(kind: string): kind is RoadPieceKind {
        return this.roadPalette.some((item) => item.kind === kind);
    }

    private isSceneryPalette(kind: string): kind is BuiltInSceneryPaletteKind | 'ys-decoration' | 'custom-decoration' {
        return kind === 'ys-decoration' || kind === 'custom-decoration'
            || this.sceneryPalette.some((item) => item.kind === kind);
    }

    private isPaletteKind(kind: string): kind is EditorPaletteKind {
        return this.objectPalette.some((item) => item.kind === kind) || this.isRoadPiece(kind) || this.isSceneryPalette(kind);
    }

    private placeEditorPaletteItem(kind: EditorPaletteKind, location: EditorDropLocation) {
        if (!this.editorMap) return;
        this.editorDropShadow = null;
        const label = [...this.objectPalette, ...this.roadPalette, ...this.sceneryPalette].find((item) => item.kind === kind)?.label
            || (kind === 'custom-decoration' ? '업로드 장식' : undefined)
            || (kind === 'ys-decoration' ? this.selectedYsDecorationPreset?.name : undefined)
            || kind;
        const laneLimit = this.editorMap.trackWidth / 2 - 0.8;
        if (!this.isRoadPiece(kind) && !this.isSceneryPalette(kind) && Math.abs(location.lane) > laneLimit) {
            this.editorMessage = `게임 요소는 ${this.editorViewMode === '3d' ? '3D 미리보기' : '2D 탑뷰'}의 도로 폭 안쪽에 놓아주세요.`;
            this.requestUiRender();
            return;
        }

        if (this.isSceneryPalette(kind)) {
            const asset = kind === 'custom-decoration'
                ? this.decorationAssets.find((item) => item.id === this.selectedDecorationAssetId)
                : undefined;
            const ysPreset = kind === 'ys-decoration' ? this.selectedYsDecorationPreset : undefined;
            if (kind === 'custom-decoration' && !asset) {
                this.editorMessage = '먼저 업로드한 3D 장식을 선택해주세요.';
                this.requestUiRender();
                return;
            }
            if (kind === 'custom-decoration' && (!asset?.format || !asset.data)) {
                this.editorMessage = 'MAX 원본에 웹 배치본을 연결한 뒤 배치해주세요.';
                this.requestUiRender();
                return;
            }
            if (kind === 'custom-decoration' && asset && !this.decorationTemplateCache.has(asset.id)) {
                const editorMapId = this.editorMap.id;
                this.editorMessage = `${asset.name} 장식을 준비한 뒤 선택한 위치에 배치합니다.`;
                this.requestUiRender();
                void this.ensureDecorationTemplateLoaded(asset)
                    .then(() => {
                        if (!this.editorMap || this.editorMap.id !== editorMapId) return;
                        this.placeEditorPaletteItem(kind, location);
                    })
                    .catch((error: any) => {
                        this.editorMessage = error?.message || `${asset.name} 장식을 준비하지 못했습니다.`;
                        this.requestUiRender();
                    });
                return;
            }
            if (kind === 'ys-decoration' && !ysPreset) {
                this.editorMessage = '먼저 YS 장식을 선택해주세요.';
                this.requestUiRender();
                return;
            }
            const sceneryKind: SceneryKind = kind === 'custom-decoration' ? 'custom' : (kind === 'ys-decoration' ? 'ys' : kind);
            const sceneryNames: Partial<Record<SceneryKind, string>> = {
                tree: '나무',
                'pine-tree': '곧은 소나무',
                'pine-tree-windswept': '바람소나무',
                'pine-tree-young': '어린 소나무',
                building: '건물',
                cone: '안전 원뿔',
                hanok: '기와집',
                'hanok-annex': '사랑채',
                'hanok-pavilion': '팔각 정자',
                'hanok-palace': '궁궐 전각',
                'hanok-courtyard': '한옥 마당',
                'hanok-library': '이층 누각',
                'hanok-study': '선비 서재',
                'lotus-pond': '연꽃 연못',
                'lotus-flower': '연꽃',
                'pond-rock': '연못 돌',
                'stone-bridge': '돌다리',
                'stone-stairs': '돌계단',
                'stone-wall': '돌담벽',
                jangdok: '장독대',
                'stone-lantern': '돌등',
                'wooden-fence': '전통 목책',
                'harbor-warehouse': '포구 창고',
                'fishing-boat': '돛단 어선',
                'kim-yeeun-old-shrine-naejaesil': '구선원전 내재실',
                'kim-yeeun-gyeonghoeru': '경회루',
                'kim-yeeun-base': '궁궐 석재 기단',
                'kim-yeeun-stairs': '궁궐 석계단',
                'kim-yeeun-stone-path-small2': '궁궐 돌길',
                'kim-yeeun-old-shrine-south-haenggak': '구선원전 남행각',
                'kim-yeeun-gwollaegaksa-annex': '궐내각사 검서청 부속채',
                'kim-yeeun-stone-block': '궁궐 돌석',
                'kim-yeeun-stone-floor': '궁궐 석재 바닥',
                'haechi-guide': '서울 해치 안내자'
            };
            const defaultName = `${sceneryNames[sceneryKind] || '장식'} ${this.editorMap.scenery.length + 1}`;
            const guideTarget = sceneryKind === 'haechi-guide'
                ? this.nearestHaechiGuideBuilding(location.worldX, location.worldZ)
                : undefined;
            const scenery: MapSceneryPlacement = {
                id: createId('scenery'),
                name: asset?.name || ysPreset?.name || defaultName,
                kind: sceneryKind,
                x: location.worldX,
                y: 0,
                z: location.worldZ,
                rotation: guideTarget?.rotation || 0,
                scale: 1,
                assetId: asset?.id || ysPreset?.id,
                fileName: asset?.fileName || (ysPreset ? `${ysPreset.id}.glb` : undefined),
                guideTargetId: guideTarget?.id,
                guideText: sceneryKind === 'haechi-guide' ? this.defaultHaechiGuideText(guideTarget) : undefined,
                interactionRadius: sceneryKind === 'haechi-guide' ? 4.5 : undefined
            };
            this.editorMap.scenery.push(scenery);
            this.selectedSceneryIndex = this.editorMap.scenery.length - 1;
            this.editorTab = 'scenery';
            this.refreshEditorPlanBounds();
        } else if (kind === 'item') {
            const type: ItemCollection = 'coins';
            const item = createBlankPlacement('coin', location.distance);
            item.lane = location.lane;
            this.editorMap[type].push(item);
            this.selectedItemKind = 'coin';
            this.editorTab = 'items';
            this.setSelectedPlacementIndex(type, this.editorMap[type].length - 1);
        } else if (kind === 'obstacle') {
            const item = createBlankPlacement('obstacle', location.distance);
            item.lane = location.lane;
            item.obstacleKind = 'basic';
            this.editorMap.obstacles.push(item);
            this.selectedObstacleIndex = this.editorMap.obstacles.length - 1;
            this.editorTab = 'obstacles';
        } else if (kind === 'mount') {
            const mount: MapMountPlacement = {
                ...createBlankPlacement('mount', location.distance),
                lane: location.lane,
                kind: 'random'
            };
            this.editorMap.mounts.push(mount);
            this.selectedMountIndex = this.editorMap.mounts.length - 1;
            this.editorTab = 'mounts';
        } else if (kind === 'gate') {
            const gate: MapGate = {
                ...createBlankPlacement('gate', location.distance),
                lane: location.lane,
                name: `게이트 ${this.editorMap.gates.length + 1}`,
                design: this.editorMap.theme === 'neon' ? 'neon' : 'hanok',
                quizEnabled: true,
                questions: [createBlankQuestion()]
            };
            this.editorMap.gates.push(gate);
            this.editorMap.gates.sort((a, b) => a.distance - b.distance);
            this.selectedGateIndex = this.editorMap.gates.findIndex((item) => item.id === gate.id);
            this.selectedQuestionIndex = 0;
            this.editorTab = 'gates';
        } else {
            const insertedIndex = insertRoadPiece(this.editorMap, kind, location.distance, {
                x: location.pathWorldX,
                z: location.pathWorldZ
            });
            if (insertedIndex < 0) {
                this.editorMessage = kind === 'loop-one' || kind === 'loop-two'
                    ? '수직 루프를 넣기에는 선택한 웨이포인트 구간이 너무 짧습니다. 더 긴 구간에 배치해주세요.'
                    : (this.isJumpRoadPiece(kind)
                        ? '점프 발판을 넣기에는 선택한 구간이 짧습니다. 약 53m 이상 이어진 구간에 배치해주세요.'
                        : '선택한 구간에 길 조각을 배치할 수 없습니다.');
                this.requestUiRender();
                return;
            }
            this.selectedWaypointIndex = insertedIndex;
            const waypoint = this.editorMap.waypoints[this.selectedWaypointIndex];
            if (waypoint && kind !== 'loop-one' && kind !== 'loop-two' && !this.isJumpRoadPiece(kind)) {
                waypoint.x = location.pathWorldX;
                waypoint.z = location.pathWorldZ;
            }
            if (kind === 's-left' && this.selectedWaypointIndex > 0) {
                this.editorMap.waypoints[this.selectedWaypointIndex - 1].x = location.pathWorldX - 10;
            }
            if (kind === 's-right' && this.selectedWaypointIndex > 0) {
                this.editorMap.waypoints[this.selectedWaypointIndex - 1].x = location.pathWorldX + 10;
            }
            this.editorTab = 'path';
            this.refreshEditorPlanBounds();
        }

        this.previewEditor();
        this.editorMessage = `${label}을(를) 선택한 ${this.editorViewMode === '3d' ? '3D 위치' : '2D 좌표'}에 배치했습니다.`;
        this.recordEditorHistory(`${label} 배치`);
        this.requestUiRender();
    }

    private updateEditorDropShadow(kind: EditorPaletteKind, location: EditorDropLocation) {
        const palette = [...this.objectPalette, ...this.roadPalette, ...this.sceneryPalette].find((item) => item.kind === kind);
        const ysPreset = kind === 'ys-decoration' ? this.selectedYsDecorationPreset : undefined;
        const laneLimit = (this.editorMap?.trackWidth || 16) / 2 - 0.8;
        this.editorDropShadow = {
            kind,
            icon: palette?.icon || ysPreset?.icon || '●',
            label: palette?.label || ysPreset?.name || kind,
            left: this.planWorldXPercent(location.pathWorldX),
            top: this.planWorldZPercent(location.pathWorldZ),
            valid: this.isRoadPiece(kind) || this.isSceneryPalette(kind) || Math.abs(location.lane) <= laneLimit
        };
        this.requestUiRender();
    }

    private setEditorZoom(value: number) {
        const canvas = this.mapCanvas?.nativeElement;
        const centerX = canvas && canvas.scrollWidth > 0
            ? (canvas.scrollLeft + canvas.clientWidth / 2) / canvas.scrollWidth
            : 0.5;
        const centerY = canvas && canvas.scrollHeight > 0
            ? (canvas.scrollTop + canvas.clientHeight / 2) / canvas.scrollHeight
            : 0.5;
        this.editorZoom = Math.round(THREE.MathUtils.clamp(value, 0.1, 3) * 20) / 20;
        this.editorDropShadow = null;
        this.requestUiRender();
        setTimeout(() => {
            const viewport = this.mapCanvas?.nativeElement;
            if (!viewport) return;
            viewport.scrollLeft = Math.max(0, centerX * viewport.scrollWidth - viewport.clientWidth / 2);
            viewport.scrollTop = Math.max(0, centerY * viewport.scrollHeight - viewport.clientHeight / 2);
            this.scheduleTerrainCanvasRender();
        }, 30);
    }

    private createTerrainDraft(): TerrainGrid | null {
        if (!this.editorMap) return null;
        if (this.editorMap.terrain) {
            return {
                columns: this.editorMap.terrain.columns,
                rows: this.editorMap.terrain.rows,
                cells: [...this.editorMap.terrain.cells]
            };
        }
        const columns = 36;
        const rows = Math.round(THREE.MathUtils.clamp(columns * (this.editorMap.mapDepth / Math.max(1, this.editorMap.mapWidth)), 24, 72));
        const kind = this.defaultTerrainKind(this.editorMap.theme);
        return { columns, rows, cells: Array.from({ length: columns * rows }, () => kind) };
    }

    private defaultTerrainKind(theme: MapTheme): TerrainKind {
        if (theme === 'beach') return 'sand';
        if (theme === 'joseon-harbor') return 'stone';
        if (theme === 'ys-palace') return 'earth';
        return 'grass';
    }

    private paintTerrainAtPointer(event: PointerEvent) {
        const canvas = this.terrainCanvas?.nativeElement;
        const draft = this.terrainDraft;
        if (!canvas || !draft) return;
        const rect = canvas.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return;
        const logicalX = ((event.clientX - rect.left) / rect.width) * canvas.width;
        const logicalY = ((event.clientY - rect.top) / rect.height) * canvas.height;
        const areaX = this.editorMapAreaX;
        const areaY = this.editorMapAreaY;
        const areaWidth = this.editorMapAreaWidth;
        const areaHeight = this.editorMapAreaHeight;
        if (logicalX < areaX || logicalX > areaX + areaWidth || logicalY < areaY || logicalY > areaY + areaHeight) return;
        const column = Math.min(draft.columns - 1, Math.floor(((logicalX - areaX) / Math.max(1, areaWidth)) * draft.columns));
        const row = Math.min(draft.rows - 1, Math.floor(((logicalY - areaY) / Math.max(1, areaHeight)) * draft.rows));
        const radius = Math.max(0, this.terrainBrushSize - 1);
        for (let rowOffset = -radius; rowOffset <= radius; rowOffset++) {
            for (let columnOffset = -radius; columnOffset <= radius; columnOffset++) {
                if (Math.hypot(columnOffset, rowOffset) > radius + 0.35) continue;
                const targetColumn = column + columnOffset;
                const targetRow = row + rowOffset;
                if (targetColumn < 0 || targetColumn >= draft.columns || targetRow < 0 || targetRow >= draft.rows) continue;
                draft.cells[targetRow * draft.columns + targetColumn] = this.selectedTerrainKind;
            }
        }
        this.terrainDraftDirty = true;
        this.renderTerrainCanvas();
    }

    private scheduleTerrainCanvasRender() {
        if (!this.editorMap || this.destroyed) return;
        if (this.terrainRenderFrame !== null) cancelAnimationFrame(this.terrainRenderFrame);
        this.terrainRenderFrame = requestAnimationFrame(() => {
            this.terrainRenderFrame = null;
            this.renderTerrainCanvas();
        });
    }

    private renderTerrainCanvas() {
        const canvas = this.terrainCanvas?.nativeElement;
        const draft = this.terrainDraft || this.editorMap?.terrain;
        if (!canvas || !draft) return;
        if (canvas.width !== 1000) canvas.width = 1000;
        if (canvas.height !== 1000) canvas.height = 1000;
        const context = canvas.getContext('2d');
        if (!context) return;
        const areaX = this.editorMapAreaX;
        const areaY = this.editorMapAreaY;
        const areaWidth = this.editorMapAreaWidth;
        const areaHeight = this.editorMapAreaHeight;
        const cellWidth = areaWidth / draft.columns;
        const cellHeight = areaHeight / draft.rows;
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = '#07110d';
        context.fillRect(0, 0, canvas.width, canvas.height);
        draft.cells.forEach((kind, index) => {
            const column = index % draft.columns;
            const row = Math.floor(index / draft.columns);
            context.fillStyle = this.terrainPalette.find((item) => item.kind === kind)?.color || '#3f6b45';
            context.fillRect(areaX + column * cellWidth, areaY + row * cellHeight, cellWidth + 0.6, cellHeight + 0.6);
        });
        if (this.editorTab !== 'terrain') return;
        context.strokeStyle = 'rgba(255,255,255,0.08)';
        context.lineWidth = 0.7;
        for (let column = 0; column <= draft.columns; column++) {
            const x = areaX + column * cellWidth;
            context.beginPath();
            context.moveTo(x, areaY);
            context.lineTo(x, areaY + areaHeight);
            context.stroke();
        }
        for (let row = 0; row <= draft.rows; row++) {
            const y = areaY + row * cellHeight;
            context.beginPath();
            context.moveTo(areaX, y);
            context.lineTo(areaX + areaWidth, y);
            context.stroke();
        }
        context.save();
        context.beginPath();
        context.rect(areaX, areaY, areaWidth, areaHeight);
        context.clip();
        context.beginPath();
        const samples = Math.min(320, Math.max(96, (this.editorMap?.waypoints.length || 4) * 14));
        for (let index = 0; index <= samples; index++) {
            const point = this.getEditorPlanPoint((index / samples) * (this.editorMap?.trackLength || 1));
            const x = this.planWorldXCoordinate(point.x);
            const y = this.planWorldZCoordinate(point.z);
            if (index === 0) context.moveTo(x, y);
            else context.lineTo(x, y);
        }
        context.strokeStyle = 'rgba(248,250,252,0.78)';
        context.lineWidth = Math.max(8, Math.min(34, this.editorNaturalRoadWidth));
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.stroke();
        context.restore();
        context.strokeStyle = 'rgba(94,234,212,0.85)';
        context.lineWidth = 2;
        context.setLineDash([10, 8]);
        context.strokeRect(areaX, areaY, areaWidth, areaHeight);
        context.setLineDash([]);
        if (this.editorMap?.waypoints.length) {
            const start = this.editorMap.waypoints[0];
            const goal = this.editorMap.waypoints[this.editorMap.waypoints.length - 1];
            for (const marker of [
                { point: start, label: 'S', color: '#34d399' },
                { point: goal, label: 'G', color: '#a78bfa' }
            ]) {
                const x = this.planWorldXCoordinate(marker.point.x);
                const y = this.planWorldZCoordinate(marker.point.z);
                context.fillStyle = marker.color;
                context.beginPath();
                context.arc(x, y, 10, 0, Math.PI * 2);
                context.fill();
                context.fillStyle = '#07111f';
                context.font = 'bold 11px sans-serif';
                context.textAlign = 'center';
                context.textBaseline = 'middle';
                context.fillText(marker.label, x, y + 0.5);
            }
        }
    }

    private resolveEditor3dDrop(clientX: number, clientY: number): EditorDropLocation | null {
        const viewport = this.editor3dViewport?.nativeElement;
        const canvas = this.renderer?.domElement;
        if (!viewport || !canvas || !this.camera || !this.editorMap || this.editorViewMode !== '3d') return null;
        const viewportRect = viewport.getBoundingClientRect();
        if (clientX < viewportRect.left || clientX > viewportRect.right
            || clientY < viewportRect.top || clientY > viewportRect.bottom) return null;
        const canvasRect = canvas.getBoundingClientRect();
        if (canvasRect.width <= 0 || canvasRect.height <= 0) return null;
        const pointer = new THREE.Vector2(
            ((clientX - canvasRect.left) / canvasRect.width) * 2 - 1,
            -((clientY - canvasRect.top) / canvasRect.height) * 2 + 1
        );
        this.editor3dRaycaster.setFromCamera(pointer, this.camera);
        this.editor3dGroundPlane.constant = -Math.max(0, this.explorePosition.y);
        const worldPoint = this.editor3dRaycaster.ray.intersectPlane(this.editor3dGroundPlane, new THREE.Vector3());
        if (!worldPoint) return null;
        const bounds = this.getRuntimeMapBounds(this.editorMap);
        if (worldPoint.x < bounds.minX || worldPoint.x > bounds.maxX
            || worldPoint.z < bounds.minZ || worldPoint.z > bounds.maxZ) return null;
        const distance = this.findNearestPathDistance(worldPoint.x, worldPoint.z);
        const frame = this.getPathFrame(this.editorMap, distance);
        const deltaX = worldPoint.x - frame.point.x;
        const deltaZ = worldPoint.z - frame.point.z;
        const lane = deltaX * frame.right.x + deltaZ * frame.right.z;
        return {
            distance: Math.round(distance),
            lane: Math.round(lane * 2) / 2,
            worldX: Math.round(worldPoint.x * 2) / 2,
            worldZ: Math.round(worldPoint.z * 2) / 2,
            pathWorldX: Math.round(worldPoint.x * 2) / 2,
            pathWorldZ: Math.round(worldPoint.z * 2) / 2
        };
    }

    private selectEditor3dObjectAt(clientX: number, clientY: number): boolean {
        const viewport = this.editor3dViewport?.nativeElement;
        const canvas = this.renderer?.domElement;
        if (!viewport || !canvas || !this.camera || !this.scene || !this.editorMap
            || this.screen !== 'editor' || this.editorViewMode !== '3d') return false;
        const viewportRect = viewport.getBoundingClientRect();
        if (clientX < viewportRect.left || clientX > viewportRect.right
            || clientY < viewportRect.top || clientY > viewportRect.bottom) return false;
        const canvasRect = canvas.getBoundingClientRect();
        if (canvasRect.width <= 0 || canvasRect.height <= 0) return false;
        const pointer = new THREE.Vector2(
            ((clientX - canvasRect.left) / canvasRect.width) * 2 - 1,
            -((clientY - canvasRect.top) / canvasRect.height) * 2 + 1
        );
        this.editor3dRaycaster.setFromCamera(pointer, this.camera);
        const intersections = this.editor3dRaycaster.intersectObjects(this.scene.children, true);
        for (const intersection of intersections) {
            const reference = this.editor3dObjectReference(intersection.object);
            if (reference && this.applyEditor3dObjectSelection(reference)) return true;
        }
        return false;
    }

    private editor3dObjectReference(object: THREE.Object3D): Editor3dObjectReference | null {
        let current: THREE.Object3D | null = object;
        while (current && current !== this.scene) {
            const reference = current.userData['editor3dObject'] as Editor3dObjectReference | undefined;
            if (reference?.id && reference.collection) return reference;
            current = current.parent;
        }
        return null;
    }

    private applyEditor3dObjectSelection(reference: Editor3dObjectReference): boolean {
        if (!this.editorMap) return false;
        let label = '';
        if (reference.collection === 'coins' || reference.collection === 'magnets'
            || reference.collection === 'supplements' || reference.collection === 'obstacles') {
            const index = this.editorMap[reference.collection].findIndex((item) => item.id === reference.id);
            if (index < 0) return false;
            this.setSelectedPlacementIndex(reference.collection, index);
            if (reference.collection === 'obstacles') this.editorTab = 'obstacles';
            else {
                this.selectedItemKind = this.itemKind(reference.collection);
                this.editorTab = 'items';
            }
            label = this.placementLabel(reference.collection);
        } else if (reference.collection === 'mounts') {
            const index = this.editorMap.mounts.findIndex((item) => item.id === reference.id);
            if (index < 0) return false;
            this.selectedMountIndex = index;
            this.editorTab = 'mounts';
            label = this.mountLabel(this.editorMap.mounts[index].kind);
        } else if (reference.collection === 'gates') {
            const index = this.editorMap.gates.findIndex((item) => item.id === reference.id);
            if (index < 0) return false;
            this.selectedGateIndex = index;
            this.selectedQuestionIndex = 0;
            this.editorTab = 'gates';
            label = this.editorMap.gates[index].name;
        } else {
            const index = this.editorMap.scenery.findIndex((item) => item.id === reference.id);
            if (index < 0) return false;
            this.selectedSceneryIndex = index;
            this.editorTab = 'scenery';
            label = this.editorMap.scenery[index].name;
        }
        this.editorMessage = `3D 화면에서 “${label}”을(를) 선택했습니다. 왼쪽 옵션을 조절하거나 Delete 키로 삭제할 수 있습니다.`;
        this.requestUiRender();
        return true;
    }

    private tagEditor3dSelectable(object: THREE.Object3D, collection: Editor3dSelectableCollection, id: string) {
        const reference: Editor3dObjectReference = { collection, id };
        object.userData['editor3dObject'] = reference;
    }

    private updateEditor3dGhost(kind: EditorPaletteKind, location: EditorDropLocation) {
        if (!this.scene || !this.editorMap) return;
        const ghostKey = this.editor3dGhostIdentity(kind);
        if (!this.editor3dGhost || this.editor3dGhostKey !== ghostKey) {
            this.clearEditor3dGhost();
            this.editor3dGhost = this.createEditor3dGhost(kind);
            this.editor3dGhostKey = ghostKey;
            this.scene.add(this.editor3dGhost);
        }
        const ghost = this.editor3dGhost;
        const frame = this.getPathFrame(this.editorMap, location.distance);
        const laneLimit = this.editorMap.trackWidth / 2 - 0.8;
        const valid = this.isRoadPiece(kind) || this.isSceneryPalette(kind) || Math.abs(location.lane) <= laneLimit;
        if (this.isSceneryPalette(kind)) {
            ghost.position.set(
                location.worldX,
                this.sceneryGroundHeightAt(location.worldX, location.worldZ) + 0.08,
                location.worldZ
            );
            ghost.rotation.set(0, 0, 0);
        } else if (this.isRoadPiece(kind)) {
            ghost.position.set(location.pathWorldX, frame.point.y + 0.12, location.pathWorldZ);
            ghost.quaternion.copy(this.createPathOrientation(frame));
        } else {
            ghost.position.copy(frame.point).addScaledVector(frame.right, location.lane).addScaledVector(frame.surfaceUp, 0.08);
            ghost.quaternion.copy(this.createPathOrientation(frame));
        }
        const color = valid ? 0x67e8f9 : 0xfb7185;
        ghost.traverse((object: THREE.Object3D) => {
            const material = (object as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
            if (!material?.isMeshBasicMaterial) return;
            material.color.setHex(color);
        });
        const palette = [...this.objectPalette, ...this.roadPalette, ...this.sceneryPalette].find((item) => item.kind === kind);
        const ysPreset = kind === 'ys-decoration' ? this.selectedYsDecorationPreset : undefined;
        const nextLabel = `${palette?.label || ysPreset?.name || kind} ${valid ? '배치 미리보기' : '도로 밖'}`;
        const statusChanged = this.editor3dGhostLabel !== nextLabel || this.editor3dGhostValid !== valid;
        this.editor3dGhostLabel = nextLabel;
        this.editor3dGhostValid = valid;
        if (statusChanged) this.requestUiRender();
    }

    private createEditor3dGhost(kind: EditorPaletteKind): THREE.Group {
        const group = new THREE.Group();
        group.name = 'editor-3d-placement-shadow';
        const previewSize = this.editor3dGhostDimensions(kind);
        const [previewOffsetX, previewOffsetZ] = isBundledNatureDecorationKind(kind)
            ? (BUNDLED_NATURE_DECORATION_SPECS[kind].ghostCenterOffset || [0, 0])
            : [0, 0];
        const material = new THREE.MeshBasicMaterial({
            color: 0x67e8f9,
            transparent: true,
            opacity: 0.42,
            depthWrite: false,
            side: THREE.DoubleSide
        });
        const add = (geometry: THREE.BufferGeometry, x: number, y: number, z: number) => {
            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.set(x, y, z);
            mesh.renderOrder = 1000;
            group.add(mesh);
        };
        if (this.isJumpRoadPiece(kind)) {
            const count = kind === 'jump-wide' ? 4 : 5;
            const depth = kind === 'jump-wide' ? 6.8 : 6;
            const spacing = kind === 'jump-wide' ? 10 : 9;
            const width = Math.max(14, this.editorMap?.trackWidth || 16);
            for (let index = 0; index < count; index++) {
                const x = kind === 'jump-zigzag' ? [-2.6, 2.6, -2.6, 2.6, 0][index] : 0;
                const height = kind === 'jump-stairs'
                    ? [0, 0.75, 1.5, 0.75, 0][index]
                    : (kind === 'jump-wide' ? [0, 0.45, 0, 0.45][index] : [0, 0.35, 0.7, 0.35, 0][index]);
                add(
                    new THREE.BoxGeometry(width, 0.32, depth),
                    x,
                    0.16 + height,
                    (index - (count - 1) / 2) * -spacing
                );
            }
        } else if (this.isRoadPiece(kind)) {
            add(new THREE.BoxGeometry(this.editorMap?.trackWidth || 16, 0.22, 14), 0, 0.11, 0);
        } else if (kind === 'gate') {
            const halfWidth = Math.max(3, (this.editorMap?.trackWidth || 16) / 2 - 0.7);
            add(new THREE.BoxGeometry(0.55, 6, 0.55), -halfWidth, 3, 0);
            add(new THREE.BoxGeometry(0.55, 6, 0.55), halfWidth, 3, 0);
            add(new THREE.BoxGeometry(halfWidth * 2 + 0.55, 0.65, 0.55), 0, 5.8, 0);
        } else if (kind === 'item') {
            add(new THREE.SphereGeometry(0.85, 16, 12), 0, 1.3, 0);
        } else if (kind === 'mount') {
            add(new THREE.BoxGeometry(1.6, 1.8, 3.2), 0, 1.15, 0);
        } else if (kind === 'obstacle') {
            add(new THREE.ConeGeometry(1.1, 2.6, 8), 0, 1.3, 0);
        } else {
            add(
                new THREE.BoxGeometry(previewSize.x, previewSize.y, previewSize.z),
                previewOffsetX,
                previewSize.y / 2,
                previewOffsetZ
            );
        }
        const footprintRadius = THREE.MathUtils.clamp(Math.max(previewSize.x, previewSize.z) / 2, 0.85, 80);
        const ring = new THREE.Mesh(new THREE.TorusGeometry(footprintRadius, 0.09, 8, 32), material);
        ring.rotation.x = Math.PI / 2;
        ring.position.set(previewOffsetX, 0.06, previewOffsetZ);
        ring.renderOrder = 1000;
        group.add(ring);
        if (Math.abs(previewOffsetX) > 0.01 || Math.abs(previewOffsetZ) > 0.01) {
            const anchor = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.08, 8, 20), material);
            anchor.rotation.x = Math.PI / 2;
            anchor.position.y = 0.07;
            anchor.renderOrder = 1001;
            group.add(anchor);
        }
        return group;
    }

    private editor3dGhostIdentity(kind: EditorPaletteKind): string {
        if (kind === 'custom-decoration') return `${kind}:${this.selectedDecorationAssetId}`;
        if (kind === 'ys-decoration') return `${kind}:${this.selectedYsDecorationId}`;
        return kind;
    }

    private editor3dGhostDimensions(kind: EditorPaletteKind): THREE.Vector3 {
        const trackWidth = this.editorMap?.trackWidth || 16;
        if (this.isJumpRoadPiece(kind)) {
            return new THREE.Vector3(Math.max(14, trackWidth), 2, kind === 'jump-wide' ? 37 : 43);
        }
        const fallback = kind === 'gate'
            ? new THREE.Vector3(Math.max(6, trackWidth - 1.4), 6, 0.55)
            : (this.isRoadPiece(kind)
                ? new THREE.Vector3(trackWidth, 0.22, 14)
                : (kind === 'item'
                    ? new THREE.Vector3(1.7, 2.6, 1.7)
                    : (kind === 'mount'
                        ? new THREE.Vector3(1.6, 2.3, 3.2)
                        : (kind === 'obstacle'
                            ? new THREE.Vector3(2.2, 2.6, 2.2)
                            : new THREE.Vector3(3.8, 3.6, 3.8)))));
        if (!this.isSceneryPalette(kind)) return fallback;
        if (kind === 'haechi-guide') return new THREE.Vector3(2.4, 3.2, 3.4);

        let source: THREE.Object3D | undefined;
        let disposableSource: THREE.Object3D | undefined;
        if (kind === 'custom-decoration') {
            source = this.decorationTemplateCache.get(this.selectedDecorationAssetId);
        } else if (kind === 'ys-decoration') {
            const preset = this.selectedYsDecorationPreset;
            if (preset) {
                source = this.ysDecorationTemplateCache.get(preset.id);
                if (!source) {
                    const surface = this.ysDecorationSurfaceCache.get(preset.id);
                    if (surface) {
                        return new THREE.Vector3(
                            Math.max(0.25, surface.columns * surface.cellSizeX),
                            Math.max(0.25, preset.targetHeight),
                            Math.max(0.25, surface.rows * surface.cellSizeZ)
                        );
                    }
                    return new THREE.Vector3(
                        Math.max(3.8, preset.targetHeight * 1.2),
                        Math.max(0.25, preset.targetHeight),
                        Math.max(3.8, preset.targetHeight * 0.8)
                    );
                }
            }
        } else if (isBundledNatureDecorationKind(kind)) {
            source = this.bundledNatureTemplateCache.get(kind);
            if (!source) {
                const [width, height, depth] = BUNDLED_NATURE_DECORATION_SPECS[kind].fallbackDimensions;
                return new THREE.Vector3(width, height, depth);
            }
        } else {
            disposableSource = this.createBuiltInDecoration(kind as SceneryKind);
            source = disposableSource;
        }

        if (!source) return fallback;
        source.updateMatrixWorld(true);
        const size = new THREE.Box3().setFromObject(source).getSize(new THREE.Vector3());
        if (disposableSource) this.disposeObjectTree(disposableSource, true);
        if (![size.x, size.y, size.z].every((value) => Number.isFinite(value) && value > 0.001)) return fallback;
        return new THREE.Vector3(
            Math.max(0.25, size.x),
            Math.max(0.25, size.y),
            Math.max(0.25, size.z)
        );
    }

    private clearEditor3dGhost() {
        if (this.editor3dGhost) {
            this.editor3dGhost.parent?.remove(this.editor3dGhost);
            this.disposeObjectTree(this.editor3dGhost, true);
        }
        this.editor3dGhost = undefined;
        this.editor3dGhostKey = '';
        this.editor3dGhostLabel = '';
        this.editor3dGhostValid = false;
    }

    private resolveEditorDrop(clientX: number, clientY: number): EditorDropLocation | null {
        if (!this.mapSurface || !this.editorMap) return null;
        const rect = this.mapSurface.nativeElement.getBoundingClientRect();
        if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return null;
        const horizontal = THREE.MathUtils.clamp(((clientX - rect.left) / rect.width - 0.04) / 0.92, 0, 1);
        const vertical = THREE.MathUtils.clamp(((clientY - rect.top) / rect.height - 0.04) / 0.92, 0, 1);
        const pathWorldX = this.editorPlanBounds.maxX - horizontal * (this.editorPlanBounds.maxX - this.editorPlanBounds.minX);
        const pathWorldZ = this.editorPlanBounds.maxZ - vertical * (this.editorPlanBounds.maxZ - this.editorPlanBounds.minZ);
        const distance = this.findNearestPathDistance(pathWorldX, pathWorldZ);
        const frame = this.getPathFrame(this.editorMap, distance);
        const deltaX = pathWorldX - frame.point.x;
        const deltaZ = pathWorldZ - frame.point.z;
        const visualLane = deltaX * frame.right.x + deltaZ * frame.right.z;
        const visualForward = deltaX * frame.forward.x + deltaZ * frame.forward.z;
        const lane = (visualLane / this.editorLateralVisualScale) * this.editorLaneDisplayDirection;
        const worldPoint = frame.point.clone()
            .addScaledVector(frame.forward, visualForward)
            .addScaledVector(frame.right, lane);
        return {
            distance: Math.round(distance),
            lane: Math.round(lane * 2) / 2,
            worldX: Math.round(worldPoint.x * 2) / 2,
            worldZ: Math.round(worldPoint.z * 2) / 2,
            pathWorldX: Math.round(pathWorldX * 2) / 2,
            pathWorldZ: Math.round(pathWorldZ * 2) / 2
        };
    }

    private findNearestPathDistance(worldX: number, worldZ: number): number {
        if (!this.editorMap) return 0;
        return THREE.MathUtils.clamp(this.findNearestDistance(this.editorMap, worldX, worldZ), 10, this.editorMap.trackLength - 10);
    }

    private findNearestDistance(map: GameMap, worldX: number, worldZ: number): number {
        const samples = Math.min(480, Math.max(160, map.waypoints.length * 24));
        let bestDistance = 0;
        let bestDistanceSquared = Number.POSITIVE_INFINITY;
        for (let index = 0; index <= samples; index++) {
            const distance = (index / samples) * map.trackLength;
            const point = this.sampleMapPath(map, distance);
            const squared = (point.x - worldX) ** 2 + (point.z - worldZ) ** 2;
            if (squared < bestDistanceSquared) {
                bestDistanceSquared = squared;
                bestDistance = distance;
            }
        }

        let radius = map.trackLength / samples;
        for (let pass = 0; pass < 3; pass++) {
            const start = Math.max(0, bestDistance - radius);
            const end = Math.min(map.trackLength, bestDistance + radius);
            for (let index = 0; index <= 12; index++) {
                const distance = start + (index / 12) * (end - start);
                const point = this.sampleMapPath(map, distance);
                const squared = (point.x - worldX) ** 2 + (point.z - worldZ) ** 2;
                if (squared < bestDistanceSquared) {
                    bestDistanceSquared = squared;
                    bestDistance = distance;
                }
            }
            radius /= 6;
        }
        return THREE.MathUtils.clamp(bestDistance, 0, map.trackLength);
    }

    private getPlanMoveItem(type: PlanMoveType, index: number): MapObjectPlacement | MapSceneryPlacement | MapWaypoint | null {
        if (!this.editorMap) return null;
        if (type === 'waypoint') return this.editorMap.waypoints[index] || null;
        return this.editorMap[type][index] || null;
    }

    private selectPlanMoveItem(type: PlanMoveType, index: number) {
        if (type === 'waypoint') {
            this.editorTab = 'path';
            this.selectedWaypointIndex = index;
        } else if (type === 'coins') {
            this.editorTab = 'items';
            this.selectedItemKind = 'coin';
            this.selectedCoinIndex = index;
        } else if (type === 'magnets') {
            this.editorTab = 'items';
            this.selectedItemKind = 'magnet';
            this.selectedMagnetIndex = index;
        } else if (type === 'supplements') {
            this.editorTab = 'items';
            this.selectedItemKind = 'supplement';
            this.selectedSupplementIndex = index;
        } else if (type === 'mounts') {
            this.editorTab = 'mounts';
            this.selectedMountIndex = index;
        } else if (type === 'obstacles') {
            this.editorTab = 'obstacles';
            this.selectedObstacleIndex = index;
        } else if (type === 'gates') {
            this.editorTab = 'gates';
            this.selectedGateIndex = index;
            this.selectedQuestionIndex = 0;
        } else {
            this.editorTab = 'scenery';
            this.selectedSceneryIndex = index;
        }
    }

    private getEditorPlanPoint(distance: number): THREE.Vector3 {
        return this.sampleMapPath(this.editorMap || this.activeMap, distance);
    }

    private sampleMapPath(map: GameMap, distance: number): THREE.Vector3 {
        const points = map.waypoints;
        if (points.length === 0) return new THREE.Vector3(0, 0, -distance);
        if (points.length === 1) return new THREE.Vector3(points[0].x, points[0].y, points[0].z);
        const selectedDistance = THREE.MathUtils.clamp(distance, 0, map.trackLength);
        let rightIndex = points.findIndex((item) => item.distance >= selectedDistance);
        if (rightIndex <= 0) rightIndex = 1;
        if (rightIndex < 0) rightIndex = points.length - 1;
        const leftIndex = rightIndex - 1;
        const p0 = points[Math.max(0, leftIndex - 1)];
        const p1 = points[leftIndex];
        const p2 = points[rightIndex];
        const p3 = points[Math.min(points.length - 1, rightIndex + 1)];
        const progress = THREE.MathUtils.clamp(
            (selectedDistance - p1.distance) / Math.max(0.001, p2.distance - p1.distance),
            0,
            1
        );
        if (p1.roadPiece && p1.roadPiece === p2.roadPiece && this.isJumpRoadPiece(p1.roadPiece)) {
            return new THREE.Vector3(
                THREE.MathUtils.lerp(p1.x, p2.x, progress),
                THREE.MathUtils.lerp(p1.y, p2.y, progress),
                THREE.MathUtils.lerp(p1.z, p2.z, progress)
            );
        }
        const span = Math.max(0.001, p2.distance - p1.distance);
        const tangent0Scale = this.pathCornerTangentScale(p0, p1, p2);
        const tangent1Scale = this.pathCornerTangentScale(p1, p2, p3);
        const tangent0X = ((p2.x - p0.x) / Math.max(0.001, p2.distance - p0.distance)) * span * tangent0Scale;
        const tangent1X = ((p3.x - p1.x) / Math.max(0.001, p3.distance - p1.distance)) * span * tangent1Scale;
        const tangent0Y = ((p2.y - p0.y) / Math.max(0.001, p2.distance - p0.distance)) * span * tangent0Scale;
        const tangent1Y = ((p3.y - p1.y) / Math.max(0.001, p3.distance - p1.distance)) * span * tangent1Scale;
        const tangent0Z = ((p2.z - p0.z) / Math.max(0.001, p2.distance - p0.distance)) * span * tangent0Scale;
        const tangent1Z = ((p3.z - p1.z) / Math.max(0.001, p3.distance - p1.distance)) * span * tangent1Scale;
        return new THREE.Vector3(
            this.hermiteValue(p1.x, p2.x, tangent0X, tangent1X, progress),
            this.hermiteValue(p1.y, p2.y, tangent0Y, tangent1Y, progress),
            this.hermiteValue(p1.z, p2.z, tangent0Z, tangent1Z, progress)
        );
    }

    private pathCornerTangentScale(before: MapWaypoint, current: MapWaypoint, after: MapWaypoint): number {
        if (before === current || current === after) return 1;
        const incomingX = current.x - before.x;
        const incomingZ = current.z - before.z;
        const outgoingX = after.x - current.x;
        const outgoingZ = after.z - current.z;
        const incomingLength = Math.hypot(incomingX, incomingZ);
        const outgoingLength = Math.hypot(outgoingX, outgoingZ);
        if (incomingLength < 0.001 || outgoingLength < 0.001) return 0.18;
        const alignment = THREE.MathUtils.clamp(
            (incomingX * outgoingX + incomingZ * outgoingZ) / (incomingLength * outgoingLength),
            -1,
            1
        );
        return THREE.MathUtils.lerp(0.08, 1, (alignment + 1) / 2);
    }

    private hermiteValue(p1: number, p2: number, tangent0: number, tangent1: number, progress: number): number {
        const squared = progress * progress;
        const cubed = squared * progress;
        return (2 * p1 - 2 * p2 + tangent0 + tangent1) * cubed
            + (-3 * p1 + 3 * p2 - 2 * tangent0 - tangent1) * squared
            + tangent0 * progress
            + p1;
    }

    private planWorldXCoordinate(worldX: number): number {
        const span = Math.max(1, this.editorPlanBounds.maxX - this.editorPlanBounds.minX);
        return 40 + THREE.MathUtils.clamp((this.editorPlanBounds.maxX - worldX) / span, 0, 1) * 920;
    }

    private planWorldZCoordinate(worldZ: number): number {
        const span = Math.max(1, this.editorPlanBounds.maxZ - this.editorPlanBounds.minZ);
        return 40 + THREE.MathUtils.clamp((this.editorPlanBounds.maxZ - worldZ) / span, 0, 1) * 920;
    }

    private get editorNaturalRoadWidth(): number {
        const span = Math.max(1, this.editorPlanBounds.maxX - this.editorPlanBounds.minX);
        return ((this.editorMap?.trackWidth || 16) / span) * 920;
    }

    private buildEditorLaneGuidePoints(direction: -1 | 1): string {
        if (!this.editorMap) return '';
        const points: string[] = [];
        const samples = Math.min(320, Math.max(96, this.editorMap.waypoints.length * 14));
        const laneCenterOffset = (this.editorMap.trackWidth / 4)
            * this.editorLateralVisualScale
            * direction;
        for (let index = 0; index <= samples; index++) {
            const distance = (index / samples) * this.editorMap.trackLength;
            const frame = this.getPathFrame(this.editorMap, distance);
            const point = frame.point.clone().addScaledVector(frame.right, laneCenterOffset);
            points.push(`${this.planWorldXCoordinate(point.x)},${this.planWorldZCoordinate(point.z)}`);
        }
        return points.join(' ');
    }

    private planWorldXPercent(worldX: number): number {
        return this.planWorldXCoordinate(worldX) / 10;
    }

    private planWorldZPercent(worldZ: number): number {
        return this.planWorldZCoordinate(worldZ) / 10;
    }

    private getMapObjectPlanPoint(item: MapObjectPlacement): THREE.Vector3 {
        const map = this.editorMap || this.activeMap;
        const frame = this.getPathFrame(map, item.distance);
        return frame.point.clone().addScaledVector(
            frame.right,
            item.lane * this.editorLateralVisualScale * this.editorLaneDisplayDirection
        );
    }

    private getSceneryPlanPoint(item: MapSceneryPlacement): THREE.Vector3 {
        return new THREE.Vector3(item.x, item.y, item.z);
    }

    private captureSceneryPathAnchors(waypointIndex: number): SceneryPathAnchor[] {
        if (!this.editorMap || this.editorMap.scenery.length === 0) return [];
        const waypoints = this.editorMap.waypoints;
        const minDistance = waypoints[Math.max(0, waypointIndex - 2)]?.distance ?? 0;
        const maxDistance = waypoints[Math.min(waypoints.length - 1, waypointIndex + 2)]?.distance ?? this.editorMap.trackLength;
        const anchors: SceneryPathAnchor[] = [];
        this.editorMap.scenery.forEach((item) => {
            const distance = this.findNearestDistance(this.editorMap, item.x, item.z);
            if (distance < minDistance || distance > maxDistance) return;
            const frame = this.getPathFrame(this.editorMap, distance);
            const deltaX = item.x - frame.point.x;
            const deltaZ = item.z - frame.point.z;
            const heading = THREE.MathUtils.radToDeg(Math.atan2(frame.forward.x, frame.forward.z));
            anchors.push({
                id: item.id,
                distance,
                lateralOffset: deltaX * frame.right.x + deltaZ * frame.right.z,
                forwardOffset: deltaX * frame.forward.x + deltaZ * frame.forward.z,
                relativeRotation: this.normalizeEditorRotation(item.rotation - heading),
                originalX: item.x,
                originalZ: item.z,
                originalRotation: item.rotation
            });
        });
        return anchors;
    }

    private applySceneryPathAnchors(anchors: SceneryPathAnchor[]) {
        if (!this.editorMap || anchors.length === 0) return;
        anchors.forEach((anchor) => {
            const item = this.editorMap.scenery.find((scenery) => scenery.id === anchor.id);
            if (!item) return;
            const frame = this.getPathFrame(this.editorMap, anchor.distance);
            item.x = frame.point.x + frame.right.x * anchor.lateralOffset + frame.forward.x * anchor.forwardOffset;
            item.z = frame.point.z + frame.right.z * anchor.lateralOffset + frame.forward.z * anchor.forwardOffset;
            const heading = THREE.MathUtils.radToDeg(Math.atan2(frame.forward.x, frame.forward.z));
            item.rotation = this.normalizeEditorRotation(anchor.relativeRotation + heading);
        });
    }

    private restoreSceneryPathAnchors(anchors: SceneryPathAnchor[]) {
        if (!this.editorMap || anchors.length === 0) return;
        anchors.forEach((anchor) => {
            const item = this.editorMap.scenery.find((scenery) => scenery.id === anchor.id);
            if (!item) return;
            item.x = anchor.originalX;
            item.z = anchor.originalZ;
            item.rotation = anchor.originalRotation;
        });
    }

    private normalizeEditorRotation(value: number): number {
        return ((value + 180) % 360 + 360) % 360 - 180;
    }

    private getPathFrame(map: GameMap, distance: number) {
        const point = this.sampleMapPath(map, distance);
        const epsilon = Math.max(0.5, Math.min(3, map.trackLength / 500));
        const before = this.sampleMapPath(map, Math.max(0, distance - epsilon));
        const after = this.sampleMapPath(map, Math.min(map.trackLength, distance + epsilon));
        const forward = after.sub(before);
        forward.y = 0;
        if (forward.lengthSq() <= 0.0001) forward.set(0, 0, -1);
        else forward.normalize();
        const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();
        return { point, forward, right };
    }

    private refreshEditorPlanBounds() {
        if (!this.editorMap || this.editorMap.waypoints.length === 0) {
            this.editorPlanBounds = { minX: -50, maxX: 50, minZ: -100, maxZ: 0 };
            return;
        }
        this.expandEditorMapToFitContent();
        const samples = Math.min(360, Math.max(96, this.editorMap.waypoints.length * 18));
        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let minZ = Number.POSITIVE_INFINITY;
        let maxZ = Number.NEGATIVE_INFINITY;
        for (let index = 0; index <= samples; index++) {
            const point = this.sampleMapPath(this.editorMap, (index / samples) * this.editorMap.trackLength);
            minX = Math.min(minX, point.x);
            maxX = Math.max(maxX, point.x);
            minZ = Math.min(minZ, point.z);
            maxZ = Math.max(maxZ, point.z);
        }
        this.editorMap.scenery.forEach((item) => {
            minX = Math.min(minX, item.x);
            maxX = Math.max(maxX, item.x);
            minZ = Math.min(minZ, item.z);
            maxZ = Math.max(maxZ, item.z);
        });
        const centerX = (minX + maxX) / 2;
        const centerZ = (minZ + maxZ) / 2;
        const spanX = Math.max(90, maxX - minX);
        const spanZ = Math.max(140, maxZ - minZ);
        const paddingX = Math.max(18, spanX * 0.12, this.editorMap.trackWidth * 1.5);
        const paddingZ = Math.max(24, spanZ * 0.08, this.editorMap.trackWidth * 1.5);
        let boundsWidth = Math.max(this.editorMap.mapWidth, spanX + paddingX * 2);
        let boundsHeight = Math.max(this.editorMap.mapDepth, spanZ + paddingZ * 2);
        const canvasWidth = Math.max(320, this.mapCanvas?.nativeElement.clientWidth || 900);
        const canvasAspect = canvasWidth / Math.max(1, this.editorCanvasHeight);
        if (boundsWidth / boundsHeight > canvasAspect) boundsHeight = boundsWidth / canvasAspect;
        else boundsWidth = boundsHeight * canvasAspect;
        this.editorPlanBounds = {
            minX: centerX - boundsWidth / 2,
            maxX: centerX + boundsWidth / 2,
            minZ: centerZ - boundsHeight / 2,
            maxZ: centerZ + boundsHeight / 2
        };
    }

    private expandEditorMapToFitContent(): { accepted: boolean; expanded: boolean } {
        if (!this.editorMap || this.editorMap.waypoints.length === 0) return { accepted: true, expanded: false };
        const bounds = this.getRuntimePathBounds(this.editorMap);
        this.editorMap.scenery.forEach((item) => {
            bounds.minX = Math.min(bounds.minX, item.x);
            bounds.maxX = Math.max(bounds.maxX, item.x);
            bounds.minZ = Math.min(bounds.minZ, item.z);
            bounds.maxZ = Math.max(bounds.maxZ, item.z);
        });
        const padding = Math.max(40, this.editorMap.trackWidth * 2.5);
        const requiredWidth = bounds.maxX - bounds.minX + padding * 2;
        const requiredDepth = bounds.maxZ - bounds.minZ + padding * 2;
        const maxMapSize = 20000;
        if (requiredWidth > maxMapSize || requiredDepth > maxMapSize) return { accepted: false, expanded: false };
        const nextWidth = Math.max(this.editorMap.mapWidth, Math.ceil(requiredWidth / 100) * 100);
        const nextDepth = Math.max(this.editorMap.mapDepth, Math.ceil(requiredDepth / 100) * 100);
        const expanded = nextWidth > this.editorMap.mapWidth || nextDepth > this.editorMap.mapDepth;
        this.editorMap.mapWidth = nextWidth;
        this.editorMap.mapDepth = nextDepth;
        return { accepted: true, expanded };
    }

    private getRuntimePathBounds(map: GameMap) {
        const samples = Math.min(500, Math.max(120, map.waypoints.length * 20));
        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let minZ = Number.POSITIVE_INFINITY;
        let maxZ = Number.NEGATIVE_INFINITY;
        for (let index = 0; index <= samples; index++) {
            const point = this.sampleMapPath(map, (index / samples) * map.trackLength);
            minX = Math.min(minX, point.x);
            maxX = Math.max(maxX, point.x);
            minZ = Math.min(minZ, point.z);
            maxZ = Math.max(maxZ, point.z);
        }
        return { minX, maxX, minZ, maxZ };
    }

    private getRuntimeMapBounds(map: GameMap): EditorPlanBounds {
        const pathBounds = this.getRuntimePathBounds(map);
        let minX = pathBounds.minX;
        let maxX = pathBounds.maxX;
        let minZ = pathBounds.minZ;
        let maxZ = pathBounds.maxZ;
        map.scenery.forEach((item) => {
            minX = Math.min(minX, item.x);
            maxX = Math.max(maxX, item.x);
            minZ = Math.min(minZ, item.z);
            maxZ = Math.max(maxZ, item.z);
        });
        const centerX = (minX + maxX) / 2;
        const centerZ = (minZ + maxZ) / 2;
        const width = Math.max(map.mapWidth, maxX - minX + 140);
        const depth = Math.max(map.mapDepth, maxZ - minZ + 140);
        return {
            minX: centerX - width / 2,
            maxX: centerX + width / 2,
            minZ: centerZ - depth / 2,
            maxZ: centerZ + depth / 2
        };
    }

    private getTerrainMapBounds(map: GameMap): EditorPlanBounds {
        const contentBounds = this.getRuntimePathBounds(map);
        map.scenery.forEach((item) => {
            contentBounds.minX = Math.min(contentBounds.minX, item.x);
            contentBounds.maxX = Math.max(contentBounds.maxX, item.x);
            contentBounds.minZ = Math.min(contentBounds.minZ, item.z);
            contentBounds.maxZ = Math.max(contentBounds.maxZ, item.z);
        });
        const centerX = (contentBounds.minX + contentBounds.maxX) / 2;
        const centerZ = (contentBounds.minZ + contentBounds.maxZ) / 2;
        return {
            minX: centerX - map.mapWidth / 2,
            maxX: centerX + map.mapWidth / 2,
            minZ: centerZ - map.mapDepth / 2,
            maxZ: centerZ + map.mapDepth / 2
        };
    }

    private cloneEditorTerrainDraft(draft: TerrainGrid | null): TerrainGrid | null {
        if (!draft) return null;
        return {
            columns: draft.columns,
            rows: draft.rows,
            cells: [...draft.cells]
        };
    }

    private cloneEditorMapSnapshot(map: GameMap): GameMap {
        const groundImage = map.groundImage;
        const snapshot = cloneMap({
            ...map,
            groundImage: groundImage ? { ...groundImage, dataUrl: '' } : undefined
        });
        if (groundImage) snapshot.groundImage = { ...groundImage };
        return snapshot;
    }

    private createEditorTextFingerprint(value: string): string {
        if (!value) return '0:0';
        const sampleStep = Math.max(1, Math.floor(value.length / 2048));
        let hash = 2166136261;
        for (let index = 0; index < value.length; index += sampleStep) {
            hash ^= value.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }
        hash ^= value.charCodeAt(value.length - 1);
        hash = Math.imul(hash, 16777619);
        return `${value.length}:${hash >>> 0}`;
    }

    private createEditorHistoryFingerprint(map: GameMap, terrainDraft: TerrainGrid | null, terrainDraftDirty: boolean): string {
        const groundImage = map.groundImage;
        const fingerprintMap: GameMap = {
            ...map,
            groundImage: groundImage
                ? { ...groundImage, dataUrl: this.createEditorTextFingerprint(groundImage.dataUrl) }
                : undefined
        };
        return JSON.stringify({ map: fingerprintMap, terrainDraft, terrainDraftDirty });
    }

    private createEditorHistoryEntry(label: string): EditorHistoryEntry | null {
        if (!this.editorMap) return null;
        return {
            map: this.cloneEditorMapSnapshot(this.editorMap),
            terrainDraft: this.cloneEditorTerrainDraft(this.terrainDraft),
            terrainDraftDirty: this.terrainDraftDirty,
            label
        };
    }

    private resetEditorHistory() {
        this.editorUndoStack = [];
        this.editorRedoStack = [];
        this.editorHistoryCurrent = this.createEditorHistoryEntry('편집 시작 상태');
        this.editorHistoryFingerprint = this.editorMap
            ? this.createEditorHistoryFingerprint(this.editorMap, this.terrainDraft, this.terrainDraftDirty)
            : '';
        this.editorHistoryRestoring = false;
    }

    private clearEditorHistory() {
        this.editorUndoStack = [];
        this.editorRedoStack = [];
        this.editorHistoryCurrent = null;
        this.editorHistoryFingerprint = '';
        this.editorHistoryRestoring = false;
    }

    private recordEditorHistory(label: string) {
        if (!this.editorMap || this.editorHistoryRestoring) return;
        const fingerprint = this.createEditorHistoryFingerprint(this.editorMap, this.terrainDraft, this.terrainDraftDirty);
        if (fingerprint === this.editorHistoryFingerprint) return;
        const nextEntry = this.createEditorHistoryEntry(label);
        if (!nextEntry) return;
        if (this.editorHistoryCurrent) this.editorUndoStack.push(this.editorHistoryCurrent);
        while (this.editorUndoStack.length > this.editorHistoryLimit) {
            if (this.editorUndoStack.length > 1) this.editorUndoStack.splice(1, 1);
            else this.editorUndoStack.shift();
        }
        this.editorRedoStack = [];
        this.editorHistoryCurrent = nextEntry;
        this.editorHistoryFingerprint = fingerprint;
    }

    private restoreEditorHistoryEntry(entry: EditorHistoryEntry, message: string) {
        this.editorHistoryRestoring = true;
        try {
            this.clearControls();
            this.clearEditor3dGhost();
            this.activePlanMove = null;
            this.activeSceneryPathAnchors = [];
            this.activeEditorPan = null;
            this.editorPanActive = false;
            this.editorDropShadow = null;
            this.editorDragActive = false;
            this.draggingPaletteKind = null;
            this.editorMap = this.cloneEditorMapSnapshot(entry.map);
            this.terrainDraft = this.cloneEditorTerrainDraft(entry.terrainDraft);
            this.terrainDraftDirty = entry.terrainDraftDirty;
            this.terrainUndoCells = null;
            if (this.editorTab === 'terrain' && !this.editorMap.groundImage && !this.terrainDraft) {
                this.terrainDraft = this.createTerrainDraft();
                this.terrainDraftDirty = false;
            }
            this.selectedWaypointIndex = this.clampEditorSelection(this.selectedWaypointIndex, this.editorMap.waypoints.length);
            this.selectedCoinIndex = this.clampEditorSelection(this.selectedCoinIndex, this.editorMap.coins.length);
            this.selectedMagnetIndex = this.clampEditorSelection(this.selectedMagnetIndex, this.editorMap.magnets.length);
            this.selectedSupplementIndex = this.clampEditorSelection(this.selectedSupplementIndex, this.editorMap.supplements.length);
            this.selectedMountIndex = this.clampEditorSelection(this.selectedMountIndex, this.editorMap.mounts.length);
            this.selectedObstacleIndex = this.clampEditorSelection(this.selectedObstacleIndex, this.editorMap.obstacles.length);
            this.selectedGateIndex = this.clampEditorSelection(this.selectedGateIndex, this.editorMap.gates.length);
            this.selectedSceneryIndex = this.clampEditorSelection(this.selectedSceneryIndex, this.editorMap.scenery.length);
            this.selectedQuestionIndex = this.clampEditorSelection(this.selectedQuestionIndex, this.selectedGate?.questions.length || 0);
            this.editorKnownTrackLength = this.editorMap.trackLength;
            this.groundImageStatus = this.editorMap.groundImage
                ? `${this.editorMap.groundImage.fileName} · ${this.editorMap.groundImage.width} × ${this.editorMap.groundImage.height}px · 저장된 바닥 이미지`
                : 'JPG, PNG, WEBP 이미지를 올리면 전체 맵 바닥에 맞춰 적용합니다.';
            this.refreshEditorPlanBounds();
            this.activeMap = this.editorMap;
            if (this.editorViewMode === '3d') this.gameMode = 'explore';
            this.rebuildWorld();
            this.screen = 'editor';
            if (this.editorViewMode === '3d') this.snapCamera();
            else this.focusEditorDistance(this.selectedEditorDistance());
            this.editorHistoryCurrent = this.createEditorHistoryEntry(entry.label);
            this.editorHistoryFingerprint = this.createEditorHistoryFingerprint(this.editorMap, this.terrainDraft, this.terrainDraftDirty);
            this.editorMessage = `${message} 편집 시작 상태 이후의 변경만 관리합니다.`;
            this.requestUiRender();
            setTimeout(() => this.scheduleTerrainCanvasRender(), 0);
        } finally {
            this.editorHistoryRestoring = false;
        }
    }

    private clampEditorSelection(index: number, length: number): number {
        if (length <= 0) return 0;
        return Math.max(0, Math.min(index, length - 1));
    }

    private openEditor(map: GameMap) {
        this.previousMap = cloneMap(this.activeMap);
        this.editorViewMode = '2d';
        this.clearControls();
        this.editorMap = normalizeMap(map);
        this.editorMap.id = map.id;
        this.editorMap.createdAt = map.createdAt;
        this.editorMap.builtIn = false;
        this.activeMap = this.editorMap;
        this.editorTab = 'settings';
        this.selectedWaypointIndex = 0;
        this.selectedCoinIndex = 0;
        this.selectedMagnetIndex = 0;
        this.selectedSupplementIndex = 0;
        this.selectedMountIndex = 0;
        this.selectedObstacleIndex = 0;
        this.selectedGateIndex = 0;
        this.selectedSceneryIndex = 0;
        this.selectedQuestionIndex = 0;
        this.selectedPaletteKind = null;
        this.activePlanMove = null;
        this.activeSceneryPathAnchors = [];
        this.activeEditorPan = null;
        this.editorPanActive = false;
        this.editorDropShadow = null;
        this.editor3dGhostLabel = '';
        this.editor3dGhostValid = false;
        this.terrainDraft = null;
        this.terrainDraftDirty = false;
        this.terrainUndoCells = null;
        this.terrainPainting = false;
        this.terrainPointerId = null;
        this.groundImageBusy = false;
        this.groundImageStatus = this.editorMap.groundImage
            ? `${this.editorMap.groundImage.fileName} · ${this.editorMap.groundImage.width} × ${this.editorMap.groundImage.height}px · 저장된 바닥 이미지`
            : 'JPG, PNG, WEBP 이미지를 올리면 전체 맵 바닥에 맞춰 적용합니다.';
        this.editorZoom = 1;
        this.editorPixelsPerDistance = 0.9;
        this.editorKnownTrackLength = this.editorMap.trackLength;
        this.refreshEditorPlanBounds();
        this.resetEditorHistory();
        this.editorMessage = '빈 공간을 끌어 맵을 이동하거나, 팔레트 항목을 원하는 좌표에 놓으세요.';
        this.rebuildWorld();
        this.screen = 'editor';
        this.setMenuHeritageShowcaseVisible(false);
        this.focusEditorDistance(0);
        this.requestUiRender();
        setTimeout(() => {
            this.refreshEditorPlanBounds();
            this.requestUiRender();
            this.scheduleTerrainCanvasRender();
        }, 30);
    }

    private loadSavedMaps() {
        try {
            const raw = localStorage.getItem(this.mapStorageKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return;
            this.savedMaps = parsed.map((item) => normalizeMap(item)).filter((item) => !item.builtIn);
        } catch {
            this.savedMaps = [];
        }
    }

    private persistMaps() {
        localStorage.setItem(this.mapStorageKey, JSON.stringify(this.savedMaps));
    }

    private markEditorChanged(message: string) {
        if (this.editorViewMode === '3d') {
            const applied = this.previewEditor();
            this.recordEditorHistory(message);
            if (applied) {
                this.editorMessage = `${message} 3D 화면에 바로 반영했습니다.`;
                this.requestUiRender();
            }
            return;
        }
        this.recordEditorHistory(message);
        this.editorMessage = `${message} 3D 반영은 “미리보기 갱신”을 누르세요.`;
        this.requestUiRender();
    }

    private selectedEditorDistance(): number {
        if (this.editorTab === 'items') return this.selectedItem?.distance || 0;
        if (this.editorTab === 'coins') return this.selectedCoin?.distance || 0;
        if (this.editorTab === 'magnets') return this.selectedMagnet?.distance || 0;
        if (this.editorTab === 'supplements') return this.selectedSupplement?.distance || 0;
        if (this.editorTab === 'mounts') return this.selectedMount?.distance || 0;
        if (this.editorTab === 'obstacles') return this.selectedObstacle?.distance || 0;
        if (this.editorTab === 'gates') return this.selectedGate?.distance || 0;
        if (this.editorTab === 'path') return this.editorMap?.waypoints[this.selectedWaypointIndex]?.distance || 0;
        if (this.editorTab === 'scenery' && this.selectedScenery) {
            return this.findNearestPathDistance(this.selectedScenery.x, this.selectedScenery.z);
        }
        return 0;
    }

    private restorePerformancePreferences() {
        const storedQuality = localStorage.getItem(this.graphicsQualityStorageKey) as GraphicsQuality | null;
        if (storedQuality && this.graphicsQualityOptions.some((item) => item.id === storedQuality)) {
            this.graphicsQuality = storedQuality;
        }
        const storedBackend = localStorage.getItem(this.rendererBackendStorageKey) as RendererBackend | null;
        this.webGpuSupported = Boolean((navigator as any).gpu) && window.isSecureContext;
        this.rendererBackendPreference = storedBackend === 'webgpu' && this.webGpuSupported ? 'webgpu' : 'webgl';
        if (storedBackend === 'webgpu' && !this.webGpuSupported) {
            localStorage.setItem(this.rendererBackendStorageKey, 'webgl');
            this.rendererStatus = 'WebGPU 미지원 환경이어서 WebGL2로 자동 복귀했습니다.';
        }
    }

    private resolveEffectiveGraphicsQuality(host: HTMLElement | null = this.gameHost?.nativeElement || null) {
        const coarsePointer = Boolean(window.matchMedia?.('(pointer: coarse)').matches);
        const narrowViewport = (host?.clientWidth || window.innerWidth) < 768;
        const coreCount = navigator.hardwareConcurrency || 4;
        const deviceMemory = Number((navigator as any).deviceMemory || 4);
        this.lowPowerRendering = coarsePointer || narrowViewport || coreCount <= 4 || deviceMemory <= 4;
        this.effectiveGraphicsQuality = this.graphicsQuality === 'auto'
            ? (this.lowPowerRendering ? 'low' : 'balanced')
            : this.graphicsQuality;
    }

    private applyRendererQuality() {
        if (!this.renderer) return;
        const devicePixelRatio = Math.max(0.75, window.devicePixelRatio || 1);
        const ratioCap = this.effectiveGraphicsQuality === 'low'
            ? 0.75
            : (this.effectiveGraphicsQuality === 'high' ? 1.5 : 1);
        this.maxRendererPixelRatio = Math.min(devicePixelRatio, ratioCap);
        this.currentRendererPixelRatio = this.maxRendererPixelRatio;
        this.renderer.setPixelRatio(this.currentRendererPixelRatio);
        this.renderer.shadowMap.enabled = this.effectiveGraphicsQuality !== 'low';
        this.renderer.shadowMap.type = this.activeRendererBackend === 'webgpu'
            ? THREE.VSMShadowMap
            : (this.effectiveGraphicsQuality === 'high' ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap);
        this.renderer.domElement.dataset.renderPixelRatio = this.currentRendererPixelRatio.toFixed(2);
        this.renderer.domElement.dataset.graphicsQuality = this.effectiveGraphicsQuality;
        this.renderer.domElement.dataset.rendererBackend = this.activeRendererBackend;
        this.handleResize();
    }

    private async initializeRenderer() {
        const host = this.gameHost?.nativeElement;
        if (!host) throw new Error('게임 표시 영역을 찾을 수 없습니다.');

        this.camera = new THREE.PerspectiveCamera(58, 1, 0.1, 2600);
        this.resolveEffectiveGraphicsQuality(host);
        const antialias = this.effectiveGraphicsQuality !== 'low';
        if (this.rendererBackendPreference === 'webgpu' && this.webGpuSupported) {
            try {
                const webGpuModule = await import('three/webgpu');
                const webGpuRenderer = new webGpuModule.WebGPURenderer({ antialias, alpha: false, powerPreference: 'high-performance' });
                await webGpuRenderer.init();
                this.renderer = webGpuRenderer as unknown as THREE.WebGLRenderer;
                this.activeRendererBackend = 'webgpu';
                this.rendererStatus = 'WebGPU 실험 렌더러가 활성화됐습니다. WebGL2와 FPS를 비교해보세요.';
            } catch {
                this.rendererBackendPreference = 'webgl';
                localStorage.setItem(this.rendererBackendStorageKey, 'webgl');
                this.rendererStatus = 'WebGPU 초기화에 실패해 WebGL2로 자동 복귀했습니다.';
            }
        }
        if (!this.renderer) {
            this.renderer = new THREE.WebGLRenderer({ antialias, alpha: false, powerPreference: 'high-performance' });
            this.activeRendererBackend = 'webgl';
            if (this.rendererBackendPreference === 'webgl') this.rendererStatus = 'WebGL2 안정 렌더러가 활성화됐습니다.';
        }
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.12;
        Object.assign(this.renderer.domElement.style, {
            width: '100%',
            height: '100%',
            display: 'block',
            touchAction: 'none'
        });
        host.appendChild(this.renderer.domElement);
        this.applyRendererQuality();
        this.lastFrameTime = performance.now();
        this.animationFrameId = requestAnimationFrame(this.animate);
    }

    private scheduleDeferredStartupWork() {
        const schedule = (callback: () => void, timeout: number) => {
            const requestIdleCallback = (window as any).requestIdleCallback as ((handler: () => void, options?: { timeout: number }) => number) | undefined;
            if (requestIdleCallback) requestIdleCallback(callback, { timeout });
            else window.setTimeout(callback, Math.min(timeout, 80));
        };
        schedule(() => {
            if (this.destroyed || this.screen !== 'menu' || this.menuHeritageShowcase) return;
            this.buildMenuHeritageShowcase();
            this.setMenuHeritageShowcaseVisible(true);
        }, 350);
        schedule(() => void this.preloadDeferredStartupAssets(), 700);
    }

    private async preloadDeferredStartupAssets() {
        if (this.destroyed) return;
        const heritageLoad = this.preloadHeritageTextures();
        await this.clearLegacyCharactersOnce();
        if (this.destroyed) return;
        await this.restoreLocalSkins();
        if (this.destroyed) return;
        await this.restoreStoredSkin();
        if (this.destroyed) return;
        await Promise.allSettled([heritageLoad, this.warmupWorldRendering()]);
        this.requestUiRender();
    }

    private async warmupWorldRendering() {
        if (!this.renderer || !this.scene || !this.camera) return;
        const renderer = this.renderer as any;
        if (typeof renderer.compileAsync === 'function') {
            await renderer.compileAsync(this.scene, this.camera);
        } else if (typeof renderer.compile === 'function') {
            renderer.compile(this.scene, this.camera);
        }
        renderer.render(this.scene, this.camera);
        this.lastFrameTime = performance.now();
    }

    private rebuildWorld() {
        this.editor3dGhost = undefined;
        this.editor3dGhostKey = '';
        this.editor3dGhostLabel = '';
        this.editor3dGhostValid = false;
        this.disposeScene();
        this.menuHeritageShowcase = undefined;
        this.scene = new THREE.Scene();
        const palette = this.themePalette();
        this.scene.background = new THREE.Color(palette.sky);
        this.scene.fog = new THREE.FogExp2(palette.fog, palette.fogDensity);
        this.coins = [];
        this.magnets = [];
        this.supplements = [];
        this.mounts = [];
        this.obstacles = [];
        this.hiddenNpcs = [];
        this.haechiGuides = [];
        this.sceneryColliders = [];
        this.autoSceneryGates.clear();
        this.activeHiddenNpc = undefined;
        this.activeHaechiGuide = undefined;
        this.performanceDecorations = [];
        this.decorationCullElapsed = 0;
        this.harborEnvironmentShipCount = 0;
        this.harborEnvironmentPierCount = 0;
        this.gates = cloneMap(this.activeMap).gates.map((gate) => ({ ...gate }));
        this.jumpPlatformSurfaces = this.createJumpPlatformSurfaces(this.activeMap);
        const exploreSampleCount = Math.min(2400, Math.max(240, Math.ceil(this.activeMap.trackLength * 2)));
        let worldDistance = 0;
        let previousPathPoint: THREE.Vector3 | null = null;
        this.explorePathSamples = Array.from({ length: exploreSampleCount + 1 }, (_, index) => {
            const distance = (index / exploreSampleCount) * this.activeMap.trackLength;
            const point = this.sampleMapPath(this.activeMap, distance);
            if (previousPathPoint) worldDistance += point.distanceTo(previousPathPoint);
            previousPathPoint = point;
            return { distance, worldDistance, point };
        });
        this.runtimePathWorldLength = worldDistance;
        this.runtimeRibbonFrames = this.createRuntimeRibbonFrames();
        this.rebuildMinimapData();
        this.exploreBounds = this.getRuntimeMapBounds(this.activeMap);

        this.addLighting();
        this.buildTrack();
        this.buildCustomGround();
        this.buildScenery();
        this.buildThemeEnvironment();
        this.createPlayer();
        this.createCoins();
        this.createMagnets();
        this.createSupplements();
        this.createMounts();
        this.createObstacles();
        this.createGates();
        this.createHiddenNpcs();
        this.createFinishLine();
        this.resetRun();
        this.setMenuHeritageShowcaseVisible(this.screen === 'menu');

        if (this.customSkinData) {
            void this.applyCustomSkin(this.customSkinData, this.customSkinFormat, this.customSkinRotationY).catch(() => undefined);
        }
    }

    private disposeScene() {
        if (this.scene) {
            this.scene.traverse((object: THREE.Object3D) => {
                if (this.usesSharedBuiltInCharacterResources(object)) return;
                const mesh = object as THREE.Mesh;
                if (mesh.geometry) mesh.geometry.dispose();
                const material: any = mesh.material;
                if (mesh.userData['uploadedGroundImage']) {
                    const materials = Array.isArray(material) ? material : [material];
                    materials.forEach((item: any) => item?.map?.dispose?.());
                }
                if (Array.isArray(material)) material.forEach((item: THREE.Material) => item.dispose());
                else if (material?.dispose) material.dispose();
            });
            this.scene.clear();
        }
        this.menuHeritageShowcase = undefined;
    }

    private addLighting() {
        if (!this.scene) return;
        const palette = this.themePalette();
        this.scene.add(new THREE.HemisphereLight(palette.hemiSky, palette.hemiGround, this.activeMap.theme === 'neon' ? 2.2 : 2.65));
        const sun = new THREE.DirectionalLight(palette.sun, this.activeMap.theme === 'neon' ? 3.5 : 3.15);
        sun.position.set(28, 42, 22);
        sun.castShadow = this.effectiveGraphicsQuality !== 'low';
        const shadowMapSize = this.effectiveGraphicsQuality === 'high' ? 2048 : 1024;
        sun.shadow.mapSize.set(shadowMapSize, shadowMapSize);
        sun.shadow.camera.left = -55;
        sun.shadow.camera.right = 55;
        sun.shadow.camera.top = 55;
        sun.shadow.camera.bottom = -55;
        this.scene.add(sun);
        if (this.activeMap.theme === 'neon' && this.effectiveGraphicsQuality !== 'low') {
            const cyan = new THREE.PointLight(0x22d3ee, 34, 90, 2);
            cyan.position.set(-24, 12, -120);
            this.scene.add(cyan);
            const violet = new THREE.PointLight(0xa78bfa, 32, 90, 2);
            violet.position.set(24, 10, -Math.min(620, this.activeMap.trackLength * 0.55));
            this.scene.add(violet);
        }
    }

    private hasPaintedTerrain(map: GameMap): boolean {
        return !!map.terrain
            && map.terrain.columns > 0
            && map.terrain.rows > 0
            && map.terrain.cells.length === map.terrain.columns * map.terrain.rows;
    }

    private hasCustomGround(map: GameMap): boolean {
        return !!map.groundImage || this.hasPaintedTerrain(map);
    }

    private buildCustomGround() {
        if (this.activeMap.groundImage) {
            this.buildUploadedGroundImage(this.activeMap.groundImage);
            return;
        }
        this.buildPaintedTerrain();
    }

    private buildUploadedGroundImage(groundImage: MapGroundImage) {
        if (!this.scene) return;
        const bounds = this.getTerrainMapBounds(this.activeMap);
        const texture = new THREE.TextureLoader().load(groundImage.dataUrl);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.center.set(0.5, 0.5);
        texture.rotation = Math.PI;
        texture.anisotropy = Math.min(this.renderer?.capabilities.getMaxAnisotropy?.() || 1, this.effectiveGraphicsQuality === 'high' ? 8 : 4);
        const ground = new THREE.Mesh(
            new THREE.PlaneGeometry(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ),
            new THREE.MeshStandardMaterial({ map: texture, color: 0xffffff, roughness: 0.96, metalness: 0, side: THREE.DoubleSide })
        );
        ground.rotation.x = -Math.PI / 2;
        ground.position.set((bounds.minX + bounds.maxX) / 2, -0.18, (bounds.minZ + bounds.maxZ) / 2);
        ground.receiveShadow = true;
        ground.name = 'uploaded-ground-image';
        ground.userData['uploadedGroundImage'] = true;
        this.scene.add(ground);
    }

    private buildPaintedTerrain() {
        if (!this.scene || !this.hasPaintedTerrain(this.activeMap) || !this.activeMap.terrain) return;
        const terrain = this.activeMap.terrain;
        const bounds = this.getTerrainMapBounds(this.activeMap);
        const width = bounds.maxX - bounds.minX;
        const depth = bounds.maxZ - bounds.minZ;
        const cellWidth = width / terrain.columns;
        const cellDepth = depth / terrain.rows;
        const kinds: TerrainKind[] = ['grass', 'earth', 'stone', 'sand', 'water', 'mountain'];
        const palette: Record<TerrainKind, { color: number; roughness: number; metalness: number; emissive?: number; opacity?: number }> = {
            grass: { color: 0x3f6b45, roughness: 1, metalness: 0 },
            earth: { color: 0x75563c, roughness: 1, metalness: 0 },
            stone: { color: 0x77756f, roughness: 0.94, metalness: 0.02 },
            sand: { color: 0xd5b779, roughness: 1, metalness: 0 },
            water: { color: 0x1887ae, roughness: 0.24, metalness: 0.16, emissive: 0x075985, opacity: 0.9 },
            mountain: { color: 0x344b36, roughness: 1, metalness: 0 }
        };
        const grouped = new Map<TerrainKind, number[]>();
        kinds.forEach((kind) => grouped.set(kind, []));
        terrain.cells.forEach((kind, index) => grouped.get(kind)?.push(index));

        kinds.forEach((kind) => {
            const indices = grouped.get(kind) || [];
            if (indices.length === 0) return;
            const materialConfig = palette[kind];
            const material = new THREE.MeshStandardMaterial({
                color: materialConfig.color,
                roughness: materialConfig.roughness,
                metalness: materialConfig.metalness,
                emissive: materialConfig.emissive || 0x000000,
                emissiveIntensity: kind === 'water' ? 0.2 : 0,
                transparent: materialConfig.opacity !== undefined,
                opacity: materialConfig.opacity ?? 1,
                side: THREE.DoubleSide
            });
            const geometry = new THREE.PlaneGeometry(cellWidth * 1.025, cellDepth * 1.025);
            geometry.rotateX(-Math.PI / 2);
            const mesh = new THREE.InstancedMesh(geometry, material, indices.length);
            const dummy = new THREE.Object3D();
            indices.forEach((cellIndex, instanceIndex) => {
                const column = cellIndex % terrain.columns;
                const row = Math.floor(cellIndex / terrain.columns);
                dummy.position.set(
                    bounds.maxX - (column + 0.5) * cellWidth,
                    kind === 'water' ? -0.05 : -0.16,
                    bounds.maxZ - (row + 0.5) * cellDepth
                );
                dummy.rotation.set(0, 0, 0);
                dummy.scale.set(1, 1, 1);
                dummy.updateMatrix();
                mesh.setMatrixAt(instanceIndex, dummy.matrix);
            });
            mesh.instanceMatrix.needsUpdate = true;
            mesh.receiveShadow = kind !== 'water';
            mesh.name = `painted-terrain-${kind}`;
            mesh.userData['paintedTerrain'] = kind;
            this.scene!.add(mesh);
        });

        const mountainCells = grouped.get('mountain') || [];
        if (mountainCells.length === 0) return;
        const maxHills = this.effectiveGraphicsQuality === 'low' ? 48 : (this.effectiveGraphicsQuality === 'high' ? 180 : 110);
        const stride = Math.max(1, Math.ceil(mountainCells.length / maxHills));
        const hillCells = mountainCells.filter((_, index) => index % stride === 0);
        const hills = new THREE.InstancedMesh(
            new THREE.ConeGeometry(1, 1, 7),
            new THREE.MeshStandardMaterial({ color: 0x405741, roughness: 0.98, flatShading: true }),
            hillCells.length
        );
        const hillDummy = new THREE.Object3D();
        hillCells.forEach((cellIndex, instanceIndex) => {
            const column = cellIndex % terrain.columns;
            const row = Math.floor(cellIndex / terrain.columns);
            const radius = THREE.MathUtils.clamp(Math.min(cellWidth, cellDepth) * 0.4, 3.5, 24);
            const height = THREE.MathUtils.clamp(radius * 1.7, 7, 42);
            hillDummy.position.set(
                bounds.maxX - (column + 0.5) * cellWidth,
                height / 2 - 0.14,
                bounds.maxZ - (row + 0.5) * cellDepth
            );
            hillDummy.rotation.set(0, this.seeded(cellIndex * 3.7) * Math.PI, 0);
            hillDummy.scale.set(radius, height, radius);
            hillDummy.updateMatrix();
            hills.setMatrixAt(instanceIndex, hillDummy.matrix);
        });
        hills.instanceMatrix.needsUpdate = true;
        hills.castShadow = this.effectiveGraphicsQuality !== 'low';
        hills.receiveShadow = true;
        hills.name = 'painted-terrain-mountains';
        hills.userData['paintedTerrain'] = 'mountain-volume';
        this.scene.add(hills);
    }

    private createJumpPlatformSurfaces(map: GameMap): JumpPlatformSurface[] {
        const surfaces: JumpPlatformSurface[] = [];
        const width = Math.max(14, map.trackWidth);
        for (let index = 0; index < map.waypoints.length - 1; index++) {
            const start = map.waypoints[index];
            const end = map.waypoints[index + 1];
            if (!start.roadPiece || start.roadPiece !== end.roadPiece || !this.isJumpRoadPiece(start.roadPiece)) continue;
            if (start.jumpGapAfter) continue;
            const deltaX = end.x - start.x;
            const deltaZ = end.z - start.z;
            const length = Math.hypot(deltaX, deltaZ);
            if (length <= 0.25) continue;
            const forwardX = deltaX / length;
            const forwardZ = deltaZ / length;
            surfaces.push({
                kind: start.roadPiece,
                startDistance: start.distance,
                endDistance: end.distance,
                centerX: (start.x + end.x) / 2,
                centerZ: (start.z + end.z) / 2,
                forwardX,
                forwardZ,
                rightX: -forwardZ,
                rightZ: forwardX,
                width,
                length,
                topY: (start.y + end.y) / 2,
                rotationY: Math.atan2(forwardX, forwardZ)
            });
        }
        return surfaces;
    }

    private buildJumpPlatforms(roadMaterial: THREE.MeshStandardMaterial, edgeMaterial: THREE.MeshStandardMaterial) {
        if (!this.scene || this.jumpPlatformSurfaces.length === 0) return;
        const pitMaterial = new THREE.MeshBasicMaterial({ color: 0x020617, side: THREE.DoubleSide });
        const jumpWaypoints = this.activeMap.waypoints;
        for (let index = 0; index < jumpWaypoints.length - 1; index++) {
            const start = jumpWaypoints[index];
            const end = jumpWaypoints[index + 1];
            if (!start.roadPiece || start.roadPiece !== end.roadPiece || !this.isJumpRoadPiece(start.roadPiece)) continue;
            const deltaX = end.x - start.x;
            const deltaZ = end.z - start.z;
            const length = Math.hypot(deltaX, deltaZ);
            if (length <= 0.25) continue;
            const pit = new THREE.Mesh(
                new THREE.BoxGeometry(Math.max(18, this.trackWidth + 4), 0.08, length + 0.3),
                pitMaterial
            );
            pit.position.set((start.x + end.x) / 2, -0.1, (start.z + end.z) / 2);
            pit.rotation.y = Math.atan2(deltaX / length, deltaZ / length);
            pit.name = 'jump-road-pit-mask';
            this.scene.add(pit);
        }

        this.jumpPlatformSurfaces.forEach((surface, index) => {
            const group = new THREE.Group();
            group.name = `jump-platform-${surface.kind}-${index + 1}`;
            group.position.set(surface.centerX, 0, surface.centerZ);
            group.rotation.y = surface.rotationY;

            const thickness = 0.68;
            const deck = new THREE.Mesh(
                new THREE.BoxGeometry(surface.width, thickness, surface.length),
                roadMaterial
            );
            deck.position.y = surface.topY - thickness / 2;
            deck.castShadow = this.effectiveGraphicsQuality !== 'low';
            deck.receiveShadow = true;
            group.add(deck);

            for (const side of [-1, 1]) {
                const trim = new THREE.Mesh(
                    new THREE.BoxGeometry(0.24, 0.16, Math.max(0.5, surface.length - 0.16)),
                    edgeMaterial
                );
                trim.position.set(side * (surface.width / 2 - 0.18), surface.topY + 0.08, 0);
                trim.castShadow = this.effectiveGraphicsQuality === 'high';
                group.add(trim);
            }

            this.scene!.add(group);
        });
    }

    private buildTrack() {
        if (!this.scene) return;
        const palette = this.themePalette();
        const stoneRoad = this.activeMap.theme === 'mountain'
            || this.activeMap.theme === 'joseon-harbor'
            || this.activeMap.theme === 'ys-palace';
        if (this.gameMode === 'explore' && !this.hasCustomGround(this.activeMap)) {
            const terrain = new THREE.Mesh(
                this.createPathRibbonGeometry(180, 0, -0.18, true),
                new THREE.MeshStandardMaterial({ color: palette.ground, roughness: 1, side: THREE.DoubleSide })
            );
            terrain.receiveShadow = true;
            this.scene.add(terrain);
        }
        const roadMaterial = new THREE.MeshStandardMaterial({ color: palette.road, roughness: 0.78, metalness: this.activeMap.theme === 'neon' ? 0.28 : 0.06, side: THREE.DoubleSide });
        const edgeMaterial = new THREE.MeshStandardMaterial({ color: palette.edge, emissive: palette.edgeEmissive, emissiveIntensity: this.activeMap.theme === 'neon' ? 2.1 : 0.16, roughness: 0.48 });
        const stripeMaterial = new THREE.MeshStandardMaterial({ color: palette.stripe, emissive: palette.stripeEmissive, emissiveIntensity: this.activeMap.theme === 'neon' ? 0.45 : 0.04, roughness: 0.55 });
        const road = new THREE.Mesh(this.createPathRibbonGeometry(this.trackWidth, 0, 0, true), roadMaterial);
        road.receiveShadow = true;
        this.scene.add(road);
        for (const side of [-1, 1]) {
            const edge = new THREE.Mesh(
                this.createPathRibbonGeometry(0.2, side * (this.trackWidth / 2 - 0.2), 0.09, true),
                edgeMaterial
            );
            this.scene.add(edge);
        }
        this.buildJumpPlatforms(roadMaterial, edgeMaterial);

        const step = this.effectiveGraphicsQuality === 'low' ? 11 : 7;
        const localForward = new THREE.Vector3();
        const rotationMatrix = new THREE.Matrix4();

        for (let distance = 0; distance < this.trackLength; distance += step) {
            const point = this.pathPoint(distance);
            const nextDistance = Math.min(this.trackLength, distance + step);
            const next = this.pathPoint(nextDistance);
            const forward = next.clone().sub(point);
            const segmentLength = forward.length();
            if (segmentLength <= 0.001) continue;
            const frame = this.getRuntimePathFrame((distance + nextDistance) / 2);
            if (this.isJumpRoadDistance((distance + nextDistance) / 2)) continue;
            localForward.copy(frame.forward).multiplyScalar(-1);
            rotationMatrix.makeBasis(frame.right, frame.surfaceUp, localForward);
            const rotation = new THREE.Quaternion().setFromRotationMatrix(rotationMatrix);
            const midpoint = point.clone().add(next).multiplyScalar(0.5);

            if (!stoneRoad && Math.round(distance / step) % 3 === 0) {
                const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.045, Math.min(4.4, segmentLength * 0.72)), stripeMaterial);
                stripe.position.copy(midpoint).addScaledVector(frame.surfaceUp, 0.025);
                stripe.quaternion.copy(rotation);
                this.scene.add(stripe);
            }

        }
        if (stoneRoad) this.buildStonePavers();

        const mapBounds = this.getRuntimeMapBounds(this.activeMap);
        const ground = new THREE.Mesh(
            new THREE.PlaneGeometry(
                mapBounds.maxX - mapBounds.minX,
                mapBounds.maxZ - mapBounds.minZ
            ),
            new THREE.MeshStandardMaterial({ color: palette.ground, roughness: 1 })
        );
        ground.rotation.x = -Math.PI / 2;
        ground.position.set((mapBounds.minX + mapBounds.maxX) / 2, -8, (mapBounds.minZ + mapBounds.maxZ) / 2);
        ground.receiveShadow = true;
        this.scene.add(ground);
    }

    private buildStonePavers() {
        if (!this.scene) return;
        const laneStep = this.effectiveGraphicsQuality === 'low' ? 4.6 : (this.effectiveGraphicsQuality === 'high' ? 2.25 : 2.55);
        const lanes: number[] = [];
        for (let lane = -this.trackWidth / 2 + 1.2; lane <= this.trackWidth / 2 - 1; lane += laneStep) lanes.push(lane);
        const distanceStep = this.effectiveGraphicsQuality === 'low' ? 5.4 : (this.effectiveGraphicsQuality === 'high' ? 2.85 : 3.15);
        const rowCount = Math.ceil(this.trackLength / distanceStep);
        const geometry = new THREE.BoxGeometry(2.35, 0.1, 2.82);
        const material = new THREE.MeshStandardMaterial({ color: 0x8b867b, roughness: 0.98, metalness: 0.01 });
        const stones = new THREE.InstancedMesh(geometry, material, rowCount * lanes.length);
        const dummy = new THREE.Object3D();
        const localYaw = new THREE.Quaternion();
        const color = new THREE.Color();
        let instance = 0;
        for (let row = 0; row < rowCount; row++) {
            const distance = Math.min(this.trackLength, row * distanceStep + (row % 2) * 0.34);
            if (this.isJumpRoadDistance(distance)) continue;
            const frame = this.getRuntimePathFrame(distance);
            lanes.forEach((lane, laneIndex) => {
                const seed = row * 17.3 + laneIndex * 5.7;
                dummy.position.copy(frame.point).addScaledVector(frame.right, lane + (this.seeded(seed) - 0.5) * 0.42);
                dummy.position.addScaledVector(frame.surfaceUp, 0.07 + this.seeded(seed * 2.1) * 0.035);
                dummy.quaternion.copy(this.createPathOrientation(frame));
                localYaw.setFromAxisAngle(new THREE.Vector3(0, 1, 0), (this.seeded(seed * 3.7) - 0.5) * 0.12);
                dummy.quaternion.multiply(localYaw);
                dummy.scale.set(0.9 + this.seeded(seed * 4.9) * 0.22, 1, 0.88 + this.seeded(seed * 6.1) * 0.2);
                dummy.updateMatrix();
                stones.setMatrixAt(instance, dummy.matrix);
                color.setHSL(0.1, 0.04, 0.47 + this.seeded(seed * 8.3) * 0.16);
                stones.setColorAt(instance, color);
                instance += 1;
            });
        }
        stones.count = instance;
        stones.instanceMatrix.needsUpdate = true;
        if (stones.instanceColor) stones.instanceColor.needsUpdate = true;
        stones.receiveShadow = true;
        this.scene.add(stones);
    }

    private createPathRibbonGeometry(width: number, centerOffset: number, elevation: number, skipJumpRoad: boolean = false): THREE.BufferGeometry {
        const positions: number[] = [];
        const uvs: number[] = [];
        const indices: number[] = [];

        this.runtimeRibbonFrames.forEach((frame, index) => {
            const center = frame.point.clone()
                .addScaledVector(frame.right, centerOffset)
                .addScaledVector(frame.surfaceUp, elevation);
            const left = center.clone().addScaledVector(frame.right, -width / 2);
            const rightPoint = center.clone().addScaledVector(frame.right, width / 2);
            positions.push(left.x, left.y, left.z, rightPoint.x, rightPoint.y, rightPoint.z);
            uvs.push(0, frame.distance / 20, 1, frame.distance / 20);
            if (index === 0) return;
            const previousFrame = this.runtimeRibbonFrames[index - 1];
            if (skipJumpRoad && this.isJumpRoadDistance((previousFrame.distance + frame.distance) / 2)) return;
            const previousLeft = (index - 1) * 2;
            const previousRight = previousLeft + 1;
            const currentLeft = index * 2;
            const currentRight = currentLeft + 1;
            indices.push(previousLeft, previousRight, currentLeft, previousRight, currentRight, currentLeft);
        });

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();
        geometry.computeBoundingSphere();
        return geometry;
    }

    private createRuntimeRibbonFrames(): RuntimeRibbonFrame[] {
        const segmentLength = this.effectiveGraphicsQuality === 'low' ? 6 : (this.effectiveGraphicsQuality === 'high' ? 2.8 : 3.5);
        const segmentCount = Math.min(2800, Math.max(80, Math.ceil(this.trackLength / segmentLength)));
        const worldUp = new THREE.Vector3(0, 1, 0);
        const frames: RuntimeRibbonFrame[] = [];
        const sampleDistances = new Set<number>(
            Array.from({ length: segmentCount + 1 }, (_, index) => (index / segmentCount) * this.trackLength)
        );
        this.activeMap.waypoints.forEach((waypoint) => sampleDistances.add(
            THREE.MathUtils.clamp(waypoint.distance, 0, this.trackLength)
        ));
        const sortedDistances = Array.from(sampleDistances).sort((a, b) => a - b);
        let previousForward: THREE.Vector3 | null = null;
        let previousRight: THREE.Vector3 | null = null;
        for (const distance of sortedDistances) {
            const point = this.pathPoint(distance);
            const before = this.pathPoint(Math.max(0, distance - 1));
            const after = this.pathPoint(Math.min(this.trackLength, distance + 1));
            const forward = after.sub(before);
            if (forward.lengthSq() <= 0.0001) forward.set(0, 0, -1);
            else forward.normalize();

            let right: THREE.Vector3;
            if (previousForward && previousRight) {
                const transport = new THREE.Quaternion().setFromUnitVectors(previousForward, forward);
                right = previousRight.clone().applyQuaternion(transport);
                right.addScaledVector(forward, -right.dot(forward));
            } else {
                right = new THREE.Vector3().crossVectors(forward, worldUp);
            }
            if (right.lengthSq() <= 0.0001) right.set(1, 0, 0);
            else right.normalize();
            const surfaceUp = new THREE.Vector3().crossVectors(right, forward).normalize();
            frames.push({ distance, point, forward, right, surfaceUp });
            previousForward = forward;
            previousRight = right;
        }
        return frames;
    }

    private getRuntimePathFrame(distance: number): RuntimeRibbonFrame {
        const selectedDistance = THREE.MathUtils.clamp(distance, 0, this.trackLength);
        if (this.runtimeRibbonFrames.length === 0) {
            const point = this.pathPoint(selectedDistance);
            const before = this.pathPoint(Math.max(0, selectedDistance - 1));
            const after = this.pathPoint(Math.min(this.trackLength, selectedDistance + 1));
            const forward = after.sub(before).normalize();
            const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0));
            if (right.lengthSq() <= 0.0001) right.set(1, 0, 0);
            else right.normalize();
            return {
                distance: selectedDistance,
                point,
                forward,
                right,
                surfaceUp: new THREE.Vector3().crossVectors(right, forward).normalize()
            };
        }

        let low = 0;
        let high = this.runtimeRibbonFrames.length - 1;
        while (low < high) {
            const middle = Math.floor((low + high) / 2);
            if (this.runtimeRibbonFrames[middle].distance < selectedDistance) low = middle + 1;
            else high = middle;
        }
        const rightIndex = low;
        const leftIndex = Math.max(0, rightIndex - 1);
        const leftFrame = this.runtimeRibbonFrames[leftIndex];
        const rightFrame = this.runtimeRibbonFrames[rightIndex];
        const frameSpan = Math.max(0.001, rightFrame.distance - leftFrame.distance);
        const ratio = THREE.MathUtils.clamp((selectedDistance - leftFrame.distance) / frameSpan, 0, 1);
        const forward = leftFrame.forward.clone().lerp(rightFrame.forward, ratio).normalize();
        const right = leftFrame.right.clone().lerp(rightFrame.right, ratio);
        right.addScaledVector(forward, -right.dot(forward));
        if (right.lengthSq() <= 0.0001) right.copy(leftFrame.right);
        else right.normalize();
        return {
            distance: selectedDistance,
            point: this.pathPoint(selectedDistance),
            forward,
            right,
            surfaceUp: new THREE.Vector3().crossVectors(right, forward).normalize()
        };
    }

    private createPathOrientation(frame: RuntimeRibbonFrame, localYaw: number = 0): THREE.Quaternion {
        const matrix = new THREE.Matrix4().makeBasis(
            frame.right,
            frame.surfaceUp,
            frame.forward.clone().multiplyScalar(-1)
        );
        const orientation = new THREE.Quaternion().setFromRotationMatrix(matrix);
        if (localYaw !== 0) {
            orientation.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), localYaw));
        }
        return orientation;
    }

    private buildScenery() {
        if (!this.scene) return;
        const mapBounds = this.getRuntimeMapBounds(this.activeMap);
        const worldWidth = mapBounds.maxX - mapBounds.minX;
        const worldDepth = mapBounds.maxZ - mapBounds.minZ;
        if (this.activeMap.theme !== 'neon') {
            this.buildMapDecorations();
            return;
        }
        const starValueCount = this.effectiveGraphicsQuality === 'low' ? 450 : (this.effectiveGraphicsQuality === 'high' ? 1800 : 1200);
        const positions = new Float32Array(starValueCount);
        for (let index = 0; index < positions.length; index += 3) {
            const seed = index / 3 + 1;
            positions[index] = mapBounds.minX + this.seeded(seed * 3.1) * worldWidth;
            positions[index + 1] = this.seeded(seed * 7.7) * 95 + 8;
            positions[index + 2] = mapBounds.minZ + this.seeded(seed * 11.3) * worldDepth;
        }
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.scene.add(new THREE.Points(geometry, new THREE.PointsMaterial({
            color: 0x9bdcff,
            size: 0.52,
            transparent: true,
            opacity: 0.78,
            depthWrite: false
        })));

        this.buildMapDecorations();
    }

    private themePalette() {
        if (this.activeMap.theme === 'mountain' || this.activeMap.theme === 'ys-palace') {
            return { sky: 0xb7d4cb, fog: 0x9bb8ad, fogDensity: 0.0065, hemiSky: 0xe8faf3, hemiGround: 0x304535, sun: 0xfff3d2, road: 0x685b48, edge: 0xd2bc8a, edgeEmissive: 0x3f351f, stripe: 0xf1e4c7, stripeEmissive: 0x3d3323, ground: 0x3e6044 };
        }
        if (this.activeMap.theme === 'coast') {
            return { sky: 0xa7dce7, fog: 0x8fcbd8, fogDensity: 0.0055, hemiSky: 0xe3f8ff, hemiGround: 0x355a56, sun: 0xfff2ce, road: 0x58636a, edge: 0xd9eef0, edgeEmissive: 0x24434a, stripe: 0xffffff, stripeEmissive: 0x38545a, ground: 0x607d63 };
        }
        if (this.activeMap.theme === 'beach') {
            return { sky: 0x93d8ed, fog: 0xaddfea, fogDensity: 0.0045, hemiSky: 0xf0fbff, hemiGround: 0x836b44, sun: 0xfff0bd, road: 0xc79f68, edge: 0xffedbf, edgeEmissive: 0x634620, stripe: 0xfff8e8, stripeEmissive: 0x604a2d, ground: 0xe4c587 };
        }
        if (this.activeMap.theme === 'joseon-harbor') {
            return { sky: 0xb9ced0, fog: 0x92a8a8, fogDensity: 0.0058, hemiSky: 0xe9f2ef, hemiGround: 0x4a4437, sun: 0xffe8ba, road: 0x625d52, edge: 0x938875, edgeEmissive: 0x2f2a22, stripe: 0xcfc7b6, stripeEmissive: 0x312c25, ground: 0x6f715b };
        }
        return { sky: 0x050816, fog: 0x071229, fogDensity: 0.0125, hemiSky: 0x8be9ff, hemiGround: 0x171027, sun: 0xffffff, road: 0x17243a, edge: 0x22d3ee, edgeEmissive: 0x0891b2, stripe: 0xdbeafe, stripeEmissive: 0x60a5fa, ground: 0x081323 };
    }

    private buildThemeEnvironment() {
        if (!this.scene || this.activeMap.theme === 'neon') return;
        const bounds = this.getRuntimeMapBounds(this.activeMap);
        const pathBounds = this.getRuntimePathBounds(this.activeMap);
        const centerX = (bounds.minX + bounds.maxX) / 2;
        const centerZ = (bounds.minZ + bounds.maxZ) / 2;
        const depth = Math.max(500, bounds.maxZ - bounds.minZ + 280);

        if (this.activeMap.theme === 'mountain' || this.activeMap.theme === 'ys-palace') {
            const mountainMaterial = new THREE.MeshStandardMaterial({ color: 0x48604a, roughness: 0.98, flatShading: true });
            const mountainCount = this.effectiveGraphicsQuality === 'low' ? 8 : (this.effectiveGraphicsQuality === 'high' ? 20 : 16);
            const mountains = new THREE.InstancedMesh(new THREE.ConeGeometry(1, 1, 7), mountainMaterial, mountainCount);
            const dummy = new THREE.Object3D();
            const distantPalaceBackdrop = this.activeMap.theme === 'ys-palace'
                || this.activeMap.id === 'builtin-ys-palace-course';
            const backdropRadius = Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ) / 2 + 170;
            for (let index = 0; index < mountainCount; index++) {
                const height = 24 + this.seeded(index * 4.7) * 35;
                const radius = height * (0.55 + this.seeded(index * 8.1) * 0.18);
                const side = index % 2 === 0 ? -1 : 1;
                if (distantPalaceBackdrop) {
                    const angle = (index / mountainCount) * Math.PI * 2 + this.seeded(index * 5.9) * 0.18;
                    const distance = backdropRadius + this.seeded(index * 2.3) * 55;
                    dummy.position.set(
                        centerX + Math.cos(angle) * distance,
                        -5 + height / 2,
                        centerZ + Math.sin(angle) * distance
                    );
                } else {
                    dummy.position.set(centerX + side * (82 + this.seeded(index * 2.3) * 45), -5 + height / 2, bounds.minZ - 80 + (index / Math.max(1, mountainCount - 1)) * depth);
                }
                dummy.rotation.set(0, this.seeded(index * 6.2) * Math.PI, 0);
                dummy.scale.set(radius, height, radius);
                dummy.updateMatrix();
                mountains.setMatrixAt(index, dummy.matrix);
            }
            mountains.instanceMatrix.needsUpdate = true;
            mountains.receiveShadow = this.effectiveGraphicsQuality !== 'low';
            mountains.name = distantPalaceBackdrop ? 'distant-palace-mountain-backdrop' : 'mountain-backdrop';
            this.scene.add(mountains);
            return;
        }

        const shorelineGap = Math.max(3.5, this.trackWidth * 0.22);
        const flatShorelineX = pathBounds.maxX + Math.max(10, this.trackWidth * 0.75);
        const paintedTerrain = this.hasPaintedTerrain(this.activeMap);
        if (!paintedTerrain) {
            const waterWidth = this.activeMap.theme === 'joseon-harbor'
                ? Math.max(180, (bounds.maxX - bounds.minX) * 0.55 + 100)
                : Math.max(180, bounds.maxX - flatShorelineX + 120);
            const waterMaterial = new THREE.MeshStandardMaterial({ color: 0x1b8fb7, emissive: 0x075985, emissiveIntensity: 0.22, roughness: 0.24, metalness: 0.18, transparent: true, opacity: 0.9, side: THREE.DoubleSide });
            let water: THREE.Mesh;
            if (this.activeMap.theme === 'joseon-harbor') {
                const waterGeometry = this.createPathRibbonGeometry(
                    waterWidth,
                    this.trackWidth / 2 + shorelineGap + waterWidth / 2,
                    0.02
                );
                const waterPositions = waterGeometry.getAttribute('position') as THREE.BufferAttribute;
                for (let index = 0; index < waterPositions.count; index++) waterPositions.setY(index, 0.02);
                waterPositions.needsUpdate = true;
                waterGeometry.computeVertexNormals();
                water = new THREE.Mesh(waterGeometry, waterMaterial);
                water.userData['themeEnvironment'] = 'joseon-harbor-path-water';
            } else {
                water = new THREE.Mesh(new THREE.PlaneGeometry(waterWidth, depth), waterMaterial);
                water.rotation.x = -Math.PI / 2;
                water.position.set(flatShorelineX + waterWidth / 2, 0.02, centerZ);
            }
            this.scene.add(water);
        }

        if (this.activeMap.theme === 'coast') {
            if (paintedTerrain) return;
            const rockMaterial = new THREE.MeshStandardMaterial({ color: 0x64736e, roughness: 0.95, flatShading: true });
            const rockCount = this.effectiveGraphicsQuality === 'low' ? 10 : (this.effectiveGraphicsQuality === 'high' ? 26 : 20);
            const rocks = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(1, 0), rockMaterial, rockCount);
            const dummy = new THREE.Object3D();
            for (let index = 0; index < rockCount; index++) {
                const radius = 2.5 + this.seeded(index * 7.2) * 4;
                dummy.position.set(flatShorelineX + 18 + this.seeded(index * 3.9) * 35, -0.5 + radius * 0.45, bounds.minZ + this.seeded(index * 9.1) * (bounds.maxZ - bounds.minZ));
                dummy.rotation.set(0, this.seeded(index * 4.2) * Math.PI, 0);
                dummy.scale.set(radius, radius * (0.6 + this.seeded(index * 5.6) * 0.7), radius);
                dummy.updateMatrix();
                rocks.setMatrixAt(index, dummy.matrix);
            }
            rocks.instanceMatrix.needsUpdate = true;
            rocks.castShadow = this.effectiveGraphicsQuality !== 'low';
            this.scene.add(rocks);
            return;
        }

        if (this.activeMap.theme === 'joseon-harbor') {
            const timber = new THREE.MeshStandardMaterial({ color: 0x654029, roughness: 0.92 });
            const lightTimber = new THREE.MeshStandardMaterial({ color: 0x8a5c36, roughness: 0.9 });
            const darkTimber = new THREE.MeshStandardMaterial({ color: 0x3b281d, roughness: 0.94 });
            const rope = new THREE.MeshStandardMaterial({ color: 0xb79a68, roughness: 1 });
            const iron = new THREE.MeshStandardMaterial({ color: 0x30343a, roughness: 0.62, metalness: 0.48 });
            const lanternGlow = new THREE.MeshStandardMaterial({ color: 0xffd58a, emissive: 0xf59e0b, emissiveIntensity: 1.25, roughness: 0.42 });
            const dockCount = this.effectiveGraphicsQuality === 'low' ? 3 : (this.effectiveGraphicsQuality === 'high' ? 8 : 6);
            for (let index = 0; index < dockCount; index++) {
                const dockDistance = THREE.MathUtils.lerp(90, Math.max(90, this.trackLength - 80), index / Math.max(1, dockCount - 1));
                const frame = this.getPathFrame(this.activeMap, dockDistance);
                const dock = new THREE.Group();
                dock.name = `harbor-timber-pier-${index + 1}`;
                dock.userData['themeEnvironment'] = 'joseon-harbor-pier';
                const bridgeStart = frame.point.clone().addScaledVector(frame.right, this.trackWidth / 2 + 1.2);
                const bridgeLength = THREE.MathUtils.clamp(shorelineGap + 19, 22.5, 34);
                const bridgeWidth = 5.8;
                const plankCount = this.effectiveGraphicsQuality === 'low' ? 14 : 24;
                const plankGeometry = new THREE.BoxGeometry((bridgeLength / plankCount) * 0.9, 0.28, bridgeWidth);
                const lightPlanks = new THREE.InstancedMesh(plankGeometry, lightTimber, Math.ceil(plankCount / 2));
                const darkPlanks = new THREE.InstancedMesh(plankGeometry, timber, Math.floor(plankCount / 2));
                const plankDummy = new THREE.Object3D();
                let lightIndex = 0;
                let darkIndex = 0;
                for (let plankIndex = 0; plankIndex < plankCount; plankIndex++) {
                    plankDummy.position.set(-bridgeLength / 2 + (plankIndex + 0.5) * (bridgeLength / plankCount), 1.42 + (this.seeded(index * 31 + plankIndex) - 0.5) * 0.035, 0);
                    plankDummy.rotation.set(0, (this.seeded(index * 47 + plankIndex) - 0.5) * 0.018, 0);
                    plankDummy.updateMatrix();
                    if (plankIndex % 2 === 0) lightPlanks.setMatrixAt(lightIndex++, plankDummy.matrix);
                    else darkPlanks.setMatrixAt(darkIndex++, plankDummy.matrix);
                }
                lightPlanks.instanceMatrix.needsUpdate = true;
                darkPlanks.instanceMatrix.needsUpdate = true;
                dock.add(lightPlanks, darkPlanks);

                for (const z of [-2.15, 2.15]) {
                    const lengthBeam = new THREE.Mesh(new THREE.BoxGeometry(bridgeLength + 0.4, 0.34, 0.34), darkTimber);
                    lengthBeam.position.set(0, 1.05, z);
                    dock.add(lengthBeam);
                    const upperRail = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.075, bridgeLength - 1.4, 8), rope);
                    upperRail.rotation.z = Math.PI / 2;
                    upperRail.position.set(0.3, 2.82, z * 1.16);
                    dock.add(upperRail);
                    const lowerRail = upperRail.clone();
                    lowerRail.position.y = 2.28;
                    dock.add(lowerRail);
                }

                const supportCount = Math.max(5, Math.ceil(bridgeLength / 4.2));
                const supportXs = Array.from({ length: supportCount }, (_, supportIndex) =>
                    THREE.MathUtils.lerp(-bridgeLength / 2 + 1.6, bridgeLength / 2 - 1.6, supportIndex / Math.max(1, supportCount - 1))
                );
                for (const x of supportXs) {
                    const crossBeam = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.34, bridgeWidth + 0.55), darkTimber);
                    crossBeam.position.set(x, 1.05, 0);
                    dock.add(crossBeam);
                    for (const z of [-2.45, 2.45]) {
                        const pile = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.3, 4.6, 9), timber);
                        pile.position.set(x, -0.2, z);
                        const railPost = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.13, 1.75, 8), darkTimber);
                        railPost.position.set(x, 2.12, z);
                        dock.add(pile, railPost);
                    }
                }

                for (let step = 0; step < 3; step++) {
                    const ramp = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.18 + step * 0.08, bridgeWidth + 0.25), step % 2 === 0 ? lightTimber : timber);
                    ramp.position.set(-bridgeLength / 2 - 0.95 + step * 0.58, 0.72 + step * 0.18, 0);
                    dock.add(ramp);
                }

                for (const z of [-2.45, 2.45]) {
                    const entrancePost = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.21, 3.3, 10), darkTimber);
                    entrancePost.position.set(-bridgeLength / 2 + 0.55, 2.65, z);
                    const lantern = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.58, 0.48), lanternGlow);
                    lantern.position.set(-bridgeLength / 2 + 0.55, 4.18, z);
                    const lanternCap = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.38, 0.12, 4), iron);
                    lanternCap.rotation.y = Math.PI / 4;
                    lanternCap.position.set(-bridgeLength / 2 + 0.55, 4.52, z);
                    dock.add(entrancePost, lantern, lanternCap);
                }
                const entranceLintel = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.3, bridgeWidth + 0.75), darkTimber);
                entranceLintel.position.set(-bridgeLength / 2 + 0.55, 3.82, 0);
                dock.add(entranceLintel);

                for (const z of [-1.85, 1.85]) {
                    const mooring = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.1, 8, 20), iron);
                    mooring.position.set(bridgeLength / 2 - 0.7, 1.93, z);
                    mooring.rotation.y = Math.PI / 2;
                    dock.add(mooring);
                }

                dock.position.copy(bridgeStart).addScaledVector(frame.right, bridgeLength / 2);
                dock.position.y = frame.point.y - 1.05;
                dock.rotation.y = Math.atan2(-frame.right.z, frame.right.x);
                this.prepareDecoration(dock);
                this.scene.add(dock);
                this.registerPerformanceDecoration(dock);
                this.harborEnvironmentPierCount += 1;
            }
            const harborShips: Array<{ model: THREE.Group; distance: number; waterOffset: number; rotationOffset: number; scale: number; name: string }> = [
                { model: this.createFishingBoatDecoration(), distance: this.trackLength * 0.18, waterOffset: 34, rotationOffset: Math.PI * 0.08, scale: 1.15, name: 'harbor-fishing-boat' },
                { model: this.createJoseonShipMount(false), distance: this.trackLength * 0.5, waterOffset: 58, rotationOffset: -Math.PI * 0.12, scale: 1, name: 'harbor-panokseon' },
                { model: this.createJoseonShipMount(true), distance: this.trackLength * 0.78, waterOffset: 82, rotationOffset: Math.PI * 0.1, scale: 1, name: 'harbor-turtle-ship' }
            ];
            harborShips.forEach((entry) => {
                const shipFrame = this.getPathFrame(this.activeMap, entry.distance);
                entry.model.name = entry.name;
                entry.model.userData['themeEnvironment'] = 'joseon-harbor-ship';
                entry.model.position.copy(shipFrame.point).addScaledVector(
                    shipFrame.right,
                    this.trackWidth / 2 + shorelineGap + entry.waterOffset
                );
                entry.model.position.y = -0.12;
                entry.model.rotation.y = Math.atan2(shipFrame.forward.x, shipFrame.forward.z) + entry.rotationOffset;
                entry.model.scale.setScalar(entry.scale);
                this.prepareDecoration(entry.model);
                this.scene!.add(entry.model);
                this.registerPerformanceDecoration(entry.model);
                this.harborEnvironmentShipCount += 1;
            });
            return;
        }

        const palmCount = this.effectiveGraphicsQuality === 'low' ? 6 : (this.effectiveGraphicsQuality === 'high' ? 16 : 12);
        for (let index = 0; index < palmCount; index++) {
            const group = new THREE.Group();
            const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.48, 6.5, 8), new THREE.MeshStandardMaterial({ color: 0x9a6a3d, roughness: 0.9 }));
            trunk.position.y = 3.25;
            group.add(trunk);
            const leaves = new THREE.Mesh(new THREE.ConeGeometry(2.8, 0.8, 8), new THREE.MeshStandardMaterial({ color: 0x2f8f63, roughness: 0.78 }));
            leaves.position.y = 6.6;
            leaves.rotation.x = Math.PI;
            group.add(leaves);
            const side = index % 2 === 0 ? -1 : 1;
            group.position.set(centerX + side * (34 + this.seeded(index * 2.7) * 25), 0, bounds.minZ + this.seeded(index * 8.4) * (bounds.maxZ - bounds.minZ));
            this.prepareDecoration(group);
            this.scene.add(group);
            this.registerPerformanceDecoration(group);
        }
        const start = this.getPathFrame(this.activeMap, 35);
        for (const side of [-1, 1]) {
            const parasol = new THREE.Group();
            const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.12, 3.6, 8), new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.62 }));
            pole.position.y = 1.8;
            const canopy = new THREE.Mesh(new THREE.ConeGeometry(2.15, 0.72, 16), new THREE.MeshStandardMaterial({ color: side < 0 ? 0xfb7185 : 0x38bdf8, roughness: 0.5 }));
            canopy.position.y = 3.65;
            parasol.add(pole, canopy);
            parasol.position.copy(start.point).addScaledVector(start.right, side * 22).addScaledVector(start.forward, 12);
            parasol.position.y = start.point.y;
            this.prepareDecoration(parasol);
            this.scene.add(parasol);
            this.registerPerformanceDecoration(parasol);
        }
    }

    private buildMapDecorations() {
        if (!this.scene) return;

        for (const placement of this.activeMap.scenery) {
            if (placement.kind === 'ys') {
                this.buildYsDecoration(placement);
                continue;
            }
            if (placement.kind === 'haechi-guide') {
                this.buildHaechiGuideDecoration(placement);
                continue;
            }
            if (isBundledNatureDecorationKind(placement.kind)) {
                this.buildBundledNatureDecoration(placement);
                continue;
            }
            if (placement.kind !== 'custom') {
                const decoration = this.createBuiltInDecorationInstance(placement.kind);
                decoration.userData['sceneryId'] = placement.id;
                this.placeSceneryDecoration(decoration, placement);
                this.scene.add(decoration);
                this.registerPerformanceDecoration(decoration);
                continue;
            }

            const asset = this.decorationAssets.find((item) => item.id === placement.assetId);
            if (!asset) continue;
            const template = this.decorationTemplateCache.get(asset.id);
            if (!template) continue;
            const wrapper = template.clone(true);
            wrapper.userData['sharedDecorationResources'] = true;
            wrapper.userData['sceneryId'] = placement.id;
            this.prepareDecoration(wrapper);
            this.placeSceneryDecoration(wrapper, placement);
            this.scene.add(wrapper);
            this.registerPerformanceDecoration(wrapper);
        }
    }

    private buildHaechiGuideDecoration(placement: MapSceneryPlacement) {
        if (!this.scene) return;
        const loaded = !!this.haetaeAssetSource || this.builtInCharacterTemplateCache.has('haetae');
        const visual = loaded
            ? this.createBuiltInPlayerVisual('haetae')
            : this.createDeferredCharacterPlaceholder('haetae');
        const wrapper = this.createHaechiGuideWrapper(visual);
        wrapper.userData['sceneryId'] = placement.id;
        this.placeSceneryDecoration(wrapper, placement, false);
        this.scene.add(wrapper);
        this.registerPerformanceDecoration(wrapper);
        const state: HaechiGuideState = { placement, mesh: wrapper, shown: false };
        this.haechiGuides.push(state);
        if (loaded) return;

        void this.ensureHaetaeAssetLoaded()
            .then(() => {
                if (!this.scene || wrapper.parent !== this.scene || this.destroyed || visual.parent !== wrapper) return;
                wrapper.remove(visual);
                this.disposeObjectTree(visual);
                wrapper.add(this.createBuiltInPlayerVisual('haetae'));
                this.prepareDecoration(wrapper);
                this.decorationStatus = '서울 해치 안내자 GLB 모델을 적용했습니다.';
                this.requestUiRender();
            })
            .catch((error: any) => {
                if (wrapper.parent !== this.scene) return;
                this.decorationStatus = error?.message || '서울 해치 안내자 모델을 불러오지 못해 대체 형상을 표시합니다.';
                this.requestUiRender();
            });
    }

    private createHaechiGuideWrapper(visual: THREE.Group): THREE.Group {
        const wrapper = new THREE.Group();
        wrapper.name = 'seoul-haechi-building-guide';
        wrapper.add(visual);
        const markerMaterial = new THREE.MeshStandardMaterial({
            color: 0xf7c948,
            emissive: 0xc77900,
            emissiveIntensity: 1.25,
            roughness: 0.36,
            metalness: 0.22
        });
        const marker = new THREE.Mesh(new THREE.TorusGeometry(0.52, 0.09, 8, 28), markerMaterial);
        marker.name = 'haechi-guide-marker';
        marker.position.y = 3.18;
        marker.rotation.x = Math.PI / 2;
        wrapper.add(marker);
        return this.prepareDecoration(wrapper);
    }

    private buildBundledNatureDecoration(placement: MapSceneryPlacement) {
        if (!this.scene) return;
        const kind = placement.kind;
        if (!isBundledNatureDecorationKind(kind)) return;
        const spec = BUNDLED_NATURE_DECORATION_SPECS[kind];
        const formatLabel = bundledDecorationFormatLabel(spec);
        const template = this.bundledNatureTemplateCache.get(kind);
        if (template) {
            this.addBundledNatureInstance(kind, template, placement);
            return;
        }

        const placeholder = this.createBundledNaturePlaceholder(kind);
        placeholder.name = `${kind}-fbx-loading`;
        placeholder.userData['sceneryId'] = placement.id;
        this.placeSceneryDecoration(placeholder, placement, spec.collider === 'bounds');
        this.registerBundledNatureSpecialCollider(kind, placement);
        this.scene.add(placeholder);
        this.registerPerformanceDecoration(placeholder);

        void this.ensureBundledNatureTemplateLoaded(kind)
            .then((loadedTemplate) => {
                if (!this.scene || placeholder.parent !== this.scene || this.destroyed) return;
                this.sceneryColliders = this.sceneryColliders.filter((collider) => collider.id !== placement.id);
                this.performanceDecorations = this.performanceDecorations.filter((item) => item !== placeholder);
                this.scene.remove(placeholder);
                this.disposeObjectTree(placeholder);
                this.addBundledNatureInstance(kind, loadedTemplate, placement);
                this.decorationStatus = `${spec.label} ${formatLabel} 기본 장식을 적용했습니다.`;
                this.requestUiRender();
            })
            .catch((error: any) => {
                if (placeholder.parent !== this.scene) return;
                this.decorationStatus = error?.message || `${spec.label} ${formatLabel} 모델을 불러오지 못해 대체 형상을 표시합니다.`;
                this.requestUiRender();
            });
    }

    private addBundledNatureInstance(
        kind: BundledNatureDecorationKind,
        template: THREE.Group,
        placement: MapSceneryPlacement
    ) {
        if (!this.scene) return;
        const spec = BUNDLED_NATURE_DECORATION_SPECS[kind];
        const wrapper = template.clone(true);
        wrapper.userData['sharedDecorationResources'] = true;
        wrapper.userData['sceneryId'] = placement.id;
        this.prepareDecoration(wrapper);
        this.placeSceneryDecoration(wrapper, placement, spec.collider === 'bounds');
        this.registerBundledNatureSpecialCollider(kind, placement);
        this.scene.add(wrapper);
        this.registerPerformanceDecoration(wrapper);
    }

    private registerBundledNatureSpecialCollider(
        kind: BundledNatureDecorationKind,
        placement: MapSceneryPlacement
    ) {
        const spec = BUNDLED_NATURE_DECORATION_SPECS[kind];
        const scale = THREE.MathUtils.clamp(Number(placement.scale) || 1, 0.1, 10);
        const verticalScale = THREE.MathUtils.clamp(Number(placement.verticalScale) || scale, 0.1, 10);
        const groundY = this.sceneryGroundHeightAt(placement.x, placement.z) + (Number(placement.y) || 0);
        const rotation = THREE.MathUtils.degToRad(Number(placement.rotation) || 0);
        if (spec.collider === 'trunk') {
            const trunkRadius = Math.max(0.2, (spec.trunkRadius || 0.4) * scale);
            this.sceneryColliders.push({
                id: placement.id,
                centerX: placement.x,
                centerZ: placement.z,
                halfWidth: trunkRadius,
                halfDepth: trunkRadius,
                rotation,
                baseY: groundY,
                topY: groundY + spec.targetHeight * verticalScale,
                scale,
                verticalScale
            });
            return;
        }
        if (spec.collider !== 'steps' || !spec.stepProfile) return;
        const profile = spec.stepProfile;
        for (let step = 0; step < profile.count; step++) {
            const localZ = profile.count * profile.depth / 2 - (step + 0.5) * profile.depth;
            const offsetX = Math.sin(rotation) * localZ * scale;
            const offsetZ = Math.cos(rotation) * localZ * scale;
            this.sceneryColliders.push({
                id: placement.id,
                centerX: placement.x + offsetX,
                centerZ: placement.z + offsetZ,
                halfWidth: profile.width * scale / 2,
                halfDepth: (profile.depth / 2 + 0.025) * scale,
                rotation,
                baseY: groundY,
                topY: groundY + profile.rise * (step + 1) * verticalScale,
                scale,
                verticalScale
            });
        }
    }

    private ensureBundledNatureTemplateLoaded(kind: BundledNatureDecorationKind): Promise<THREE.Group> {
        const cached = this.bundledNatureTemplateCache.get(kind);
        if (cached) return Promise.resolve(cached);
        const pending = this.bundledNatureLoadPromises.get(kind);
        if (pending) return pending;
        const spec = BUNDLED_NATURE_DECORATION_SPECS[kind];
        const format = spec.format || 'fbx';

        const load = (async () => {
            const response = await fetch(spec.url, { cache: 'force-cache' });
            if (!response.ok) throw new Error(`${spec.label} ${format.toUpperCase()} 모델을 불러오지 못했습니다. (${response.status})`);
            const data = await response.arrayBuffer();
            this.validateSkinData(data, format);
            const model = await this.parseSkinModel(data, format) as THREE.Group;
            if (this.destroyed) {
                this.disposeObjectTree(model, true);
                throw new Error(`화면이 종료되어 ${spec.label} ${format.toUpperCase()} 로딩을 중단했습니다.`);
            }
            return this.storeBundledNatureTemplate(kind, model);
        })();
        this.bundledNatureLoadPromises.set(kind, load);
        void load.finally(() => {
            if (this.bundledNatureLoadPromises.get(kind) === load) {
                this.bundledNatureLoadPromises.delete(kind);
            }
        }).catch(() => undefined);
        return load;
    }

    private storeBundledNatureTemplate(
        kind: BundledNatureDecorationKind,
        model: THREE.Group
    ): THREE.Group {
        const spec = BUNDLED_NATURE_DECORATION_SPECS[kind];
        const format = spec.format || 'fbx';
        model.updateMatrixWorld(true);
        const initialBox = new THREE.Box3().setFromObject(model);
        const initialSize = initialBox.getSize(new THREE.Vector3());
        if (!Number.isFinite(initialSize.y) || initialSize.y <= 0.001) {
            this.disposeObjectTree(model, true);
            throw new Error(`${spec.label} ${format.toUpperCase()} 모델의 크기를 계산할 수 없습니다.`);
        }

        const wrapper = new THREE.Group();
        wrapper.name = `${kind}-${format}-template`;
        wrapper.add(model);
        model.scale.multiplyScalar(spec.targetHeight / initialSize.y);
        model.updateMatrixWorld(true);
        const normalizedBox = new THREE.Box3().setFromObject(model);
        const center = normalizedBox.getCenter(new THREE.Vector3());
        if (spec.collider !== 'trunk') {
            model.position.x -= center.x;
            model.position.z -= center.z;
        }
        model.position.y -= normalizedBox.min.y;
        this.prepareDecoration(model);
        this.bundledNatureTemplateCache.set(kind, wrapper);
        return wrapper;
    }

    private createBundledNaturePlaceholder(kind: BundledNatureDecorationKind): THREE.Group {
        const spec = BUNDLED_NATURE_DECORATION_SPECS[kind];
        if (spec.placeholder === 'pine') {
            const source = this.createTreeDecoration();
            source.updateMatrixWorld(true);
            const initialBox = new THREE.Box3().setFromObject(source);
            const initialHeight = initialBox.getSize(new THREE.Vector3()).y;
            if (!Number.isFinite(initialHeight) || initialHeight <= 0.001) return source;
            const wrapper = new THREE.Group();
            wrapper.add(source);
            source.scale.multiplyScalar(spec.targetHeight / initialHeight);
            source.updateMatrixWorld(true);
            const normalizedBox = new THREE.Box3().setFromObject(source);
            source.position.y -= normalizedBox.min.y;
            return this.prepareDecoration(wrapper);
        }

        const group = new THREE.Group();
        if (spec.placeholder === 'lotus') {
            const leafMaterial = new THREE.MeshStandardMaterial({ color: 0x4ca96a, roughness: 0.88 });
            const petalMaterial = new THREE.MeshStandardMaterial({ color: 0xf3a3bc, roughness: 0.7 });
            for (const [x, z, radius] of [[-0.62, -0.1, 0.78], [0.58, -0.24, 0.68], [0.05, 0.55, 0.74]] as const) {
                const leaf = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, 0.05, 20), leafMaterial);
                leaf.position.set(x, 0.03, z);
                group.add(leaf);
            }
            for (const [x, z, size] of [[-0.28, 0.02, 0.34], [0.62, 0.34, 0.26]] as const) {
                const flower = new THREE.Mesh(new THREE.SphereGeometry(size, 10, 7), petalMaterial);
                flower.position.set(x, 0.42, z);
                group.add(flower);
            }
        } else if (spec.placeholder === 'rock') {
            const rockMaterial = new THREE.MeshStandardMaterial({ color: 0x7b817d, roughness: 0.98 });
            for (const [x, y, z, size] of [[-0.65, 0.58, 0.05, 1.0], [0.72, 0.44, 0.12, 0.78], [0.05, 0.3, -0.72, 0.6]] as const) {
                const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(size, 0), rockMaterial);
                rock.position.set(x, y, z);
                rock.scale.y = 0.72;
                group.add(rock);
            }
        } else if (spec.placeholder === 'stairs') {
            const granite = new THREE.MeshStandardMaterial({ color: 0x777873, roughness: 0.98 });
            const profile = spec.stepProfile || { count: 6, width: 5.4, depth: 0.7, rise: 0.28 };
            for (let step = 0; step < profile.count; step++) {
                const height = profile.rise * (step + 1);
                const tread = new THREE.Mesh(new THREE.BoxGeometry(profile.width, height, profile.depth), granite);
                tread.position.set(0, height / 2, profile.count * profile.depth / 2 - (step + 0.5) * profile.depth);
                group.add(tread);
            }
        } else {
            const graniteDark = new THREE.MeshStandardMaterial({ color: 0x555955, roughness: 0.98 });
            const graniteLight = new THREE.MeshStandardMaterial({ color: 0x898983, roughness: 0.96 });
            for (let row = 0; row < 4; row++) {
                const course = new THREE.Mesh(new THREE.BoxGeometry(6.4, 0.4, 0.88), row % 2 ? graniteLight : graniteDark);
                course.position.y = 0.2 + row * 0.4;
                group.add(course);
            }
            const cap = new THREE.Mesh(new THREE.BoxGeometry(6.4, 0.2, 1), graniteLight);
            cap.position.y = 1.7;
            group.add(cap);
        }
        return this.prepareDecoration(group);
    }

    private buildYsDecoration(placement: MapSceneryPlacement) {
        if (!this.scene) return;
        const preset = this.ysDecorationPresets.find((item) => item.id === placement.assetId);
        if (!preset) return;
        const template = this.ysDecorationTemplateCache.get(preset.id);
        const surface = this.ysDecorationSurfaceCache.get(preset.id);
        if (template && surface) {
            this.addYsDecorationInstance(template, placement, surface);
            return;
        }

        const placeholder = this.createYsDecorationPlaceholder(preset);
        placeholder.userData['sceneryId'] = placement.id;
        this.placeSceneryDecoration(placeholder, placement);
        this.registerAutoSceneryGate(placeholder, placement);
        this.scene.add(placeholder);
        this.registerPerformanceDecoration(placeholder);
        this.ysDecorationStatus = `${preset.name} 모델을 불러오는 중입니다.`;
        this.requestUiRender();

        void Promise.all([
            this.ensureYsDecorationTemplateLoaded(preset),
            this.ensureYsDecorationSurfaceLoaded(preset)
        ])
            .then(([loadedTemplate, loadedSurface]) => {
                if (!this.scene || placeholder.parent !== this.scene || this.destroyed) return;
                this.sceneryColliders = this.sceneryColliders.filter((collider) => collider.id !== placement.id);
                this.performanceDecorations = this.performanceDecorations.filter((item) => item !== placeholder);
                this.scene.remove(placeholder);
                this.disposeObjectTree(placeholder);
                this.addYsDecorationInstance(loadedTemplate, placement, loadedSurface);
                this.ysDecorationStatus = `${preset.name} 모델·텍스처·지붕 이동 표면을 적용했습니다.`;
                this.requestUiRender();
            })
            .catch((error: any) => {
                if (placeholder.parent !== this.scene) return;
                this.ysDecorationStatus = error?.message || `${preset.name} 모델을 불러오지 못했습니다.`;
                this.requestUiRender();
            });
    }

    private addYsDecorationInstance(
        template: THREE.Group,
        placement: MapSceneryPlacement,
        surface: YsDecorationSurfaceMap
    ) {
        if (!this.scene) return;
        const wrapper = template.clone(true);
        wrapper.userData['sharedDecorationResources'] = true;
        wrapper.userData['sceneryId'] = placement.id;
        this.prepareDecoration(wrapper);
        this.placeSceneryDecoration(wrapper, placement, false);
        this.registerAutoSceneryGate(wrapper, placement);
        this.registerYsDecorationSurface(placement, surface);
        this.scene.add(wrapper);
        this.registerPerformanceDecoration(wrapper);
    }

    private createYsDecorationPlaceholder(preset: YsDecorationPreset): THREE.Group {
        const group = new THREE.Group();
        group.name = `ys-decoration-loading-${preset.id}`;
        const wallMaterial = new THREE.MeshStandardMaterial({ color: 0x8a5a36, roughness: 0.82 });
        const roofMaterial = new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.76 });
        const wall = new THREE.Mesh(new THREE.BoxGeometry(4.8, 2.4, 3.2), wallMaterial);
        wall.position.y = 1.2;
        const roof = new THREE.Mesh(new THREE.ConeGeometry(3.8, 1.4, 4), roofMaterial);
        roof.position.y = 3.1;
        roof.rotation.y = Math.PI / 4;
        group.add(wall, roof);
        return this.prepareDecoration(group);
    }

    private enqueueYsDecorationLoad<T>(task: () => Promise<T>): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.ysDecorationLoadQueue.push(() => {
                if (this.destroyed) {
                    reject(new Error('화면이 종료되어 YS 장식 로딩을 중단했습니다.'));
                    return;
                }
                this.ysDecorationActiveLoadCount += 1;
                void task()
                    .then(resolve, reject)
                    .finally(() => {
                        this.ysDecorationActiveLoadCount = Math.max(0, this.ysDecorationActiveLoadCount - 1);
                        this.drainYsDecorationLoadQueue();
                    });
            });
            this.drainYsDecorationLoadQueue();
        });
    }

    private drainYsDecorationLoadQueue() {
        while (this.ysDecorationActiveLoadCount < this.ysDecorationLoadConcurrency) {
            const next = this.ysDecorationLoadQueue.shift();
            if (!next) return;
            next();
        }
    }

    private ensureYsDecorationTemplateLoaded(preset: YsDecorationPreset): Promise<THREE.Group> {
        const cached = this.ysDecorationTemplateCache.get(preset.id);
        if (cached) return Promise.resolve(cached);
        const pending = this.ysDecorationLoadPromises.get(preset.id);
        if (pending) return pending;

        const load = this.enqueueYsDecorationLoad(async () => {
            const urls = [preset.performanceUrl, preset.url]
                .filter((url, index, list): url is string => Boolean(url) && list.indexOf(url) === index);
            let lastError: unknown;
            for (const url of urls) {
                try {
                    const response = await fetch(url, { cache: 'force-cache' });
                    if (!response.ok) throw new Error(`${preset.name} 모델을 불러오지 못했습니다. (${response.status})`);
                    const data = await response.arrayBuffer();
                    this.validateSkinData(data, 'glb');
                    const model = await this.parseSkinModel(data, 'glb') as THREE.Group;
                    if (this.destroyed) {
                        this.disposeObjectTree(model, true);
                        throw new Error('화면이 종료되어 YS 장식 로딩을 중단했습니다.');
                    }
                    return this.storeYsDecorationTemplate(preset, model);
                } catch (error) {
                    if (this.destroyed) throw error;
                    lastError = error;
                }
            }
            throw lastError instanceof Error
                ? lastError
                : new Error(`${preset.name} 모델을 불러오지 못했습니다.`);
        });
        this.ysDecorationLoadPromises.set(preset.id, load);
        void load.finally(() => this.ysDecorationLoadPromises.delete(preset.id)).catch(() => undefined);
        return load;
    }

    private ensureYsDecorationSurfaceLoaded(preset: YsDecorationPreset): Promise<YsDecorationSurfaceMap> {
        const cached = this.ysDecorationSurfaceCache.get(preset.id);
        if (cached) return Promise.resolve(cached);
        const pending = this.ysDecorationSurfaceLoadPromises.get(preset.id);
        if (pending) return pending;
        const load = (async () => {
            const response = await fetch(preset.collisionUrl, { cache: 'force-cache' });
            if (!response.ok) throw new Error(`${preset.name} 이동 표면을 불러오지 못했습니다. (${response.status})`);
            const surface = await response.json() as YsDecorationSurfaceMap;
            const expectedCells = surface.columns * surface.rows;
            if (surface.id !== preset.id
                || surface.version !== 1
                || !Number.isFinite(surface.cellSizeX)
                || !Number.isFinite(surface.cellSizeZ)
                || expectedCells <= 0
                || surface.heights.length !== expectedCells) {
                throw new Error(`${preset.name} 이동 표면 데이터가 올바르지 않습니다.`);
            }
            this.ysDecorationSurfaceCache.set(preset.id, surface);
            return surface;
        })();
        this.ysDecorationSurfaceLoadPromises.set(preset.id, load);
        void load.finally(() => this.ysDecorationSurfaceLoadPromises.delete(preset.id)).catch(() => undefined);
        return load;
    }

    private storeYsDecorationTemplate(preset: YsDecorationPreset, model: THREE.Group): THREE.Group {
        model.updateMatrixWorld(true);
        const initialBox = new THREE.Box3().setFromObject(model);
        const initialSize = initialBox.getSize(new THREE.Vector3());
        if (!Number.isFinite(initialSize.y) || initialSize.y <= 0.001) {
            this.disposeObjectTree(model, true);
            throw new Error(`${preset.name} 모델의 크기를 계산할 수 없습니다.`);
        }
        const wrapper = new THREE.Group();
        wrapper.name = `ys-decoration-template-${preset.id}`;
        wrapper.add(model);
        model.scale.setScalar(preset.targetHeight / initialSize.y);
        model.updateMatrixWorld(true);
        const normalizedBox = new THREE.Box3().setFromObject(model);
        const center = normalizedBox.getCenter(new THREE.Vector3());
        model.position.x -= center.x;
        model.position.z -= center.z;
        model.position.y -= normalizedBox.min.y;
        this.ysDecorationTemplateCache.set(preset.id, wrapper);
        return wrapper;
    }

    private placeSceneryDecoration(
        object: THREE.Group,
        placement: MapSceneryPlacement,
        registerCollider: boolean = true
    ) {
        object.position.set(0, 0, 0);
        object.rotation.set(0, 0, 0);
        object.scale.setScalar(1);
        this.tagEditor3dSelectable(object, 'scenery', placement.id);
        object.updateMatrixWorld(true);
        const localBox = new THREE.Box3().setFromObject(object);
        const localSize = localBox.getSize(new THREE.Vector3());
        const localCenter = localBox.getCenter(new THREE.Vector3());
        const scale = THREE.MathUtils.clamp(Number(placement.scale) || 1, 0.1, 10);
        const verticalScale = THREE.MathUtils.clamp(Number(placement.verticalScale) || scale, 0.1, 10);
        const rotation = THREE.MathUtils.degToRad(Number(placement.rotation) || 0);
        const groundY = this.sceneryGroundHeightAt(placement.x, placement.z) + (Number(placement.y) || 0);
        object.rotation.y = rotation;
        object.scale.set(scale, verticalScale, scale);
        object.position.set(placement.x, groundY - localBox.min.y * verticalScale, placement.z);
        object.updateMatrixWorld(true);

        if (!registerCollider || !Number.isFinite(localSize.x) || !Number.isFinite(localSize.z)) return;
        const centerOffset = new THREE.Vector3(localCenter.x * scale, 0, localCenter.z * scale)
            .applyAxisAngle(new THREE.Vector3(0, 1, 0), rotation);
        this.sceneryColliders.push({
            id: placement.id,
            centerX: placement.x + centerOffset.x,
            centerZ: placement.z + centerOffset.z,
            halfWidth: Math.max(0.35, localSize.x * scale * 0.5),
            halfDepth: Math.max(0.35, localSize.z * scale * 0.5),
            rotation,
            baseY: groundY,
            topY: groundY + localSize.y * verticalScale,
            scale,
            verticalScale,
            autoGate: placement.autoOpen === true
        });
    }

    private registerYsDecorationSurface(placement: MapSceneryPlacement, surface: YsDecorationSurfaceMap) {
        const scale = THREE.MathUtils.clamp(Number(placement.scale) || 1, 0.1, 10);
        const verticalScale = THREE.MathUtils.clamp(Number(placement.verticalScale) || scale, 0.1, 10);
        const rotation = THREE.MathUtils.degToRad(Number(placement.rotation) || 0);
        const groundY = this.sceneryGroundHeightAt(placement.x, placement.z) + (Number(placement.y) || 0);
        const width = surface.columns * surface.cellSizeX * scale;
        const depth = surface.rows * surface.cellSizeZ * scale;
        this.sceneryColliders.push({
            id: placement.id,
            centerX: placement.x,
            centerZ: placement.z,
            halfWidth: Math.max(0.35, width * 0.5),
            halfDepth: Math.max(0.35, depth * 0.5),
            rotation,
            baseY: groundY,
            topY: groundY + surface.maxY * verticalScale,
            scale,
            verticalScale,
            surface,
            autoGate: placement.autoOpen === true
        });
    }

    private registerAutoSceneryGate(object: THREE.Object3D, placement: MapSceneryPlacement) {
        if (!placement.autoOpen) return;
        const previous = this.autoSceneryGates.get(placement.id);
        const openAmount = previous?.openAmount || 0;
        const closedY = object.position.y;
        const state: AutoSceneryGateState = {
            id: placement.id,
            object,
            closedY,
            openHeight: THREE.MathUtils.clamp(Number(placement.openHeight) || 7, 2, 30),
            openRadius: THREE.MathUtils.clamp(Number(placement.openRadius) || 14, 4, 40),
            openAmount
        };
        object.position.y = closedY + state.openHeight * this.smoothStep(openAmount);
        object.userData['autoGate'] = true;
        this.autoSceneryGates.set(placement.id, state);
    }

    private updateAutoSceneryGates(dt: number) {
        if (this.autoSceneryGates.size === 0) return;
        const canOpen = this.screen === 'playing' || (this.screen === 'editor' && this.editorViewMode === '3d');
        const playerPosition = canOpen ? this.player?.position : undefined;
        this.autoSceneryGates.forEach((gate) => {
            const distance = playerPosition
                ? this.horizontalDistance(playerPosition, gate.object.position)
                : Number.POSITIVE_INFINITY;
            const target = distance <= gate.openRadius
                ? 1
                : (distance >= gate.openRadius + 5 ? 0 : gate.openAmount);
            const response = 1 - Math.exp(-6.5 * Math.max(0, dt));
            gate.openAmount = THREE.MathUtils.lerp(gate.openAmount, target, response);
            if (Math.abs(gate.openAmount - target) < 0.002) gate.openAmount = target;
            gate.object.position.y = gate.closedY + gate.openHeight * this.smoothStep(gate.openAmount);
        });
    }

    private smoothStep(value: number): number {
        const clamped = THREE.MathUtils.clamp(value, 0, 1);
        return clamped * clamped * (3 - 2 * clamped);
    }

    private sceneryGroundHeightAt(worldX: number, worldZ: number): number {
        const jumpPlatform = this.jumpPlatformAtPosition(worldX, worldZ, 0.12);
        if (jumpPlatform) return jumpPlatform.topY;
        let nearestHeight = 0;
        let nearestSquared = Number.POSITIVE_INFINITY;
        this.explorePathSamples.forEach((sample) => {
            const squared = (sample.point.x - worldX) ** 2 + (sample.point.z - worldZ) ** 2;
            if (squared >= nearestSquared) return;
            nearestSquared = squared;
            nearestHeight = sample.point.y;
        });
        return Math.max(0, nearestHeight);
    }

    private buildMenuHeritageShowcase() {
        if (!this.scene) return;
        const showcase = new THREE.Group();
        showcase.name = 'menu-heritage-showcase';
        const start = this.getPathFrame(this.activeMap, 0);
        const heading = Math.atan2(start.forward.x, start.forward.z);
        const place = (object: THREE.Object3D, forwardDistance: number, sideDistance: number, scale: number, rotationOffset: number = 0) => {
            object.position.copy(start.point)
                .addScaledVector(start.forward, forwardDistance)
                .addScaledVector(start.right, sideDistance);
            object.rotation.y = heading + rotationOffset;
            object.scale.setScalar(scale);
            showcase.add(object);
        };

        const courtyard = new THREE.Mesh(
            new THREE.BoxGeometry(58, 0.18, 54),
            new THREE.MeshStandardMaterial({ color: 0x756f5d, roughness: 1 })
        );
        courtyard.position.copy(start.point).addScaledVector(start.forward, 29);
        courtyard.position.y = start.point.y - 0.08;
        courtyard.rotation.y = heading;
        courtyard.receiveShadow = true;
        showcase.add(courtyard);

        place(this.createHanokDecoration(), 36, -13, 0.92);
        place(this.createHanokAnnexDecoration(), 44, 14.5, 0.7, Math.PI * 0.04);
        place(this.createJangdokDecoration(), 17, -9.5, 0.76, -Math.PI * 0.08);
        place(this.createHeritageScarecrowDecoration(), 19, 9.5, 0.92, Math.PI * 0.08);
        place(this.createWoodenFenceDecoration(), 30, 1, 0.75, Math.PI / 2);

        const warmFill = new THREE.DirectionalLight(0xffdba6, 1.6);
        warmFill.position.copy(start.point)
            .addScaledVector(start.right, -18)
            .addScaledVector(start.forward, 8)
            .add(new THREE.Vector3(0, 24, 0));
        showcase.add(warmFill);
        this.menuHeritageShowcase = showcase;
        this.scene.add(showcase);
    }

    private setMenuHeritageShowcaseVisible(visible: boolean) {
        if (visible && this.worldReady && !this.menuHeritageShowcase) this.buildMenuHeritageShowcase();
        if (this.menuHeritageShowcase) this.menuHeritageShowcase.visible = visible;
        if (!this.scene) return;
        const palette = visible
            ? { sky: 0xb9ced0, fog: 0x92a8a8, fogDensity: 0.0058 }
            : this.themePalette();
        this.scene.background = new THREE.Color(palette.sky);
        if (this.scene.fog instanceof THREE.FogExp2) {
            this.scene.fog.color.set(palette.fog);
            this.scene.fog.density = palette.fogDensity;
        }

        if (this.player) this.player.visible = !visible;
        this.coins.forEach((item) => item.mesh.visible = visible ? false : !item.collected);
        this.magnets.forEach((item) => item.mesh.visible = visible ? false : !item.collected);
        this.supplements.forEach((item) => item.mesh.visible = visible ? false : !item.collected);
        this.mounts.forEach((item) => item.mesh.visible = visible ? false : !item.collected);
        this.obstacles.forEach((item) => item.mesh.visible = visible ? false : !item.hit);
        this.hiddenNpcs.forEach((item) => item.mesh.visible = visible ? false : !item.resolved);
        this.gates.forEach((item) => {
            if (item.mesh) item.mesh.visible = visible ? false : !item.resolved;
        });
        this.performanceDecorations.forEach((item) => item.visible = !visible);
    }

    private createHeritageScarecrowDecoration(): THREE.Group {
        const group = new THREE.Group();
        const wood = new THREE.MeshStandardMaterial({ color: 0x60432d, roughness: 0.92 });
        const straw = new THREE.MeshStandardMaterial({ color: 0xd8b45d, roughness: 0.96 });
        const cloth = new THREE.MeshStandardMaterial({ color: 0x4a6b82, roughness: 0.88 });
        const rope = new THREE.MeshStandardMaterial({ color: 0x9b7a3c, roughness: 0.94 });
        const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.13, 3.4, 8), wood);
        pole.position.y = 1.55;
        const arms = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 3.25, 8), wood);
        arms.rotation.z = Math.PI / 2;
        arms.position.y = 2.15;
        const body = new THREE.Mesh(new THREE.ConeGeometry(0.82, 1.65, 8), cloth);
        body.position.y = 1.5;
        const head = new THREE.Mesh(new THREE.SphereGeometry(0.42, 14, 10), straw);
        head.position.y = 2.86;
        const hatBrim = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.72, 0.07, 16), rope);
        hatBrim.position.y = 3.22;
        const hat = new THREE.Mesh(new THREE.ConeGeometry(0.55, 0.68, 16), rope);
        hat.position.y = 3.56;
        group.add(pole, arms, body, head, hatBrim, hat);
        for (const side of [-1, 1]) {
            const sleeve = new THREE.Mesh(new THREE.ConeGeometry(0.32, 0.82, 8), cloth);
            sleeve.rotation.z = side * Math.PI / 2;
            sleeve.position.set(side * 1.02, 2.14, 0);
            const strawHand = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.5, 7), straw);
            strawHand.rotation.z = side * Math.PI / 2;
            strawHand.position.set(side * 1.72, 2.14, 0);
            group.add(sleeve, strawHand);
        }
        return this.prepareDecoration(group);
    }

    private createBuiltInDecoration(kind: SceneryKind): THREE.Group {
        let decoration: THREE.Group;
        if (isBundledNatureDecorationKind(kind)) decoration = this.createBundledNaturePlaceholder(kind);
        else if (kind === 'building') decoration = this.createBuildingDecoration();
        else if (kind === 'cone') decoration = this.createConeDecoration();
        else if (kind === 'hanok') decoration = this.createHanokDecoration();
        else if (kind === 'hanok-annex') decoration = this.createHanokAnnexDecoration();
        else if (kind === 'hanok-pavilion') decoration = this.createHanokPavilionDecoration();
        else if (kind === 'hanok-palace') decoration = this.createPalaceHanokDecoration();
        else if (kind === 'hanok-courtyard') decoration = this.createHanokCourtyardDecoration();
        else if (kind === 'hanok-library') decoration = this.createHanokLibraryDecoration();
        else if (kind === 'hanok-study') decoration = this.createHanokStudyDecoration();
        else if (kind === 'lotus-pond') decoration = this.createLotusPondDecoration();
        else if (kind === 'stone-bridge') decoration = this.createStoneBridgeDecoration();
        else if (kind === 'jangdok') decoration = this.createJangdokDecoration();
        else if (kind === 'stone-lantern') decoration = this.createStoneLanternDecoration();
        else if (kind === 'wooden-fence') decoration = this.createWoodenFenceDecoration();
        else if (kind === 'harbor-warehouse') decoration = this.createHarborWarehouseDecoration();
        else if (kind === 'fishing-boat') decoration = this.createFishingBoatDecoration();
        else if (kind === 'haechi-guide') decoration = this.createDeferredCharacterPlaceholder('haetae');
        else decoration = this.createTreeDecoration();
        return this.prepareDecoration(decoration);
    }

    private createBuiltInDecorationInstance(kind: SceneryKind): THREE.Group {
        let template = this.builtInDecorationTemplateCache.get(kind);
        if (!template) {
            template = this.createBuiltInDecoration(kind);
            template.name = `${kind}-shared-template`;
            template.userData['sharedDecorationResources'] = true;
            this.builtInDecorationTemplateCache.set(kind, template);
        }
        const instance = template.clone(true);
        instance.userData['sharedDecorationResources'] = true;
        return instance;
    }

    private prepareDecoration(group: THREE.Group): THREE.Group {
        group.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (mesh.isMesh) {
                mesh.castShadow = this.effectiveGraphicsQuality !== 'low';
                mesh.receiveShadow = false;
            }
        });
        return group;
    }

    private registerPerformanceDecoration(object: THREE.Object3D) {
        if (this.isDenseSceneryMap()) {
            object.traverse((child: THREE.Object3D) => {
                const mesh = child as THREE.Mesh;
                if (mesh.isMesh) mesh.castShadow = false;
            });
        }
        if (this.screen === 'menu') object.visible = false;
        this.performanceDecorations.push(object);
    }

    private isDenseSceneryMap(map: GameMap = this.activeMap): boolean {
        return map.scenery.length >= 180;
    }

    private async preloadHeritageTextures() {
        const fileNames: Array<'korean-roof-tile.webp' | 'korean-timber.webp' | 'hanji-paper.webp'> = [
            'korean-roof-tile.webp',
            'korean-timber.webp',
            'hanji-paper.webp'
        ];
        fileNames.forEach((fileName) => this.getHeritageBaseTexture(fileName));
        await Promise.all(fileNames.map((fileName) => this.heritageTextureLoadPromises.get(fileName) || Promise.resolve()));
    }

    private getHeritageBaseTexture(fileName: 'korean-roof-tile.webp' | 'korean-timber.webp' | 'hanji-paper.webp'): THREE.Texture {
        const cached = this.heritageBaseTextureCache.get(fileName);
        if (cached) return cached;
        let texture!: THREE.Texture;
        const loadPromise = new Promise<void>((resolve) => {
            texture = new THREE.TextureLoader().load(
                `/assets/quiz-runner/heritage/${fileName}`,
                (loaded) => {
                    loaded.colorSpace = THREE.SRGBColorSpace;
                    resolve();
                },
                undefined,
                () => resolve()
            );
        });
        texture.colorSpace = THREE.SRGBColorSpace;
        this.heritageBaseTextureCache.set(fileName, texture);
        this.heritageTextureLoadPromises.set(fileName, loadPromise);
        return texture;
    }

    private createHeritageMaterial(fileName: 'korean-roof-tile.webp' | 'korean-timber.webp' | 'hanji-paper.webp', color: number, repeatX: number, repeatY: number, roughness: number): THREE.MeshStandardMaterial {
        const cacheKey = `${fileName}:${repeatX}:${repeatY}`;
        let texture = this.heritageTextureCache.get(cacheKey);
        if (!texture) {
            texture = this.getHeritageBaseTexture(fileName).clone();
            texture.colorSpace = THREE.SRGBColorSpace;
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.repeat.set(repeatX, repeatY);
            const maxAnisotropy = (this.renderer as any)?.capabilities?.getMaxAnisotropy?.() || 1;
            const qualityAnisotropy = this.effectiveGraphicsQuality === 'low' ? 2 : (this.effectiveGraphicsQuality === 'high' ? 8 : 4);
            texture.anisotropy = Math.min(qualityAnisotropy, maxAnisotropy);
            texture.needsUpdate = true;
            this.heritageTextureCache.set(cacheKey, texture);
        }
        return new THREE.MeshStandardMaterial({ map: texture, color, roughness, metalness: fileName === 'korean-roof-tile.webp' ? 0.07 : 0.01 });
    }

    private createHanokDecoration(): THREE.Group {
        const group = new THREE.Group();
        const stone = new THREE.MeshStandardMaterial({ color: 0x8a8175, roughness: 0.96 });
        const plaster = new THREE.MeshStandardMaterial({ color: 0xeadfca, roughness: 0.9 });
        const timber = this.createHeritageMaterial('korean-timber.webp', 0x9c6847, 2.4, 1.2, 0.8);
        const paper = this.createHeritageMaterial('hanji-paper.webp', 0xfff4d8, 1.4, 1.8, 0.84);
        const dancheong = new THREE.MeshStandardMaterial({ color: 0x245d54, emissive: 0x12352f, emissiveIntensity: 0.08, roughness: 0.66 });
        const redPaint = new THREE.MeshStandardMaterial({ color: 0x8f332b, roughness: 0.72 });
        const brass = new THREE.MeshStandardMaterial({ color: 0xc6a04f, roughness: 0.42, metalness: 0.4 });

        const lowerPlinth = new THREE.Mesh(new THREE.BoxGeometry(10, 0.22, 6.35), stone);
        lowerPlinth.position.y = 0.11;
        group.add(lowerPlinth);
        const foundation = new THREE.Mesh(new THREE.BoxGeometry(9.4, 0.5, 5.8), stone);
        foundation.position.y = 0.42;
        group.add(foundation);
        const wall = new THREE.Mesh(new THREE.BoxGeometry(8.4, 3.25, 4.8), plaster);
        wall.position.y = 2.05;
        group.add(wall);
        const porch = new THREE.Mesh(new THREE.BoxGeometry(9, 0.24, 1.3), timber);
        porch.position.set(0, 0.68, -2.88);
        group.add(porch);
        for (let index = 0; index < 3; index++) {
            const step = new THREE.Mesh(new THREE.BoxGeometry(3.4 + index * 0.42, 0.18, 0.5), stone);
            step.position.set(0, 0.09 + index * 0.17, -3.9 + index * 0.38);
            group.add(step);
        }
        for (const x of [-4.05, -2, 0, 2, 4.05]) {
            const footing = new THREE.Mesh(new THREE.CylinderGeometry(0.31, 0.38, 0.22, 10), stone);
            footing.position.set(x, 0.73, -2.46);
            group.add(footing);
            const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.28, 3.7, 0.34), timber);
            pillar.position.set(x, 2.48, -2.46);
            group.add(pillar);
        }
        for (const x of [-4.05, 0, 4.05]) {
            const footing = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.37, 0.22, 10), stone);
            footing.position.set(x, 0.73, 2.46);
            const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.28, 3.7, 0.34), timber);
            pillar.position.set(x, 2.48, 2.46);
            group.add(footing, pillar);
        }
        for (const side of [-1, 1]) {
            for (const z of [-1.15, 0, 1.15]) {
                const sidePost = new THREE.Mesh(new THREE.BoxGeometry(0.26, 3.35, 0.3), timber);
                sidePost.position.set(side * 4.14, 2.32, z);
                group.add(sidePost);
            }
            for (const y of [1.2, 2.2, 3.22]) {
                const sideBeam = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.16, 4.45), timber);
                sideBeam.position.set(side * 4.24, y, 0);
                group.add(sideBeam);
            }
        }
        for (const x of [-3.05, -1.02, 1.02, 3.05]) {
            const door = new THREE.Mesh(new THREE.BoxGeometry(1.62, 2.7, 0.1), paper);
            door.position.set(x, 1.87, -2.53);
            group.add(door);
            for (const offset of [-0.45, 0, 0.45]) {
                const grid = new THREE.Mesh(new THREE.BoxGeometry(0.055, 2.55, 0.04), timber);
                grid.position.set(x + offset, 1.87, -2.6);
                group.add(grid);
            }
            for (const y of [1.2, 1.75, 2.3, 2.85]) {
                const grid = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.045, 0.04), timber);
                grid.position.set(x, y, -2.6);
                group.add(grid);
            }
            const handle = new THREE.Mesh(new THREE.SphereGeometry(0.065, 10, 8), brass);
            handle.position.set(x + (x < 0 ? 0.42 : -0.42), 1.74, -2.68);
            group.add(handle);
        }
        const beam = new THREE.Mesh(new THREE.BoxGeometry(8.6, 0.34, 0.38), timber);
        beam.position.set(0, 3.75, -2.48);
        group.add(beam);
        const paintedBeam = new THREE.Mesh(new THREE.BoxGeometry(9.1, 0.22, 0.46), dancheong);
        paintedBeam.position.set(0, 4.05, -2.51);
        group.add(paintedBeam);
        for (const [index, x] of [-3.65, -2.45, -1.25, 0, 1.25, 2.45, 3.65].entries()) {
            this.addHanokBracketCluster(group, x, 4.05, -2.52, -1, timber, dancheong, redPaint, index % 2 === 0);
            this.addHanokBracketCluster(group, x, 4.05, 2.52, 1, timber, dancheong, redPaint, index % 2 !== 0);
        }
        this.addHanokRoof(group, 10.4, 6.5, 4.35);
        return this.prepareDecoration(group);
    }

    private createHanokAnnexDecoration(): THREE.Group {
        const group = new THREE.Group();
        const stone = new THREE.MeshStandardMaterial({ color: 0x91897e, roughness: 0.96 });
        const timber = this.createHeritageMaterial('korean-timber.webp', 0x946044, 3, 1.2, 0.82);
        const plaster = new THREE.MeshStandardMaterial({ color: 0xeee4d2, roughness: 0.9 });
        const paper = this.createHeritageMaterial('hanji-paper.webp', 0xfff6df, 1.6, 1.8, 0.86);
        const dancheong = new THREE.MeshStandardMaterial({ color: 0x315f55, roughness: 0.68 });
        const redPaint = new THREE.MeshStandardMaterial({ color: 0x8a372e, roughness: 0.74 });
        const lowerPlinth = new THREE.Mesh(new THREE.BoxGeometry(12.85, 0.2, 6.25), stone);
        lowerPlinth.position.y = 0.1;
        group.add(lowerPlinth);
        const base = new THREE.Mesh(new THREE.BoxGeometry(12.2, 0.55, 5.8), stone);
        base.position.y = 0.28;
        group.add(base);
        const deck = new THREE.Mesh(new THREE.BoxGeometry(12, 0.28, 1.45), timber);
        deck.position.set(0, 0.72, -2.75);
        group.add(deck);
        const wall = new THREE.Mesh(new THREE.BoxGeometry(11.2, 3, 4.3), plaster);
        wall.position.set(0, 2.05, 0.35);
        group.add(wall);
        for (let index = -5; index <= 5; index++) {
            const footing = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.31, 0.2, 10), stone);
            footing.position.set(index * 1.08, 0.64, -2.28);
            group.add(footing);
            const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.23, 3.55, 0.28), timber);
            pillar.position.set(index * 1.08, 2.25, -2.28);
            group.add(pillar);
        }
        for (const x of [-5.4, 0, 5.4]) {
            const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.23, 3.35, 0.28), timber);
            pillar.position.set(x, 2.18, 2.25);
            group.add(pillar);
        }
        for (let index = -4; index <= 4; index += 2) {
            const door = new THREE.Mesh(new THREE.BoxGeometry(1.72, 2.5, 0.1), paper);
            door.position.set(index * 1.08, 2.03, -2.36);
            group.add(door);
            for (const offset of [-0.5, 0, 0.5]) {
                const mullion = new THREE.Mesh(new THREE.BoxGeometry(0.045, 2.38, 0.04), timber);
                mullion.position.set(index * 1.08 + offset, 2.03, -2.43);
                group.add(mullion);
            }
            for (const y of [1.36, 1.92, 2.48, 3.05]) {
                const lattice = new THREE.Mesh(new THREE.BoxGeometry(1.62, 0.045, 0.04), timber);
                lattice.position.set(index * 1.08, y, -2.43);
                group.add(lattice);
            }
        }
        const rail = new THREE.Mesh(new THREE.BoxGeometry(12, 0.16, 0.18), timber);
        rail.position.set(0, 1.2, -3.35);
        group.add(rail);
        const paintedBeam = new THREE.Mesh(new THREE.BoxGeometry(12.1, 0.24, 0.4), dancheong);
        paintedBeam.position.set(0, 3.77, -2.3);
        group.add(paintedBeam);
        for (const [index, x] of [-5.2, -3.9, -2.6, -1.3, 0, 1.3, 2.6, 3.9, 5.2].entries()) {
            this.addHanokBracketCluster(group, x, 3.86, -2.3, -1, timber, dancheong, redPaint, index % 2 === 0);
            this.addHanokBracketCluster(group, x, 3.86, 2.3, 1, timber, dancheong, redPaint, index % 2 !== 0);
        }
        for (const x of [-5, -3.75, -2.5, -1.25, 0, 1.25, 2.5, 3.75, 5]) {
            const railing = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.72, 0.12), timber);
            railing.position.set(x, 0.92, -3.36);
            group.add(railing);
        }
        for (let index = 0; index < 3; index++) {
            const step = new THREE.Mesh(new THREE.BoxGeometry(3.8 + index * 0.48, 0.17, 0.5), stone);
            step.position.set(0, 0.08 + index * 0.16, -4.02 + index * 0.38);
            group.add(step);
        }
        this.addHanokRoof(group, 13.4, 6.5, 4.18);
        return this.prepareDecoration(group);
    }

    private createHanokPavilionDecoration(): THREE.Group {
        const group = new THREE.Group();
        const stone = new THREE.MeshStandardMaterial({ color: 0x8e877c, roughness: 0.97 });
        const timber = this.createHeritageMaterial('korean-timber.webp', 0x9a5c3c, 2.4, 1.2, 0.8);
        const painted = new THREE.MeshStandardMaterial({ color: 0x2f665b, emissive: 0x15372f, emissiveIntensity: 0.12, roughness: 0.7 });
        const tile = this.createHeritageMaterial('korean-roof-tile.webp', 0x88949b, 4.2, 2.4, 0.68);
        const foundation = new THREE.Mesh(new THREE.CylinderGeometry(4.2, 4.45, 0.55, 8), stone);
        foundation.position.y = 0.28;
        group.add(foundation);
        const deck = new THREE.Mesh(new THREE.CylinderGeometry(3.85, 3.85, 0.35, 8), timber);
        deck.position.y = 0.72;
        group.add(deck);
        for (let index = 0; index < 8; index++) {
            const angle = (index / 8) * Math.PI * 2;
            const footing = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.36, 0.2, 8), stone);
            footing.position.set(Math.cos(angle) * 3.15, 0.88, Math.sin(angle) * 3.15);
            group.add(footing);
            const post = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, 4.1, 8), painted);
            post.position.set(Math.cos(angle) * 3.15, 2.85, Math.sin(angle) * 3.15);
            group.add(post);
            const nextAngle = ((index + 1) / 8) * Math.PI * 2;
            const rail = new THREE.Mesh(new THREE.BoxGeometry(2.45, 0.13, 0.13), timber);
            rail.position.set((Math.cos(angle) + Math.cos(nextAngle)) * 1.58, 1.45, (Math.sin(angle) + Math.sin(nextAngle)) * 1.58);
            rail.rotation.y = -((angle + nextAngle) / 2);
            group.add(rail);
            const bracket = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.28, 0.52), painted);
            bracket.position.set(Math.cos(angle) * 3.15, 4.68, Math.sin(angle) * 3.15);
            bracket.rotation.y = -angle;
            group.add(bracket);
            const bracketArm = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.15, 0.28), timber);
            bracketArm.position.set(Math.cos(angle) * 3.42, 4.88, Math.sin(angle) * 3.42);
            bracketArm.rotation.y = -angle;
            group.add(bracketArm);
        }
        const upperBeam = new THREE.Mesh(new THREE.CylinderGeometry(3.65, 3.65, 0.2, 8), painted);
        upperBeam.position.y = 4.72;
        group.add(upperBeam);
        const ceiling = new THREE.Mesh(new THREE.CylinderGeometry(3.25, 3.25, 0.1, 8), new THREE.MeshStandardMaterial({ color: 0xb25842, roughness: 0.74 }));
        ceiling.position.y = 4.58;
        group.add(ceiling);
        for (let index = 0; index < 8; index++) {
            const angle = index / 8 * Math.PI * 2;
            const bench = new THREE.Mesh(new THREE.BoxGeometry(2.18, 0.18, 0.62), timber);
            bench.position.set(Math.cos(angle) * 2.4, 1.12, Math.sin(angle) * 2.4);
            bench.rotation.y = -angle;
            group.add(bench);
        }
        const ceilingMedallion = new THREE.Mesh(new THREE.TorusGeometry(0.78, 0.1, 8, 32), painted);
        ceilingMedallion.rotation.x = Math.PI / 2;
        ceilingMedallion.position.y = 4.5;
        group.add(ceilingMedallion);
        const roofSkirt = new THREE.Mesh(new THREE.CylinderGeometry(4.72, 5.55, 0.55, 8), tile);
        roofSkirt.position.y = 4.82;
        roofSkirt.rotation.y = Math.PI / 8;
        group.add(roofSkirt);
        const roof = new THREE.Mesh(new THREE.ConeGeometry(5.2, 2.2, 8), tile);
        roof.position.y = 5.35;
        roof.rotation.y = Math.PI / 8;
        group.add(roof);
        const pavilionRidge = new THREE.MeshStandardMaterial({ color: 0x1d2529, roughness: 0.58 });
        for (let index = 0; index < 8; index++) {
            const angle = Math.PI / 8 + index * Math.PI / 4;
            const radial = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle));
            const ridgeCurve = new THREE.CatmullRomCurve3([
                new THREE.Vector3(0, 6.43, 0),
                new THREE.Vector3(radial.x * 2.7, 5.74, radial.z * 2.7),
                new THREE.Vector3(radial.x * 4.45, 5.08, radial.z * 4.45),
                new THREE.Vector3(radial.x * 5.48, 5.02, radial.z * 5.48)
            ]);
            const hipRidge = new THREE.Mesh(new THREE.TubeGeometry(ridgeCurve, 12, 0.09, 7, false), pavilionRidge);
            hipRidge.name = 'pavilion-hip-ridge';
            group.add(hipRidge);
            const endTile = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 0.2, 12), pavilionRidge);
            endTile.name = 'pavilion-wadang-end-tile';
            endTile.position.set(radial.x * 5.52, 5.01, radial.z * 5.52);
            endTile.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), radial);
            group.add(endTile);
        }
        const finial = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.24, 0.7, 8), tile);
        finial.position.y = 6.75;
        group.add(finial);
        const finialOrb = new THREE.Mesh(new THREE.SphereGeometry(0.2, 12, 9), tile);
        finialOrb.position.y = 7.15;
        group.add(finialOrb);
        for (let index = 0; index < 3; index++) {
            const step = new THREE.Mesh(new THREE.BoxGeometry(2.6 + index * 0.38, 0.16, 0.46), stone);
            step.position.set(0, 0.08 + index * 0.15, -4.82 + index * 0.34);
            group.add(step);
        }
        return this.prepareDecoration(group);
    }

    private createPalaceHanokDecoration(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'premium-palace-hanok';
        const granite = new THREE.MeshStandardMaterial({ color: 0xa09a8e, roughness: 0.98 });
        const darkStone = new THREE.MeshStandardMaterial({ color: 0x68645d, roughness: 0.96 });
        const bronze = new THREE.MeshStandardMaterial({ color: 0xb18a3b, roughness: 0.42, metalness: 0.48 });
        const lowerTerrace = new THREE.Mesh(new THREE.BoxGeometry(18, 0.34, 11.5), darkStone);
        lowerTerrace.name = 'lower-woldae';
        lowerTerrace.position.y = 0.17;
        const upperTerrace = new THREE.Mesh(new THREE.BoxGeometry(16.4, 0.52, 10), granite);
        upperTerrace.name = 'upper-woldae';
        upperTerrace.position.y = 0.6;
        group.add(lowerTerrace, upperTerrace);

        const hall = this.createHanokAnnexDecoration();
        hall.name = 'five-bay-palace-hall';
        hall.position.y = 0.86;
        hall.scale.setScalar(1.08);
        group.add(hall);

        for (let index = 0; index < 5; index++) {
            const step = new THREE.Mesh(new THREE.BoxGeometry(5.2 + index * 0.46, 0.17, 0.58), granite);
            step.name = `front-stair-${index + 1}`;
            step.position.set(0, 0.09 + index * 0.16, -7.85 + index * 0.45);
            group.add(step);
        }
        for (const side of [-1, 1]) {
            const railBase = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.34, 8.2), granite);
            railBase.position.set(side * 7.62, 0.98, 0.35);
            group.add(railBase);
            for (let z = -3.2; z <= 3.9; z += 1.18) {
                const baluster = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.17, 0.78, 10), granite);
                baluster.position.set(side * 7.62, 1.42, z);
                const cap = new THREE.Mesh(new THREE.SphereGeometry(0.18, 10, 8), granite);
                cap.position.set(side * 7.62, 1.85, z);
                group.add(baluster, cap);
            }
            const lantern = this.createStoneLanternDecoration();
            lantern.name = side < 0 ? 'west-stone-lantern' : 'east-stone-lantern';
            lantern.position.set(side * 7.25, 0.86, -5.1);
            lantern.scale.setScalar(0.58);
            group.add(lantern);
        }
        const royalPlaque = new THREE.Mesh(new THREE.BoxGeometry(3.25, 0.78, 0.16), bronze);
        royalPlaque.name = 'royal-hall-plaque';
        royalPlaque.position.set(0, 4.9, -3.55);
        group.add(royalPlaque);
        return this.prepareDecoration(group);
    }

    private createHanokCourtyardDecoration(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'premium-hanok-courtyard';
        const earth = new THREE.MeshStandardMaterial({ color: 0x8b8069, roughness: 1 });
        const stone = new THREE.MeshStandardMaterial({ color: 0x918b80, roughness: 0.98 });
        const timber = this.createHeritageMaterial('korean-timber.webp', 0x855035, 2.6, 1.2, 0.84);
        const courtyard = new THREE.Mesh(new THREE.BoxGeometry(27, 0.24, 21), earth);
        courtyard.name = 'courtyard-ground';
        courtyard.position.y = 0.12;
        group.add(courtyard);

        const mainHouse = this.createHanokDecoration();
        mainHouse.name = 'courtyard-main-house';
        mainHouse.position.set(0, 0.24, 5.1);
        mainHouse.scale.setScalar(0.96);
        group.add(mainHouse);
        const sideHouse = this.createHanokAnnexDecoration();
        sideHouse.name = 'courtyard-side-house';
        sideHouse.position.set(-8.4, 0.24, -1.2);
        sideHouse.rotation.y = Math.PI / 2;
        sideHouse.scale.setScalar(0.68);
        group.add(sideHouse);
        const pavilion = this.createHanokPavilionDecoration();
        pavilion.name = 'courtyard-pavilion';
        pavilion.position.set(7.5, 0.24, -1.2);
        pavilion.scale.setScalar(0.48);
        group.add(pavilion);
        const jangdok = this.createJangdokDecoration();
        jangdok.name = 'courtyard-jangdok';
        jangdok.position.set(3.6, 0.24, -5.8);
        jangdok.scale.setScalar(0.62);
        group.add(jangdok);

        const backWall = new THREE.Mesh(new THREE.BoxGeometry(26, 2.15, 0.42), stone);
        backWall.position.set(0, 1.18, 10.05);
        group.add(backWall);
        for (const side of [-1, 1]) {
            const sideWall = new THREE.Mesh(new THREE.BoxGeometry(0.42, 2.15, 19.7), stone);
            sideWall.position.set(side * 13, 1.18, 0);
            group.add(sideWall);
        }

        const gate = new THREE.Group();
        gate.name = 'courtyard-main-gate';
        for (const side of [-1, 1]) {
            const footing = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.4, 0.26, 10), stone);
            footing.position.set(side * 2.55, 0.37, 0);
            const post = new THREE.Mesh(new THREE.CylinderGeometry(0.23, 0.29, 3.8, 12), timber);
            post.position.set(side * 2.55, 2.25, 0);
            gate.add(footing, post);
        }
        const lintel = new THREE.Mesh(new THREE.BoxGeometry(5.8, 0.38, 0.48), timber);
        lintel.position.y = 4.12;
        gate.add(lintel);
        this.addHanokRoof(gate, 7.1, 3.2, 4.45);
        gate.position.set(0, 0.24, -10.1);
        group.add(gate);
        return this.prepareDecoration(group);
    }

    private createHanokLibraryDecoration(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'two-story-palace-library';
        const granite = new THREE.MeshStandardMaterial({ color: 0x99958c, roughness: 0.98 });
        const timber = this.createHeritageMaterial('korean-timber.webp', 0x855038, 2.8, 1.2, 0.82);
        const paper = this.createHeritageMaterial('hanji-paper.webp', 0xf4ead3, 1.8, 1.8, 0.88);
        const green = new THREE.MeshStandardMaterial({ color: 0x27665b, roughness: 0.68 });
        const red = new THREE.MeshStandardMaterial({ color: 0x913a31, roughness: 0.72 });
        const plaqueMaterial = new THREE.MeshStandardMaterial({ color: 0x17342d, roughness: 0.58, metalness: 0.08 });
        const bookMaterials = [
            new THREE.MeshStandardMaterial({ color: 0x74412c, roughness: 0.84 }),
            new THREE.MeshStandardMaterial({ color: 0x34564c, roughness: 0.8 }),
            new THREE.MeshStandardMaterial({ color: 0x8a3430, roughness: 0.8 })
        ];

        const terrace = new THREE.Mesh(new THREE.BoxGeometry(12.6, 0.38, 8.2), granite);
        terrace.position.y = 0.19;
        const lowerDeck = new THREE.Mesh(new THREE.BoxGeometry(11.6, 0.34, 7.2), timber);
        lowerDeck.position.y = 0.62;
        const upperDeck = new THREE.Mesh(new THREE.BoxGeometry(11.4, 0.42, 6.9), timber);
        upperDeck.position.y = 4.02;
        group.add(terrace, lowerDeck, upperDeck);

        for (const x of [-5, -3, -1, 1, 3, 5]) {
            for (const z of [-2.75, 2.75]) {
                const footing = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.34, 0.22, 10), granite);
                footing.position.set(x, 0.82, z);
                const lowerPost = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.23, 3.08, 10), red);
                lowerPost.position.set(x, 2.3, z);
                const upperPost = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.21, 2.82, 10), red);
                upperPost.position.set(x, 5.45, z);
                group.add(footing, lowerPost, upperPost);
            }
        }

        const upperRoom = new THREE.Mesh(new THREE.BoxGeometry(9.8, 2.62, 5.15), paper);
        upperRoom.position.y = 5.35;
        group.add(upperRoom);
        for (const x of [-4, -2, 0, 2, 4]) {
            const lattice = new THREE.Mesh(new THREE.BoxGeometry(1.5, 2.12, 0.08), paper);
            lattice.position.set(x, 5.33, -2.65);
            group.add(lattice);
            for (const dx of [-0.48, 0, 0.48]) {
                const mullion = new THREE.Mesh(new THREE.BoxGeometry(0.045, 2.04, 0.045), timber);
                mullion.position.set(x + dx, 5.33, -2.72);
                group.add(mullion);
            }
            for (const y of [4.65, 5.32, 5.99]) {
                const rail = new THREE.Mesh(new THREE.BoxGeometry(1.42, 0.045, 0.045), timber);
                rail.position.set(x, y, -2.72);
                group.add(rail);
            }
        }
        for (const x of [-3.55, -1.18, 1.18, 3.55]) {
            const shelf = new THREE.Mesh(new THREE.BoxGeometry(1.75, 1.22, 0.42), timber);
            shelf.position.set(x, 5.17, 2.46);
            group.add(shelf);
            for (let index = 0; index < 5; index++) {
                const book = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.76 + (index % 2) * 0.12, 0.24), bookMaterials[index % bookMaterials.length]);
                book.position.set(x - 0.58 + index * 0.29, 5.17, 2.22);
                group.add(book);
            }
        }

        for (const z of [-3.15, 3.15]) {
            const rail = new THREE.Mesh(new THREE.BoxGeometry(10.65, 0.13, 0.13), green);
            rail.position.set(0, 4.65, z);
            group.add(rail);
            for (const x of [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5]) {
                const baluster = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.72, 0.11), timber);
                baluster.position.set(x, 4.35, z);
                group.add(baluster);
            }
        }
        for (const [index, x] of [-4.8, -3.2, -1.6, 0, 1.6, 3.2, 4.8].entries()) {
            this.addHanokBracketCluster(group, x, 6.75, -2.82, -1, timber, green, red, index % 2 === 0);
            this.addHanokBracketCluster(group, x, 6.75, 2.82, 1, timber, green, red, index % 2 !== 0);
        }
        const plaque = new THREE.Mesh(new THREE.BoxGeometry(2.7, 0.64, 0.16), plaqueMaterial);
        plaque.name = 'library-hyeonpan';
        plaque.position.set(0, 6.58, -2.94);
        group.add(plaque);
        for (let index = 0; index < 5; index++) {
            const step = new THREE.Mesh(new THREE.BoxGeometry(3.6 + index * 0.36, 0.17, 0.48), granite);
            step.position.set(0, 0.09 + index * 0.16, -5.2 + index * 0.38);
            group.add(step);
        }
        this.addHanokRoof(group, 13.2, 7.45, 7.18);
        return this.prepareDecoration(group);
    }

    private createHanokStudyDecoration(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'scholar-study-hanok';
        const stone = new THREE.MeshStandardMaterial({ color: 0x918b80, roughness: 0.98 });
        const clay = new THREE.MeshStandardMaterial({ color: 0x8b6549, roughness: 0.96 });
        const timber = this.createHeritageMaterial('korean-timber.webp', 0x825038, 2.2, 1.1, 0.84);
        const paper = this.createHeritageMaterial('hanji-paper.webp', 0xf4ead7, 1.6, 1.6, 0.88);
        const green = new THREE.MeshStandardMaterial({ color: 0x315f55, roughness: 0.72 });
        const red = new THREE.MeshStandardMaterial({ color: 0x8e3a31, roughness: 0.75 });
        const plinth = new THREE.Mesh(new THREE.BoxGeometry(9.7, 0.42, 6.8), stone);
        plinth.position.y = 0.21;
        const room = new THREE.Mesh(new THREE.BoxGeometry(7.9, 3.15, 4.95), paper);
        room.position.set(0.6, 2.1, 0.35);
        const porch = new THREE.Mesh(new THREE.BoxGeometry(8.6, 0.28, 1.55), timber);
        porch.position.set(0, 0.66, -2.72);
        group.add(plinth, room, porch);
        for (const x of [-3.7, -1.85, 0, 1.85, 3.7]) {
            const footing = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.31, 0.2, 10), stone);
            footing.position.set(x, 0.76, -2.3);
            const post = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.21, 3.55, 10), timber);
            post.position.set(x, 2.43, -2.3);
            group.add(footing, post);
        }
        for (const x of [-2.75, -0.92, 0.92, 2.75]) {
            const door = new THREE.Mesh(new THREE.BoxGeometry(1.54, 2.48, 0.08), paper);
            door.position.set(x, 2.07, -2.22);
            group.add(door);
            for (const dx of [-0.5, 0, 0.5]) {
                const mullion = new THREE.Mesh(new THREE.BoxGeometry(0.04, 2.38, 0.04), timber);
                mullion.position.set(x + dx, 2.07, -2.29);
                group.add(mullion);
            }
            for (const y of [1.36, 1.92, 2.48, 3.04]) {
                const lattice = new THREE.Mesh(new THREE.BoxGeometry(1.46, 0.04, 0.04), timber);
                lattice.position.set(x, y, -2.29);
                group.add(lattice);
            }
        }
        const chimneyBase = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.82, 1.35, 8), clay);
        chimneyBase.position.set(4.5, 0.86, 1.85);
        const chimney = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.52, 1.78, 8), clay);
        chimney.position.set(4.5, 2.34, 1.85);
        const chimneyCap = new THREE.Mesh(new THREE.CylinderGeometry(0.68, 0.54, 0.22, 8), stone);
        chimneyCap.position.set(4.5, 3.32, 1.85);
        group.add(chimneyBase, chimney, chimneyCap);
        for (const [index, x] of [-3.5, -2.32, -1.16, 0, 1.16, 2.32, 3.5].entries()) {
            this.addHanokBracketCluster(group, x, 3.84, -2.35, -1, timber, green, red, index % 2 === 0);
        }
        const writingDesk = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.16, 0.86), timber);
        writingDesk.position.set(-2.2, 0.86, -3.18);
        const inkStone = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.08, 0.25), new THREE.MeshStandardMaterial({ color: 0x23292b, roughness: 0.64 }));
        inkStone.position.set(-2.2, 0.99, -3.18);
        group.add(writingDesk, inkStone);
        this.addHanokRoof(group, 10.5, 6.6, 4.18);
        return this.prepareDecoration(group);
    }

    private createLotusPondDecoration(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'palace-lotus-pond';
        const bank = new THREE.MeshStandardMaterial({ color: 0x87877d, roughness: 0.98, flatShading: true });
        const water = new THREE.MeshPhysicalMaterial({ color: 0x2a7c81, roughness: 0.18, metalness: 0.04, transmission: 0.08, transparent: true, opacity: 0.86, clearcoat: 0.72, clearcoatRoughness: 0.18 });
        const leaf = new THREE.MeshStandardMaterial({ color: 0x3f7d48, roughness: 0.82, side: THREE.DoubleSide });
        const petal = new THREE.MeshStandardMaterial({ color: 0xf4a8bd, roughness: 0.72 });
        const center = new THREE.MeshStandardMaterial({ color: 0xf1c84a, roughness: 0.6 });
        const waterSurface = new THREE.Mesh(new THREE.CircleGeometry(6.2, 48), water);
        waterSurface.rotation.x = -Math.PI / 2;
        waterSurface.scale.set(1.28, 0.82, 1);
        waterSurface.position.y = 0.08;
        group.add(waterSurface);
        const stoneRing = new THREE.Mesh(new THREE.TorusGeometry(6.32, 0.42, 8, 48), bank);
        stoneRing.rotation.x = Math.PI / 2;
        stoneRing.scale.set(1.28, 0.82, 1);
        stoneRing.position.y = 0.16;
        group.add(stoneRing);
        const lotusPositions: Array<[number, number, number, boolean]> = [
            [-4.2, -1.5, 0.8, true], [-2.1, 1.75, 0.64, false], [0.2, -1.1, 0.92, true],
            [2.35, 1.55, 0.72, true], [4.15, -0.65, 0.58, false], [1.2, 2.55, 0.52, false]
        ];
        lotusPositions.forEach(([x, z, scale, flowering], lotusIndex) => {
            const pad = new THREE.Mesh(new THREE.CircleGeometry(0.62 * scale, 24, 0.22, Math.PI * 1.78), leaf);
            pad.rotation.x = -Math.PI / 2;
            pad.rotation.z = lotusIndex * 0.82;
            pad.position.set(x, 0.14 + lotusIndex * 0.002, z);
            group.add(pad);
            if (!flowering) return;
            for (let petalIndex = 0; petalIndex < 8; petalIndex++) {
                const angle = petalIndex / 8 * Math.PI * 2;
                const lotusPetal = new THREE.Mesh(new THREE.SphereGeometry(0.17 * scale, 10, 8), petal);
                lotusPetal.scale.set(0.72, 0.38, 1.55);
                lotusPetal.position.set(x + Math.cos(angle) * 0.22 * scale, 0.28, z + Math.sin(angle) * 0.22 * scale);
                lotusPetal.rotation.y = -angle;
                lotusPetal.rotation.x = 0.34;
                group.add(lotusPetal);
            }
            const flowerCenter = new THREE.Mesh(new THREE.SphereGeometry(0.13 * scale, 10, 8), center);
            flowerCenter.position.set(x, 0.34, z);
            group.add(flowerCenter);
        });
        for (const [x, z, scale] of [[-5.1, 2.85, 0.9], [-3.9, 2.5, 0.72], [-2.8, 2.12, 0.62], [-1.8, 1.72, 0.56]] as Array<[number, number, number]>) {
            const steppingStone = new THREE.Mesh(new THREE.CylinderGeometry(0.72 * scale, 0.82 * scale, 0.2, 9), bank);
            steppingStone.position.set(x, 0.19, z);
            steppingStone.rotation.y = x * 0.17;
            group.add(steppingStone);
        }
        return this.prepareDecoration(group);
    }

    private createStoneBridgeDecoration(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'low-arched-stone-bridge';
        const granite = new THREE.MeshStandardMaterial({ color: 0x9b978d, roughness: 0.99, flatShading: true });
        const darkStone = new THREE.MeshStandardMaterial({ color: 0x706f69, roughness: 1, flatShading: true });
        const deck = new THREE.Mesh(new THREE.BoxGeometry(10.4, 0.42, 3.6), granite);
        deck.position.y = 1.62;
        group.add(deck);
        for (const x of [-4.35, 0, 4.35]) {
            const pier = new THREE.Mesh(new THREE.BoxGeometry(1.15, 1.45, 3.3), darkStone);
            pier.position.set(x, 0.73, 0);
            group.add(pier);
            const cap = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.22, 3.55), granite);
            cap.position.set(x, 1.47, 0);
            group.add(cap);
        }
        for (let index = -5; index <= 5; index++) {
            const crown = 0.22 * (1 - Math.pow(index / 5, 2));
            const paving = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.14, 3.18), index % 2 === 0 ? granite : darkStone);
            paving.position.set(index * 0.92, 1.9 + crown, 0);
            paving.rotation.z = -index * 0.008;
            group.add(paving);
        }
        for (const z of [-1.72, 1.72]) {
            const rail = new THREE.Mesh(new THREE.BoxGeometry(9.7, 0.18, 0.18), darkStone);
            rail.position.set(0, 2.68, z);
            group.add(rail);
            for (const x of [-4.6, -3.45, -2.3, -1.15, 0, 1.15, 2.3, 3.45, 4.6]) {
                const baluster = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.16, 0.78, 8), granite);
                baluster.position.set(x, 2.3, z);
                const finial = new THREE.Mesh(new THREE.SphereGeometry(0.17, 9, 7), granite);
                finial.position.set(x, 2.72, z);
                group.add(baluster, finial);
            }
        }
        return this.prepareDecoration(group);
    }

    private createHarborWarehouseDecoration(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'joseon-harbor-warehouse';
        const stone = new THREE.MeshStandardMaterial({ color: 0x817c70, roughness: 0.98 });
        const timber = this.createHeritageMaterial('korean-timber.webp', 0x5d3a27, 2.2, 1.1, 0.88);
        const wall = new THREE.MeshStandardMaterial({ color: 0xb59b72, roughness: 0.94 });
        const green = new THREE.MeshStandardMaterial({ color: 0x315b50, roughness: 0.74 });
        const red = new THREE.MeshStandardMaterial({ color: 0x82372e, roughness: 0.76 });
        const base = new THREE.Mesh(new THREE.BoxGeometry(9.5, 0.45, 6.4), stone);
        base.position.y = 0.22;
        const body = new THREE.Mesh(new THREE.BoxGeometry(8.8, 4.2, 5.8), wall);
        body.position.y = 2.55;
        group.add(base, body);
        for (const x of [-4.15, 0, 4.15]) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.34, 4.7, 0.42), timber);
            post.position.set(x, 2.65, -2.98);
            group.add(post);
        }
        const door = new THREE.Mesh(new THREE.BoxGeometry(3.2, 3.45, 0.16), timber);
        door.position.set(0, 2.08, -3.02);
        group.add(door);
        for (const y of [0.95, 1.75, 2.55, 3.35]) {
            const brace = new THREE.Mesh(new THREE.BoxGeometry(3.05, 0.11, 0.24), red);
            brace.position.set(0, y, -3.13);
            group.add(brace);
        }
        for (const [index, x] of [-4, -2, 0, 2, 4].entries()) {
            this.addHanokBracketCluster(group, x, 4.62, -2.95, -1, timber, green, red, index % 2 === 0);
        }
        this.addHanokRoof(group, 10.8, 6.65, 4.92);
        for (const [x, z, scale] of [[-3.2, -4.2, 1], [2.9, -4.1, 0.8], [1.2, -4.6, 0.6]] as Array<[number, number, number]>) {
            const crate = new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.25, 1.4), timber);
            crate.position.set(x, 0.63 * scale, z);
            crate.scale.setScalar(scale);
            group.add(crate);
        }
        for (const x of [-3.35, 3.35]) {
            const sack = new THREE.Mesh(new THREE.SphereGeometry(0.68, 12, 9), new THREE.MeshStandardMaterial({ color: 0xa88b63, roughness: 1 }));
            sack.scale.set(0.72, 1.05, 0.54);
            sack.position.set(x, 0.72, -3.7);
            group.add(sack);
        }
        return this.prepareDecoration(group);
    }

    private createFishingBoatDecoration(): THREE.Group {
        const group = new THREE.Group();
        const hullMaterial = new THREE.MeshStandardMaterial({ color: 0x5b3522, roughness: 0.82 });
        const trimMaterial = new THREE.MeshStandardMaterial({ color: 0x9a633b, roughness: 0.78 });
        const sailMaterial = new THREE.MeshStandardMaterial({ color: 0xe8dfc9, roughness: 0.88, side: THREE.DoubleSide });
        const hull = new THREE.Mesh(new THREE.CapsuleGeometry(1.25, 4.8, 5, 12), hullMaterial);
        hull.rotation.z = Math.PI / 2;
        hull.scale.z = 0.72;
        hull.position.y = 0.72;
        group.add(hull);
        const deck = new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.22, 1.6), trimMaterial);
        deck.position.y = 1.18;
        group.add(deck);
        for (const x of [-2.05, -1.02, 0, 1.02, 2.05]) {
            const rib = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.42, 1.88), trimMaterial);
            rib.position.set(x, 1.04, 0);
            group.add(rib);
        }
        const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.14, 6.2, 8), trimMaterial);
        mast.position.y = 4.15;
        group.add(mast);
        const sailShape = new THREE.Shape();
        sailShape.moveTo(0, 0);
        sailShape.lineTo(0, 4.5);
        sailShape.lineTo(2.55, 0.65);
        sailShape.closePath();
        const sail = new THREE.Mesh(new THREE.ShapeGeometry(sailShape), sailMaterial);
        sail.position.set(0.12, 1.75, 0);
        sail.rotation.y = Math.PI / 2;
        group.add(sail);
        for (let index = 0; index < 5; index++) {
            const batten = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 2.25 - index * 0.34, 6), trimMaterial);
            batten.rotation.z = Math.PI / 2;
            batten.rotation.y = Math.PI / 2;
            batten.position.set(0.38 + index * 0.28, 2.24 + index * 0.72, 0.01);
            group.add(batten);
        }
        for (const side of [-1, 1]) {
            const oar = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.065, 4.2, 7), trimMaterial);
            oar.rotation.z = side * 1.12;
            oar.position.set(0.15, 1.28, side * 1.25);
            const rope = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 5.2, 6), new THREE.MeshStandardMaterial({ color: 0x806a4d, roughness: 0.98 }));
            rope.rotation.z = side * 0.43;
            rope.position.set(side * 1.08, 3.9, 0);
            group.add(oar, rope);
        }
        return this.prepareDecoration(group);
    }

    private createJangdokDecoration(): THREE.Group {
        const group = new THREE.Group();
        const stone = new THREE.MeshStandardMaterial({ color: 0x81776a, roughness: 0.95 });
        const clayColors = [0x6d351f, 0x82442c, 0x563024, 0x985334, 0x6f3d2c];
        const platform = new THREE.Mesh(new THREE.BoxGeometry(5.8, 0.35, 3.4), stone);
        platform.position.y = 0.18;
        group.add(platform);
        const positions: Array<[number, number, number]> = [[-1.8, 0.8, -0.7], [0, 1.05, -0.65], [1.8, 0.72, -0.55], [-0.9, 0.65, 0.8], [1, 0.85, 0.75]];
        positions.forEach(([x, size, z], index) => {
            const potGroup = new THREE.Group();
            const clay = new THREE.MeshStandardMaterial({ color: clayColors[index], roughness: 0.48, metalness: 0.03 });
            const body = new THREE.Mesh(new THREE.SphereGeometry(size, 18, 14), clay);
            body.scale.y = 1.12;
            body.position.y = size * 1.05;
            potGroup.add(body);
            const neck = new THREE.Mesh(new THREE.CylinderGeometry(size * 0.55, size * 0.62, size * 0.38, 18), clay);
            neck.position.y = size * 1.78;
            potGroup.add(neck);
            const lid = new THREE.Mesh(new THREE.CylinderGeometry(size * 0.66, size * 0.58, size * 0.18, 18), clay);
            lid.position.y = size * 2.02;
            potGroup.add(lid);
            potGroup.position.set(x, 0.35, z);
            group.add(potGroup);
        });
        return this.prepareDecoration(group);
    }

    private createStoneLanternDecoration(): THREE.Group {
        const group = new THREE.Group();
        const stone = new THREE.MeshStandardMaterial({ color: 0x9a968b, roughness: 1, flatShading: true });
        const glow = new THREE.MeshStandardMaterial({ color: 0xffe7a0, emissive: 0xf59e0b, emissiveIntensity: 1.4, roughness: 0.35 });
        const base = new THREE.Mesh(new THREE.CylinderGeometry(1.15, 1.35, 0.45, 8), stone);
        base.position.y = 0.23;
        const column = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.52, 2.2, 8), stone);
        column.position.y = 1.55;
        const chamber = new THREE.Mesh(new THREE.BoxGeometry(1.55, 1.35, 1.55), stone);
        chamber.position.y = 3.1;
        const light = new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.7, 1.58), glow);
        light.position.y = 3.1;
        const roof = new THREE.Mesh(new THREE.ConeGeometry(1.45, 0.7, 4), stone);
        roof.position.y = 4.08;
        roof.rotation.y = Math.PI / 4;
        const top = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), stone);
        top.position.y = 4.63;
        group.add(base, column, chamber, light, roof, top);
        return this.prepareDecoration(group);
    }

    private createWoodenFenceDecoration(): THREE.Group {
        const group = new THREE.Group();
        const timber = new THREE.MeshStandardMaterial({ color: 0x72472d, roughness: 0.88 });
        const stone = new THREE.MeshStandardMaterial({ color: 0x8a8175, roughness: 0.98 });
        const foundation = new THREE.Mesh(new THREE.BoxGeometry(8.5, 0.35, 0.8), stone);
        foundation.position.y = 0.18;
        group.add(foundation);
        for (const x of [-4, -2, 0, 2, 4]) {
            const post = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.23, 2.35, 8), timber);
            post.position.set(x, 1.35, 0);
            group.add(post);
        }
        for (const y of [0.85, 1.65]) {
            const rail = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 8.2, 8), timber);
            rail.position.y = y;
            rail.rotation.z = Math.PI / 2;
            group.add(rail);
        }
        return this.prepareDecoration(group);
    }

    private addHanokBracketCluster(
        group: THREE.Group,
        x: number,
        y: number,
        z: number,
        outward: number,
        timber: THREE.MeshStandardMaterial,
        greenPaint: THREE.MeshStandardMaterial,
        redPaint: THREE.MeshStandardMaterial,
        alternate: boolean
    ) {
        const lowerBlock = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.34, 0.5), alternate ? redPaint : greenPaint);
        lowerBlock.position.set(x, y + 0.12, z + outward * 0.16);
        const lowerArm = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.16, 0.9), alternate ? greenPaint : redPaint);
        lowerArm.position.set(x, y + 0.38, z + outward * 0.3);
        lowerArm.rotation.x = outward * -0.08;
        const upperBlock = new THREE.Mesh(new THREE.BoxGeometry(0.27, 0.28, 0.66), timber);
        upperBlock.position.set(x, y + 0.55, z + outward * 0.42);
        const upperArm = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.13, 1.08), alternate ? redPaint : greenPaint);
        upperArm.position.set(x, y + 0.72, z + outward * 0.53);
        upperArm.rotation.x = outward * -0.12;
        const pendant = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.14, 0.32, 8), alternate ? greenPaint : redPaint);
        pendant.position.set(x, y - 0.09, z + outward * 0.34);
        group.add(lowerBlock, lowerArm, upperBlock, upperArm, pendant);
    }

    private createCurvedHanokRoofGeometry(width: number, depth: number, y: number, side: number): THREE.BufferGeometry {
        const xSegments = this.effectiveGraphicsQuality === 'high' ? 24 : (this.effectiveGraphicsQuality === 'low' ? 8 : 16);
        const slopeSegments = this.effectiveGraphicsQuality === 'high' ? 10 : (this.effectiveGraphicsQuality === 'low' ? 5 : 8);
        const vertices: number[] = [];
        const uvs: number[] = [];
        const indices: number[] = [];
        for (let row = 0; row <= slopeSegments; row++) {
            const u = row / slopeSegments;
            for (let column = 0; column <= xSegments; column++) {
                const xRatio = column / xSegments;
                const x = (xRatio - 0.5) * width;
                const cornerLift = 0.28 * Math.pow(Math.abs(x / (width / 2)), 3) * Math.pow(u, 1.7);
                const roofCurve = 0.15 * Math.pow(u, 4);
                const height = y + 0.75 * (1 - u) - 0.44 * u + roofCurve + cornerLift;
                const z = side * depth * 0.52 * u;
                vertices.push(x, height, z);
                uvs.push(xRatio, 1 - u);
            }
        }
        const rowSize = xSegments + 1;
        for (let row = 0; row < slopeSegments; row++) {
            for (let column = 0; column < xSegments; column++) {
                const a = row * rowSize + column;
                const b = a + 1;
                const c = a + rowSize;
                const d = c + 1;
                if (side > 0) indices.push(a, c, b, b, c, d);
                else indices.push(a, b, c, b, d, c);
            }
        }
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();
        return geometry;
    }

    private addHanokRoof(group: THREE.Group, width: number, depth: number, y: number) {
        const tile = this.createHeritageMaterial('korean-roof-tile.webp', 0x89969d, Math.max(2, width / 2.2), Math.max(2, depth / 2.4), 0.64);
        tile.side = THREE.DoubleSide;
        const ridge = new THREE.MeshStandardMaterial({ color: 0x182126, roughness: 0.56 });
        const eavePaint = new THREE.MeshStandardMaterial({ color: 0x285e55, roughness: 0.68 });
        const redPaint = new THREE.MeshStandardMaterial({ color: 0x8f332b, roughness: 0.7 });
        const gableWall = new THREE.MeshStandardMaterial({ color: 0xe7dcc8, roughness: 0.9, side: THREE.DoubleSide });
        const rafterWood = this.createHeritageMaterial('korean-timber.webp', 0x7c4a32, 2.2, 1, 0.84);
        const ornament = new THREE.MeshStandardMaterial({ color: 0x232b2f, roughness: 0.6, metalness: 0.06 });
        const detailed = this.effectiveGraphicsQuality !== 'low';
        const tileCount = Math.max(12, Math.min(this.effectiveGraphicsQuality === 'high' ? 30 : 22, Math.round(width / (detailed ? 0.52 : 0.9))));
        const tileXs = Array.from({ length: tileCount }, (_, index) => -width / 2 + (index + 0.5) * width / tileCount);
        const matrixDummy = new THREE.Object3D();

        for (const side of [-1, 1]) {
            const slope = new THREE.Mesh(this.createCurvedHanokRoofGeometry(width, depth, y, side), tile);
            slope.name = side < 0 ? 'front-curved-giwa-roof' : 'rear-curved-giwa-roof';
            group.add(slope);

            const eavePoints: THREE.Vector3[] = [];
            for (let index = 0; index <= 10; index++) {
                const x = -width / 2 + width * index / 10;
                const cornerLift = 0.28 * Math.pow(Math.abs(x / (width / 2)), 3);
                eavePoints.push(new THREE.Vector3(x, y - 0.29 + cornerLift, side * depth * 0.535));
            }
            const eaveCurve = new THREE.CatmullRomCurve3(eavePoints, false, 'catmullrom', 0.45);
            const paintedFascia = new THREE.Mesh(new THREE.TubeGeometry(eaveCurve, 24, 0.105, 7, false), eavePaint);
            paintedFascia.name = 'curved-dancheong-eave';
            group.add(paintedFascia);

            const tileRunPoints: THREE.Vector3[] = [];
            for (let index = 0; index <= 8; index++) {
                const u = index / 8;
                const height = y + 0.75 * (1 - u) - 0.44 * u + 0.15 * Math.pow(u, 4);
                tileRunPoints.push(new THREE.Vector3(0, height + 0.055, side * depth * 0.52 * u));
            }
            if (detailed) {
                const tileRunGeometry = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(tileRunPoints), 10, 0.052, 6, false);
                const tileRuns = new THREE.InstancedMesh(tileRunGeometry, ridge, tileXs.length);
                tileRuns.name = 'convex-giwa-tile-rows';
                tileXs.forEach((x, index) => {
                    const cornerLift = 0.28 * Math.pow(Math.abs(x / (width / 2)), 3);
                    matrixDummy.position.set(x, cornerLift, 0);
                    matrixDummy.rotation.set(0, 0, 0);
                    matrixDummy.scale.set(1, 1, 1);
                    matrixDummy.updateMatrix();
                    tileRuns.setMatrixAt(index, matrixDummy.matrix);
                });
                tileRuns.instanceMatrix.needsUpdate = true;
                group.add(tileRuns);
            }

            const endTileGeometry = new THREE.CylinderGeometry(0.14, 0.14, 0.16, 12);
            const endTiles = new THREE.InstancedMesh(endTileGeometry, ridge, tileXs.length);
            endTiles.name = 'round-wadang-end-tiles';
            tileXs.forEach((x, index) => {
                const cornerLift = 0.28 * Math.pow(Math.abs(x / (width / 2)), 3);
                matrixDummy.position.set(x, y - 0.25 + cornerLift, side * depth * 0.55);
                matrixDummy.rotation.set(Math.PI / 2, 0, 0);
                matrixDummy.scale.set(1, 1, 1);
                matrixDummy.updateMatrix();
                endTiles.setMatrixAt(index, matrixDummy.matrix);
            });
            endTiles.instanceMatrix.needsUpdate = true;
            group.add(endTiles);

            const rafterCount = detailed ? tileXs.length : Math.ceil(tileXs.length / 2);
            const rafterGeometry = new THREE.BoxGeometry(0.085, 0.1, depth * 0.2);
            const rafters = new THREE.InstancedMesh(rafterGeometry, rafterWood, rafterCount);
            rafters.name = 'exposed-eave-rafters';
            const rafterXs = detailed ? tileXs : tileXs.filter((_, index) => index % 2 === 0);
            rafterXs.forEach((x, index) => {
                const cornerLift = 0.22 * Math.pow(Math.abs(x / (width / 2)), 3);
                matrixDummy.position.set(x, y - 0.48 + cornerLift, side * depth * 0.49);
                matrixDummy.rotation.set(side * 0.18, 0, 0);
                matrixDummy.scale.set(1, 1, 1);
                matrixDummy.updateMatrix();
                rafters.setMatrixAt(index, matrixDummy.matrix);
            });
            rafters.instanceMatrix.needsUpdate = true;
            group.add(rafters);
        }

        for (const x of [-width / 2 - 0.015, width / 2 + 0.015]) {
            const gableGeometry = new THREE.BufferGeometry();
            gableGeometry.setAttribute('position', new THREE.Float32BufferAttribute([
                x, y - 0.15, -depth * 0.43,
                x, y - 0.15, depth * 0.43,
                x, y + 0.69, 0
            ], 3));
            gableGeometry.setIndex([0, 1, 2]);
            gableGeometry.computeVertexNormals();
            const gable = new THREE.Mesh(gableGeometry, gableWall);
            gable.name = 'plastered-gable-wall';
            group.add(gable);
            const gableBeam = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.14, depth * 0.88), rafterWood);
            gableBeam.position.set(x, y - 0.14, 0);
            const centerPost = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.82, 0.16), redPaint);
            centerPost.position.set(x, y + 0.25, 0);
            group.add(gableBeam, centerPost);
            if (detailed) {
                for (const side of [-1, 1]) {
                    const points: THREE.Vector3[] = [];
                    for (let index = 0; index <= 8; index++) {
                        const u = index / 8;
                        const height = y + 0.75 * (1 - u) - 0.44 * u + 0.15 * Math.pow(u, 4) + 0.28 * Math.pow(u, 1.7);
                        points.push(new THREE.Vector3(x, height + 0.08, side * depth * 0.52 * u));
                    }
                    const gableRidge = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 10, 0.085, 6, false), ridge);
                    gableRidge.name = 'curved-gable-ridge';
                    group.add(gableRidge);
                }
            }
        }

        const ridgeTile = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, width + 0.3, 12), ridge);
        ridgeTile.name = 'yongmaru-ridge';
        ridgeTile.position.set(0, y + 0.76, 0);
        ridgeTile.rotation.z = Math.PI / 2;
        group.add(ridgeTile);
        for (const x of [-width / 2 - 0.2, width / 2 + 0.2]) {
            for (const z of [-depth * 0.54, depth * 0.54]) {
                const corner = new THREE.Mesh(new THREE.SphereGeometry(0.25, 12, 8), ridge);
                corner.name = 'upturned-eave-corner';
                corner.scale.set(1.5, 0.62, 0.82);
                corner.position.set(x, y + 0.02, z);
                group.add(corner);
            }
        }
        for (const x of [-width / 2 + 0.28, width / 2 - 0.28]) {
            const chwiduBase = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.28, 0.48, 8), ornament);
            chwiduBase.position.set(x, y + 1.02, 0);
            const chwiduCrest = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.62, 5), ornament);
            chwiduCrest.position.set(x, y + 1.5, 0);
            chwiduCrest.rotation.y = Math.PI / 5;
            group.add(chwiduBase, chwiduCrest);
        }
        if (detailed) {
            for (const side of [-1, 1]) {
                for (let index = 0; index < 3; index++) {
                    const japsang = new THREE.Mesh(new THREE.ConeGeometry(0.09 + index * 0.012, 0.26 + index * 0.04, 6), ornament);
                    japsang.position.set(side * (width / 2 - 0.95 - index * 0.42), y + 1.02, 0);
                    japsang.rotation.z = side * -0.08;
                    group.add(japsang);
                }
            }
        }
    }

    private createTreeDecoration(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'windswept-korean-pine';
        const bark = new THREE.MeshStandardMaterial({ color: 0x714124, roughness: 0.96, flatShading: true });
        const barkDark = new THREE.MeshStandardMaterial({ color: 0x3f2b20, roughness: 0.98, flatShading: true });
        const needleMaterials = [
            new THREE.MeshStandardMaterial({ color: 0x244f37, roughness: 0.9, flatShading: true }),
            new THREE.MeshStandardMaterial({ color: 0x356744, roughness: 0.88, flatShading: true }),
            new THREE.MeshStandardMaterial({ color: 0x173e2d, roughness: 0.92, flatShading: true })
        ];
        const trunkCurve = new THREE.CatmullRomCurve3([
            new THREE.Vector3(0, 0.05, 0),
            new THREE.Vector3(0.16, 1.6, 0.08),
            new THREE.Vector3(-0.18, 3.2, 0.02),
            new THREE.Vector3(0.22, 4.85, -0.12),
            new THREE.Vector3(0.1, 6.25, 0.04)
        ]);
        const trunk = new THREE.Mesh(new THREE.TubeGeometry(trunkCurve, 18, 0.42, 8, false), bark);
        group.add(trunk);
        for (let rootIndex = 0; rootIndex < 6; rootIndex++) {
            const angle = rootIndex / 6 * Math.PI * 2;
            const root = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.24, 1.65, 7), barkDark);
            root.rotation.z = Math.PI / 2.7;
            root.rotation.y = angle;
            root.position.set(Math.cos(angle) * 0.58, 0.18, Math.sin(angle) * 0.58);
            group.add(root);
        }
        const branches: Array<[number, number, number, number, number]> = [
            [-1.8, 4.05, 0.25, -0.68, 1.2], [1.95, 4.65, -0.2, 0.72, 1.15],
            [-1.4, 5.3, -0.42, -0.58, 0.95], [1.28, 5.75, 0.3, 0.54, 0.88]
        ];
        branches.forEach(([x, y, z, rotation, scale], branchIndex) => {
            const branch = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.2, 2.75 * scale, 8), bark);
            branch.rotation.z = rotation;
            branch.rotation.x = z * 0.3;
            branch.position.set(x * 0.48, y, z);
            group.add(branch);
            for (let padIndex = 0; padIndex < 3; padIndex++) {
                const crown = new THREE.Mesh(new THREE.SphereGeometry(0.9 - padIndex * 0.08, 10, 7), needleMaterials[(branchIndex + padIndex) % needleMaterials.length]);
                crown.scale.set(1.75, 0.5, 1.05);
                crown.position.set(x + (padIndex - 1) * 0.42, y + 0.25 + padIndex * 0.24, z + (padIndex % 2 === 0 ? 0.28 : -0.18));
                group.add(crown);
            }
        });
        const crown = new THREE.Mesh(new THREE.SphereGeometry(1.15, 11, 8), needleMaterials[0]);
        crown.scale.set(1.45, 0.72, 1.08);
        crown.position.set(0.18, 6.55, 0);
        group.add(crown);
        return this.prepareDecoration(group);
    }

    private createBuildingDecoration(): THREE.Group {
        const group = new THREE.Group();
        const bodyMaterial = new THREE.MeshStandardMaterial({
            color: 0x1e3a5f,
            emissive: 0x0c4a6e,
            emissiveIntensity: 0.55,
            roughness: 0.42,
            metalness: 0.55
        });
        const windowMaterial = new THREE.MeshStandardMaterial({
            color: 0x67e8f9,
            emissive: 0x06b6d4,
            emissiveIntensity: 2.2,
            roughness: 0.18
        });
        const body = new THREE.Mesh(new THREE.BoxGeometry(6.5, 12, 6.5), bodyMaterial);
        body.position.y = 6;
        group.add(body);
        for (let floor = 0; floor < 4; floor++) {
            for (const side of [-1, 1]) {
                const strip = new THREE.Mesh(new THREE.BoxGeometry(3.6, 0.48, 0.12), windowMaterial);
                strip.position.set(0, 2.2 + floor * 2.6, side * 3.31);
                group.add(strip);
            }
        }
        const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 3.6, 8), windowMaterial);
        antenna.position.y = 13.8;
        group.add(antenna);
        group.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (mesh.isMesh) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
            }
        });
        return group;
    }

    private createConeDecoration(): THREE.Group {
        const group = new THREE.Group();
        const rubber = new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.7, metalness: 0.08 });
        const orange = new THREE.MeshStandardMaterial({ color: 0xfb6a22, emissive: 0x9a3412, emissiveIntensity: 0.35, roughness: 0.52 });
        const reflective = new THREE.MeshStandardMaterial({ color: 0xf8fafc, emissive: 0xfef3c7, emissiveIntensity: 0.72, roughness: 0.28 });
        const base = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.16, 1.45), rubber);
        base.position.y = 0.08;
        const cone = new THREE.Mesh(new THREE.ConeGeometry(0.58, 1.75, 24), orange);
        cone.position.y = 1.02;
        const stripe = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.44, 0.28, 24), reflective);
        stripe.position.y = 0.8;
        group.add(base, cone, stripe);
        group.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (mesh.isMesh) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
            }
        });
        return group;
    }

    private async ensureKing2AssetLoaded(): Promise<void> {
        if (this.king2AssetSource || this.builtInCharacterTemplateCache.has('joseon-king-2')) return;
        if (!this.king2AssetLoadPromise) {
            this.king2AssetLoadPromise = (async () => {
                const asset = await new GLTFLoader().loadAsync(this.king2GlbPath);
                const model = asset.scene;
                model.name = 'public-king-character-2-source';
                model.userData['webAssetSource'] = this.king2GlbPath;
                this.prepareImportedCharacterAsset(model);
                const summary = this.inspectImportedModel(model);
                if (summary.meshCount === 0) {
                    this.disposeObjectTree(model, true);
                    throw new Error('조선 임금 GLB에 표시할 메시가 없습니다.');
                }
                this.king2AssetSource = model;
            })().catch((error) => {
                this.king2AssetLoadPromise = undefined;
                throw error;
            });
        }
        await this.king2AssetLoadPromise;
    }

    private cloneKing2Asset(): THREE.Group | null {
        if (!this.king2AssetSource) return null;
        const clone = this.king2AssetSource.clone(true);
        clone.name = 'public-king-character-2';
        // 공용 OBJ의 정면 축이 게임 캐릭터 기준과 반대이므로 이 모델만 보정한다.
        clone.rotation.y = Math.PI;
        clone.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (!mesh.isMesh) return;
            mesh.geometry = mesh.geometry.clone();
            mesh.material = Array.isArray(mesh.material)
                ? mesh.material.map((item) => item.clone())
                : mesh.material.clone();
        });
        return clone;
    }

    private async ensureHaetaeAssetLoaded(): Promise<void> {
        if (this.haetaeAssetSource || this.builtInCharacterTemplateCache.has('haetae')) return;
        if (!this.haetaeAssetLoadPromise) {
            this.haetaeAssetLoadPromise = (async () => {
                const asset = await new GLTFLoader().loadAsync(this.haetaeGlbPath);
                const model = asset.scene;
                model.name = 'public-haetae-character-source';
                model.userData['publicAssetSource'] = this.haetaeGlbPath;
                this.prepareImportedCharacterAsset(model);
                const summary = this.inspectImportedModel(model);
                if (summary.meshCount === 0) {
                    this.disposeObjectTree(model, true);
                    throw new Error('공용 해태 GLB에 표시할 메시가 없습니다.');
                }
                if (this.destroyed) {
                    this.disposeObjectTree(model, true);
                    throw new Error('화면이 종료되어 서울 해치 안내자 GLB 로딩을 중단했습니다.');
                }
                this.haetaeAssetSource = model;
            })().catch((error) => {
                this.haetaeAssetLoadPromise = undefined;
                throw error;
            });
        }
        await this.haetaeAssetLoadPromise;
    }

    private prepareImportedCharacterAsset(model: THREE.Group) {
        const maxAnisotropy = (this.renderer as any)?.capabilities?.getMaxAnisotropy?.() || 1;
        const anisotropy = Math.min(this.effectiveGraphicsQuality === 'high' ? 8 : 4, maxAnisotropy);
        model.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (!mesh.isMesh) return;
            mesh.castShadow = this.effectiveGraphicsQuality !== 'low';
            mesh.receiveShadow = this.effectiveGraphicsQuality !== 'low';
            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            materials.forEach((material: THREE.Material) => {
                const map = (material as THREE.MeshStandardMaterial).map;
                if (!map) return;
                map.colorSpace = THREE.SRGBColorSpace;
                map.anisotropy = anisotropy;
                map.needsUpdate = true;
            });
        });
    }

    private cloneHaetaeAsset(): THREE.Group | null {
        if (!this.haetaeAssetSource) return null;
        const clone = this.haetaeAssetSource.clone(true);
        clone.name = 'public-haetae-character';
        // 같은 공용 3D 생성 계열의 OBJ 축을 게임 캐릭터 정면(-Z)에 맞춘다.
        clone.rotation.y = Math.PI;
        clone.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (!mesh.isMesh) return;
            mesh.geometry = mesh.geometry.clone();
            mesh.material = Array.isArray(mesh.material)
                ? mesh.material.map((item) => item.clone())
                : mesh.material.clone();
        });
        return clone;
    }

    private createPlayer() {
        if (!this.scene) return;
        this.player = new THREE.Group();
        this.playerVisual = undefined;
        this.playerMountVisual = undefined;
        this.replacePlayerVisual(this.createBuiltInPlayerVisual(this.selectedBuiltInSkinId || 'joseon-king-2'));
        const trail = new THREE.PointLight(0x22d3ee, 8, 12, 2);
        trail.position.set(0, 1.1, 1.6);
        this.player.add(trail);
        this.scene.add(this.player);
    }

    private createBuiltInPlayerVisual(id: BuiltInSkinId): THREE.Group {
        const cacheableId = id === 'joseon-king-2' || id === 'sejong-the-great' || id === 'haetae' ? id : null;
        const cachedTemplate = cacheableId ? this.builtInCharacterTemplateCache.get(cacheableId) : undefined;
        if (cachedTemplate) return this.cloneBuiltInCharacterTemplate(cachedTemplate);
        const group = new THREE.Group();
        let resolvedCacheableAsset = true;
        const standard = (color: number, emissive: number = 0x000000, emissiveIntensity: number = 0.35) => new THREE.MeshStandardMaterial({
            color,
            emissive,
            emissiveIntensity,
            roughness: 0.32,
            metalness: 0.58
        });

        if (id === 'joseon-king-2') {
            const importedKing = this.cloneKing2Asset();
            if (importedKing) group.add(importedKing);
            else {
                group.add(this.createDeferredCharacterPlaceholder(id));
                resolvedCacheableAsset = false;
            }
        } else if (id === 'sejong-the-great') {
            group.add(this.createSejongCharacterVisual());
        } else if (id === 'haetae') {
            const importedHaetae = this.cloneHaetaeAsset();
            if (importedHaetae) group.add(importedHaetae);
            else {
                group.add(this.createDeferredCharacterPlaceholder(id));
                resolvedCacheableAsset = false;
            }
        } else if (id === 'neon-runner') {
            const bodyMaterial = standard(0x22d3ee, 0x0891b2, 0.82);
            const darkMaterial = standard(0x111827, 0x030712, 0.18);
            const visorMaterial = standard(0xfef08a, 0xf59e0b, 1.8);
            const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.56, 0.9, 6, 12), bodyMaterial);
            body.position.y = 1.35;
            group.add(body);
            const head = new THREE.Mesh(new THREE.SphereGeometry(0.56, 20, 16), darkMaterial);
            head.position.y = 2.25;
            group.add(head);
            const visor = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.22, 0.14), visorMaterial);
            visor.position.set(0, 2.29, -0.5);
            group.add(visor);
            const chestPlate = new THREE.Mesh(new THREE.BoxGeometry(0.64, 0.46, 0.13), darkMaterial);
            chestPlate.position.set(0, 1.48, -0.53);
            group.add(chestPlate);
            const chestCore = new THREE.Mesh(new THREE.OctahedronGeometry(0.13, 0), visorMaterial);
            chestCore.position.set(0, 1.49, -0.62);
            group.add(chestCore);
            const collar = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.065, 8, 24), visorMaterial);
            collar.position.y = 1.88;
            collar.rotation.x = Math.PI / 2;
            group.add(collar);
            for (const side of [-1, 1]) {
                const ear = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.12, 16), bodyMaterial);
                ear.position.set(side * 0.57, 2.26, 0);
                ear.rotation.z = Math.PI / 2;
                group.add(ear);
                const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.16, 0.78, 12), bodyMaterial);
                arm.position.set(side * 0.66, 1.34, 0.02);
                arm.rotation.z = side * 0.15;
                group.add(arm);
                const hand = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 8), darkMaterial);
                hand.position.set(side * 0.71, 0.93, 0);
                group.add(hand);
                const leg = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.72, 0.34), darkMaterial);
                leg.position.set(side * 0.28, 0.45, 0);
                group.add(leg);
                const boot = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.2, 0.56), bodyMaterial);
                boot.position.set(side * 0.28, 0.11, -0.09);
                group.add(boot);
            }
        } else if (id === 'coral-bot') {
            const coral = standard(0xfb7185, 0xbe123c, 0.92);
            const navy = standard(0x172554, 0x1e3a8a, 0.22);
            const glow = standard(0x67e8f9, 0x06b6d4, 1.65);
            const torso = new THREE.Mesh(new THREE.BoxGeometry(1.05, 1.15, 0.68), coral);
            torso.position.y = 1.38;
            group.add(torso);
            const head = new THREE.Mesh(new THREE.BoxGeometry(0.84, 0.62, 0.72), navy);
            head.position.y = 2.28;
            group.add(head);
            const visor = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.16, 0.1), glow);
            visor.position.set(0, 2.3, -0.41);
            group.add(visor);
            const core = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.12, 18), glow);
            core.rotation.x = Math.PI / 2;
            core.position.set(0, 1.43, -0.4);
            group.add(core);
            const chestPanel = new THREE.Mesh(new THREE.BoxGeometry(0.68, 0.58, 0.12), navy);
            chestPanel.position.set(0, 1.42, -0.4);
            group.add(chestPanel);
            core.position.z = -0.49;
            const waist = new THREE.Mesh(new THREE.BoxGeometry(0.84, 0.18, 0.58), glow);
            waist.position.y = 0.82;
            group.add(waist);
            const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 0.35, 8), navy);
            antenna.position.y = 2.76;
            group.add(antenna);
            const antennaLight = new THREE.Mesh(new THREE.SphereGeometry(0.11, 12, 8), glow);
            antennaLight.position.y = 2.98;
            group.add(antennaLight);
            for (const side of [-1, 1]) {
                const ear = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.13, 16), coral);
                ear.position.set(side * 0.49, 2.3, 0);
                ear.rotation.z = Math.PI / 2;
                group.add(ear);
                const shoulder = new THREE.Mesh(new THREE.SphereGeometry(0.19, 12, 8), coral);
                shoulder.position.set(side * 0.69, 1.68, 0);
                group.add(shoulder);
                const arm = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.82, 0.3), navy);
                arm.position.set(side * 0.72, 1.38, 0);
                group.add(arm);
                const hand = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.23, 0.36), coral);
                hand.position.set(side * 0.72, 0.91, -0.02);
                group.add(hand);
                const leg = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.72, 0.4), navy);
                leg.position.set(side * 0.3, 0.42, 0);
                group.add(leg);
                const foot = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.2, 0.58), coral);
                foot.position.set(side * 0.3, 0.1, -0.1);
                group.add(foot);
            }
        } else if (id === 'forest-scout') {
            const green = standard(0x4ade80, 0x15803d, 0.72);
            const deepGreen = standard(0x14532d, 0x052e16, 0.18);
            const face = standard(0xd9f99d, 0x84cc16, 0.82);
            const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.52, 0.82, 6, 12), green);
            body.position.y = 1.34;
            group.add(body);
            const hood = new THREE.Mesh(new THREE.SphereGeometry(0.62, 18, 14), deepGreen);
            hood.position.y = 2.27;
            group.add(hood);
            const facePlate = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.28, 0.13), face);
            facePlate.position.set(0, 2.24, -0.55);
            group.add(facePlate);
            for (const side of [-1, 1]) {
                const eye = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 8), deepGreen);
                eye.position.set(side * 0.17, 2.26, -0.63);
                group.add(eye);
            }
            const scarf = new THREE.Mesh(new THREE.TorusGeometry(0.46, 0.095, 9, 24), face);
            scarf.position.y = 1.88;
            scarf.rotation.x = Math.PI / 2;
            group.add(scarf);
            const backpack = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.92, 0.38), deepGreen);
            backpack.position.set(0, 1.42, 0.48);
            group.add(backpack);
            const belt = new THREE.Mesh(new THREE.BoxGeometry(0.88, 0.16, 0.6), deepGreen);
            belt.position.y = 0.92;
            group.add(belt);
            const leafStem = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.05, 0.32, 8), face);
            leafStem.position.set(0.12, 2.83, 0);
            leafStem.rotation.z = -0.3;
            group.add(leafStem);
            const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 8), green);
            leaf.position.set(0.22, 3.04, 0);
            leaf.scale.set(0.68, 1.35, 0.45);
            leaf.rotation.z = -0.55;
            group.add(leaf);
            for (const side of [-1, 1]) {
                const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.16, 0.78, 10), green);
                arm.position.set(side * 0.64, 1.35, 0);
                arm.rotation.z = side * 0.16;
                group.add(arm);
                const glove = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 8), deepGreen);
                glove.position.set(side * 0.69, 0.92, 0);
                group.add(glove);
                const boot = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.7, 0.42), deepGreen);
                boot.position.set(side * 0.28, 0.4, -0.02);
                group.add(boot);
                const toe = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.2, 0.58), green);
                toe.position.set(side * 0.28, 0.1, -0.12);
                group.add(toe);
            }
        } else if (id.startsWith('joseon-')) {
            const joseon = this.createJoseonCharacterVisual(id as JoseonSkinId);
            group.add(...[...joseon.children]);
        } else {
            const gold = standard(0xfacc15, 0xf59e0b, 1.05);
            const charcoal = standard(0x27272a, 0x18181b, 0.2);
            const coreMaterial = standard(0xfef9c3, 0xfacc15, 2.1);
            const body = new THREE.Mesh(new THREE.SphereGeometry(0.68, 20, 16), gold);
            body.position.y = 1.38;
            group.add(body);
            const ring = new THREE.Mesh(new THREE.TorusGeometry(0.84, 0.09, 10, 28), coreMaterial);
            ring.position.y = 1.38;
            ring.rotation.x = Math.PI / 2;
            group.add(ring);
            const head = new THREE.Mesh(new THREE.SphereGeometry(0.46, 18, 14), charcoal);
            head.position.y = 2.3;
            group.add(head);
            const visor = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.18, 0.12), coreMaterial);
            visor.position.set(0, 2.31, -0.42);
            group.add(visor);
            const chestCore = new THREE.Mesh(new THREE.SphereGeometry(0.21, 14, 10), coreMaterial);
            chestCore.position.set(0, 1.4, -0.63);
            group.add(chestCore);
            const secondRing = new THREE.Mesh(new THREE.TorusGeometry(0.76, 0.045, 8, 28), gold);
            secondRing.position.y = 1.38;
            secondRing.rotation.set(Math.PI / 2, 0.45, 0.3);
            group.add(secondRing);
            for (const side of [-1, 1]) {
                const shoulder = new THREE.Mesh(new THREE.SphereGeometry(0.22, 12, 8), gold);
                shoulder.position.set(side * 0.78, 1.45, 0);
                group.add(shoulder);
                const forearm = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.16, 0.66, 12), charcoal);
                forearm.position.set(side * 0.82, 1.1, 0);
                forearm.rotation.z = side * 0.08;
                group.add(forearm);
                const hand = new THREE.Mesh(new THREE.SphereGeometry(0.15, 12, 8), coreMaterial);
                hand.position.set(side * 0.85, 0.73, 0);
                group.add(hand);
                const leg = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.68, 0.38), charcoal);
                leg.position.set(side * 0.28, 0.4, 0);
                group.add(leg);
                const foot = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.2, 0.56), gold);
                foot.position.set(side * 0.28, 0.1, -0.1);
                group.add(foot);
                const orbitNode = new THREE.Mesh(new THREE.SphereGeometry(0.095, 10, 8), coreMaterial);
                orbitNode.position.set(side * 0.82, 1.38, side * 0.22);
                group.add(orbitNode);
            }
        }

        if (group.children.length === 0) {
            const pendingWrapper = new THREE.Group();
            pendingWrapper.name = 'pending-character-model';
            pendingWrapper.add(group);
            return pendingWrapper;
        }

        group.updateMatrixWorld(true);
        const sourceBox = new THREE.Box3().setFromObject(group);
        const sourceSize = sourceBox.getSize(new THREE.Vector3());
        const fixedScale = sourceSize.y > 0.001 ? 2.8 / sourceSize.y : 1;
        group.scale.setScalar(fixedScale);
        group.updateMatrixWorld(true);
        const fixedBox = new THREE.Box3().setFromObject(group);
        const fixedCenter = fixedBox.getCenter(new THREE.Vector3());
        group.position.set(-fixedCenter.x, -fixedBox.min.y, -fixedCenter.z);

        const wrapper = new THREE.Group();
        wrapper.add(group);
        wrapper.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (mesh.isMesh) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
            }
        });
        if (cacheableId && resolvedCacheableAsset) {
            wrapper.userData['builtInCharacterTemplate'] = cacheableId;
            this.builtInCharacterTemplateCache.set(cacheableId, wrapper);
            return this.cloneBuiltInCharacterTemplate(wrapper);
        }
        return wrapper;
    }

    private createDeferredCharacterPlaceholder(id: 'joseon-king-2' | 'haetae'): THREE.Group {
        const group = new THREE.Group();
        group.name = `deferred-${id}-placeholder`;
        const primary = new THREE.MeshStandardMaterial({
            color: id === 'haetae' ? 0x4f8f83 : 0x8f2f2a,
            emissive: id === 'haetae' ? 0x173f3a : 0x3f1110,
            emissiveIntensity: 0.24,
            roughness: 0.76
        });
        const accent = new THREE.MeshStandardMaterial({ color: 0xd7b45a, roughness: 0.54, metalness: 0.18 });
        if (id === 'haetae') {
            const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.48, 0.82, 4, 8), primary);
            body.rotation.x = Math.PI / 2;
            body.position.y = 0.86;
            const head = new THREE.Mesh(new THREE.SphereGeometry(0.43, 10, 8), primary);
            head.position.set(0, 1.2, -0.82);
            const horn = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.44, 7), accent);
            horn.position.set(0, 1.66, -0.86);
            group.add(body, head, horn);
        } else {
            const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.46, 0.9, 4, 8), primary);
            body.position.y = 1.18;
            const head = new THREE.Mesh(new THREE.SphereGeometry(0.4, 10, 8), primary);
            head.position.y = 2.12;
            const crown = new THREE.Mesh(new THREE.BoxGeometry(0.76, 0.28, 0.42), accent);
            crown.position.y = 2.57;
            group.add(body, head, crown);
        }
        group.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (!mesh.isMesh) return;
            mesh.castShadow = this.effectiveGraphicsQuality !== 'low';
            mesh.receiveShadow = false;
        });
        return group;
    }

    private cloneBuiltInCharacterTemplate(template: THREE.Group): THREE.Group {
        const clone = template.clone(true);
        clone.userData['sharedBuiltInCharacterResources'] = true;
        clone.animations = template.animations || [];
        return clone;
    }

    private createSejongCharacterVisual(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'sejong-the-great-character';
        group.userData['sourceSpec'] = '세종해태.txt';
        const material = (color: number, roughness: number, metalness: number = 0) => new THREE.MeshPhysicalMaterial({
            color,
            roughness,
            metalness,
            clearcoat: metalness > 0.2 ? 0.22 : 0.04,
            clearcoatRoughness: 0.42
        });
        const skin = material(0xc77a52, 0.72);
        const skinLight = material(0xf2ad80, 0.68);
        const red = material(0x6b0609, 0.52);
        const redLight = material(0xad0b0e, 0.56);
        const hair = material(0x070504, 0.78);
        const black = material(0x040303, 0.42);
        const gold = material(0xd17a0e, 0.28, 0.55);
        const goldDark = material(0x612e06, 0.34, 0.45);
        const ivory = material(0xe0d1ab, 0.65);
        const eyeWhite = material(0xebebe0, 0.4);
        const eyeDark = material(0x020201, 0.25);
        const shoeMaterial = material(0x090b0e, 0.5);
        const ellipsoid = (name: string, position: [number, number, number], scale: [number, number, number], meshMaterial: THREE.Material, segments: number = 32, rings: number = 20) => {
            const mesh = new THREE.Mesh(new THREE.SphereGeometry(1, segments, rings), meshMaterial);
            mesh.name = name;
            mesh.position.set(...position);
            mesh.scale.set(...scale);
            group.add(mesh);
            return mesh;
        };

        const robe = new THREE.Mesh(new THREE.CylinderGeometry(0.54, 0.72, 1.55, 48), red);
        robe.name = 'Sejong_Robe_Body';
        robe.position.set(0, 1.55, 0);
        const skirt = new THREE.Mesh(new THREE.CylinderGeometry(0.66, 0.88, 0.82, 48), red);
        skirt.name = 'Sejong_Robe_Skirt';
        skirt.position.set(0, 0.78, 0.02);
        const belt = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.58, 0.16, 48), goldDark);
        belt.name = 'Sejong_Royal_Belt';
        belt.position.set(0, 1.25, -0.01);
        group.add(robe, skirt, belt);

        ellipsoid('Sejong_Head', [0, 2.85, -0.02], [0.86, 0.9, 0.76], skin, 48, 32);
        for (const side of [-1, 1]) {
            ellipsoid(side < 0 ? 'Sejong_Ear_L' : 'Sejong_Ear_R', [side * 0.78, 2.88, -0.01], [0.19, 0.27, 0.12], skinLight, 24, 16);
            ellipsoid(side < 0 ? 'Sejong_EyeWhite_L' : 'Sejong_EyeWhite_R', [side * 0.3, 2.98, -0.735], [0.19, 0.23, 0.07], eyeWhite, 32, 20);
            ellipsoid(side < 0 ? 'Sejong_Pupil_L' : 'Sejong_Pupil_R', [side * 0.3, 2.98, -0.805], [0.085, 0.12, 0.035], eyeDark, 24, 16);
            ellipsoid('Sejong_Eye_Highlight', [side * 0.3 - 0.025, 3.035, -0.838], [0.025, 0.035, 0.012], eyeWhite, 16, 10);
            const brow = ellipsoid('Sejong_Eyebrow', [side * 0.3, 3.22, -0.765], [0.22, 0.045, 0.035], hair, 20, 12);
            brow.rotation.z = side * 0.08;
        }
        ellipsoid('Sejong_Nose', [0, 2.8, -0.79], [0.12, 0.16, 0.12], skinLight, 24, 16);
        ellipsoid('Sejong_Mouth', [0, 2.62, -0.78], [0.19, 0.055, 0.025], hair, 24, 12);
        const beardCenter = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.48, 24), hair);
        beardCenter.name = 'Sejong_Beard_Center';
        beardCenter.position.set(0, 2.35, -0.75);
        group.add(beardCenter);
        for (const side of [-1, 1]) {
            const beard = ellipsoid('Sejong_Beard_Side', [side * 0.18, 2.52, -0.75], [0.18, 0.26, 0.07], hair, 24, 16);
            beard.rotation.z = side * 0.2;
            const moustache = ellipsoid('Sejong_Moustache', [side * 0.09, 2.69, -0.82], [0.12, 0.055, 0.035], hair, 20, 12);
            moustache.rotation.z = side * 0.22;
        }

        const hatBase = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.48, 0.28, 48), black);
        hatBase.name = 'Ikseongwan_Base';
        hatBase.position.y = 3.76;
        const hatTop = new THREE.Mesh(new THREE.CylinderGeometry(0.31, 0.4, 0.3, 48), black);
        hatTop.name = 'Ikseongwan_Top';
        hatTop.position.y = 3.99;
        group.add(hatBase, hatTop);
        for (const side of [-1, 1]) {
            const wing = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.2, 0.16), black);
            wing.name = 'Ikseongwan_Wing';
            wing.position.set(side * 0.3, 4.04, 0.17);
            wing.rotation.z = side * 0.2;
            group.add(wing);
            ellipsoid('Ikseongwan_Gold_Pin', [side * 0.3, 4.08, 0.08], [0.055, 0.11, 0.035], gold, 20, 12);
        }

        for (const side of [-1, 1]) {
            const collar = new THREE.Mesh(new THREE.BoxGeometry(0.26, 1.12, 0.09), ivory);
            collar.name = 'Gonryongpo_Collar';
            collar.position.set(side * 0.22, 2.05, -0.54);
            collar.rotation.z = side * 0.31;
            group.add(collar);
            const sleeve = ellipsoid(side < 0 ? 'Sejong_Sleeve_L' : 'Sejong_Sleeve_R', [side * 0.78, 1.7, 0], [0.34, 0.62, 0.4], redLight, 32, 20);
            sleeve.rotation.z = side * -0.26;
            const cuff = ellipsoid('Sejong_Cuff', [side * 1.03, 1.48, -0.02], [0.25, 0.17, 0.34], ivory, 28, 18);
            cuff.rotation.z = side * -0.26;
            ellipsoid('Sejong_Hand', [side * 1.06, 1.3, -0.1], [0.16, 0.2, 0.15], skinLight, 24, 16);
        }
        ellipsoid('Royal_Collar_Ornament', [0, 2.04, -0.58], [0.12, 0.12, 0.045], gold, 24, 12);
        const emblem = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 0.055, 48), gold);
        emblem.name = 'Dragon_Chest_Emblem';
        emblem.rotation.x = Math.PI / 2;
        emblem.position.set(0, 1.84, -0.6);
        group.add(emblem);
        const dragonRing = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.025, 10, 32), goldDark);
        dragonRing.name = 'Dragon_Emblem_Ring';
        dragonRing.position.set(0, 1.84, -0.635);
        group.add(dragonRing);
        for (let angle = 0; angle < 360; angle += 60) {
            const radians = THREE.MathUtils.degToRad(angle);
            const ornament = ellipsoid('Dragon_Ornament', [0.12 * Math.cos(radians), 1.84 + 0.12 * Math.sin(radians), -0.66], [0.045, 0.075, 0.025], goldDark, 16, 10);
            ornament.rotation.z = radians;
        }
        for (const side of [-1, 1]) {
            ellipsoid('Sejong_Leg', [side * 0.28, 0.35, 0.02], [0.24, 0.42, 0.24], red, 28, 18);
            ellipsoid('Sejong_Shoe', [side * 0.28, 0.1, -0.14], [0.34, 0.18, 0.48], shoeMaterial, 32, 20);
            const shoeGold = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.07, 0.05), gold);
            shoeGold.name = 'Shoe_Gold_Detail';
            shoeGold.position.set(side * 0.28, 0.13, -0.53);
            group.add(shoeGold);
        }
        return group;
    }

    private createJoseonCharacterVisual(id: JoseonSkinId): THREE.Group {
        const group = new THREE.Group();
        const cloth = (color: number, roughness: number = 0.82) => new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.01 });
        const skin = cloth(0xf0c9a5, 0.72);
        const black = cloth(0x171c1d, 0.64);
        const white = cloth(0xf2eadb, 0.9);
        const isKing = id === 'joseon-king';
        const isFarmer = id === 'joseon-farmer';
        const isWoman = id === 'joseon-woman';
        const isGisaeng = id === 'joseon-gisaeng';
        const isGuard = id === 'joseon-guard';
        const isGeneral = id === 'joseon-general';
        const isArmored = isGuard || isGeneral;
        const robeColor = isKing
            ? 0xa8292f
            : (isFarmer ? 0x8b7652 : (isWoman ? 0x8bcaad : (isGisaeng ? 0xd65472 : (isGuard ? 0x384b57 : (isGeneral ? 0x922f2b : 0x8db7c5)))));
        const accentColor = isKing
            ? 0xe3b64b
            : (isFarmer ? 0x4d5e3e : (isWoman ? 0xe8b84c : (isGisaeng ? 0x3b8b86 : (isGuard ? 0x9aa4a8 : (isGeneral ? 0xd1a849 : 0x314e68)))));
        const robe = cloth(robeColor, isArmored ? 0.62 : 0.82);
        const accent = cloth(accentColor, isArmored ? 0.52 : 0.78);

        const skirt = new THREE.Mesh(new THREE.CylinderGeometry(isWoman || isGisaeng ? 0.55 : 0.48, isWoman || isGisaeng ? 0.94 : 0.72, 1.45, 18), robe);
        skirt.position.y = 0.98;
        group.add(skirt);
        const skirtHem = new THREE.Mesh(new THREE.TorusGeometry(isWoman || isGisaeng ? 0.91 : 0.69, 0.035, 7, 28), accent);
        skirtHem.rotation.x = Math.PI / 2;
        skirtHem.position.y = 0.27;
        group.add(skirtHem);
        const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.56, 0.92, 16), robe);
        torso.position.y = 1.83;
        group.add(torso);
        const collarLeft = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.72, 0.08), white);
        collarLeft.position.set(-0.12, 1.96, -0.5);
        collarLeft.rotation.z = -0.35;
        const collarRight = collarLeft.clone();
        collarRight.position.x = 0.12;
        collarRight.rotation.z = 0.35;
        group.add(collarLeft, collarRight);
        const collarTie = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.52, 0.07), accent);
        collarTie.position.set(0.2, 1.76, -0.53);
        collarTie.rotation.z = -0.18;
        group.add(collarTie);
        const belt = new THREE.Mesh(new THREE.CylinderGeometry(0.57, 0.57, 0.16, 16), accent);
        belt.position.y = 1.38;
        group.add(belt);
        const frontGore = new THREE.Mesh(new THREE.BoxGeometry(isWoman || isGisaeng ? 0.12 : 0.16, 1.15, 0.045), accent);
        frontGore.position.set(0, 0.82, -(isWoman || isGisaeng ? 0.75 : 0.6));
        group.add(frontGore);
        for (const side of [-1, 1]) {
            const robeFold = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.95, 0.035), accent);
            robeFold.position.set(side * (isWoman || isGisaeng ? 0.34 : 0.27), 0.78, -(isWoman || isGisaeng ? 0.79 : 0.63));
            group.add(robeFold);
        }
        if (isArmored) {
            for (let row = 0; row < 3; row++) {
                for (const column of [-1, 0, 1]) {
                    const lamella = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.2, 0.08), accent);
                    lamella.position.set(column * 0.25, 1.62 + row * 0.22, -0.51);
                    group.add(lamella);
                }
            }
        }

        const head = new THREE.Mesh(new THREE.SphereGeometry(0.48, 20, 16), skin);
        head.position.y = 2.64;
        head.scale.set(0.97, 1.06, 0.94);
        group.add(head);
        for (const side of [-1, 1]) {
            const ear = new THREE.Mesh(new THREE.SphereGeometry(0.095, 12, 9), skin);
            ear.position.set(side * 0.47, 2.64, 0);
            ear.scale.set(0.62, 1, 0.52);
            group.add(ear);
        }
        const hairCap = new THREE.Mesh(new THREE.SphereGeometry(0.5, 18, 12, 0, Math.PI * 2, 0, Math.PI * 0.48), black);
        hairCap.position.y = 2.75;
        group.add(hairCap);
        const customizationAnchor = new THREE.Group();
        customizationAnchor.name = 'character-customization-anchor';
        customizationAnchor.position.set(0, 2.64, -0.5);
        group.add(customizationAnchor);
        if (!this.characterCustomizationEnabled) {
            const faceInk = cloth(0x33221d, 0.74);
            const lip = cloth(0xa85e59, 0.72);
            for (const side of [-1, 1]) {
                const eye = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 7), faceInk);
                eye.scale.set(1.25, 0.7, 0.54);
                eye.position.set(side * 0.18, 2.71, -0.45);
                const brow = new THREE.Mesh(new THREE.BoxGeometry(0.19, 0.035, 0.035), faceInk);
                brow.position.set(side * 0.18, 2.83, -0.43);
                brow.rotation.z = side * 0.08;
                group.add(eye, brow);
            }
            const nose = new THREE.Mesh(new THREE.SphereGeometry(0.075, 10, 8), skin);
            nose.scale.set(0.82, 1.18, 0.75);
            nose.position.set(0, 2.61, -0.48);
            const mouth = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.028, 0.035), lip);
            mouth.position.set(0, 2.48, -0.46);
            group.add(nose, mouth);
        }
        for (const side of [-1, 1]) {
            const sleeve = new THREE.Mesh(new THREE.CylinderGeometry(isArmored ? 0.24 : 0.2, isArmored ? 0.29 : 0.33, 1.1, 12), isArmored ? accent : robe);
            sleeve.position.set(side * 0.65, 1.72, 0);
            sleeve.rotation.z = side * 0.17;
            group.add(sleeve);
            const hand = new THREE.Mesh(new THREE.SphereGeometry(0.17, 12, 9), skin);
            hand.position.set(side * 0.75, 1.15, -0.02);
            group.add(hand);
            const cuff = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.25, 0.14, 12), isArmored ? accent : white);
            cuff.position.set(side * 0.73, 1.28, 0);
            cuff.rotation.z = side * 0.17;
            group.add(cuff);
            if (isArmored) {
                const shoulderGuard = new THREE.Mesh(new THREE.SphereGeometry(0.34, 14, 9, 0, Math.PI * 2, 0, Math.PI * 0.58), accent);
                shoulderGuard.rotation.z = side * 0.28;
                shoulderGuard.position.set(side * 0.63, 2.12, 0);
                group.add(shoulderGuard);
            }
            const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, 0.72, 10), isFarmer || isWoman || isGisaeng ? white : black);
            leg.position.set(side * 0.25, 0.33, 0);
            group.add(leg);
            const shoe = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.16, 0.52), isFarmer ? cloth(0xb59660, 0.94) : black);
            shoe.position.set(side * 0.25, 0.07, -0.11);
            group.add(shoe);
        }

        if (isKing) {
            const badge = new THREE.Mesh(new THREE.CylinderGeometry(0.29, 0.29, 0.06, 24), accent);
            badge.rotation.x = Math.PI / 2;
            badge.position.set(0, 1.88, -0.55);
            group.add(badge);
            const dragon = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.035, 8, 18), cloth(0xf7d56a, 0.48));
            dragon.position.set(0, 1.88, -0.6);
            group.add(dragon);
            const dragonPearl = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 8), cloth(0xf8e59a, 0.38));
            dragonPearl.position.set(0, 1.88, -0.65);
            group.add(dragonPearl);
            for (const side of [-1, 1]) {
                const cloud = new THREE.Mesh(new THREE.TorusGeometry(0.095, 0.018, 6, 14, Math.PI * 1.45), accent);
                cloud.position.set(side * 0.34, 1.85, -0.6);
                cloud.rotation.z = side * 0.42;
                group.add(cloud);
            }
        } else if (isFarmer) {
            const vest = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.72, 0.1), accent);
            vest.position.set(0, 1.78, -0.52);
            group.add(vest);
            const pouch = new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), cloth(0x705034, 0.9));
            pouch.position.set(0.48, 1.25, -0.32);
            group.add(pouch);
        } else if (isWoman || isGisaeng) {
            const jeogori = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.56, 0.16), isGisaeng ? cloth(0xf3d8b6, 0.8) : cloth(0xf3ede2, 0.86));
            jeogori.position.set(0, 1.92, -0.49);
            group.add(jeogori);
            const skirtBand = new THREE.Mesh(new THREE.TorusGeometry(isGisaeng ? 0.78 : 0.72, 0.055, 8, 24), accent);
            skirtBand.rotation.x = Math.PI / 2;
            skirtBand.position.y = 1.37;
            group.add(skirtBand);
            const ribbon = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.74, 0.09), accent);
            ribbon.position.set(-0.18, 1.72, -0.62);
            ribbon.rotation.z = -0.13;
            group.add(ribbon);
            const norigae = new THREE.Mesh(new THREE.SphereGeometry(0.1, 10, 8), cloth(0xf6c84f, 0.5));
            norigae.position.set(0.25, 1.35, -0.58);
            group.add(norigae);
            for (const side of [-1, 1]) {
                const floralMark = new THREE.Mesh(new THREE.TorusGeometry(0.08, 0.018, 6, 12), accent);
                floralMark.position.set(side * 0.38, 0.76, -0.82);
                floralMark.rotation.z = side * 0.35;
                group.add(floralMark);
            }
            if (isGisaeng) {
                const flower = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.055, 7, 12), cloth(0xf9a8d4, 0.72));
                flower.position.set(0.38, 2.88, -0.12);
                flower.rotation.x = Math.PI / 2;
                group.add(flower);
            }
        } else if (isArmored) {
            if (isGeneral) {
                const cape = new THREE.Mesh(new THREE.BoxGeometry(0.92, 1.46, 0.07), cloth(0x7f1d1d, 0.86));
                cape.name = 'joseon-general-cape';
                cape.position.set(0, 1.43, 0.43);
                cape.rotation.x = -0.06;
                group.add(cape);
            }
            const armor = new THREE.Mesh(new THREE.BoxGeometry(isGeneral ? 1.08 : 0.96, 0.98, 0.68), isGeneral ? cloth(0x55231f, 0.56) : cloth(0x263740, 0.62));
            armor.name = isGeneral ? 'joseon-general-lamellar-armor' : 'joseon-guard-armor';
            armor.position.y = 1.78;
            group.add(armor);
            if (isGeneral) {
                const lamellarDark = cloth(0x281d27, 0.58);
                const lamellarRed = cloth(0x8c332d, 0.58);
                for (let row = 0; row < 4; row++) {
                    for (let column = -2; column <= 2; column++) {
                        const lamella = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.17, 0.055), (row + column) % 2 === 0 ? lamellarRed : lamellarDark);
                        lamella.name = 'joseon-general-lamellar-plate';
                        lamella.position.set(column * 0.19, 2.05 - row * 0.2, -0.375);
                        group.add(lamella);
                    }
                }
                const chestEmblem = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.04, 8, 22), cloth(0xd6ad55, 0.42));
                chestEmblem.name = 'joseon-general-chest-emblem';
                chestEmblem.position.set(0, 1.77, -0.42);
                group.add(chestEmblem);
                for (let column = -2; column <= 2; column++) {
                    const tasset = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.48, 0.09), column % 2 === 0 ? lamellarRed : lamellarDark);
                    tasset.name = 'joseon-general-skirt-plate';
                    tasset.position.set(column * 0.19, 1.15, -0.27);
                    tasset.rotation.z = column * 0.025;
                    group.add(tasset);
                }
                for (const side of [-1, 1]) {
                    const moustache = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.035, 0.035), black);
                    moustache.name = 'joseon-general-moustache';
                    moustache.position.set(side * 0.09, 2.48, -0.47);
                    moustache.rotation.z = side * 0.22;
                    group.add(moustache);
                }
            }
            for (const side of [-1, 1]) {
                const shoulder = new THREE.Mesh(new THREE.SphereGeometry(isGeneral ? 0.34 : 0.28, 12, 8), accent);
                shoulder.scale.set(1.2, 0.72, 1);
                shoulder.position.set(side * 0.66, 2.02, 0);
                group.add(shoulder);
                if (isGeneral) {
                    const shoulderGuard = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.46, 0.46), cloth(0x6f2a27, 0.55));
                    shoulderGuard.name = 'joseon-general-shoulder-guard';
                    shoulderGuard.position.set(side * 0.73, 1.82, 0.01);
                    shoulderGuard.rotation.z = side * -0.12;
                    group.add(shoulderGuard);
                }
                const plate = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.7, 0.06), accent);
                plate.position.set(side * 0.28, 1.78, -0.38);
                group.add(plate);
            }
            const weapon = new THREE.Group();
            const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.04, isGeneral ? 2.8 : 2.1, 7), cloth(0x4b3022, 0.86));
            shaft.position.y = 1.1;
            weapon.add(shaft);
            const blade = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.45, 6), cloth(0xc8d2d5, 0.32));
            blade.position.y = isGeneral ? 2.62 : 2.25;
            weapon.add(blade);
            if (isGeneral) {
                const tassel = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.42, 10), cloth(0xb91c1c, 0.74));
                tassel.name = 'joseon-general-spear-tassel';
                tassel.position.y = 2.31;
                tassel.rotation.z = Math.PI;
                weapon.add(tassel);
            }
            weapon.position.set(0.86, 0, 0.04);
            weapon.rotation.z = isGeneral ? -0.1 : -0.22;
            group.add(weapon);
        } else {
            const book = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.62, 0.12), cloth(0x6b3e28, 0.85));
            book.position.set(0.62, 1.3, -0.42);
            book.rotation.z = -0.16;
            group.add(book);
        }
        return group;
    }

    private initializeCustomizationPreview() {
        const host = this.customizationPreviewHost?.nativeElement;
        if (!host || this.destroyed) return;
        if (!this.customizationPreviewRenderer) {
            this.customizationPreviewScene = new THREE.Scene();
            this.customizationPreviewCamera = new THREE.PerspectiveCamera(32, 2 / 3, 0.1, 50);
            this.customizationPreviewCamera.position.set(0, 1.45, -6.15);
            this.customizationPreviewCamera.lookAt(0, 1.4, 0);

            this.customizationPreviewRenderer = new THREE.WebGLRenderer({
                antialias: this.effectiveGraphicsQuality !== 'low',
                alpha: true,
                powerPreference: 'high-performance'
            });
            const previewRatioCap = this.effectiveGraphicsQuality === 'low' ? 0.75 : (this.effectiveGraphicsQuality === 'high' ? 1.5 : 1.1);
            this.customizationPreviewRenderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, previewRatioCap));
            this.customizationPreviewRenderer.outputColorSpace = THREE.SRGBColorSpace;
            this.customizationPreviewRenderer.toneMapping = THREE.ACESFilmicToneMapping;
            this.customizationPreviewRenderer.toneMappingExposure = 1.08;
            this.customizationPreviewRenderer.shadowMap.enabled = this.effectiveGraphicsQuality !== 'low';
            this.customizationPreviewRenderer.shadowMap.type = this.effectiveGraphicsQuality === 'high' ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
            Object.assign(this.customizationPreviewRenderer.domElement.style, {
                width: '100%',
                height: '100%',
                display: 'block',
                pointerEvents: 'none'
            });

            this.customizationPreviewScene.add(new THREE.HemisphereLight(0xb9e6ff, 0x261b31, 2.25));
            const keyLight = new THREE.DirectionalLight(0xfff1d2, 4.2);
            keyLight.position.set(-3.6, 6, -4.5);
            keyLight.castShadow = this.effectiveGraphicsQuality !== 'low';
            keyLight.shadow.mapSize.set(512, 512);
            this.customizationPreviewScene.add(keyLight);
            const rimLight = new THREE.DirectionalLight(0x8b5cf6, 2.6);
            rimLight.position.set(4, 3.5, 4);
            this.customizationPreviewScene.add(rimLight);
            const floor = new THREE.Mesh(
                new THREE.CircleGeometry(1.8, 48),
                new THREE.MeshStandardMaterial({ color: 0x17233f, roughness: 0.72, metalness: 0.12 })
            );
            floor.rotation.x = -Math.PI / 2;
            floor.position.y = -0.03;
            floor.receiveShadow = true;
            this.customizationPreviewScene.add(floor);
        }
        if (this.customizationPreviewRenderer.domElement.parentElement !== host) {
            host.replaceChildren(this.customizationPreviewRenderer.domElement);
        }
        this.refreshCustomizationPreviewModel();
        this.resizeCustomizationPreview();
        this.renderCustomizationPreview();
    }

    private refreshCustomizationPreviewModel() {
        if (!this.customizationPreviewScene) return;
        if (this.customizationPreviewModel) {
            this.customizationPreviewModel.removeFromParent();
            this.disposeObjectTree(this.customizationPreviewModel);
        }
        let visual: THREE.Group;
        if (this.hasCustomSkin && this.playerVisual) {
            visual = this.playerVisual.clone(true);
            visual.traverse((object: THREE.Object3D) => {
                const mesh = object as THREE.Mesh;
                if (!mesh.isMesh) return;
                mesh.geometry = mesh.geometry.clone();
                mesh.material = Array.isArray(mesh.material)
                    ? mesh.material.map((material) => material.clone())
                    : mesh.material.clone();
            });
        } else {
            visual = this.createBuiltInPlayerVisual(this.selectedBuiltInSkinId || 'joseon-king-2');
        }
        this.removeLegacyCustomizationVisual(visual);
        this.customizationPreviewModel = visual;
        this.applyCustomizationPreviewRotation();
        this.customizationPreviewScene.add(visual);
    }

    private applyCustomizationPreviewRotation() {
        if (this.customizationPreviewModel) {
            this.customizationPreviewModel.rotation.y = THREE.MathUtils.degToRad(this.customizationPreviewRotation);
        }
    }

    private resizeCustomizationPreview() {
        const host = this.customizationPreviewHost?.nativeElement;
        if (!host || !this.customizationPreviewRenderer || !this.customizationPreviewCamera) return;
        const width = Math.max(1, host.clientWidth || 220);
        const height = Math.max(1, host.clientHeight || 330);
        this.customizationPreviewCamera.aspect = width / height;
        this.customizationPreviewCamera.updateProjectionMatrix();
        this.customizationPreviewRenderer.setSize(width, height, false);
    }

    private renderCustomizationPreview() {
        if (this.screen !== 'skins') return;
        if (!this.customizationPreviewRenderer || !this.customizationPreviewScene || !this.customizationPreviewCamera) return;
        this.customizationPreviewRenderer.render(this.customizationPreviewScene, this.customizationPreviewCamera);
    }

    private disposeCustomizationPreview() {
        if (this.customizationPreviewScene) {
            this.customizationPreviewScene.traverse((object: THREE.Object3D) => {
                if (this.usesSharedBuiltInCharacterResources(object)) return;
                const mesh = object as THREE.Mesh;
                mesh.geometry?.dispose();
                const material = mesh.material;
                if (Array.isArray(material)) material.forEach((item) => item.dispose());
                else material?.dispose?.();
            });
            this.customizationPreviewScene.clear();
        }
        this.customizationPreviewRenderer?.dispose();
        this.customizationPreviewRenderer?.domElement.remove();
        this.customizationPreviewScene = undefined;
        this.customizationPreviewCamera = undefined;
        this.customizationPreviewRenderer = undefined;
        this.customizationPreviewModel = undefined;
    }

    private initializeNfcPreview() {
        const host = this.nfcPreviewHost?.nativeElement;
        if (!host || this.destroyed || !this.nfcRegistrationOpen) return;
        if (!this.nfcPreviewRenderer) {
            this.nfcPreviewScene = new THREE.Scene();
            this.nfcPreviewCamera = new THREE.PerspectiveCamera(32, 2 / 3, 0.1, 50);
            this.nfcPreviewCamera.position.set(0, 1.45, -6.15);
            this.nfcPreviewCamera.lookAt(0, 1.4, 0);

            this.nfcPreviewRenderer = new THREE.WebGLRenderer({
                antialias: this.effectiveGraphicsQuality !== 'low',
                alpha: true,
                powerPreference: 'high-performance'
            });
            const previewRatioCap = this.effectiveGraphicsQuality === 'low' ? 0.75 : (this.effectiveGraphicsQuality === 'high' ? 1.5 : 1.1);
            this.nfcPreviewRenderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, previewRatioCap));
            this.nfcPreviewRenderer.outputColorSpace = THREE.SRGBColorSpace;
            this.nfcPreviewRenderer.toneMapping = THREE.ACESFilmicToneMapping;
            this.nfcPreviewRenderer.toneMappingExposure = 1.08;
            this.nfcPreviewRenderer.shadowMap.enabled = this.effectiveGraphicsQuality !== 'low';
            this.nfcPreviewRenderer.shadowMap.type = this.effectiveGraphicsQuality === 'high' ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
            Object.assign(this.nfcPreviewRenderer.domElement.style, {
                width: '100%',
                height: '100%',
                display: 'block',
                pointerEvents: 'none'
            });

            this.nfcPreviewScene.add(new THREE.HemisphereLight(0xb9e6ff, 0x261b31, 2.25));
            const keyLight = new THREE.DirectionalLight(0xfff1d2, 4.2);
            keyLight.position.set(-3.6, 6, -4.5);
            keyLight.castShadow = this.effectiveGraphicsQuality !== 'low';
            keyLight.shadow.mapSize.set(512, 512);
            this.nfcPreviewScene.add(keyLight);
            const rimLight = new THREE.DirectionalLight(0x8b5cf6, 2.6);
            rimLight.position.set(4, 3.5, 4);
            this.nfcPreviewScene.add(rimLight);
            const floor = new THREE.Mesh(
                new THREE.CircleGeometry(1.8, 48),
                new THREE.MeshStandardMaterial({ color: 0x17233f, roughness: 0.72, metalness: 0.12 })
            );
            floor.rotation.x = -Math.PI / 2;
            floor.position.y = -0.03;
            floor.receiveShadow = true;
            this.nfcPreviewScene.add(floor);
        }
        if (this.nfcPreviewRenderer.domElement.parentElement !== host) {
            host.replaceChildren(this.nfcPreviewRenderer.domElement);
        }
        if (this.nfcPreviewModel && this.nfcPreviewModel.parent !== this.nfcPreviewScene) {
            this.nfcPreviewScene?.add(this.nfcPreviewModel);
        }
        this.resizeNfcPreview();
        this.renderNfcPreview();
    }

    private setNfcPreviewModel(visual?: THREE.Group) {
        if (this.nfcPreviewModel) {
            this.nfcPreviewModel.removeFromParent();
            this.disposeObjectTree(this.nfcPreviewModel);
        }
        this.nfcPreviewModel = visual;
        if (!visual) return;
        this.applyNfcPreviewRotation();
        this.nfcPreviewScene?.add(visual);
    }

    private applyNfcPreviewRotation() {
        const orientation = this.nfcPreviewModel?.getObjectByName('uploaded-skin-orientation');
        if (orientation) orientation.rotation.y = THREE.MathUtils.degToRad(this.nfcPreviewRotationY);
    }

    private resizeNfcPreview() {
        const host = this.nfcPreviewHost?.nativeElement;
        if (!host || !this.nfcPreviewRenderer || !this.nfcPreviewCamera) return;
        const width = Math.max(1, host.clientWidth || 220);
        const height = Math.max(1, host.clientHeight || 330);
        this.nfcPreviewCamera.aspect = width / height;
        this.nfcPreviewCamera.updateProjectionMatrix();
        this.nfcPreviewRenderer.setSize(width, height, false);
    }

    private renderNfcPreview() {
        if (!this.nfcRegistrationOpen) return;
        if (!this.nfcPreviewRenderer || !this.nfcPreviewScene || !this.nfcPreviewCamera) return;
        this.nfcPreviewRenderer.render(this.nfcPreviewScene, this.nfcPreviewCamera);
    }

    private clearNfcRegistrationDraft() {
        this.setNfcPreviewModel(undefined);
        this.nfcRegistrationDraft = null;
        this.nfcCharacterNameDraft = '';
        this.nfcPreviewRotationY = 180;
        this.nfcPreviewInitialRotationY = 180;
        this.nfcPreviewDragging = false;
        this.nfcPreviewPointerId = null;
        this.nfcPreviewReady = false;
        this.nfcDraftSaved = false;
    }

    private disposeNfcPreview() {
        if (this.nfcPreviewModel) {
            this.nfcPreviewModel.removeFromParent();
            this.disposeObjectTree(this.nfcPreviewModel);
            this.nfcPreviewModel = undefined;
        }
        if (this.nfcPreviewScene) {
            this.nfcPreviewScene.traverse((object: THREE.Object3D) => {
                const mesh = object as THREE.Mesh;
                mesh.geometry?.dispose();
                const material = mesh.material;
                if (Array.isArray(material)) material.forEach((item) => item.dispose());
                else material?.dispose?.();
            });
            this.nfcPreviewScene.clear();
        }
        this.nfcPreviewRenderer?.dispose();
        this.nfcPreviewRenderer?.domElement.remove();
        this.nfcPreviewScene = undefined;
        this.nfcPreviewCamera = undefined;
        this.nfcPreviewRenderer = undefined;
    }

    private replacePlayerVisual(visual: THREE.Group) {
        if (!this.player) return;
        this.playerAnimationMixer?.stopAllAction();
        this.playerAnimationMixer = undefined;
        if (this.playerVisual) {
            this.player.remove(this.playerVisual);
            this.disposeObjectTree(this.playerVisual);
        }
        this.removeLegacyCustomizationVisual(visual);
        this.playerVisual = visual;
        this.player.add(visual);
        if (visual.animations.length > 0) {
            this.playerAnimationMixer = new THREE.AnimationMixer(visual);
            this.playerAnimationMixer.clipAction(visual.animations[0]).play();
        }
        this.applyMountRiderOffset();
    }

    private removeLegacyCustomizationVisual(root: THREE.Object3D) {
        let legacy = root.getObjectByName('character-customization');
        while (legacy) {
            legacy.removeFromParent();
            this.disposeObjectTree(legacy);
            legacy = root.getObjectByName('character-customization');
        }
    }

    private commitCharacterCustomization(message: string) {
        this.persistCharacterCustomizationSettings();
        this.applyCharacterCustomizationToPlayer();
        this.refreshCustomizationPreviewModel();
        this.customizationStatus = this.characterCustomizationEnabled
            ? `${message} 미리보기와 3D 캐릭터에 반영했습니다.`
            : `${message} 원본 캐릭터를 유지합니다.`;
        if (this.hasCustomSkin) {
            void this.persistSkin().then(() => this.syncActiveLocalSkin()).catch(() => undefined);
        }
        this.requestUiRender();
    }

    private persistCharacterCustomizationSettings() {
        localStorage.setItem(this.characterCustomizationStorageKey, JSON.stringify({
            ...this.characterCustomization,
            enabled: this.characterCustomizationEnabled
        }));
    }

    private restoreCharacterCustomization() {
        const raw = localStorage.getItem(this.characterCustomizationStorageKey);
        if (!raw) return;
        try {
            const value = JSON.parse(raw);
            this.characterCustomization = {
                eyes: this.eyeStyleOptions.some((item) => item.id === value?.eyes) ? value.eyes : 'calm',
                nose: this.noseStyleOptions.some((item) => item.id === value?.nose) ? value.nose : 'soft',
                mouth: this.mouthStyleOptions.some((item) => item.id === value?.mouth) ? value.mouth : 'gentle',
                hair: this.hairStyleOptions.some((item) => item.id === value?.hair) ? value.hair : 'gat'
            };
            this.characterCustomizationEnabled = value?.enabled !== false;
            this.customizationStatus = this.characterCustomizationEnabled
                ? '저장된 캐릭터 꾸미기를 불러왔습니다.'
                : '커스터마이징 없이 원본 캐릭터를 사용합니다.';
        } catch {
            localStorage.removeItem(this.characterCustomizationStorageKey);
        }
    }

    private applyCharacterCustomizationToPlayer() {
        if (!this.playerVisual) return;
        const previous = this.playerVisual.getObjectByName('character-customization');
        if (previous) {
            previous.removeFromParent();
            this.disposeObjectTree(previous);
        }
        let anchor = this.playerVisual.getObjectByName('character-customization-anchor') as THREE.Group | undefined;
        if (!anchor) {
            anchor = new THREE.Group();
            anchor.name = 'character-customization-anchor';
            anchor.position.set(0, 2.24, -0.48);
            this.playerVisual.add(anchor);
        }
        const customization = this.createCharacterCustomizationVisual();
        if (customization.children.length > 0) anchor.add(customization);
    }

    private createCharacterCustomizationVisual(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'character-customization';
        if (!this.characterCustomizationEnabled) return group;
        const ink = () => new THREE.MeshStandardMaterial({ color: 0x171717, roughness: 0.72 });
        const skinAccent = () => new THREE.MeshStandardMaterial({ color: 0xb96d62, roughness: 0.7 });
        const royal = () => new THREE.MeshStandardMaterial({ color: 0x1b2022, roughness: 0.58 });
        const cloth = () => new THREE.MeshStandardMaterial({ color: 0x6b5138, roughness: 0.9 });
        const add = (mesh: THREE.Mesh) => {
            mesh.castShadow = true;
            group.add(mesh);
        };

        if (this.characterCustomization.eyes === 'calm') {
            for (const side of [-1, 1]) {
                const eye = new THREE.Mesh(new THREE.BoxGeometry(0.21, 0.045, 0.04), ink());
                eye.position.set(side * 0.2, 0.08, -0.05);
                eye.rotation.z = side * -0.08;
                add(eye);
            }
        } else if (this.characterCustomization.eyes === 'bright') {
            for (const side of [-1, 1]) {
                const eye = new THREE.Mesh(new THREE.SphereGeometry(0.085, 12, 9), ink());
                eye.position.set(side * 0.2, 0.08, -0.05);
                add(eye);
            }
        } else if (this.characterCustomization.eyes === 'dignified') {
            for (const side of [-1, 1]) {
                const eye = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.055, 0.04), ink());
                eye.position.set(side * 0.2, 0.07, -0.05);
                const brow = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.055, 0.045), ink());
                brow.position.set(side * 0.2, 0.21, -0.04);
                brow.rotation.z = side * 0.14;
                add(eye);
                add(brow);
            }
        }

        if (this.characterCustomization.nose === 'soft') {
            const nose = new THREE.Mesh(new THREE.SphereGeometry(0.085, 12, 9), skinAccent());
            nose.position.set(0, -0.08, -0.12);
            add(nose);
        } else if (this.characterCustomization.nose === 'straight') {
            const nose = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.25, 8), skinAccent());
            nose.rotation.x = -Math.PI / 2;
            nose.position.set(0, -0.08, -0.14);
            add(nose);
        } else if (this.characterCustomization.nose === 'round') {
            const nose = new THREE.Mesh(new THREE.SphereGeometry(0.12, 12, 9), skinAccent());
            nose.scale.set(1.15, 0.85, 0.8);
            nose.position.set(0, -0.08, -0.12);
            add(nose);
        }

        if (this.characterCustomization.mouth === 'smile') {
            const mouth = new THREE.Mesh(new THREE.TorusGeometry(0.135, 0.02, 8, 22, Math.PI), skinAccent());
            mouth.rotation.z = Math.PI;
            mouth.position.set(0, -0.21, -0.025);
            mouth.name = 'character-customization-smile';
            add(mouth);
        } else if (this.characterCustomization.mouth !== 'none') {
            const mouth = new THREE.Mesh(new THREE.BoxGeometry(this.characterCustomization.mouth === 'firm' ? 0.27 : 0.19, 0.035, 0.04), skinAccent());
            mouth.position.set(0, -0.27, -0.09);
            add(mouth);
        }

        if (this.characterCustomization.hair === 'sangtu') {
            const bun = new THREE.Mesh(new THREE.SphereGeometry(0.19, 12, 9), ink());
            bun.scale.y = 1.3;
            bun.position.set(0, 0.52, 0.48);
            add(bun);
        } else if (this.characterCustomization.hair === 'gat') {
            const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.72, 0.055, 28), royal());
            brim.position.set(0, 0.34, 0.5);
            add(brim);
            const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.31, 0.56, 18), royal());
            crown.position.set(0, 0.64, 0.5);
            add(crown);
            for (const side of [-1, 1]) {
                const strap = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.88, 6), ink());
                strap.position.set(side * 0.31, -0.04, 0.36);
                strap.rotation.z = side * 0.18;
                add(strap);
            }
        } else if (this.characterCustomization.hair === 'royal-crown') {
            const crown = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.48, 0.48), royal());
            crown.position.set(0, 0.58, 0.5);
            add(crown);
            for (const side of [-1, 1]) {
                const wing = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.12, 0.2), royal());
                wing.position.set(side * 0.52, 0.68, 0.52);
                wing.rotation.z = side * -0.13;
                add(wing);
            }
        } else if (this.characterCustomization.hair === 'headscarf') {
            const scarf = new THREE.Mesh(new THREE.SphereGeometry(0.54, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.55), cloth());
            scarf.scale.y = 0.65;
            scarf.position.set(0, 0.14, 0.5);
            add(scarf);
            const knot = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), cloth());
            knot.position.set(0.42, 0.13, 0.68);
            add(knot);
        } else if (this.characterCustomization.hair === 'binyeo' || this.characterCustomization.hair === 'jokduri') {
            const bun = new THREE.Mesh(new THREE.SphereGeometry(0.27, 14, 10), ink());
            bun.scale.set(1.1, 0.78, 0.72);
            bun.position.set(0, 0.36, 0.83);
            add(bun);
            const pin = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 1.08, 8), new THREE.MeshStandardMaterial({ color: 0xd4a73c, roughness: 0.42, metalness: 0.45 }));
            pin.rotation.z = Math.PI / 2;
            pin.position.set(0, 0.39, 0.78);
            add(pin);
            if (this.characterCustomization.hair === 'jokduri') {
                const jokduri = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.3, 0.34, 8), royal());
                jokduri.position.set(0, 0.48, 0.5);
                add(jokduri);
                const jewel = new THREE.Mesh(new THREE.SphereGeometry(0.07, 10, 8), new THREE.MeshStandardMaterial({ color: 0xfbbf24, emissive: 0x78350f, emissiveIntensity: 0.3 }));
                jewel.position.set(0, 0.51, 0.3);
                add(jewel);
            }
        } else if (this.characterCustomization.hair === 'warrior-helmet' || this.characterCustomization.hair === 'general-helmet') {
            const general = this.characterCustomization.hair === 'general-helmet';
            const helmetMaterial = new THREE.MeshStandardMaterial({ color: general ? 0x8f2f2a : 0x33434b, roughness: 0.55, metalness: 0.28 });
            const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.56, 18, 12, 0, Math.PI * 2, 0, Math.PI * 0.58), helmetMaterial);
            helmet.position.set(0, 0.22, 0.5);
            add(helmet);
            const crestMaterial = general
                ? new THREE.MeshStandardMaterial({ color: 0xb91c1c, roughness: 0.7 })
                : royal();
            const crest = new THREE.Mesh(new THREE.BoxGeometry(0.12, general ? 0.72 : 0.48, 0.16), crestMaterial);
            crest.position.set(0, general ? 0.68 : 0.56, 0.54);
            add(crest);
            const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.64, 0.64, 0.08, 22), helmetMaterial);
            brim.position.set(0, 0.18, 0.5);
            add(brim);
            if (general) {
                const helmetBand = new THREE.Mesh(new THREE.TorusGeometry(0.49, 0.045, 8, 26), new THREE.MeshStandardMaterial({ color: 0xd6ad55, roughness: 0.4, metalness: 0.5 }));
                helmetBand.name = 'joseon-general-helmet-band';
                helmetBand.rotation.x = Math.PI / 2;
                helmetBand.position.set(0, 0.22, 0.2);
                add(helmetBand);
                const plumeTop = new THREE.Mesh(new THREE.SphereGeometry(0.11, 12, 9), new THREE.MeshStandardMaterial({ color: 0xd6ad55, roughness: 0.42, metalness: 0.38 }));
                plumeTop.name = 'joseon-general-plume-finial';
                plumeTop.position.set(0, 1.05, 0.54);
                add(plumeTop);
            }
        }
        return group;
    }

    private usesSharedBuiltInCharacterResources(object: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current.userData?.['sharedBuiltInCharacterResources'] === true
                || current.userData?.['sharedDecorationResources'] === true) return true;
            current = current.parent;
        }
        return false;
    }

    private disposeObjectTree(object: THREE.Object3D, force: boolean = false) {
        if (!force && this.usesSharedBuiltInCharacterResources(object)) return;
        object.traverse((child: THREE.Object3D) => {
            const mesh = child as THREE.Mesh;
            mesh.geometry?.dispose();
            const material = mesh.material;
            if (Array.isArray(material)) material.forEach((item) => item.dispose());
            else material?.dispose?.();
        });
    }

    private downloadBlob(data: BlobPart, fileName: string, type: string) {
        const url = URL.createObjectURL(new Blob([data], { type }));
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    private createCoins() {
        if (!this.scene) return;
        this.activeMap.coins.forEach((config) => {
            const mesh = this.createYeopjeonVisual();
            this.positionMapObject(mesh, config, 1.75);
            mesh.scale.setScalar(config.scale);
            this.tagEditor3dSelectable(mesh, 'coins', config.id);
            this.scene!.add(mesh);
            this.coins.push({ config, mesh, collected: false, homePosition: mesh.position.clone() });
        });
        this.totalCoins = this.coins.length;
    }

    private createYeopjeonVisual(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'yeopjeon-coin';
        const bronze = new THREE.MeshStandardMaterial({
            color: 0xc98932,
            emissive: 0x7c3f0d,
            emissiveIntensity: 0.7,
            roughness: 0.42,
            metalness: 0.78
        });
        const darkBronze = new THREE.MeshStandardMaterial({ color: 0x724014, emissive: 0x3f2108, emissiveIntensity: 0.35, roughness: 0.52, metalness: 0.72 });
        const shape = new THREE.Shape();
        shape.absarc(0, 0, 0.58, 0, Math.PI * 2, false);
        const hole = new THREE.Path();
        hole.moveTo(-0.17, -0.17);
        hole.lineTo(-0.17, 0.17);
        hole.lineTo(0.17, 0.17);
        hole.lineTo(0.17, -0.17);
        hole.closePath();
        shape.holes.push(hole);
        const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: 0.15,
            bevelEnabled: true,
            bevelSize: 0.035,
            bevelThickness: 0.035,
            bevelSegments: 2,
            curveSegments: 28
        });
        geometry.translate(0, 0, -0.075);
        group.add(new THREE.Mesh(geometry, bronze));
        for (const z of [-0.115, 0.115]) {
            const rim = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.025, 7, 36), darkBronze);
            rim.position.z = z;
            group.add(rim);
            for (const [x, y, width, height] of [
                [0, 0.19, 0.4, 0.035], [0, -0.19, 0.4, 0.035],
                [-0.19, 0, 0.035, 0.4], [0.19, 0, 0.035, 0.4]
            ] as Array<[number, number, number, number]>) {
                const frame = new THREE.Mesh(new THREE.BoxGeometry(width, height, 0.035), darkBronze);
                frame.position.set(x, y, z);
                group.add(frame);
            }
        }
        group.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (mesh.isMesh) mesh.castShadow = true;
        });
        return group;
    }

    private createMagnets() {
        if (!this.scene) return;
        this.activeMap.magnets.forEach((config) => {
            const group = new THREE.Group();
            const red = new THREE.MeshStandardMaterial({ color: 0xfb7185, emissive: 0xe11d48, emissiveIntensity: 1.25, roughness: 0.28, metalness: 0.72 });
            const blue = new THREE.MeshStandardMaterial({ color: 0x60a5fa, emissive: 0x2563eb, emissiveIntensity: 1.25, roughness: 0.28, metalness: 0.72 });
            const curve = new THREE.Mesh(new THREE.TorusGeometry(0.68, 0.2, 10, 28, Math.PI), red);
            curve.position.y = 0.48;
            const leftPole = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.72, 0.4), red);
            leftPole.position.set(-0.68, 0.15, 0);
            const rightPole = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.72, 0.4), blue);
            rightPole.position.set(0.68, 0.15, 0);
            group.add(curve, leftPole, rightPole);
            group.traverse((object: THREE.Object3D) => {
                const mesh = object as THREE.Mesh;
                if (mesh.isMesh) mesh.castShadow = true;
            });
            this.positionMapObject(group, config, 1.25);
            group.scale.setScalar(config.scale);
            this.tagEditor3dSelectable(group, 'magnets', config.id);
            this.scene!.add(group);
            this.magnets.push({ config, mesh: group, collected: false, homePosition: group.position.clone() });
        });
    }

    private createSupplements() {
        if (!this.scene) return;
        this.activeMap.supplements.forEach((config) => {
            const group = new THREE.Group();
            const bottleMaterial = new THREE.MeshStandardMaterial({
                color: 0xf8fafc,
                emissive: 0x22d3ee,
                emissiveIntensity: 0.65,
                roughness: 0.25,
                metalness: 0.4
            });
            const capMaterial = new THREE.MeshStandardMaterial({
                color: 0x34d399,
                emissive: 0x059669,
                emissiveIntensity: 1.15,
                roughness: 0.3,
                metalness: 0.48
            });
            const bottle = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.58, 1.05, 18), bottleMaterial);
            bottle.position.y = 0.52;
            const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.36, 0.28, 16), capMaterial);
            cap.position.y = 1.18;
            const crossHorizontal = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.18, 0.08), capMaterial);
            crossHorizontal.position.set(0, 0.55, -0.55);
            const crossVertical = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.62, 0.08), capMaterial);
            crossVertical.position.set(0, 0.55, -0.55);
            group.add(bottle, cap, crossHorizontal, crossVertical);
            group.traverse((object: THREE.Object3D) => {
                const mesh = object as THREE.Mesh;
                if (mesh.isMesh) mesh.castShadow = true;
            });
            this.positionMapObject(group, config, 1.15);
            group.scale.setScalar(config.scale);
            this.tagEditor3dSelectable(group, 'supplements', config.id);
            this.scene!.add(group);
            this.supplements.push({ config, mesh: group, collected: false, homePosition: group.position.clone() });
        });
    }

    private createMounts() {
        if (!this.scene) return;
        this.activeMap.mounts.forEach((config) => {
            const group = new THREE.Group();
            const model = this.createMountVisual(config.kind);
            model.scale.setScalar(0.72);
            model.position.y = 0.08;
            model.rotation.y = Math.PI;
            group.add(model);

            const ringMaterial = new THREE.MeshStandardMaterial({
                color: 0xf8e7b2,
                emissive: 0xc0841a,
                emissiveIntensity: 1.35,
                roughness: 0.34,
                metalness: 0.45
            });
            const ring = new THREE.Mesh(new THREE.TorusGeometry(1.42, 0.09, 8, 32), ringMaterial);
            ring.name = 'mount-pickup-ring';
            ring.rotation.x = Math.PI / 2;
            ring.position.y = 0.12;
            group.add(ring);
            this.positionMapObject(group, config, 1.05);
            group.scale.setScalar(config.scale);
            this.tagEditor3dSelectable(group, 'mounts', config.id);
            this.scene!.add(group);
            this.mounts.push({ config, mesh: group, collected: false, homePosition: group.position.clone() });
        });
    }

    private createMountVisual(kind: MountKind): THREE.Group {
        const group = new THREE.Group();
        const material = (color: number, roughness: number = 0.82, metalness: number = 0.02) => new THREE.MeshStandardMaterial({ color, roughness, metalness });
        const wood = material(0x6b3822, 0.86);
        const darkWood = material(0x342218, 0.9);
        const brass = material(0xd5a744, 0.48, 0.42);
        const clothRed = material(0xa6332c, 0.74);
        const clothBlue = material(0x365b70, 0.8);
        const black = material(0x17191a, 0.84);

        if (kind === 'random') {
            const mystery = new THREE.Mesh(new THREE.DodecahedronGeometry(1.05, 0), material(0xf5c84c, 0.42, 0.38));
            mystery.position.y = 1.2;
            mystery.rotation.set(0.32, 0.5, 0.18);
            group.add(mystery);
            for (const [x, y, z] of [[0, 1.35, -0.96], [-0.72, 1.45, -0.55], [0.72, 1.45, -0.55]]) {
                const dot = new THREE.Mesh(new THREE.SphereGeometry(0.13, 10, 8), black);
                dot.position.set(x, y, z);
                group.add(dot);
            }
            const ring = new THREE.Mesh(new THREE.TorusGeometry(1.35, 0.1, 8, 28), brass);
            ring.rotation.x = Math.PI / 2;
            ring.position.y = 0.18;
            group.add(ring);
        } else if (kind === 'panokseon' || kind === 'turtle-ship') {
            group.add(this.createJoseonShipMount(kind === 'turtle-ship'));
        } else if (kind === 'tiger') {
            group.add(this.createTigerMountVisual());
        } else if (kind === 'palanquin') {
            const platform = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.24, 1.45), wood);
            platform.position.y = 1.34;
            group.add(platform);
            for (const side of [-1, 1]) {
                const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.09, 5.8, 8), darkWood);
                pole.rotation.x = Math.PI / 2;
                pole.position.set(side * 0.72, 1.23, 0);
                group.add(pole);
            }
            const seat = new THREE.Mesh(new THREE.BoxGeometry(1.52, 0.22, 0.92), clothBlue);
            seat.position.set(0, 1.53, 0.1);
            group.add(seat);
            const back = new THREE.Mesh(new THREE.BoxGeometry(1.58, 1.35, 0.16), clothRed);
            back.position.set(0, 2.08, 0.62);
            group.add(back);
            const frontPanel = new THREE.Mesh(new THREE.BoxGeometry(1.56, 0.86, 0.14), clothBlue);
            frontPanel.position.set(0, 1.72, -0.62);
            group.add(frontPanel);
            const cushion = new THREE.Mesh(new THREE.BoxGeometry(1.34, 0.24, 0.72), material(0xc44b43, 0.68));
            cushion.position.set(0, 1.72, 0.08);
            group.add(cushion);
            for (const side of [-1, 1]) {
                const armrest = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.92, 8), brass);
                armrest.rotation.x = Math.PI / 2;
                armrest.position.set(side * 0.7, 1.98, 0.08);
                group.add(armrest);
                const sidePanel = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.82, 1.12), side < 0 ? clothBlue : clothRed);
                sidePanel.position.set(side * 0.82, 1.82, 0.06);
                group.add(sidePanel);
                const tassel = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.36, 8), brass);
                tassel.position.set(side * 0.82, 1.17, -0.48);
                tassel.rotation.z = Math.PI;
                group.add(tassel);
            }
            for (const z of [-2.08, 2.08]) {
                for (const x of [-0.72, 0.72]) {
                    const bearer = new THREE.Group();
                    const tunic = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.25, 0.88, 10), z < 0 ? clothBlue : clothRed);
                    tunic.position.y = 0.75;
                    bearer.add(tunic);
                    const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 12, 9), material(0xe0b28b, 0.78));
                    head.position.y = 1.37;
                    bearer.add(head);
                    const hat = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.06, 18), black);
                    hat.position.y = 1.6;
                    bearer.add(hat);
                    const legLeft = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 0.64, 8), black);
                    legLeft.position.set(-0.1, 0.27, 0);
                    const legRight = legLeft.clone();
                    legRight.position.x = 0.1;
                    bearer.add(legLeft, legRight);
                    const shoulderPad = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.12, 0.25), material(0xe6d2a8, 0.9));
                    shoulderPad.position.y = 1.18;
                    bearer.add(shoulderPad);
                    for (const side of [-1, 1]) {
                        const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.07, 0.58, 7), material(0xe0b28b, 0.78));
                        arm.position.set(side * 0.23, 1.03, z < 0 ? -0.08 : 0.08);
                        arm.rotation.z = side * 0.65;
                        bearer.add(arm);
                    }
                    bearer.position.set(x, 0, z);
                    bearer.rotation.y = z < 0 ? 0 : Math.PI;
                    group.add(bearer);
                }
            }
            const emblem = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.06, 20), brass);
            emblem.rotation.x = Math.PI / 2;
            emblem.position.set(0, 1.78, -0.72);
            group.add(emblem);
        } else {
            const isOx = kind === 'ox';
            const hide = isOx ? material(0x8a6748, 0.92) : material(0x6d3d2b, 0.86);
            const muzzle = isOx ? material(0xc8ad85, 0.9) : material(0x2b211c, 0.9);
            const body = new THREE.Mesh(new THREE.CapsuleGeometry(isOx ? 0.68 : 0.48, isOx ? 1.35 : 1.76, 7, 14), hide);
            body.rotation.x = Math.PI / 2;
            body.scale.x = isOx ? 1.38 : 0.88;
            body.position.y = isOx ? 1.04 : 1.12;
            group.add(body);
            const neck = new THREE.Mesh(new THREE.CylinderGeometry(isOx ? 0.42 : 0.24, isOx ? 0.54 : 0.36, 1.08, 12), hide);
            neck.rotation.x = -0.42;
            neck.position.set(0, isOx ? 1.38 : 1.5, -1.08);
            group.add(neck);
            const head = new THREE.Mesh(new THREE.SphereGeometry(isOx ? 0.5 : 0.36, 16, 12), hide);
            head.scale.set(isOx ? 1.08 : 0.86, 0.88, 1.18);
            head.position.set(0, isOx ? 1.6 : 1.76, -1.58);
            group.add(head);
            const snout = new THREE.Mesh(new THREE.SphereGeometry(isOx ? 0.32 : 0.21, 14, 10), muzzle);
            snout.scale.set(1.1, 0.62, 0.82);
            snout.position.set(0, isOx ? 1.49 : 1.66, -1.96);
            group.add(snout);
            const legX = isOx ? 0.5 : 0.3;
            for (const x of [-legX, legX]) {
                for (const z of [-0.62, 0.62]) {
                    const leg = new THREE.Mesh(new THREE.CylinderGeometry(isOx ? 0.15 : 0.09, isOx ? 0.19 : 0.12, 0.9, 9), hide);
                    leg.position.set(x, 0.48, z);
                    group.add(leg);
                    const hoof = new THREE.Mesh(new THREE.BoxGeometry(isOx ? 0.36 : 0.28, 0.16, 0.42), black);
                    hoof.position.set(x, 0.05, z - 0.05);
                    group.add(hoof);
                }
            }
            const saddle = new THREE.Mesh(new THREE.BoxGeometry(isOx ? 1.42 : 0.82, 0.18, 1.16), isOx ? clothBlue : clothRed);
            saddle.position.set(0, isOx ? 1.58 : 1.65, 0.02);
            group.add(saddle);
            const saddleBlanket = new THREE.Mesh(new THREE.BoxGeometry(isOx ? 1.58 : 1.12, 0.08, 1.5), isOx ? clothRed : clothBlue);
            saddleBlanket.position.set(0, isOx ? 1.5 : 1.58, 0.08);
            saddleBlanket.rotation.x = 0.03;
            group.add(saddleBlanket);
            const girth = new THREE.Mesh(new THREE.TorusGeometry(isOx ? 0.72 : 0.52, 0.045, 7, 22), darkWood);
            girth.rotation.z = Math.PI / 2;
            girth.scale.z = isOx ? 1.28 : 1.12;
            girth.position.set(0, isOx ? 1.08 : 1.16, 0.04);
            group.add(girth);
            for (const side of [-1, 1]) {
                const nostril = new THREE.Mesh(new THREE.SphereGeometry(0.035, 8, 6), black);
                nostril.position.set(side * (isOx ? 0.15 : 0.1), isOx ? 1.51 : 1.67, isOx ? -2.22 : -2.12);
                group.add(nostril);
            }
            for (const side of [-1, 1]) {
                const ear = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.4, 8), hide);
                ear.rotation.z = side * -1.1;
                ear.position.set(side * 0.36, isOx ? 1.86 : 2, -1.48);
                group.add(ear);
                const eye = new THREE.Mesh(new THREE.SphereGeometry(0.055, 9, 7), black);
                eye.position.set(side * 0.27, isOx ? 1.68 : 1.83, -1.84);
                group.add(eye);
                if (isOx) {
                    const horn = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.7, 9), material(0xede0c3, 0.7));
                    horn.rotation.z = side * -1.05;
                    horn.position.set(side * 0.47, 1.96, -1.47);
                    group.add(horn);
                }
            }
            if (kind === 'horse') {
                for (let index = 0; index < 6; index++) {
                    const mane = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.38, 6), black);
                    mane.position.set(0, 1.68 + index * 0.07, -1.12 + index * 0.17);
                    mane.rotation.x = 0.72;
                    group.add(mane);
                }
                const bridle = new THREE.Mesh(new THREE.TorusGeometry(0.32, 0.025, 6, 22), darkWood);
                bridle.rotation.x = Math.PI / 2;
                bridle.scale.set(0.82, 1, 1.25);
                bridle.position.set(0, 1.74, -1.64);
                group.add(bridle);
                for (const side of [-1, 1]) {
                    const stirrupStrap = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.78, 6), darkWood);
                    stirrupStrap.position.set(side * 0.48, 1.26, 0.16);
                    const stirrup = new THREE.Mesh(new THREE.TorusGeometry(0.15, 0.025, 6, 16, Math.PI * 1.55), brass);
                    stirrup.rotation.z = Math.PI;
                    stirrup.position.set(side * 0.48, 0.88, 0.16);
                    group.add(stirrupStrap, stirrup);
                }
            } else {
                const hump = new THREE.Mesh(new THREE.SphereGeometry(0.58, 14, 10), hide);
                hump.scale.set(1.15, 0.65, 0.95);
                hump.position.set(0, 1.52, -0.52);
                const yoke = new THREE.Mesh(new THREE.BoxGeometry(2.05, 0.18, 0.24), darkWood);
                yoke.position.set(0, 1.83, -1.16);
                group.add(hump, yoke);
            }
            const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.06, 1.1, 7), hide);
            tail.rotation.x = -0.48;
            tail.position.set(0, 1.02, 1.18);
            group.add(tail);
        }

        group.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (!mesh.isMesh) return;
            mesh.castShadow = true;
            mesh.receiveShadow = true;
        });
        return group;
    }

    private createTigerMountVisual(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'striped-korean-tiger';
        const material = (color: number, roughness: number = 0.82) => new THREE.MeshStandardMaterial({ color, roughness });
        const orange = material(0xe87818, 0.86);
        const cream = material(0xf3d4a1, 0.9);
        const black = material(0x171411, 0.78);
        const eyeGold = new THREE.MeshStandardMaterial({ color: 0xfacc15, emissive: 0xa16207, emissiveIntensity: 0.45, roughness: 0.4 });
        const saddleCloth = material(0x8f2f2a, 0.72);

        const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.57, 1.72, 8, 18), orange);
        body.rotation.x = Math.PI / 2;
        body.scale.set(1.12, 1, 0.9);
        body.position.y = 1.08;
        group.add(body);
        const chest = new THREE.Mesh(new THREE.SphereGeometry(0.59, 18, 14), orange);
        chest.scale.set(1.08, 1.05, 0.88);
        chest.position.set(0, 1.22, -1.05);
        group.add(chest);
        const head = new THREE.Mesh(new THREE.SphereGeometry(0.52, 20, 16), orange);
        head.scale.set(1.04, 0.94, 0.9);
        head.position.set(0, 1.63, -1.62);
        group.add(head);
        for (const side of [-1, 1]) {
            const cheek = new THREE.Mesh(new THREE.SphereGeometry(0.26, 14, 11), cream);
            cheek.scale.set(1, 0.78, 0.72);
            cheek.position.set(side * 0.2, 1.49, -2.02);
            group.add(cheek);
            const ear = new THREE.Mesh(new THREE.ConeGeometry(0.21, 0.38, 10), orange);
            ear.rotation.z = side * -0.24;
            ear.position.set(side * 0.35, 2.03, -1.58);
            group.add(ear);
            const innerEar = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.22, 10), black);
            innerEar.rotation.z = side * -0.24;
            innerEar.position.set(side * 0.35, 2.04, -1.72);
            group.add(innerEar);
            const eye = new THREE.Mesh(new THREE.SphereGeometry(0.075, 12, 9), eyeGold);
            eye.scale.set(1.25, 0.65, 0.52);
            eye.position.set(side * 0.22, 1.73, -2.02);
            group.add(eye);
            const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.035, 10, 8), black);
            pupil.scale.y = 1.4;
            pupil.position.set(side * 0.22, 1.73, -2.06);
            group.add(pupil);
            for (let index = 0; index < 3; index++) {
                const whisker = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.009, 0.6 + index * 0.08, 6), cream);
                whisker.rotation.z = Math.PI / 2 + side * (0.12 + index * 0.07);
                whisker.position.set(side * (0.39 + index * 0.03), 1.47 - index * 0.07, -2.12);
                group.add(whisker);
            }
            for (let index = 0; index < 5; index++) {
                const sideStripe = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.32, 0.28), black);
                sideStripe.position.set(side * 0.6, 1.18 + (index % 2) * 0.11, -0.55 + index * 0.38);
                sideStripe.rotation.x = (index % 2 === 0 ? -1 : 1) * 0.22;
                group.add(sideStripe);
            }
        }
        const nose = new THREE.Mesh(new THREE.SphereGeometry(0.11, 12, 9), black);
        nose.scale.set(1.2, 0.7, 0.72);
        nose.position.set(0, 1.53, -2.2);
        group.add(nose);
        const browMark = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.26, 0.045), black);
        browMark.position.set(0, 1.88, -2.03);
        group.add(browMark);
        for (let index = 0; index < 7; index++) {
            const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.84 - Math.abs(index - 3) * 0.055, 0.055, 0.15), black);
            stripe.position.set(0, 1.62, -0.72 + index * 0.27);
            stripe.rotation.y = (index % 2 === 0 ? -1 : 1) * 0.12;
            group.add(stripe);
        }
        for (const x of [-0.42, 0.42]) {
            for (const z of [-0.75, 0.7]) {
                const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.15, 0.46, 5, 10), orange);
                leg.position.set(x, 0.48, z);
                group.add(leg);
                const paw = new THREE.Mesh(new THREE.SphereGeometry(0.23, 14, 10), orange);
                paw.scale.set(1.15, 0.55, 1.35);
                paw.position.set(x, 0.11, z - 0.12);
                group.add(paw);
                const ankleStripe = new THREE.Mesh(new THREE.TorusGeometry(0.155, 0.025, 6, 18), black);
                ankleStripe.rotation.x = Math.PI / 2;
                ankleStripe.position.set(x, 0.47, z);
                group.add(ankleStripe);
            }
        }
        const saddle = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.12, 0.92), saddleCloth);
        saddle.position.set(0, 1.65, 0.12);
        group.add(saddle);
        for (let index = 0; index < 7; index++) {
            const tailSegment = new THREE.Mesh(new THREE.SphereGeometry(0.115 - index * 0.006, 10, 8), index % 2 === 0 ? orange : black);
            tailSegment.position.set(Math.sin(index * 0.45) * 0.24, 1.08 + index * 0.11, 1.46 + index * 0.24);
            group.add(tailSegment);
        }
        return this.prepareDecoration(group);
    }

    private createJoseonShipMount(turtleShip: boolean): THREE.Group {
        const group = new THREE.Group();
        const material = (color: number, roughness: number = 0.82, metalness: number = 0.02) => new THREE.MeshStandardMaterial({ color, roughness, metalness });
        const hullWood = material(turtleShip ? 0x40352b : 0x6d4329, 0.9);
        const darkWood = material(0x2c2119, 0.92);
        const painted = material(turtleShip ? 0x455a4d : 0x9b3d30, 0.76);
        const brass = material(0xc39b43, 0.48, 0.34);
        const hull = new THREE.Mesh(new THREE.BoxGeometry(2.7, 0.72, 5.6), hullWood);
        hull.position.y = 0.62;
        group.add(hull);
        const keel = new THREE.Mesh(new THREE.BoxGeometry(2.05, 0.45, 5.95), darkWood);
        keel.position.y = 0.25;
        group.add(keel);
        for (const z of [-2.48, 2.48]) {
            const end = new THREE.Mesh(new THREE.ConeGeometry(1.36, 1.25, 4), hullWood);
            end.rotation.x = z < 0 ? -Math.PI / 2 : Math.PI / 2;
            end.rotation.y = Math.PI / 4;
            end.position.set(0, 0.63, z + (z < 0 ? -0.48 : 0.48));
            group.add(end);
        }
        const deck = new THREE.Mesh(new THREE.BoxGeometry(2.95, 0.22, 4.9), painted);
        deck.position.y = 1.12;
        group.add(deck);
        for (const side of [-1, 1]) {
            const rail = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.62, 5.15), darkWood);
            rail.position.set(side * 1.42, 1.43, 0);
            group.add(rail);
            for (const z of [-2, -1, 0, 1, 2]) {
                const post = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.07, 0.78, 7), darkWood);
                post.position.set(side * 1.42, 1.45, z);
                group.add(post);
            }
            for (const z of [-1.75, -0.58, 0.58, 1.75]) {
                const oar = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.055, 2.6, 7), darkWood);
                oar.rotation.z = side * 1.16;
                oar.position.set(side * 1.88, 1.16, z);
                const blade = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.55, 0.08), darkWood);
                blade.rotation.z = side * 1.16;
                blade.position.set(side * 2.78, 0.77, z);
                group.add(oar, blade);
            }
        }
        if (turtleShip) {
            const shell = new THREE.Mesh(new THREE.SphereGeometry(1.45, 20, 12, 0, Math.PI * 2, 0, Math.PI * 0.55), material(0x5f6c58, 0.82, 0.08));
            shell.scale.set(0.92, 0.82, 1.82);
            shell.position.y = 1.28;
            group.add(shell);
            for (let row = 0; row < 3; row++) {
                for (let column = -2; column <= 2; column++) {
                    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.34, 7), brass);
                    spike.position.set(column * 0.45, 2.28 - Math.abs(column) * 0.05, -1 + row * 0.92);
                    group.add(spike);
                }
            }
            const dragonHead = new THREE.Mesh(new THREE.SphereGeometry(0.42, 14, 10), material(0x7b3f2e, 0.7));
            dragonHead.scale.set(0.92, 0.72, 1.35);
            dragonHead.position.set(0, 1.35, -3.45);
            group.add(dragonHead);
            for (const side of [-1, 1]) {
                const horn = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.52, 7), brass);
                horn.rotation.z = side * 0.45;
                horn.position.set(side * 0.28, 1.78, -3.38);
                group.add(horn);
                for (const z of [-1.85, -0.65, 0.65, 1.85]) {
                    const gunport = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.15, 12), darkWood);
                    gunport.rotation.z = Math.PI / 2;
                    gunport.position.set(side * 1.48, 1.38, z);
                    group.add(gunport);
                }
            }
        } else {
            const cabin = new THREE.Mesh(new THREE.BoxGeometry(2.15, 1.15, 1.65), material(0xd7c7a1, 0.9));
            cabin.position.set(0, 1.75, 0.8);
            group.add(cabin);
            const roof = new THREE.Mesh(new THREE.CylinderGeometry(1.48, 1.48, 0.18, 4), darkWood);
            roof.rotation.y = Math.PI / 4;
            roof.scale.z = 0.72;
            roof.position.set(0, 2.42, 0.8);
            group.add(roof);
            const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 4.6, 9), darkWood);
            mast.position.set(0, 3.05, -0.9);
            group.add(mast);
            const sail = new THREE.Mesh(new THREE.PlaneGeometry(2.55, 2.55), material(0xe6dcc4, 0.94));
            sail.position.set(0, 3.35, -0.84);
            sail.rotation.y = Math.PI;
            group.add(sail);
            for (const y of [2.3, 2.82, 3.34, 3.86, 4.38]) {
                const batten = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 2.48, 6), darkWood);
                batten.rotation.z = Math.PI / 2;
                batten.position.set(0, y, -0.9);
                group.add(batten);
            }
        }
        return group;
    }

    private createObstacles() {
        if (!this.scene) return;
        this.activeMap.obstacles.forEach((config, index) => {
            const group = new THREE.Group();
            const kind = config.obstacleKind || 'basic';
            const material = (color: number, roughness: number = 0.82, metalness: number = 0.02) => new THREE.MeshStandardMaterial({ color, roughness, metalness });
            if (kind === 'fence') {
                const wood = material(0x6e4629, 0.92);
                for (const x of [-1.5, 0, 1.5]) {
                    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.18, 2.35, 8), wood);
                    post.position.set(x, 1.1, 0);
                    group.add(post);
                }
                for (const y of [0.72, 1.55]) {
                    const rail = new THREE.Mesh(new THREE.BoxGeometry(3.5, 0.22, 0.28), wood);
                    rail.position.set(0, y, 0);
                    group.add(rail);
                }
            } else if (kind === 'scarecrow') {
                const wood = material(0x60432d, 0.9);
                const straw = material(0xd8b45d, 0.94);
                const cloth = material(index % 2 === 0 ? 0x4a6b82 : 0x8b493c, 0.86);
                const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 3.3, 8), wood);
                pole.position.y = 1.55;
                group.add(pole);
                const arms = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 3.25, 8), wood);
                arms.rotation.z = Math.PI / 2;
                arms.position.y = 2.15;
                group.add(arms);
                const body = new THREE.Mesh(new THREE.ConeGeometry(0.82, 1.65, 8), cloth);
                body.position.y = 1.5;
                group.add(body);
                const head = new THREE.Mesh(new THREE.SphereGeometry(0.42, 12, 9), straw);
                head.position.y = 2.85;
                group.add(head);
                const hat = new THREE.Mesh(new THREE.ConeGeometry(0.72, 0.72, 12), material(0x3f3021, 0.94));
                hat.position.y = 3.38;
                group.add(hat);
            } else if (kind === 'fortress-wall') {
                const stone = material(0x817c70, 0.98);
                const wall = new THREE.Mesh(new THREE.BoxGeometry(4.8, 2.5, 1.55), stone);
                wall.position.y = 1.25;
                group.add(wall);
                for (const x of [-1.9, -0.95, 0, 0.95, 1.9]) {
                    const merlon = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.72, 1.7), stone);
                    merlon.position.set(x, 2.85, 0);
                    group.add(merlon);
                }
                const gateMark = new THREE.Mesh(new THREE.BoxGeometry(1.25, 1.55, 0.12), material(0x43352b, 0.9));
                gateMark.position.set(0, 0.82, -0.84);
                group.add(gateMark);
            } else {
                const neon = new THREE.MeshStandardMaterial({
                    color: index % 2 === 0 ? 0xfb7185 : 0xa78bfa,
                    emissive: index % 2 === 0 ? 0xbe123c : 0x6d28d9,
                    emissiveIntensity: 0.85,
                    roughness: 0.38,
                    metalness: 0.45
                });
                const base = new THREE.Mesh(new THREE.BoxGeometry(2.7, 1.45, 1.8), neon);
                base.position.y = 0.72;
                group.add(base);
                const band = new THREE.Mesh(new THREE.BoxGeometry(2.82, 0.18, 1.92), new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 0.5 }));
                band.position.y = 0.85;
                group.add(band);
            }
            group.traverse((object: THREE.Object3D) => {
                const mesh = object as THREE.Mesh;
                if (!mesh.isMesh) return;
                mesh.castShadow = true;
                mesh.receiveShadow = true;
            });
            this.positionMapObject(group, config, 0);
            group.scale.setScalar(config.scale);
            this.tagEditor3dSelectable(group, 'obstacles', config.id);
            this.scene!.add(group);
            this.obstacles.push({ config, mesh: group, hit: false });
        });
    }

    private createGates() {
        if (!this.scene) return;
        this.gates.forEach((gate, index) => {
            const group = gate.design === 'hanok' ? this.createHanokGate() : this.createNeonGate(index);
            this.positionMapObject(group, gate, 0);
            group.scale.setScalar(gate.scale);
            this.tagEditor3dSelectable(group, 'gates', gate.id);
            group.traverse((object: THREE.Object3D) => {
                const mesh = object as THREE.Mesh;
                const item = mesh.material as THREE.MeshStandardMaterial;
                if (!item || item.type !== 'MeshStandardMaterial') return;
                item.userData['baseColor'] = item.color.getHex();
                item.userData['baseEmissive'] = item.emissive.getHex();
                item.userData['baseEmissiveIntensity'] = item.emissiveIntensity;
            });
            gate.mesh = group;
            this.scene!.add(group);
        });
    }

    private createHiddenNpcs() {
        if (!this.scene || this.gameMode !== 'explore' || this.activeMap.hiddenNpcCount <= 0) return;
        for (let index = 0; index < this.activeMap.hiddenNpcCount; index++) {
            const mesh = this.createHiddenNpcVisual(index);
            const npc: HiddenNpcState = { id: createId('hidden-npc'), mesh, resolved: false };
            this.placeHiddenNpc(npc, index);
            this.scene.add(mesh);
            this.hiddenNpcs.push(npc);
        }
    }

    private createHiddenNpcVisual(index: number): THREE.Group {
        const group = new THREE.Group();
        group.name = 'hidden-npc';
        const colors = [0x4f46e5, 0x0f766e, 0x9f1239, 0x92400e];
        const cloth = new THREE.MeshStandardMaterial({ color: colors[index % colors.length], roughness: 0.78 });
        const trim = new THREE.MeshStandardMaterial({ color: 0xfbbf24, emissive: 0x92400e, emissiveIntensity: 0.18, roughness: 0.45, metalness: 0.2 });
        const skin = new THREE.MeshStandardMaterial({ color: 0xf0c7a5, roughness: 0.82 });
        const dark = new THREE.MeshStandardMaterial({ color: 0x171717, roughness: 0.66 });
        const body = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.82, 1.7, 12), cloth);
        body.position.y = 1.05;
        group.add(body);
        const belt = new THREE.Mesh(new THREE.TorusGeometry(0.58, 0.07, 6, 18), trim);
        belt.rotation.x = Math.PI / 2;
        belt.position.y = 1.25;
        group.add(belt);
        const head = new THREE.Mesh(new THREE.SphereGeometry(0.42, 16, 12), skin);
        head.position.y = 2.18;
        group.add(head);
        const hatBrim = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.72, 0.06, 24), dark);
        hatBrim.position.y = 2.5;
        group.add(hatBrim);
        const hatCrown = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.29, 0.5, 16), dark);
        hatCrown.position.y = 2.76;
        group.add(hatCrown);
        for (const side of [-1, 1]) {
            const eye = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 6), dark);
            eye.position.set(side * 0.14, 2.22, -0.38);
            group.add(eye);
        }
        const aura = new THREE.Mesh(new THREE.TorusGeometry(1.05, 0.08, 8, 28), trim.clone());
        aura.name = 'hidden-npc-aura';
        aura.rotation.x = Math.PI / 2;
        aura.position.y = 0.1;
        group.add(aura);
        const marker = new THREE.Mesh(new THREE.TorusGeometry(0.24, 0.06, 8, 20, Math.PI * 1.55), trim.clone());
        marker.name = 'hidden-npc-marker';
        marker.position.y = 3.62;
        marker.rotation.z = 0.2;
        group.add(marker);
        const dot = new THREE.Mesh(new THREE.SphereGeometry(0.075, 10, 8), trim.clone());
        dot.position.y = 3.18;
        group.add(dot);
        group.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (!mesh.isMesh) return;
            mesh.castShadow = true;
            mesh.receiveShadow = true;
        });
        return group;
    }

    private placeHiddenNpc(npc: HiddenNpcState, index: number) {
        const safeLength = Math.max(1, this.trackLength - 44);
        const distance = Math.min(this.trackLength - 14, 22 + Math.random() * safeLength);
        const frame = this.getPathFrame(this.activeMap, distance);
        const direction = index % 2 === 0 ? 1 : -1;
        const offset = direction * (this.trackWidth / 2 + 5 + Math.random() * 14);
        npc.mesh.position.copy(frame.point).addScaledVector(frame.right, offset);
        npc.mesh.position.y = frame.point.y;
        npc.mesh.rotation.y = Math.atan2(frame.forward.x, frame.forward.z) + (direction > 0 ? Math.PI / 2 : -Math.PI / 2);
        npc.mesh.userData['pathDistance'] = distance;
    }

    private createNeonGate(index: number): THREE.Group {
        const group = new THREE.Group();
        group.name = 'layered-neon-quiz-gate';
        const material = new THREE.MeshStandardMaterial({
            color: index % 3 === 0 ? 0x22d3ee : (index % 3 === 1 ? 0xa78bfa : 0x34d399),
            emissive: index % 3 === 0 ? 0x0891b2 : (index % 3 === 1 ? 0x6d28d9 : 0x059669),
            emissiveIntensity: 1.65,
            roughness: 0.25,
            metalness: 0.68
        });
        const frameMaterial = new THREE.MeshStandardMaterial({ color: 0x111827, emissive: 0x020617, emissiveIntensity: 0.35, roughness: 0.3, metalness: 0.82 });
        for (const side of [-1, 1]) {
            const base = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 1.08, 0.42, 8), frameMaterial);
            base.position.set(side * (this.trackWidth / 2 - 1), 0.21, 0);
            group.add(base);
            const outerPost = new THREE.Mesh(new THREE.BoxGeometry(0.96, 7.2, 1), frameMaterial);
            outerPost.position.set(side * (this.trackWidth / 2 - 1), 3.6, 0.14);
            group.add(outerPost);
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.62, 6.8, 0.62), material);
            post.position.set(side * (this.trackWidth / 2 - 1), 3.4, 0);
            group.add(post);
            for (const y of [1.2, 2.75, 4.3, 5.85]) {
                const node = new THREE.Mesh(new THREE.OctahedronGeometry(0.22, 0), material);
                node.position.set(side * (this.trackWidth / 2 - 1), y, -0.48);
                group.add(node);
            }
        }
        const outerHeader = new THREE.Mesh(new THREE.BoxGeometry(this.trackWidth - 0.7, 1.02, 1.06), frameMaterial);
        outerHeader.position.y = 6.58;
        group.add(outerHeader);
        const header = new THREE.Mesh(new THREE.BoxGeometry(this.trackWidth - 1.5, 0.68, 0.72), material);
        header.position.y = 6.55;
        group.add(header);
        const doorWidth = Math.max(1, (this.trackWidth - 2.4) / 2);
        const doorMaterial = new THREE.MeshStandardMaterial({
            color: material.color,
            emissive: material.emissive,
            emissiveIntensity: 1.4,
            roughness: 0.24,
            metalness: 0.52,
            transparent: true,
            opacity: 0.72
        });
        for (const side of [-1, 1]) {
            const door = new THREE.Mesh(new THREE.BoxGeometry(doorWidth - 0.14, 5.45, 0.18), doorMaterial.clone());
            door.name = side < 0 ? 'gate-door-left' : 'gate-door-right';
            door.userData['gateDoor'] = true;
            door.position.set(side * doorWidth / 2, 3.02, 0.08);
            group.add(door);
            for (const x of [-0.28, 0.28]) {
                const energyLine = new THREE.Mesh(new THREE.BoxGeometry(0.055, 4.95, 0.08), material.clone());
                energyLine.userData['gateDoor'] = true;
                energyLine.position.set(side * doorWidth / 2 + x * doorWidth, 3.02, -0.08);
                group.add(energyLine);
            }
        }
        const marker = new THREE.Mesh(new THREE.TorusGeometry(1.15, 0.22, 10, 28), new THREE.MeshStandardMaterial({
            color: 0xffffff,
            emissive: material.color,
            emissiveIntensity: 2.3,
            roughness: 0.2
        }));
        marker.name = 'gate-marker';
        marker.position.y = 5.25;
        group.add(marker);
        return this.prepareDecoration(group);
    }

    private createHanokGate(): THREE.Group {
        const group = new THREE.Group();
        group.name = 'premium-hanok-quiz-gate';
        const timber = this.createHeritageMaterial('korean-timber.webp', 0x713a26, 2.3, 1.1, 0.78);
        const painted = new THREE.MeshStandardMaterial({ color: 0x9d3b2d, emissive: 0x3b100d, emissiveIntensity: 0.12, roughness: 0.7 });
        const dancheong = new THREE.MeshStandardMaterial({ color: 0x296459, roughness: 0.68 });
        const stone = new THREE.MeshStandardMaterial({ color: 0x8a8175, roughness: 0.96 });
        const brass = new THREE.MeshStandardMaterial({ color: 0xc49b43, roughness: 0.42, metalness: 0.48 });
        const signMaterial = new THREE.MeshStandardMaterial({ color: 0xe8c871, emissive: 0x7c4a0d, emissiveIntensity: 0.38, roughness: 0.45 });
        const postX = this.trackWidth / 2 - 1;
        for (const side of [-1, 1]) {
            const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.78, 0.45, 8), stone);
            foot.position.set(side * postX, 0.23, 0);
            group.add(foot);
            const post = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.5, 6.2, 10), painted);
            post.position.set(side * postX, 3.35, 0);
            group.add(post);
            const door = new THREE.Mesh(new THREE.BoxGeometry(2.15, 3.7, 0.24), timber);
            door.userData['gateDoor'] = true;
            door.position.set(side * (postX - 1.25), 2.25, 0.12);
            group.add(door);
            for (const y of [1.15, 2.25, 3.35]) {
                const brace = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.12, 0.32), painted);
                brace.userData['gateDoor'] = true;
                brace.position.set(side * (postX - 1.25), y, -0.03);
                group.add(brace);
            }
        }
        const crossbeam = new THREE.Mesh(new THREE.BoxGeometry(this.trackWidth - 0.8, 0.58, 0.75), timber);
        crossbeam.position.y = 5.85;
        group.add(crossbeam);
        const threshold = new THREE.Mesh(new THREE.BoxGeometry(this.trackWidth - 1.35, 0.32, 1.08), stone);
        threshold.position.y = 0.16;
        group.add(threshold);
        const doorWidth = Math.max(1, (this.trackWidth - 2.6) / 2);
        for (const side of [-1, 1]) {
            const door = new THREE.Mesh(new THREE.BoxGeometry(doorWidth - 0.12, 4.8, 0.25), painted.clone());
            door.name = side < 0 ? 'gate-door-left' : 'gate-door-right';
            door.userData['gateDoor'] = true;
            door.position.set(side * doorWidth / 2, 2.65, 0.08);
            group.add(door);
            for (const y of [1.2, 2.45, 3.7]) {
                const brace = new THREE.Mesh(new THREE.BoxGeometry(doorWidth - 0.3, 0.14, 0.34), timber);
                brace.name = `${door.name}-brace`;
                brace.userData['gateDoor'] = true;
                brace.position.set(side * doorWidth / 2, y, -0.08);
                group.add(brace);
            }
            for (const rowY of [1.15, 2.05, 2.95, 3.85]) {
                for (const column of [-0.32, 0, 0.32]) {
                    const stud = new THREE.Mesh(new THREE.SphereGeometry(0.075, 9, 7), brass);
                    stud.userData['gateDoor'] = true;
                    stud.position.set(side * doorWidth / 2 + column * doorWidth, rowY, -0.19);
                    group.add(stud);
                }
            }
            const ringHandle = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.035, 7, 20), brass);
            ringHandle.userData['gateDoor'] = true;
            ringHandle.position.set(side * 0.38, 2.45, -0.23);
            group.add(ringHandle);
        }
        for (const [index, x] of [-postX, -postX * 0.5, 0, postX * 0.5, postX].entries()) {
            this.addHanokBracketCluster(group, x, 5.92, -0.34, -1, timber, dancheong, painted, index % 2 === 0);
            this.addHanokBracketCluster(group, x, 5.92, 0.34, 1, timber, dancheong, painted, index % 2 !== 0);
        }
        this.addHanokRoof(group, this.trackWidth + 1.6, 3.2, 6.55);
        const marker = new THREE.Mesh(new THREE.TorusGeometry(0.95, 0.18, 10, 28), signMaterial);
        marker.name = 'gate-marker';
        marker.position.y = 5.05;
        group.add(marker);
        const plaque = new THREE.Mesh(new THREE.BoxGeometry(2.8, 0.82, 0.18), new THREE.MeshStandardMaterial({ color: 0x17322a, roughness: 0.62 }));
        plaque.position.set(0, 6.05, -0.58);
        group.add(plaque);
        return this.prepareDecoration(group);
    }

    private createFinishLine() {
        if (!this.scene) return;
        const group = new THREE.Group();
        const material = new THREE.MeshStandardMaterial({ color: 0xf8fafc, emissive: 0x38bdf8, emissiveIntensity: 1.15, roughness: 0.25, metalness: 0.55 });
        for (const side of [-1, 1]) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.8, 8.5, 0.8), material);
            post.position.set(side * (this.trackWidth / 2 - 1), 4.25, 0);
            group.add(post);
        }
        const top = new THREE.Mesh(new THREE.BoxGeometry(this.trackWidth - 1.4, 1, 0.9), material);
        top.position.y = 8;
        group.add(top);
        const config = createBlankPlacement('goal', this.trackLength);
        this.positionMapObject(group, config, 0);
        this.scene.add(group);
    }

    private positionMapObject(object: THREE.Object3D, config: MapObjectPlacement, baseHeight: number) {
        const frame = this.getRuntimePathFrame(config.distance);
        object.position.copy(frame.point)
            .addScaledVector(frame.right, config.lane)
            .addScaledVector(frame.surfaceUp, config.height + baseHeight);
        object.quaternion.copy(this.createPathOrientation(
            frame,
            -Math.PI + THREE.MathUtils.degToRad(config.rotation)
        ));
    }

    private rebuildMinimapData() {
        if (this.explorePathSamples.length === 0) {
            this.minimapPathPoints = '';
            this.minimapGateMarkers = [];
            return;
        }
        const step = Math.max(1, Math.ceil(this.explorePathSamples.length / 220));
        const minimapSamples = this.explorePathSamples.filter((_, index) => index % step === 0);
        const lastSample = this.explorePathSamples[this.explorePathSamples.length - 1];
        if (minimapSamples[minimapSamples.length - 1] !== lastSample) minimapSamples.push(lastSample);
        this.minimapPathPoints = minimapSamples
            .map((sample) => `${sample.point.x.toFixed(2)},${sample.point.z.toFixed(2)}`)
            .join(' ');

        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let minZ = Number.POSITIVE_INFINITY;
        let maxZ = Number.NEGATIVE_INFINITY;
        minimapSamples.forEach((sample) => {
            minX = Math.min(minX, sample.point.x);
            maxX = Math.max(maxX, sample.point.x);
            minZ = Math.min(minZ, sample.point.z);
            maxZ = Math.max(maxZ, sample.point.z);
        });
        const padding = Math.max(12, this.activeMap.trackWidth * 1.25);
        const centerX = (minX + maxX) / 2;
        const centerZ = (minZ + maxZ) / 2;
        this.minimapBaseSize = Math.max(40, maxX - minX + padding * 2, maxZ - minZ + padding * 2);
        this.minimapBounds = {
            minX: centerX - this.minimapBaseSize / 2,
            maxX: centerX + this.minimapBaseSize / 2,
            minZ: centerZ - this.minimapBaseSize / 2,
            maxZ: centerZ + this.minimapBaseSize / 2
        };
        this.minimapStartX = this.explorePathSamples[0].point.x;
        this.minimapStartZ = this.explorePathSamples[0].point.z;
        this.minimapFinishX = lastSample.point.x;
        this.minimapFinishZ = lastSample.point.z;
        this.minimapGateMarkers = this.gates.map((gate) => {
            const frame = this.getPathFrame(this.activeMap, gate.distance);
            const point = frame.point.clone().addScaledVector(frame.right, gate.lane);
            return { id: gate.id, x: point.x, z: point.z, gate };
        });
    }

    private refreshMinimapPlayerMarker() {
        if (this.gameMode === 'explore') {
            this.minimapPlayerX = this.explorePosition.x;
            this.minimapPlayerZ = this.explorePosition.z;
            this.minimapPlayerRotation = -THREE.MathUtils.radToDeg(this.exploreHeading);
            return;
        }
        const frame = this.getPathFrame(this.activeMap, this.progress);
        const point = frame.point.clone().addScaledVector(frame.right, this.lateralOffset);
        this.minimapPlayerX = point.x;
        this.minimapPlayerZ = point.z;
        this.minimapPlayerRotation = -THREE.MathUtils.radToDeg(Math.atan2(frame.forward.x, frame.forward.z));
    }

    private resetRun() {
        this.clearActiveMount(false);
        this.progress = 0;
        this.lateralOffset = 0;
        this.coinSteerBonus = 0;
        this.magnetRemaining = 0;
        this.supplementRemaining = 0;
        this.jumpHeight = 0;
        this.jumpVelocity = 0;
        this.jumpsUsed = 0;
        this.grounded = true;
        this.falling = false;
        this.fallOffset = 0;
        this.fallVelocity = 0;
        this.checkpoint = 0;
        this.sceneryCollisionStatusAt = 0;
        if (this.player) this.player.userData['runnerBank'] = 0;
        this.clearControls();
        this.exploreCameraPitchOffset = 0;
        this.minimapZoom = 4;
        this.exploreCameraAdjustMode = this.gameMode === 'explore';
        const startFrame = this.getPathFrame(this.activeMap, 0);
        this.explorePosition.copy(startFrame.point);
        this.exploreHeading = Math.atan2(startFrame.forward.x, startFrame.forward.z);
        this.exploreCameraHeading = this.exploreHeading;
        this.coinCount = 0;
        this.progressPercent = 0;
        this.gatesPassed = 0;
        this.correctCount = 0;
        this.answerRecords = [];
        this.activeQuizKind = 'gate';
        this.hiddenNpcScore = 0;
        this.obstacleCollisionCount = 0;
        this.hiddenNpcFound = 0;
        this.hiddenNpcReward = 0;
        this.hiddenNpcAnswerRecords = [];
        this.activeHiddenNpc = undefined;
        this.activeHaechiGuide = undefined;
        this.activeQuestion = null;
        this.quizSelectedAnswer = null;
        this.activeGateName = '';
        this.runTime = 0;
        this.statusExpiresAt = 0;
        this.coins.forEach((coin) => {
            coin.collected = false;
            coin.mesh.visible = true;
            this.positionMapObject(coin.mesh, coin.config, 1.75);
            coin.homePosition.copy(coin.mesh.position);
        });
        this.magnets.forEach((magnet) => {
            magnet.collected = false;
            magnet.mesh.visible = true;
            this.positionMapObject(magnet.mesh, magnet.config, 1.25);
            magnet.homePosition.copy(magnet.mesh.position);
        });
        this.supplements.forEach((supplement) => {
            supplement.collected = false;
            supplement.mesh.visible = true;
            this.positionMapObject(supplement.mesh, supplement.config, 1.15);
            supplement.homePosition.copy(supplement.mesh.position);
        });
        this.mounts.forEach((mount) => {
            mount.collected = false;
            mount.mesh.visible = true;
            this.positionMapObject(mount.mesh, mount.config, 1.05);
            mount.homePosition.copy(mount.mesh.position);
        });
        this.obstacles.forEach((obstacle) => {
            obstacle.hit = false;
            obstacle.mesh.visible = true;
        });
        this.gates.forEach((gate) => {
            gate.resolved = false;
            gate.selectedQuestion = undefined;
            if (gate.mesh) gate.mesh.visible = true;
            this.updateGateAppearance(gate);
        });
        this.hiddenNpcs.forEach((npc, index) => {
            npc.resolved = false;
            npc.question = undefined;
            npc.mesh.visible = true;
            this.placeHiddenNpc(npc, index);
        });
        this.haechiGuides.forEach((guide) => guide.shown = false);
        this.updatePlayerTransform(0);
        this.refreshMinimapPlayerMarker();
        this.snapCamera();
    }

    private readonly animate = (time: number) => {
        if (this.destroyed) return;
        const frameSeconds = Math.max((time - this.lastFrameTime) / 1000, 0);
        const minimumFrameSeconds = this.effectiveGraphicsQuality === 'low' ? 1 / 30 : 0;
        if (minimumFrameSeconds > 0 && frameSeconds < minimumFrameSeconds) {
            this.animationFrameId = requestAnimationFrame(this.animate);
            return;
        }
        const dt = Math.min(frameSeconds, 0.034);
        this.lastFrameTime = time;
        if (this.screen === 'skins') {
            this.renderCustomizationPreview();
            this.renderNfcPreview();
            this.resetFrameBudgetWindow();
        } else if (this.screen === 'editor' && this.editorViewMode === '3d') {
            this.updateEditor3dNavigation(dt, time);
            if (this.renderer && this.scene && this.camera) {
                this.renderer.render(this.scene, this.camera);
                this.updateFrameBudget(frameSeconds);
            }
        } else if (this.screen === 'editor' || this.screen === 'maps') {
            this.resetFrameBudgetWindow();
        } else {
            if (this.screen === 'playing') this.updateGame(dt);
            else if (this.screen !== 'paused') {
                if (this.screen === 'quiz' || this.screen === 'quizFeedback') {
                    this.runTime += dt;
                    this.refreshHud(dt);
                }
                this.animateWorld(dt, time);
            } else {
                this.resetFrameBudgetWindow();
            }
            if (this.renderer && this.scene && this.camera) {
                this.renderer.render(this.scene, this.camera);
                this.updateFrameBudget(frameSeconds);
            }
        }
        this.animationFrameId = requestAnimationFrame(this.animate);
    };

    private resetFrameBudgetWindow() {
        this.frameBudgetElapsed = 0;
        this.frameBudgetFrames = 0;
    }

    private updateFrameBudget(frameSeconds: number) {
        if (!this.renderer || document.hidden || frameSeconds <= 0 || frameSeconds > 0.25) {
            this.resetFrameBudgetWindow();
            return;
        }
        this.frameBudgetElapsed += frameSeconds;
        this.frameBudgetFrames += 1;
        if (this.frameBudgetElapsed < 2) return;

        const averageFps = this.frameBudgetFrames / this.frameBudgetElapsed;
        const renderInfo = (this.renderer as any).info?.render;
        this.averageFps = Math.round(averageFps);
        this.renderDrawCalls = Number(renderInfo?.calls || 0);
        this.renderTriangles = Number(renderInfo?.triangles || 0);
        this.renderer.domElement.dataset.averageFps = averageFps.toFixed(1);
        this.renderer.domElement.dataset.drawCalls = String(this.renderDrawCalls);
        this.renderer.domElement.dataset.triangles = String(this.renderTriangles);
        this.resetFrameBudgetWindow();
    }

    private updateGame(dt: number) {
        if (this.activeHaechiGuide) {
            this.updateCamera(dt);
            this.animateWorld(dt, performance.now());
            this.refreshHud(0);
            return;
        }
        this.runTime += dt;
        this.updateStatusTimer();
        const magnetWasActive = this.magnetRemaining > 0;
        const supplementWasActive = this.supplementRemaining > 0;
        this.magnetRemaining = Math.max(0, this.magnetRemaining - dt);
        this.supplementRemaining = Math.max(0, this.supplementRemaining - dt);
        if (magnetWasActive && this.magnetRemaining === 0) this.showTemporaryStatus('자석 효과가 끝났습니다.', 3);
        if (supplementWasActive && this.supplementRemaining === 0) this.showTemporaryStatus('영양제 효과가 끝났습니다.', 3);
        if (this.gameMode === 'runner' && this.mountRemaining > 0) {
            this.mountRemaining = Math.max(0, this.mountRemaining - dt);
            if (this.mountRemaining === 0) this.clearActiveMount(true);
        }
        if (this.gameMode === 'explore') {
            this.updateExploreGame(dt);
            return;
        }
        if (this.falling) {
            this.updateFall(dt);
            this.updatePlayerTransform(dt);
            this.updateCamera(dt);
            return;
        }

        const steer = (this.controls.right ? 1 : 0) - (this.controls.left ? 1 : 0);
        const previousLateral = this.lateralOffset;
        this.lateralOffset = THREE.MathUtils.clamp(this.lateralOffset + steer * this.currentSteeringSpeed * dt, -this.trackWidth / 2 - 3, this.trackWidth / 2 + 3);
        if (this.isRunnerSceneryBlocked(this.progress, this.lateralOffset)) this.lateralOffset = previousLateral;
        const previousProgress = this.progress;
        this.progress = this.advancePathProgress(this.progress, this.baseSpeed * dt);
        if (this.isRunnerSceneryBlocked(this.progress, this.lateralOffset)) {
            this.progress = previousProgress;
            if (performance.now() - this.sceneryCollisionStatusAt > 1500) {
                this.sceneryCollisionStatusAt = performance.now();
                this.showTemporaryStatus('장식에 길이 막혔습니다. 좌우로 피해가세요.', 3);
            }
        }
        if (!this.grounded) {
            this.jumpVelocity -= 27 * dt;
            this.jumpHeight += this.jumpVelocity * dt;
            if (this.jumpHeight <= 0) {
                this.jumpHeight = 0;
                this.jumpVelocity = 0;
                this.jumpsUsed = 0;
                this.grounded = true;
            }
        }

        const jumpPlatform = this.jumpPlatformAtDistance(this.progress);
        const onJumpRoad = this.isJumpRoadDistance(this.progress);
        if (this.grounded && onJumpRoad && !jumpPlatform) this.beginFall();
        const roadHalfWidth = onJumpRoad
            ? (jumpPlatform?.width || Math.max(14, this.trackWidth)) / 2
            : this.trackWidth / 2 + 0.75;
        if (!this.falling && Math.abs(this.lateralOffset) > roadHalfWidth + 0.15) this.beginFall();
        if (!this.falling) {
            this.collectNearbyMagnets();
            this.collectNearbySupplements();
            this.collectNearbyMounts();
            this.collectNearbyCoins();
            this.checkObstacleCollisions();
            this.checkHaechiGuideEntry();
            if (!this.activeHaechiGuide) {
                this.checkGateEntry();
                this.checkFinish();
            }
        }

        this.updatePlayerTransform(dt);
        this.updateCamera(dt);
        this.animateWorld(dt, performance.now());
        this.updateMagnetAttraction(dt);
        this.refreshHud(dt);
    }

    private updateEditor3dNavigation(dt: number, time: number) {
        const forward = new THREE.Vector3(Math.sin(this.exploreCameraHeading), 0, Math.cos(this.exploreCameraHeading));
        const right = new THREE.Vector3(-forward.z, 0, forward.x);
        const movement = new THREE.Vector3();
        if (this.controls.forward) movement.add(forward);
        if (this.controls.backward) movement.sub(forward);
        if (this.controls.right) movement.add(right);
        if (this.controls.left) movement.sub(right);
        if (movement.lengthSq() > 0) {
            movement.normalize();
            const speed = this.exploreBaseSpeed
                * this.exploreBaseSpeedMultiplier
                * this.statMultiplier(this.activeCharacterStats.steering)
                * (this.exploreSprintActive ? this.exploreSprintMultiplier : 1);
            const previousPosition = this.explorePosition.clone();
            const desiredPosition = this.explorePosition.clone().addScaledVector(movement, speed * dt);
            desiredPosition.x = THREE.MathUtils.clamp(desiredPosition.x, this.exploreBounds.minX, this.exploreBounds.maxX);
            desiredPosition.z = THREE.MathUtils.clamp(desiredPosition.z, this.exploreBounds.minZ, this.exploreBounds.maxZ);
            this.resolveExploreSceneryMovement(previousPosition, desiredPosition);
            this.exploreHeading = Math.atan2(movement.x, movement.z);
        }
        this.progress = this.findNearestExploreDistance(this.explorePosition.x, this.explorePosition.z);
        if (!this.grounded) {
            this.jumpVelocity -= 27 * dt;
            this.jumpHeight += this.jumpVelocity * dt;
            if (this.jumpHeight <= 0) {
                this.jumpHeight = 0;
                this.jumpVelocity = 0;
                this.jumpsUsed = 0;
                this.grounded = true;
            }
        }
        this.updatePlayerTransform(dt);
        this.updateCamera(dt);
        this.animateWorld(dt, time);
    }

    private updateExploreGame(dt: number) {
        if (this.falling) {
            this.updateFall(dt);
            this.updatePlayerTransform(dt);
            this.updateCamera(dt);
            this.animateWorld(dt, performance.now());
            this.refreshHud(dt);
            return;
        }
        const forward = new THREE.Vector3(Math.sin(this.exploreCameraHeading), 0, Math.cos(this.exploreCameraHeading));
        const right = new THREE.Vector3(-forward.z, 0, forward.x);
        const movement = new THREE.Vector3();
        if (this.controls.forward) movement.add(forward);
        if (this.controls.backward) movement.sub(forward);
        if (this.controls.right) movement.add(right);
        if (this.controls.left) movement.sub(right);
        if (movement.lengthSq() > 0) {
            movement.normalize();
            const speed = this.exploreBaseSpeed
                * this.exploreBaseSpeedMultiplier
                * this.statMultiplier(this.activeCharacterStats.steering)
                * (this.exploreSprintActive ? this.exploreSprintMultiplier : 1)
                * (this.supplementRemaining > 0 ? this.supplementSpeedMultiplier : 1)
                * (this.activeMountKind ? 2 : 1);
            const previousPosition = this.explorePosition.clone();
            const desiredPosition = this.explorePosition.clone().addScaledVector(movement, speed * dt);
            desiredPosition.x = THREE.MathUtils.clamp(desiredPosition.x, this.exploreBounds.minX, this.exploreBounds.maxX);
            desiredPosition.z = THREE.MathUtils.clamp(desiredPosition.z, this.exploreBounds.minZ, this.exploreBounds.maxZ);
            this.resolveExploreSceneryMovement(previousPosition, desiredPosition);
            this.exploreHeading = Math.atan2(movement.x, movement.z);
        }

        this.explorePosition.x = THREE.MathUtils.clamp(this.explorePosition.x, this.exploreBounds.minX, this.exploreBounds.maxX);
        this.explorePosition.z = THREE.MathUtils.clamp(this.explorePosition.z, this.exploreBounds.minZ, this.exploreBounds.maxZ);
        this.progress = this.findNearestExploreDistance(this.explorePosition.x, this.explorePosition.z);

        if (!this.grounded) {
            this.jumpVelocity -= 27 * dt;
            this.jumpHeight += this.jumpVelocity * dt;
            if (this.jumpHeight <= 0) {
                this.jumpHeight = 0;
                this.jumpVelocity = 0;
                this.jumpsUsed = 0;
                this.grounded = true;
            }
        }
        if (this.grounded && this.isExploreJumpVoid(this.explorePosition.x, this.explorePosition.z, this.progress)) {
            this.beginFall();
        }

        this.updatePlayerTransform(dt);
        this.collectNearbyMagnets();
        this.collectNearbySupplements();
        this.collectNearbyMounts();
        this.collectNearbyCoins();
        this.checkObstacleCollisions();
        this.checkHaechiGuideEntry();
        if (!this.activeHaechiGuide) this.checkHiddenNpcEntry();
        if (this.screen !== 'playing') {
            this.updateCamera(dt);
            this.animateWorld(dt, performance.now());
            this.refreshHud(dt);
            return;
        }
        if (!this.activeHaechiGuide) {
            this.checkGateEntry();
            this.checkFinish();
        }
        this.updateCamera(dt);
        this.animateWorld(dt, performance.now());
        this.updateMagnetAttraction(dt);
        this.refreshHud(dt);
    }

    private resolveExploreSceneryMovement(previous: THREE.Vector3, desired: THREE.Vector3) {
        const desiredSupport = this.reachableExploreSupport(desired.x, desired.z);
        if (desiredSupport !== null) return this.commitExplorePosition(desired, desiredSupport);
        const xOnly = previous.clone();
        xOnly.x = desired.x;
        const zOnly = previous.clone();
        zOnly.z = desired.z;
        const xSupport = this.reachableExploreSupport(xOnly.x, xOnly.z);
        const zSupport = this.reachableExploreSupport(zOnly.x, zOnly.z);
        if (xSupport !== null && zSupport !== null) {
            if (Math.abs(desired.x - previous.x) >= Math.abs(desired.z - previous.z)) {
                this.commitExplorePosition(xOnly, xSupport);
            } else {
                this.commitExplorePosition(zOnly, zSupport);
            }
        } else if (xSupport !== null) {
            this.commitExplorePosition(xOnly, xSupport);
        } else if (zSupport !== null) {
            this.commitExplorePosition(zOnly, zSupport);
        } else {
            this.explorePosition.copy(previous);
        }
    }

    private reachableExploreSupport(worldX: number, worldZ: number): number | null {
        const playerRadius = this.activeMountKind ? 1.25 : 0.72;
        const support = this.scenerySupportHeightAt(worldX, worldZ, playerRadius);
        const absoluteFootY = this.explorePosition.y + this.jumpHeight;
        const maxGroundedStep = 0.78;
        if (support <= this.explorePosition.y + maxGroundedStep) return support;
        if (!this.grounded && absoluteFootY >= support - 0.22) return support;
        const canGrabRoofEdge = !this.grounded
            && this.jumpsUsed >= this.maxJumpCount
            && this.jumpVelocity > 0
            && support - absoluteFootY <= 2.4;
        return canGrabRoofEdge ? support : null;
    }

    private commitExplorePosition(position: THREE.Vector3, support: number) {
        const previousSupport = this.explorePosition.y;
        const absoluteFootY = previousSupport + this.jumpHeight;
        this.explorePosition.copy(position);
        this.explorePosition.y = support;
        if (!this.grounded) {
            this.jumpHeight = Math.max(0, absoluteFootY - support);
            return;
        }
        if (previousSupport - support > 0.78) {
            this.grounded = false;
            this.jumpVelocity = Math.min(0, this.jumpVelocity);
            this.jumpHeight = Math.max(0, absoluteFootY - support);
            return;
        }
        this.jumpHeight = 0;
    }

    private scenerySupportHeightAt(worldX: number, worldZ: number, radius: number = 0): number {
        let support = this.sceneryGroundHeightAt(worldX, worldZ);
        const samples = radius > 0
            ? [[0, 0], [radius, 0], [-radius, 0], [0, radius], [0, -radius]]
            : [[0, 0]];
        this.sceneryColliders.forEach((collider) => {
            samples.forEach(([offsetX, offsetZ]) => {
                const height = this.sceneryColliderHeightAt(collider, worldX + offsetX, worldZ + offsetZ);
                if (height !== null) support = Math.max(support, height);
            });
        });
        return support;
    }

    private sceneryColliderHeightAt(collider: SceneryCollider, worldX: number, worldZ: number): number | null {
        if (collider.autoGate && (this.autoSceneryGates.get(collider.id)?.openAmount || 0) >= 0.45) return null;
        const dx = worldX - collider.centerX;
        const dz = worldZ - collider.centerZ;
        const cosine = Math.cos(collider.rotation);
        const sine = Math.sin(collider.rotation);
        const localX = dx * cosine - dz * sine;
        const localZ = dx * sine + dz * cosine;
        if (!collider.surface) {
            return Math.abs(localX) <= collider.halfWidth && Math.abs(localZ) <= collider.halfDepth
                ? collider.topY
                : null;
        }
        const surface = collider.surface;
        const modelX = localX / collider.scale;
        const modelZ = localZ / collider.scale;
        const column = Math.floor((modelX - surface.minX) / surface.cellSizeX);
        const row = Math.floor((modelZ - surface.minZ) / surface.cellSizeZ);
        if (column < 0 || column >= surface.columns || row < 0 || row >= surface.rows) return null;
        const height = surface.heights[row * surface.columns + column];
        return height === null || !Number.isFinite(height)
            ? null
            : collider.baseY + height * collider.verticalScale;
    }

    private isSceneryPositionBlocked(worldX: number, worldZ: number, footY: number): boolean {
        const playerRadius = this.activeMountKind ? 1.25 : 0.72;
        const terrainY = this.sceneryGroundHeightAt(worldX, worldZ);
        const supportY = this.scenerySupportHeightAt(worldX, worldZ, playerRadius);
        return supportY > terrainY + 0.78 && footY < supportY - 0.22;
    }

    private isRunnerSceneryBlocked(progress: number, lateral: number): boolean {
        if (this.sceneryColliders.length === 0) return false;
        const frame = this.getRuntimePathFrame(progress);
        const point = frame.point.clone().addScaledVector(frame.right, lateral);
        return this.isSceneryPositionBlocked(point.x, point.z, frame.point.y + this.jumpHeight);
    }

    private updateFall(dt: number) {
        this.fallVelocity += 24 * dt;
        this.fallOffset -= this.fallVelocity * dt;
        if (this.fallOffset < -13) {
            this.progress = this.safeRespawnDistance(Math.max(this.checkpoint, this.progress - 18));
            this.lateralOffset = 0;
            if (this.gameMode === 'explore') {
                const respawn = this.getPathFrame(this.activeMap, this.progress);
                this.explorePosition.copy(respawn.point);
                this.exploreHeading = Math.atan2(respawn.forward.x, respawn.forward.z);
                this.exploreCameraHeading = this.exploreHeading;
            }
            this.jumpHeight = 0;
            this.jumpVelocity = 0;
            this.jumpsUsed = 0;
            this.grounded = true;
            this.falling = false;
            this.fallOffset = 0;
            this.fallVelocity = 0;
            this.coinCount = Math.max(0, this.coinCount - 2);
            this.showTemporaryStatus('코스로 복귀했습니다. 엽전 2개가 차감됐습니다.', 3);
        }
    }

    private beginFall() {
        if (this.falling) return;
        this.falling = true;
        this.fallVelocity = 2;
        this.showTemporaryStatus('길에서 이탈했습니다. 체크포인트로 복귀합니다.', 3);
    }

    private jump() {
        if (this.falling || this.jumpsUsed >= this.maxJumpCount) return;
        this.grounded = false;
        this.jumpsUsed += 1;
        this.jumpVelocity = this.baseJumpVelocity
            * this.baseJumpVelocityMultiplier
            * this.statMultiplier(this.activeCharacterStats.jump);
        this.showTemporaryStatus(this.jumpsUsed === this.maxJumpCount ? '이단 점프!' : '점프!', 3);
    }

    private collectNearbyCoins() {
        this.coins.forEach((coin) => {
            if (coin.collected) return;
            if (this.gameMode === 'explore') {
                if (!this.isExploreNear(coin.mesh, this.itemPickupRange(2.35))) return;
                this.collectCoin(coin, false);
                return;
            }
            if (Math.abs(coin.config.distance - this.progress) > this.itemPickupRange(1.55)) return;
            if (Math.abs(coin.config.lane - this.lateralOffset) > this.itemPickupRange(1.35) * coin.config.scale) return;
            if (this.jumpHeight > this.itemPickupRange(2.7) + coin.config.height) return;
            this.collectCoin(coin, false);
        });
    }

    private collectNearbyMagnets() {
        this.magnets.forEach((magnet) => {
            if (magnet.collected) return;
            if (this.gameMode === 'explore') {
                if (!this.isExploreNear(magnet.mesh, this.itemPickupRange(2.5))) return;
            } else if (Math.abs(magnet.config.distance - this.progress) > this.itemPickupRange(1.7)) return;
            if (this.gameMode === 'runner' && Math.abs(magnet.config.lane - this.lateralOffset) > this.itemPickupRange(1.5) * magnet.config.scale) return;
            if (this.gameMode === 'explore') {
                magnet.collected = true;
                magnet.mesh.visible = false;
                this.magnetRemaining = 3;
                this.showTemporaryStatus('자석 활성화! 3초간 주변 코인을 끌어당깁니다.', 3);
                return;
            }
            if (this.jumpHeight > this.itemPickupRange(2.8) + magnet.config.height) return;
            magnet.collected = true;
            magnet.mesh.visible = false;
            this.magnetRemaining = 3;
            this.showTemporaryStatus('자석 활성화! 3초간 주변 코인을 끌어당깁니다.', 3);
        });
    }

    private collectNearbySupplements() {
        this.supplements.forEach((supplement) => {
            if (supplement.collected) return;
            if (this.gameMode === 'explore') {
                if (!this.isExploreNear(supplement.mesh, this.itemPickupRange(2.5))) return;
            } else if (Math.abs(supplement.config.distance - this.progress) > this.itemPickupRange(1.7)) return;
            if (this.gameMode === 'runner' && Math.abs(supplement.config.lane - this.lateralOffset) > this.itemPickupRange(1.5) * supplement.config.scale) return;
            if (this.gameMode === 'explore') {
                supplement.collected = true;
                supplement.mesh.visible = false;
                this.supplementRemaining = 10;
                this.showTemporaryStatus('영양제 효과! 10초간 자유 이동 속도가 1.5배가 됩니다.', 3);
                return;
            }
            if (this.jumpHeight > this.itemPickupRange(2.8) + supplement.config.height) return;
            supplement.collected = true;
            supplement.mesh.visible = false;
            this.supplementRemaining = 10;
            this.showTemporaryStatus('영양제 효과! 10초간 좌우 이동 속도가 1.5배가 됩니다.', 3);
        });
    }

    private collectNearbyMounts() {
        this.mounts.forEach((mount) => {
            if (mount.collected) return;
            if (this.gameMode === 'explore') {
                if (!this.isExploreNear(mount.mesh, 2.8)) return;
            } else {
                if (Math.abs(mount.config.distance - this.progress) > 2) return;
                if (Math.abs(mount.config.lane - this.lateralOffset) > 1.75 * mount.config.scale) return;
                if (this.jumpHeight > 3 + mount.config.height) return;
            }
            mount.collected = true;
            mount.mesh.visible = false;
            this.activateMount(mount.config.kind);
        });
    }

    private activateMount(kind: MountKind) {
        if (!this.player) return;
        const resolvedKind = this.resolveMountKind(kind);
        if (this.playerMountVisual) {
            this.player.remove(this.playerMountVisual);
            this.disposeObjectTree(this.playerMountVisual);
        }
        this.activeMountKind = resolvedKind;
        this.mountRemaining = this.gameMode === 'explore' ? 0 : 15;
        this.playerMountVisual = this.createMountVisual(resolvedKind);
        this.playerMountVisual.name = 'active-player-mount';
        this.player.add(this.playerMountVisual);
        this.applyMountRiderOffset();
        this.showTemporaryStatus(this.gameMode === 'explore'
            ? `${this.mountLabel(resolvedKind)} 탑승! 자유 탐색을 마칠 때까지 이동 속도가 2배가 됩니다.`
            : `${this.mountLabel(resolvedKind)} 탑승! 15초간 좌우 이동 민첩성이 35% 높아집니다.`, 3);
    }

    private resolveMountKind(kind: MountKind): Exclude<MountKind, 'random'> {
        if (kind !== 'random') return kind;
        const choices: Exclude<MountKind, 'random'>[] = ['palanquin', 'horse', 'ox', 'tiger', 'panokseon', 'turtle-ship'];
        return choices[Math.floor(Math.random() * choices.length)];
    }

    private clearActiveMount(expired: boolean) {
        if (this.playerMountVisual && this.player) {
            this.player.remove(this.playerMountVisual);
            this.disposeObjectTree(this.playerMountVisual);
        }
        const hadMount = !!this.activeMountKind;
        this.playerMountVisual = undefined;
        this.activeMountKind = null;
        this.mountRemaining = 0;
        this.applyMountRiderOffset();
        if (expired && hadMount) {
            this.showTemporaryStatus('탈것 효과가 끝나 다시 걸어서 이동합니다.', 3);
        }
    }

    private applyMountRiderOffset() {
        if (!this.playerVisual) return;
        if (this.activeMountKind === 'horse') this.playerVisual.position.y = 1.5;
        else if (this.activeMountKind === 'ox') this.playerVisual.position.y = 1.42;
        else if (this.activeMountKind === 'tiger') this.playerVisual.position.y = 1.5;
        else if (this.activeMountKind === 'palanquin') this.playerVisual.position.y = 1.32;
        else if (this.activeMountKind === 'panokseon') this.playerVisual.position.y = 1.72;
        else if (this.activeMountKind === 'turtle-ship') this.playerVisual.position.y = 1.9;
        else this.playerVisual.position.y = 0;
    }

    private updateMagnetAttraction(dt: number) {
        if (!this.player) return;
        const target = this.player.position.clone();
        target.y += 1.2;
        const attraction = 1 - Math.exp(-Math.max(dt, 0.001) * 10);
        const restore = 1 - Math.exp(-Math.max(dt, 0.001) * 7);
        this.coins.forEach((coin) => {
            if (coin.collected) return;
            const pathGap = Math.abs(coin.config.distance - this.progress);
            const exploreGap = this.player ? this.horizontalDistance(this.player.position, coin.mesh.position) : Number.POSITIVE_INFINITY;
            const inRange = this.gameMode === 'explore' ? exploreGap <= 20 : pathGap <= 20;
            if (this.magnetRemaining > 0 && inRange) {
                coin.mesh.position.lerp(target, attraction);
                if (coin.mesh.position.distanceTo(target) <= this.itemPickupRange(2.4)
                    || (this.gameMode === 'runner' && pathGap <= this.itemPickupRange(2.4))) this.collectCoin(coin, true);
            } else {
                coin.mesh.position.lerp(coin.homePosition, restore);
            }
        });
    }

    private collectCoin(coin: CoinState, attracted: boolean) {
        if (coin.collected) return;
        coin.collected = true;
        coin.mesh.visible = false;
        this.coinCount += 1;
        this.coinSteerBonus = Math.min(5, this.coinSteerBonus + 0.25);
        this.showTemporaryStatus(attracted
            ? '자석이 주변 엽전을 끌어당겼습니다.'
            : '엽전 획득! 좌우 이동 속도가 올랐습니다.', 3);
    }

    private checkObstacleCollisions() {
        this.obstacles.forEach((obstacle) => {
            const item = obstacle.config;
            if (obstacle.hit) return;
            if (this.gameMode === 'explore') {
                if (!this.isExploreNear(obstacle.mesh, 2.1 * item.scale)) return;
            } else if (Math.abs(item.distance - this.progress) > 1.7 * item.scale) return;
            if (this.gameMode === 'runner' && Math.abs(item.lane - this.lateralOffset) > 1.65 * item.scale) return;
            if (this.jumpHeight > 1.45 * item.scale + item.height) return;
            obstacle.hit = true;
            this.obstacleCollisionCount += 1;
            if (this.gameMode === 'runner') this.progress = Math.max(this.checkpoint, this.progress - 6);
            else this.explorePosition.add(new THREE.Vector3(-Math.sin(this.exploreHeading), 0, -Math.cos(this.exploreHeading)).multiplyScalar(3));
            const lostCoins = Math.min(5, this.coinCount);
            this.coinCount = Math.max(0, this.coinCount - 5);
            this.showTemporaryStatus(lostCoins > 0
                ? `장애물 충돌! 코인 ${lostCoins}개가 차감됐습니다.`
                : '장애물 충돌! 차감할 코인이 없습니다.', 3);
            obstacle.mesh.visible = false;
        });
    }

    private checkHaechiGuideEntry() {
        if (this.screen !== 'playing' || this.activeHaechiGuide) return;
        let runnerPosition: THREE.Vector3 | undefined;
        if (this.gameMode === 'runner') {
            const frame = this.getRuntimePathFrame(this.progress);
            runnerPosition = frame.point.clone().addScaledVector(frame.right, this.lateralOffset);
        }
        const guide = this.haechiGuides.find((item) => {
            if (item.shown || !item.mesh.visible) return false;
            const radius = THREE.MathUtils.clamp(Number(item.placement.interactionRadius) || 4.5, 2, 12);
            if (this.gameMode === 'explore') return this.isExploreNear(item.mesh, radius);
            return !!runnerPosition && this.horizontalDistance(runnerPosition, item.mesh.position) <= radius;
        });
        if (!guide) return;
        guide.shown = true;
        this.activeHaechiGuide = guide;
        this.clearControls();
        this.statusText = `서울 해치가 ${this.activeHaechiGuideTargetName} 설명을 시작합니다.`;
        this.requestUiRender();
    }

    private checkHiddenNpcEntry() {
        if (this.gameMode !== 'explore' || this.screen !== 'playing') return;
        const npc = this.hiddenNpcs.find((item) => !item.resolved && this.isExploreNear(item.mesh, 3.4));
        if (!npc) return;
        const question = this.hiddenNpcQuestionBank[Math.floor(Math.random() * this.hiddenNpcQuestionBank.length)];
        npc.question = { ...question };
        this.activeHiddenNpc = npc;
        this.activeQuizKind = 'hiddenNpc';
        this.hiddenNpcReward = 0;
        this.activeQuestion = npc.question;
        this.quizSelectedAnswer = null;
        this.activeGateName = '숨은 조선 NPC';
        this.screen = 'quiz';
        this.clearControls();
        this.requestUiRender();
    }

    private checkGateEntry() {
        const gate = this.gates.find((item) => !item.resolved && !item.selectedQuestion && (
            this.gameMode === 'runner'
                ? this.progress >= item.distance
                : !!item.mesh && this.isExploreNear(item.mesh, Math.max(4, this.trackWidth * 0.35))
        ));
        if (!gate) return;

        if (!gate.quizEnabled) {
            gate.resolved = true;
            this.gatesPassed += 1;
            this.checkpoint = gate.distance + 4;
            this.updateGateAppearance(gate);
            this.showTemporaryStatus(`${gate.name} 통과!`, 3);
            return;
        }

        const questionIndex = Math.floor(Math.random() * gate.questions.length);
        gate.selectedQuestion = gate.questions[questionIndex];
        this.activeQuizKind = 'gate';
        this.activeHiddenNpc = undefined;
        this.hiddenNpcReward = 0;
        this.activeQuestion = gate.selectedQuestion;
        this.quizSelectedAnswer = null;
        this.activeGateName = gate.name;
        this.screen = 'quiz';
        this.clearControls();
        this.requestUiRender();
    }

    private checkFinish() {
        const reachedFinish = this.gameMode === 'runner'
            ? this.progress >= this.trackLength - 2
            : !!this.player && this.horizontalDistance(this.player.position, this.pathPoint(this.trackLength)) <= 5;
        if (!reachedFinish || this.gates.some((gate) => !gate.resolved)) return;
        this.progress = this.trackLength;
        this.progressPercent = 100;
        this.screen = 'result';
        this.statusText = '코스 완주!';
        this.rankingSubmitted = false;
        this.rankingNameInput = (localStorage.getItem(this.rankingNameStorageKey) || this.rankingNameInput).slice(0, 20);
        this.rankingStatus = '이름을 입력하면 현재 맵·모드의 로컬 랭킹에 등록됩니다.';
        this.clearControls();
        this.requestUiRender();
    }

    private updatePlayerTransform(dt: number) {
        if (!this.player) return;
        if (this.gameMode === 'explore') {
            this.player.position.set(
                this.explorePosition.x,
                this.explorePosition.y + 0.08 + this.jumpHeight + this.fallOffset,
                this.explorePosition.z
            );
            const moving = this.controls.forward || this.controls.backward || this.controls.left || this.controls.right;
            const targetBank = ((this.controls.left ? 1 : 0) - (this.controls.right ? 1 : 0)) * 0.16;
            const smoothing = dt > 0 ? 1 - Math.exp(-dt * 8) : 1;
            const currentBank = THREE.MathUtils.lerp(this.player.rotation.z, targetBank, smoothing);
            this.player.rotation.set(0, this.exploreHeading + Math.PI, currentBank);
            if (this.screen === 'playing' && this.grounded && !this.falling && moving) this.player.position.y += Math.sin(performance.now() * 0.018) * 0.08;
            return;
        }
        const frame = this.getRuntimePathFrame(this.progress);
        this.player.position.copy(frame.point)
            .addScaledVector(frame.right, this.lateralOffset)
            .addScaledVector(frame.surfaceUp, 0.08 + this.jumpHeight + this.fallOffset);
        const targetBank = ((this.controls.left ? 1 : 0) - (this.controls.right ? 1 : 0)) * 0.25;
        const smoothing = dt > 0 ? 1 - Math.exp(-dt * 8) : 1;
        const bank = THREE.MathUtils.lerp(Number(this.player.userData['runnerBank'] || 0), targetBank, smoothing);
        this.player.userData['runnerBank'] = bank;
        this.player.quaternion.copy(this.createPathOrientation(frame));
        this.player.quaternion.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), bank));
        if (this.screen === 'playing' && this.grounded && !this.falling) {
            this.player.position.addScaledVector(frame.surfaceUp, Math.sin(performance.now() * 0.018) * 0.08);
        }
    }

    private updateCamera(dt: number) {
        if (!this.camera) return;
        if (this.gameMode === 'explore' && this.player) {
            const pose = this.resolveExploreCameraPose(this.player.position);
            this.camera.position.lerp(pose.position, 1 - Math.exp(-Math.max(dt, 0.001) * 12));
            this.cameraLookTarget.lerp(pose.target, 1 - Math.exp(-Math.max(dt, 0.001) * 12));
            this.camera.up.lerp(new THREE.Vector3(0, 1, 0), 1 - Math.exp(-Math.max(dt, 0.001) * 12)).normalize();
            this.camera.lookAt(this.cameraLookTarget);
            return;
        }
        const frame = this.getRuntimePathFrame(this.progress);
        const desired = frame.point.clone()
            .addScaledVector(frame.right, this.lateralOffset)
            .addScaledVector(frame.forward, -this.cameraFollowDistance)
            .addScaledVector(frame.surfaceUp, 7.2 + this.fallOffset * 0.18);

        const positionSmoothing = 1 - Math.exp(-Math.max(dt, 0.001) * 16);
        this.camera.position.lerp(desired, positionSmoothing);
        const cameraDrift = this.camera.position.distanceTo(desired);
        if (cameraDrift > this.cameraMaxDrift) {
            this.camera.position.lerp(desired, (cameraDrift - this.cameraMaxDrift) / cameraDrift);
        }

        const target = frame.point.clone()
            .addScaledVector(frame.right, this.lateralOffset)
            .addScaledVector(frame.forward, 12)
            .addScaledVector(frame.surfaceUp, 1.8);
        this.cameraLookTarget.lerp(target, 1 - Math.exp(-Math.max(dt, 0.001) * 14));
        const upSmoothing = 1 - Math.exp(-Math.max(dt, 0.001) * 16);
        this.camera.up.lerp(frame.surfaceUp, upSmoothing);
        if (this.camera.up.lengthSq() <= 0.0001) this.camera.up.copy(frame.surfaceUp);
        else this.camera.up.normalize();
        this.camera.lookAt(this.cameraLookTarget);
    }

    private resolveExploreCameraPose(origin: THREE.Vector3): { position: THREE.Vector3; target: THREE.Vector3 } {
        const forward = new THREE.Vector3(Math.sin(this.exploreCameraHeading), 0, Math.cos(this.exploreCameraHeading));
        const right = new THREE.Vector3(-forward.z, 0, forward.x);
        const isTop = this.exploreCameraMode === 'top';
        const isShoulder = this.exploreCameraMode === 'shoulder';
        const baseDistance = isTop ? 1.8 : (isShoulder ? 5.2 : 12);
        const baseHeight = isTop ? 28 : (isShoulder ? 3.7 : 7);
        const distance = Math.max(2.4, baseDistance - this.exploreCameraPitchOffset * (isTop ? 0.4 : 3.5));
        const height = Math.max(isTop ? 14 : 2.6, baseHeight + this.exploreCameraPitchOffset * (isTop ? 20 : 9));
        const position = origin.clone().addScaledVector(forward, -distance);
        if (isShoulder) position.addScaledVector(right, 1.55);
        position.y = origin.y + height;
        const target = origin.clone().addScaledVector(forward, isTop ? 1.5 : (isShoulder ? 7 : 6));
        target.y = origin.y + (isTop ? 0.2 : 1.7);
        return { position, target };
    }

    private snapCamera() {
        if (!this.camera) return;
        const startFrame = this.getRuntimePathFrame(0);
        if (this.gameMode === 'explore') {
            const origin = this.player?.position || startFrame.point;
            const pose = this.resolveExploreCameraPose(origin);
            this.camera.position.copy(pose.position);
            this.cameraLookTarget.copy(pose.target);
            this.camera.up.set(0, 1, 0);
        } else {
            this.camera.position.copy(startFrame.point)
                .addScaledVector(startFrame.forward, -this.cameraFollowDistance)
                .addScaledVector(startFrame.surfaceUp, 7.2);
            this.cameraLookTarget.copy(startFrame.point)
                .addScaledVector(startFrame.forward, 12)
                .addScaledVector(startFrame.surfaceUp, 1.8);
            this.camera.up.copy(startFrame.surfaceUp);
        }
        this.camera.lookAt(this.cameraLookTarget);
    }

    private focusEditorDistance(distance: number) {
        if (!this.camera || this.screen !== 'editor') return;
        const focus = THREE.MathUtils.clamp(distance, 0, this.trackLength);
        const point = this.pathPoint(focus);
        const behind = Math.max(0, focus - 32);
        const behindFrame = this.getPathFrame(this.activeMap, behind);
        this.camera.position.copy(behindFrame.point)
            .addScaledVector(behindFrame.right, 18)
            .addScaledVector(behindFrame.forward, -8);
        this.camera.position.y = behindFrame.point.y + 20;
        this.cameraLookTarget.copy(point);
        this.camera.lookAt(this.cameraLookTarget);
    }

    private animateWorld(dt: number, time: number) {
        this.playerAnimationMixer?.update(dt);
        this.updateAutoSceneryGates(dt);
        this.updatePerformanceDecorationVisibility(dt);
        this.coins.forEach((coin, index) => {
            if (!coin.mesh.visible) return;
            coin.mesh.rotation.y += dt * 2.8;
            coin.mesh.position.y = coin.homePosition.y + Math.sin(time * 0.003 + index) * 0.16;
        });
        this.magnets.forEach((magnet, index) => {
            if (!magnet.mesh.visible) return;
            magnet.mesh.rotation.y += dt * 1.8;
            magnet.mesh.position.y = magnet.homePosition.y + Math.sin(time * 0.0035 + index) * 0.18;
        });
        this.supplements.forEach((supplement, index) => {
            if (!supplement.mesh.visible) return;
            supplement.mesh.rotation.y += dt * 2.1;
            supplement.mesh.position.y = supplement.homePosition.y + Math.sin(time * 0.0032 + index) * 0.18;
        });
        this.mounts.forEach((mount, index) => {
            if (!mount.mesh.visible) return;
            mount.mesh.rotation.y += dt * 0.85;
            mount.mesh.position.y = mount.homePosition.y + Math.sin(time * 0.0025 + index) * 0.16;
            const ring = mount.mesh.getObjectByName('mount-pickup-ring');
            if (ring) ring.rotation.z += dt * 1.6;
        });
        this.gates.forEach((gate, index) => {
            const marker = gate.mesh?.getObjectByName('gate-marker');
            if (!marker) return;
            marker.rotation.z += dt * (0.8 + index * 0.12);
            marker.scale.setScalar(1 + Math.sin(time * 0.002 + index) * 0.055);
        });
        this.hiddenNpcs.forEach((npc, index) => {
            if (!npc.mesh.visible) return;
            const aura = npc.mesh.getObjectByName('hidden-npc-aura');
            if (aura) aura.rotation.z += dt * (0.9 + index * 0.04);
            const marker = npc.mesh.getObjectByName('hidden-npc-marker');
            if (marker) marker.rotation.z = 0.2 + Math.sin(time * 0.0025 + index) * 0.18;
        });
        if (this.screen === 'menu' && this.player) this.player.rotation.y += dt * 0.24;
    }

    private updatePerformanceDecorationVisibility(dt: number) {
        if (!this.camera || this.performanceDecorations.length === 0) return;
        this.decorationCullElapsed += dt;
        if (this.decorationCullElapsed < 0.25) return;
        this.decorationCullElapsed = 0;
        if (this.screen === 'menu') return;
        const denseMap = this.isDenseSceneryMap();
        if (this.effectiveGraphicsQuality === 'high' && !denseMap) {
            this.performanceDecorations.forEach((object) => object.visible = true);
            return;
        }
        const showDistance = denseMap
            ? (this.effectiveGraphicsQuality === 'low' ? 125 : (this.effectiveGraphicsQuality === 'high' ? 260 : 210))
            : (this.effectiveGraphicsQuality === 'low' ? 165 : 380);
        const hideDistance = denseMap
            ? (this.effectiveGraphicsQuality === 'low' ? 150 : (this.effectiveGraphicsQuality === 'high' ? 300 : 245))
            : (this.effectiveGraphicsQuality === 'low' ? 190 : 430);
        this.performanceDecorations.forEach((object) => {
            const limit = object.visible ? hideDistance : showDistance;
            object.visible = object.position.distanceToSquared(this.camera!.position) <= limit * limit;
        });
    }

    private isExploreNear(object: THREE.Object3D, radius: number): boolean {
        if (!this.player || !object.visible) return false;
        return this.horizontalDistance(this.player.position, object.position) <= radius;
    }

    private itemPickupRange(baseRange: number): number {
        return baseRange * this.itemPickupRangeMultiplier;
    }

    private findNearestExploreDistance(worldX: number, worldZ: number): number {
        let bestDistance = this.progress;
        let bestSquared = Number.POSITIVE_INFINITY;
        const localWindow = Math.max(60, this.trackWidth * 4);
        const localStart = Math.max(0, this.progress - localWindow);
        const localEnd = Math.min(this.trackLength, this.progress + localWindow);
        const sampleStep = this.trackLength / Math.max(1, this.explorePathSamples.length - 1);
        const startIndex = Math.max(0, Math.floor(localStart / Math.max(0.001, sampleStep)));
        const endIndex = Math.min(this.explorePathSamples.length - 1, Math.ceil(localEnd / Math.max(0.001, sampleStep)));
        for (let index = startIndex; index <= endIndex; index++) {
            const sample = this.explorePathSamples[index];
            const squared = (sample.point.x - worldX) ** 2 + (sample.point.z - worldZ) ** 2;
            if (squared < bestSquared) {
                bestSquared = squared;
                bestDistance = sample.distance;
            }
        }
        const localCaptureRadius = Math.max(18, this.trackWidth * 1.5);
        if (bestSquared <= localCaptureRadius ** 2) return bestDistance;

        this.explorePathSamples.forEach((sample) => {
            const squared = (sample.point.x - worldX) ** 2 + (sample.point.z - worldZ) ** 2;
            if (squared < bestSquared) {
                bestSquared = squared;
                bestDistance = sample.distance;
            }
        });
        return bestDistance;
    }

    private advancePathProgress(progress: number, worldDelta: number): number {
        if (this.explorePathSamples.length < 2 || this.runtimePathWorldLength <= 0) {
            return Math.min(this.trackLength, progress + worldDelta);
        }
        const currentWorldDistance = this.interpolatePathSample(progress, 'distance', 'worldDistance');
        const targetWorldDistance = Math.min(this.runtimePathWorldLength, currentWorldDistance + Math.max(0, worldDelta));
        return this.interpolatePathSample(targetWorldDistance, 'worldDistance', 'distance');
    }

    private interpolatePathSample(value: number, source: 'distance' | 'worldDistance', target: 'distance' | 'worldDistance'): number {
        const samples = this.explorePathSamples;
        if (samples.length === 0) return 0;
        if (value <= samples[0][source]) return samples[0][target];
        const last = samples[samples.length - 1];
        if (value >= last[source]) return last[target];
        let low = 0;
        let high = samples.length - 1;
        while (low + 1 < high) {
            const middle = Math.floor((low + high) / 2);
            if (samples[middle][source] <= value) low = middle;
            else high = middle;
        }
        const span = Math.max(0.000001, samples[high][source] - samples[low][source]);
        const ratio = THREE.MathUtils.clamp((value - samples[low][source]) / span, 0, 1);
        return THREE.MathUtils.lerp(samples[low][target], samples[high][target], ratio);
    }

    private horizontalDistance(a: THREE.Vector3, b: THREE.Vector3): number {
        return Math.hypot(a.x - b.x, a.z - b.z);
    }

    private clearControls() {
        this.controls.left = false;
        this.controls.right = false;
        this.controls.forward = false;
        this.controls.backward = false;
        this.exploreSprintActive = false;
        this.exploreCameraPointerId = null;
    }

    private showTemporaryStatus(message: string, duration: number = 3) {
        this.statusText = message;
        this.statusExpiresAt = performance.now() + duration * 1000;
        this.requestUiRender();
    }

    private updateStatusTimer() {
        if (this.statusExpiresAt <= 0 || performance.now() < this.statusExpiresAt) return;
        this.statusExpiresAt = 0;
        this.statusText = '';
        this.requestUiRender();
    }

    private refreshHud(dt: number) {
        this.uiRefreshElapsed += dt;
        const refreshInterval = this.effectiveGraphicsQuality === 'low' ? 0.2 : 0.125;
        if (this.uiRefreshElapsed < refreshInterval) return;
        this.uiRefreshElapsed = 0;
        this.progressPercent = Math.min(100, Math.round((this.progress / this.trackLength) * 100));
        this.refreshMinimapPlayerMarker();
        this.requestUiRender();
    }

    private updateGateAppearance(gate: RuntimeGate) {
        gate.mesh?.traverse((object: THREE.Object3D) => {
            if (object.userData['gateDoor']) object.visible = !gate.resolved;
            const mesh = object as THREE.Mesh;
            const material = mesh.material as THREE.MeshStandardMaterial;
            if (!material || material.type !== 'MeshStandardMaterial') return;
            if (gate.resolved) {
                material.emissive = new THREE.Color(0x10b981);
                material.color = new THREE.Color(0x6ee7b7);
                material.emissiveIntensity = 1.2;
            } else {
                material.color = new THREE.Color(material.userData['baseColor']);
                material.emissive = new THREE.Color(material.userData['baseEmissive']);
                material.emissiveIntensity = material.userData['baseEmissiveIntensity'];
            }
        });
    }

    private get trackLength(): number {
        return this.activeMap.trackLength;
    }

    private statMultiplier(stat: number): number {
        return 0.5 + stat * 0.1;
    }

    private get currentSteeringSpeed(): number {
        const baseSteering = 12.8 * this.statMultiplier(this.activeCharacterStats.steering) + this.coinSteerBonus;
        return baseSteering * (this.supplementRemaining > 0 ? this.supplementSpeedMultiplier : 1) * (this.activeMountKind ? 1.35 : 1);
    }

    private get trackWidth(): number {
        return this.activeMap.trackWidth;
    }

    private isJumpRoadDistance(distance: number): boolean {
        const waypoints = this.activeMap.waypoints;
        for (let index = 0; index < waypoints.length - 1; index++) {
            const current = waypoints[index];
            const next = waypoints[index + 1];
            if (distance < current.distance || distance > next.distance) continue;
            if (current.roadPiece
                && current.roadPiece === next.roadPiece
                && this.isJumpRoadPiece(current.roadPiece)) return true;
        }
        return false;
    }

    private jumpPlatformAtDistance(distance: number): JumpPlatformSurface | undefined {
        return this.jumpPlatformSurfaces.find((surface) =>
            distance >= surface.startDistance - 0.001 && distance <= surface.endDistance + 0.001
        );
    }

    private jumpPlatformAtPosition(worldX: number, worldZ: number, margin: number = 0): JumpPlatformSurface | undefined {
        return this.jumpPlatformSurfaces.find((surface) => {
            const deltaX = worldX - surface.centerX;
            const deltaZ = worldZ - surface.centerZ;
            const along = deltaX * surface.forwardX + deltaZ * surface.forwardZ;
            const lateral = deltaX * surface.rightX + deltaZ * surface.rightZ;
            return Math.abs(along) <= surface.length / 2 + margin
                && Math.abs(lateral) <= surface.width / 2 + margin;
        });
    }

    private isExploreJumpVoid(worldX: number, worldZ: number, distance: number): boolean {
        if (!this.isJumpRoadDistance(distance)) return false;
        const pathPoint = this.pathPoint(distance);
        const corridorRadius = Math.max(14, this.trackWidth) / 2 + 6;
        if (Math.hypot(worldX - pathPoint.x, worldZ - pathPoint.z) > corridorRadius) return false;
        return !this.jumpPlatformAtPosition(worldX, worldZ, 0.12);
    }

    private safeRespawnDistance(requestedDistance: number): number {
        let distance = THREE.MathUtils.clamp(requestedDistance, 0, this.trackLength);
        for (let attempt = 0; attempt <= Math.ceil(this.trackLength); attempt++) {
            const platform = this.jumpPlatformAtDistance(distance);
            if (platform) return (platform.startDistance + platform.endDistance) / 2;
            if (!this.isJumpRoadDistance(distance)) return distance;
            distance = Math.max(0, distance - 1);
        }
        return 0;
    }

    private isJumpGapDistance(distance: number): boolean {
        const waypoints = this.activeMap.waypoints;
        for (let index = 0; index < waypoints.length - 1; index++) {
            const current = waypoints[index];
            const next = waypoints[index + 1];
            if (!current.jumpGapAfter) continue;
            if (distance > current.distance + 0.001 && distance < next.distance - 0.001) return true;
        }
        return false;
    }

    private pathPoint(distance: number): THREE.Vector3 {
        return this.sampleMapPath(this.activeMap, distance);
    }

    private seeded(value: number): number {
        const raw = Math.sin(value * 12.9898) * 43758.5453;
        return raw - Math.floor(raw);
    }

    private normalizeCharacterStats(value?: Partial<CharacterStats>): CharacterStats {
        const jump = Math.round(Number(value?.jump));
        const steering = Math.round(Number(value?.steering));
        if (jump >= 3 && steering >= 3 && jump + steering === 10) return { jump, steering };
        return { jump: 5, steering: 5 };
    }

    private persistBuiltInCharacterProfiles() {
        const profiles = this.builtInSkins.reduce((result, preset) => {
            result[preset.id] = { name: preset.name, stats: this.normalizeCharacterStats(preset.stats) };
            return result;
        }, {} as Record<string, { name: string; stats: CharacterStats }>);
        localStorage.setItem(this.characterProfilesStorageKey, JSON.stringify(profiles));
    }

    private restoreBuiltInCharacterProfiles() {
        const raw = localStorage.getItem(this.characterProfilesStorageKey);
        if (raw) {
            try {
                const profiles = JSON.parse(raw) as Record<string, { name?: string; stats?: CharacterStats }>;
                this.builtInSkins.forEach((preset) => {
                    const saved = profiles?.[preset.id];
                    const name = String(saved?.name || '').trim().slice(0, 30);
                    if (name) preset.name = name;
                    preset.stats = this.normalizeCharacterStats(saved?.stats);
                });
            } catch {
                localStorage.removeItem(this.characterProfilesStorageKey);
            }
        }
        const selected = this.builtInSkins.find((item) => item.id === this.selectedBuiltInSkinId);
        if (selected) {
            this.skinName = selected.name;
            this.characterNameDraft = selected.name;
        }
    }

    private applyCharacterBalanceMigration() {
        if (localStorage.getItem(this.characterBalanceStorageKey) === 'complete') return;
        const haetae = this.builtInSkins.find((preset) => preset.id === 'haetae');
        if (haetae) haetae.stats = { jump: 4, steering: 6 };
        this.persistBuiltInCharacterProfiles();
        localStorage.setItem(this.characterBalanceStorageKey, 'complete');
    }

    private async syncActiveLocalSkin() {
        if (!this.activeLocalSkinId) return;
        const current = this.localSkins.find((skin) => skin.id === this.activeLocalSkinId);
        if (!current) return;
        const updated: StoredSkin = {
            ...current,
            name: this.skinName,
            customizationEnabled: this.characterCustomizationEnabled,
            stats: { ...this.customCharacterStats },
            updatedAt: new Date().toISOString()
        };
        await this.skinStorage.put(updated);
        this.localSkins = this.sortLocalSkins(this.localSkins.map((skin) => skin.id === updated.id ? updated : skin));
    }

    private restoreRankings() {
        this.rankingNameInput = (localStorage.getItem(this.rankingNameStorageKey) || '').slice(0, 20);
        const raw = localStorage.getItem(this.rankingStorageKey);
        if (!raw) return;
        try {
            const entries = JSON.parse(raw);
            if (!Array.isArray(entries)) throw new Error('랭킹 형식 오류');
            this.localRankings = entries.filter((entry: any) =>
                entry && typeof entry.id === 'string' && typeof entry.mapId === 'string'
                && (entry.mode === 'runner' || entry.mode === 'explore')
                && typeof entry.playerName === 'string' && Number.isFinite(entry.score)
            ).map((entry: any) => ({
                ...entry,
                playerName: entry.playerName.slice(0, 20),
                characterName: String(entry.characterName || '캐릭터').slice(0, 30),
                baseScore: Number.isFinite(entry.baseScore) ? entry.baseScore : entry.score,
                hiddenBonus: Number.isFinite(entry.hiddenBonus) ? entry.hiddenBonus : 0,
                coinScore: Number.isFinite(entry.coinScore)
                    ? entry.coinScore
                    : (Number.isFinite(entry.coins) ? entry.coins * 5 : 0),
                hiddenScore: Number.isFinite(entry.hiddenScore)
                    ? entry.hiddenScore
                    : (Number.isFinite(entry.hiddenBonus) ? entry.hiddenBonus : 0),
                timeScore: Number.isFinite(entry.timeScore) ? entry.timeScore : 0,
                quizScore: Number.isFinite(entry.quizScore) ? entry.quizScore : 0,
                obstacleCollisions: Number.isFinite(entry.obstacleCollisions)
                    ? Math.max(0, Math.floor(entry.obstacleCollisions))
                    : 0,
                time: Number.isFinite(entry.time) ? entry.time : 0,
                coins: Number.isFinite(entry.coins) ? entry.coins : 0,
                accuracy: Number.isFinite(entry.accuracy) ? entry.accuracy : 0,
                createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : new Date(0).toISOString()
            })).slice(0, 500);
        } catch {
            this.localRankings = [];
            localStorage.removeItem(this.rankingStorageKey);
        }
    }

    private rankingsFor(mapId: string, mode: GameMode): LocalRankingEntry[] {
        return this.localRankings
            .filter((entry) => entry.mapId === mapId && entry.mode === mode)
            .sort((a, b) => b.score - a.score || a.time - b.time || b.createdAt.localeCompare(a.createdAt));
    }

    private async restoreDecorationAssets() {
        try {
            this.decorationAssets = (await this.decorationStorage.list())
                .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
            await this.ensureServerDecorationsRegistered();
            if (this.decorationAssets.length > 0) {
                const readyAsset = this.decorationAssets.find((asset) => Boolean(asset.format && asset.data));
                const sourceOnlyCount = this.decorationAssets.filter((asset) => !asset.format || !asset.data).length;
                this.selectedDecorationAssetId = readyAsset?.id || '';
                this.decorationStatus = sourceOnlyCount > 0
                    ? `${this.decorationAssets.length}개의 장식을 불러왔습니다. 이전 MAX ${sourceOnlyCount}개는 원본을 다시 추가하면 자동 변환됩니다.`
                    : `${this.decorationAssets.length}개의 저장된 3D 장식을 불러왔습니다.`;
            }
        } catch {
            this.decorationAssets = [];
            this.decorationStatus = '저장된 3D 장식을 불러오지 못했습니다.';
        }
    }

    private ensureDecorationAssetsReady(): Promise<void> {
        if (this.decorationAssetsReady) return Promise.resolve();
        if (!this.decorationAssetsLoadPromise) {
            this.decorationAssetsLoadPromise = this.restoreDecorationAssets()
                .finally(() => {
                    this.decorationAssetsReady = true;
                    this.decorationAssetsLoadPromise = undefined;
                    this.requestUiRender();
                });
        }
        return this.decorationAssetsLoadPromise;
    }

    private async prepareMapDecorationAssets(map: GameMap) {
        const assetIds = [...new Set(map.scenery
            .filter((item) => item.kind === 'custom' && item.assetId)
            .map((item) => item.assetId as string))];
        const loads: Promise<unknown>[] = [];
        const ysPresets = [...new Set(map.scenery
            .filter((item) => item.kind === 'ys' && item.assetId)
            .map((item) => item.assetId as string))]
            .map((assetId) => this.ysDecorationPresets.find((preset) => preset.id === assetId))
            .filter((preset): preset is YsDecorationPreset => Boolean(preset));
        const natureKinds = [...new Set(map.scenery
            .map((item) => item.kind)
            .filter(isBundledNatureDecorationKind))];
        loads.push(...natureKinds.map((kind) => this.ensureBundledNatureTemplateLoaded(kind)));
        loads.push(...ysPresets.flatMap((preset) => [
            this.ensureYsDecorationTemplateLoaded(preset),
            this.ensureYsDecorationSurfaceLoaded(preset)
        ]));
        if (map.scenery.some((item) => item.kind === 'haechi-guide')) {
            loads.push(this.ensureHaetaeAssetLoaded());
        }
        if (assetIds.length > 0) {
            await this.ensureDecorationAssetsReady();
            for (let index = 0; index < assetIds.length; index += 2) {
                const batch = assetIds.slice(index, index + 2).map((assetId) => {
                    const asset = this.decorationAssets.find((item) => item.id === assetId);
                    return asset ? this.ensureDecorationTemplateLoaded(asset) : Promise.resolve();
                });
                await Promise.allSettled(batch);
            }
        }
        if (loads.length > 0) await Promise.allSettled(loads);
    }

    private async ensureServerDecorationsRegistered() {
        for (const preset of this.serverDecorationPresets) {
            const existing = this.decorationAssets.find((asset) => asset.id === preset.id);
            if (
                existing?.format === 'glb'
                && existing.data
                && (!preset.version || existing.serverVersion === preset.version)
            ) continue;
            const response = await fetch(preset.url, { cache: 'force-cache' });
            if (!response.ok) throw new Error(`${preset.name} 서버 에셋을 불러오지 못했습니다.`);
            const data = await response.arrayBuffer();
            if (data.byteLength > this.maxConvertedDecorationFileSize) {
                throw new Error(`${preset.name} 서버 에셋이 24MB를 초과합니다.`);
            }
            this.validateSkinData(data, 'glb');
            const record: StoredDecorationAsset = {
                ...(existing || {} as StoredDecorationAsset),
                id: existing?.id || preset.id,
                name: preset.name,
                fileName: preset.fileName,
                format: 'glb',
                data: data.slice(0),
                sourceFileName: preset.sourceFileName,
                sourceFormat: preset.sourceFormat,
                serverVersion: preset.version,
                sourceData: undefined,
                updatedAt: new Date().toISOString()
            };
            await this.decorationStorage.put(record);
            this.decorationAssets = existing
                ? this.decorationAssets.map((asset) => asset.id === existing.id ? record : asset)
                : [record, ...this.decorationAssets];
        }
        this.decorationAssets.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    }

    private async ensureDecorationTemplateLoaded(asset: StoredDecorationAsset) {
        if (this.decorationTemplateCache.has(asset.id) || !asset.format || !asset.data) return;
        const pending = this.decorationTemplateLoadPromises.get(asset.id);
        if (pending) return pending;
        const load = (async () => {
            const model = await this.parseSkinModel(asset.data!, asset.format!);
            if (this.destroyed) {
                this.disposeObjectTree(model, true);
                return;
            }
            this.storeDecorationTemplate(asset.id, model);
        })();
        this.decorationTemplateLoadPromises.set(asset.id, load);
        try {
            await load;
        } finally {
            this.decorationTemplateLoadPromises.delete(asset.id);
        }
    }

    private storeDecorationTemplate(assetId: string, model: THREE.Group) {
        model.updateMatrixWorld(true);
        const initialBox = new THREE.Box3().setFromObject(model);
        const initialSize = initialBox.getSize(new THREE.Vector3());
        if (!Number.isFinite(initialSize.y) || initialSize.y <= 0.001) {
            this.disposeObjectTree(model, true);
            throw new Error('장식 모델의 크기를 계산할 수 없습니다.');
        }
        this.prepareDecoration(model);
        const wrapper = new THREE.Group();
        wrapper.name = `decoration-template-${assetId}`;
        wrapper.add(model);
        model.scale.setScalar(6 / initialSize.y);
        model.updateMatrixWorld(true);
        const normalizedBox = new THREE.Box3().setFromObject(model);
        const center = normalizedBox.getCenter(new THREE.Vector3());
        model.position.x -= center.x;
        model.position.z -= center.z;
        model.position.y -= normalizedBox.min.y;
        const previous = this.decorationTemplateCache.get(assetId);
        if (previous) this.disposeObjectTree(previous, true);
        this.decorationTemplateCache.set(assetId, wrapper);
    }

    private normalizeSkinRotation(rotationY?: number): number {
        if (!Number.isFinite(rotationY)) return 180;
        const normalized = ((Number(rotationY) % 360) + 360) % 360;
        return Math.round(normalized * 10) / 10;
    }

    private normalizeLocalSkinSearch(value: string): string {
        return String(value || '').normalize('NFKC').trim().toLocaleLowerCase('ko-KR');
    }

    private sortLocalSkins(skins: StoredSkin[]): StoredSkin[] {
        return [...skins].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    }

    private async clearLegacyCharactersOnce() {
        this.characterCustomizationEnabled = false;
        localStorage.removeItem(this.characterCustomizationStorageKey);
        if (localStorage.getItem(this.legacyCharacterCleanupStorageKey) === 'complete') return;
        try {
            await this.skinStorage.clearAll();
            this.customSkinData = null;
            this.customSkinFormat = 'glb';
            this.customSkinRotationY = 180;
            this.hasCustomSkin = false;
            this.activeLocalSkinId = '';
            this.activeNfcCharacterMetadata = null;
            this.localSkins = [];
            localStorage.setItem(this.legacyCharacterCleanupStorageKey, 'complete');
        } catch {
            this.localSkinStatus = '기존 로컬 캐릭터를 자동 정리하지 못했습니다.';
        }
    }

    private async restoreLocalSkins() {
        try {
            this.localSkins = this.sortLocalSkins(await this.skinStorage.listLocal());
            this.localSkinStatus = this.localSkins.length > 0
                ? `${this.localSkins.length}개의 로컬 캐릭터를 불러왔습니다.`
                : '업로드한 캐릭터를 이 브라우저에 등록할 수 있습니다.';
        } catch {
            this.localSkins = [];
            this.localSkinStatus = '로컬 캐릭터 목록을 불러오지 못했습니다.';
        }
    }

    private async restoreStoredSkin() {
        try {
            let stored = await this.skinStorage.get();
            if (stored && (stored.nfcAssetId || stored.nfcIdentifier)) {
                await this.skinStorage.clear();
                stored = null;
                this.activeNfcCharacterMetadata = null;
                this.skinStatus = 'CharDB 캐릭터 캐시는 보관했습니다. NFC 태그로 다시 인증하면 적용됩니다.';
            }
            if (stored) {
                this.customSkinData = stored.data;
                this.skinName = stored.name;
                this.characterNameDraft = stored.name;
                this.skinFileName = stored.fileName;
                this.customSkinFormat = stored.format || this.detectSkinFormat(stored.fileName);
                this.customSkinRotationY = this.normalizeSkinRotation(stored.rotationY);
                this.characterCustomizationEnabled = false;
                this.customCharacterStats = this.normalizeCharacterStats(stored.stats);
                this.activeLocalSkinId = stored.libraryId && this.localSkins.some((skin) => skin.id === stored.libraryId)
                    ? stored.libraryId
                    : '';
                this.activeNfcCharacterMetadata = this.readNfcCharacterMetadata(stored);
                this.selectedBuiltInSkinId = null;
                await this.applyCustomSkin(stored.data, this.customSkinFormat, this.customSkinRotationY);
                this.hasCustomSkin = true;
                this.refreshCustomizationPreviewModel();
                this.skinStatus = `저장된 사용자 ${this.customSkinFormat.toUpperCase()} 스킨을 불러왔습니다.`;
                return;
            }
            const savedSettings = this.readBuiltInSkinSettings();
            if (!savedSettings) {
                this.selectedBuiltInSkinId = 'joseon-king-2';
                this.skinName = '조선 임금';
                this.characterNameDraft = '조선 임금';
                this.characterCustomizationEnabled = false;
                this.persistBuiltInSkinSettings('joseon-king-2');
                await this.ensureKing2AssetLoaded();
                this.replacePlayerVisual(this.createBuiltInPlayerVisual('joseon-king-2'));
                this.refreshCustomizationPreviewModel();
                return;
            }
            const preset = this.builtInSkins.find((item) => item.id === savedSettings.id)!;
            if (savedSettings.id === 'joseon-king-2') {
                await this.ensureKing2AssetLoaded();
            }
            if (savedSettings.id === 'haetae') {
                await this.ensureHaetaeAssetLoaded();
            }
            this.characterCustomizationEnabled = false;
            this.selectedBuiltInSkinId = savedSettings.id;
            this.skinName = preset.name;
            this.characterNameDraft = preset.name;
            this.replacePlayerVisual(this.createBuiltInPlayerVisual(savedSettings.id));
            this.skinStatus = `저장된 ${preset.name} 기본 캐릭터를 불러왔습니다.`;
        } catch {
            this.skinStatus = '저장된 스킨을 불러오지 못해 기본 캐릭터를 사용합니다.';
        }
    }

    private isBuiltInSkinId(value: string | null): value is BuiltInSkinId {
        return this.builtInSkins.some((item) => item.id === value);
    }

    private persistBuiltInSkinSettings(id: BuiltInSkinId) {
        localStorage.setItem(this.skinPresetStorageKey, JSON.stringify({ id }));
    }

    private readBuiltInSkinSettings(): { id: BuiltInSkinId } | null {
        const raw = localStorage.getItem(this.skinPresetStorageKey);
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            if (!this.isBuiltInSkinId(parsed?.id)) {
                localStorage.removeItem(this.skinPresetStorageKey);
                return null;
            }
            return { id: parsed.id };
        } catch {
            if (this.isBuiltInSkinId(raw)) return { id: raw };
            localStorage.removeItem(this.skinPresetStorageKey);
            return null;
        }
    }

    private validateGlb(data: ArrayBuffer) {
        if (data.byteLength < 20) throw new Error('유효한 GLB 파일이 아닙니다.');
        const magic = new DataView(data).getUint32(0, true);
        if (magic !== 0x46546c67) throw new Error('GLB 바이너리 헤더를 확인할 수 없습니다.');
    }

    private detectSkinFormat(fileName: string): SkinFormat {
        const extension = fileName.toLowerCase().split('.').pop() || '';
        if (extension === 'glb' || extension === 'gltf' || extension === 'obj' || extension === 'fbx'
            || extension === 'stl' || extension === 'ply' || extension === 'dae' || extension === '3ds') return extension;
        throw new Error('지원 형식은 GLB, glTF, OBJ, FBX, STL, PLY, DAE, 3DS입니다.');
    }

    private validateSkinData(data: ArrayBuffer, format: SkinFormat) {
        if (format === 'glb') {
            this.validateGlb(data);
            return;
        }
        if (format === 'gltf') {
            let document: any;
            try {
                document = JSON.parse(new TextDecoder().decode(data));
            } catch {
                throw new Error('glTF JSON 파일을 해석할 수 없습니다.');
            }
            if (!document?.asset?.version) throw new Error('유효한 glTF 2.0 파일이 아닙니다.');
            const resources = [...(document.buffers || []), ...(document.images || [])];
            if (resources.some((item: any) => item?.uri && !String(item.uri).startsWith('data:'))) {
                throw new Error('glTF는 텍스처와 버퍼를 data URI로 포함한 단일 파일만 지원합니다.');
            }
            return;
        }
        if (format === 'obj') {
            const source = new TextDecoder().decode(data);
            if (!/(^|\n)\s*v\s+[-+\d.]/m.test(source)) throw new Error('OBJ 정점 데이터를 확인할 수 없습니다.');
            return;
        }
        if (format === 'stl') {
            const source = new TextDecoder().decode(data.slice(0, Math.min(data.byteLength, 256))).trimStart();
            if (!source.startsWith('solid') && data.byteLength < 84) throw new Error('유효한 STL 파일이 아닙니다.');
            return;
        }
        if (format === 'ply') {
            const header = new TextDecoder().decode(data.slice(0, Math.min(data.byteLength, 64))).trimStart();
            if (!header.startsWith('ply')) throw new Error('PLY 헤더를 확인할 수 없습니다.');
            return;
        }
        if (format === 'dae') {
            const source = new TextDecoder().decode(data.slice(0, Math.min(data.byteLength, 4096)));
            if (!/<COLLADA[\s>]/i.test(source)) throw new Error('COLLADA 문서를 확인할 수 없습니다.');
            return;
        }
        if (format === '3ds') {
            if (data.byteLength < 6 || new DataView(data).getUint16(0, true) !== 0x4d4d) throw new Error('3DS 헤더를 확인할 수 없습니다.');
            return;
        }
        if (data.byteLength < 64) throw new Error('유효한 FBX 파일이 아닙니다.');
        const header = new TextDecoder().decode(data.slice(0, Math.min(data.byteLength, 4096)));
        const binaryFbx = header.startsWith('Kaydara FBX Binary  \0');
        const asciiVersion = header.match(/FBXVersion:\s*(\d+)/)?.[1];
        const asciiFbx = /FBXHeaderExtension\s*:/.test(header) && Boolean(asciiVersion);
        if (!binaryFbx && !asciiFbx) throw new Error('FBX 헤더와 버전을 확인할 수 없습니다.');
        if (asciiVersion && Number(asciiVersion) < 7000) throw new Error('FBX 7.0 이상 파일을 사용해주세요.');
    }

    private parseSkinModel(data: ArrayBuffer, format: SkinFormat): Promise<THREE.Object3D> {
        if (format === 'obj') {
            try {
                return Promise.resolve(new OBJLoader().parse(new TextDecoder().decode(data)));
            } catch (error) {
                return Promise.reject(error);
            }
        }
        if (format === 'fbx') {
            try {
                return Promise.resolve(new FBXLoader().parse(data.slice(0), ''));
            } catch (error) {
                return Promise.reject(error);
            }
        }
        if (format === 'stl' || format === 'ply') {
            try {
                const geometry = format === 'stl'
                    ? new STLLoader().parse(data.slice(0))
                    : new PLYLoader().parse(data.slice(0));
                if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
                const material = new THREE.MeshStandardMaterial({
                    color: geometry.getAttribute('color') ? 0xffffff : 0x94a3b8,
                    vertexColors: Boolean(geometry.getAttribute('color')),
                    roughness: 0.58,
                    metalness: 0.12
                });
                return Promise.resolve(new THREE.Mesh(geometry, material));
            } catch (error) {
                return Promise.reject(error);
            }
        }
        if (format === 'dae') {
            try {
                const collada = new ColladaLoader().parse(new TextDecoder().decode(data), '');
                return Promise.resolve(collada.scene);
            } catch (error) {
                return Promise.reject(error);
            }
        }
        if (format === '3ds') {
            try {
                return Promise.resolve(new TDSLoader().parse(data.slice(0), ''));
            } catch (error) {
                return Promise.reject(error);
            }
        }
        const payload = format === 'gltf' ? new TextDecoder().decode(data) : data.slice(0);
        return new Promise((resolve, reject) => {
            new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).parse(
                payload,
                '',
                (gltf) => {
                    gltf.scene.animations = gltf.animations;
                    resolve(gltf.scene);
                },
                (error) => reject(error instanceof Error ? error : new Error(`${format.toUpperCase()} 모델을 해석하지 못했습니다.`))
            );
        });
    }

    private inspectImportedModel(model: THREE.Object3D): ImportedModelSummary {
        const summary: ImportedModelSummary = {
            meshCount: 0,
            skinnedMeshCount: 0,
            boneCount: 0,
            animationCount: model.animations?.length || 0
        };
        model.traverse((object: THREE.Object3D) => {
            if ((object as THREE.Mesh).isMesh) summary.meshCount += 1;
            if ((object as THREE.SkinnedMesh).isSkinnedMesh) summary.skinnedMeshCount += 1;
            if ((object as THREE.Bone).isBone) summary.boneCount += 1;
        });
        return summary;
    }

    private restoreAdminSettings() {
        this.readAdminPassword();
        const storedApiUrl = localStorage.getItem(this.nfcApiUrlStorageKey) || '';
        if (!storedApiUrl) return;
        try {
            this.nfcApiUrl = this.normalizeNfcApiBaseUrl(storedApiUrl);
            this.nfcApiUrlDraft = this.nfcApiUrl;
        } catch {
            localStorage.removeItem(this.nfcApiUrlStorageKey);
            this.nfcApiUrl = '';
            this.nfcApiUrlDraft = '';
        }
    }

    private readAdminPassword(): string {
        const stored = localStorage.getItem(this.adminPasswordStorageKey) || '';
        if (/^\d{4}$/.test(stored)) return stored;
        if (stored) localStorage.removeItem(this.adminPasswordStorageKey);
        return '0000';
    }

    private normalizeNfcApiBaseUrl(value: string): string {
        const raw = String(value || '').trim();
        if (!raw) throw new Error('CharDB 서버 주소를 입력해주세요.');
        let url: URL;
        try {
            url = new URL(raw);
        } catch {
            throw new Error('올바른 CharDB 서버 주소를 입력해주세요.');
        }
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            throw new Error('CharDB 서버 주소는 HTTP 또는 HTTPS만 사용할 수 있습니다.');
        }
        if (url.username || url.password) throw new Error('CharDB 서버 주소에 인증정보를 포함할 수 없습니다.');
        url.search = '';
        url.hash = '';
        let path = url.pathname.replace(/\/+$/, '');
        if (/\/api-test\.html$/i.test(path)) path = path.replace(/\/api-test\.html$/i, '');
        path = path.replace(/\/+$/, '');
        return `${url.origin}${path}`;
    }

    private formatNfcUidInput(value: string): string {
        const raw = String(value || '').replace(/[\u0000-\u001f\u007f]/g, '').trim().toUpperCase().slice(0, 20);
        const compact = raw.replace(/[\s:-]/g, '');
        if (/^[0-9A-F]{14}$/.test(compact)) return compact.match(/.{2}/g)!.join(':');
        return raw;
    }

    private normalizeNfcUid(value: string): string {
        const raw = String(value || '').trim();
        if (!/^[0-9a-f:\s-]+$/i.test(raw)) return '';
        const compact = raw.replace(/[\s:-]/g, '').toUpperCase();
        if (!/^[0-9A-F]{14}$/.test(compact)) return '';
        return compact.match(/.{2}/g)!.join(':');
    }

    private abortNfcScan() {
        if (this.nfcScanAbortController) {
            try {
                this.nfcScanAbortController.abort();
            } catch {
                // 이미 종료된 스캔은 무시한다.
            }
        }
        this.nfcScanAbortController = undefined;
        this.nfcScanning = false;
    }

    private async handleNfcReading(event: any) {
        if (!this.nfcRegistrationOpen) return;
        const uid = this.normalizeNfcUid(String(event?.serialNumber || ''));
        if (!uid) {
            this.nfcRegistrationStatus = 'NFC 태그에서 7바이트 UID를 읽지 못했습니다.';
            this.requestUiRender();
            return;
        }
        this.nfcIdentifierDraft = uid;
        this.abortNfcScan();
        this.nfcRegistrationStatus = 'NFC 태그를 인식했습니다. CharDB 사용자 인증과 미리보기를 준비합니다.';
        this.requestUiRender();
        await this.loadNfcCharacter(uid, true);
    }

    private captureKeyboardNfcInput(event: KeyboardEvent): boolean {
        if (!this.nfcRegistrationOpen || !this.nfcApiUrl || event.ctrlKey || event.metaKey || event.altKey || event.repeat) return false;
        const target = event.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) {
            return false;
        }
        const now = performance.now();
        if (now - this.nfcKeyboardLastInputAt > 350) this.nfcKeyboardBuffer = '';
        if (event.key === 'Enter') {
            const uid = this.normalizeNfcUid(this.nfcKeyboardBuffer);
            this.nfcKeyboardBuffer = '';
            this.nfcKeyboardLastInputAt = 0;
            if (!uid) return false;
            event.preventDefault();
            this.nfcIdentifierDraft = uid;
            this.nfcRegistrationStatus = '키보드형 NFC 리더가 태그를 인식했습니다. CharDB 인증과 미리보기를 준비합니다.';
            this.requestUiRender();
            void this.loadNfcCharacter(uid, true);
            return true;
        }
        if (/^[0-9a-f:-]$/i.test(event.key)) {
            this.nfcKeyboardBuffer = (this.nfcKeyboardBuffer + event.key).slice(-20);
            this.nfcKeyboardLastInputAt = now;
        } else if (event.key !== 'Shift') {
            this.nfcKeyboardBuffer = '';
            this.nfcKeyboardLastInputAt = 0;
        }
        return false;
    }

    private createCharDbError(message: string, status?: number, code?: string): CharDbRequestError {
        const error = new Error(message) as CharDbRequestError;
        error.status = status;
        error.code = code;
        return error;
    }

    private assertNfcRequestActive(requestId: number) {
        if (this.destroyed || requestId !== this.nfcSessionSequence) {
            throw this.createCharDbError('NFC 요청이 새 태그로 교체되었습니다.', undefined, 'NFC_CANCELLED');
        }
    }

    private async waitForNfcRetry(milliseconds: number, requestId: number) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
        this.assertNfcRequestActive(requestId);
    }

    private async callCharDbProxy(functionName: string, data: Record<string, string>): Promise<any> {
        const response: any = await wiz.call(functionName, data);
        const envelope = response?.responseJSON || response || {};
        const status = Number(envelope?.code || response?.status || 0);
        const payload = envelope?.data !== undefined ? envelope.data : envelope;
        if (status >= 200 && status < 300) return payload;
        const message = typeof payload === 'string'
            ? payload
            : (payload?.error || payload?.message || envelope?.error || `CharDB 중계 요청에 실패했습니다. (HTTP ${status || 0})`);
        throw this.createCharDbError(String(message), status || 0);
    }

    private async authenticateNfcUid(uid: string, requestId: number): Promise<CharDbAuthResponse> {
        const payload = await this.callCharDbProxy('chardb_nfc_auth', { baseUrl: this.nfcApiUrl, uid });
        this.assertNfcRequestActive(requestId);
        if (!payload || typeof payload.token !== 'string' || !payload.token
            || typeof payload.participantId !== 'string' || !payload.participantId) {
            throw this.createCharDbError('CharDB NFC 인증 응답에 필수 필드가 없습니다.', 502);
        }
        return { token: payload.token, participantId: payload.participantId, expiresAt: payload.expiresAt };
    }

    private async getCharDbModel(token: string, requestId: number): Promise<CharDbModelMetadata | null> {
        let lastError: CharDbRequestError | undefined;
        for (let attempt = 0; attempt < 3; attempt += 1) {
            try {
                const payload = await this.callCharDbProxy('chardb_model', { baseUrl: this.nfcApiUrl, token });
                this.assertNfcRequestActive(requestId);
                if (payload?.model === null) return null;
                return this.validateCharDbModelMetadata(payload?.model);
            } catch (error: any) {
                const current = error as CharDbRequestError;
                if (current.code === 'NFC_CANCELLED' || current.status === 401) throw current;
                lastError = current;
                const retryable = !current.status || current.status >= 500;
                if (!retryable || attempt === 2) throw current;
                await this.waitForNfcRetry(500 * Math.pow(2, attempt), requestId);
            }
        }
        throw lastError || this.createCharDbError('CharDB 모델 정보를 불러오지 못했습니다.');
    }

    private async waitForCharDbModel(token: string, requestId: number): Promise<CharDbModelMetadata> {
        let delay = 2000;
        while (true) {
            const model = await this.getCharDbModel(token, requestId);
            if (model) {
                this.nfcWaitingForModel = false;
                return model;
            }
            this.nfcWaitingForModel = true;
            this.nfcRegistrationStatus = `NFC 인증 완료 · 3D 캐릭터 생성 대기 중 (${Math.round(delay / 1000)}초 후 다시 확인)`;
            this.skinStatus = 'CharDB에서 3D 캐릭터를 생성하고 있습니다.';
            this.requestUiRender();
            await this.waitForNfcRetry(delay, requestId);
            delay = Math.min(5000, delay + 1000);
        }
    }

    private validateCharDbModelMetadata(value: any): CharDbModelMetadata {
        const model = value && typeof value === 'object' ? value : {};
        const id = String(model.id || '').trim();
        const assetId = String(model.asset_id || '').trim();
        const originalName = this.cleanNfcFileName(String(model.original_name || ''));
        const format = String(model.format || '').trim().toUpperCase();
        const fileSize = Number(model.file_size);
        const checksum = String(model.checksum || '').trim().toLowerCase();
        const status = String(model.status || '').trim().toUpperCase();
        const downloadUrl = String(model.downloadUrl || '').trim();
        if (!id || !assetId || !originalName || !downloadUrl) throw this.createCharDbError('CharDB 모델 응답에 필수 필드가 없습니다.', 502);
        if (!['GLB', 'GLTF', 'OBJ'].includes(format)) throw this.createCharDbError(`지원하지 않는 CharDB 모델 형식입니다. (${format || 'UNKNOWN'})`, 415);
        if (!Number.isSafeInteger(fileSize) || fileSize <= 0 || fileSize > this.maxNfcModelFileSize) {
            throw this.createCharDbError('CharDB 모델 파일 크기가 허용 범위를 벗어났습니다.', 413);
        }
        if (!/^[0-9a-f]{64}$/.test(checksum)) throw this.createCharDbError('CharDB 모델 SHA-256 체크섬 형식이 올바르지 않습니다.', 502);
        if (status !== 'AVAILABLE') throw this.createCharDbError(`CharDB 모델을 아직 사용할 수 없습니다. (${status || 'UNKNOWN'})`, 409);
        if (!downloadUrl.startsWith('/')) throw this.createCharDbError('CharDB 모델 downloadUrl은 서버 상대경로여야 합니다.', 502);
        return {
            id,
            asset_id: assetId,
            name: typeof model.name === 'string' ? model.name.trim().slice(0, 60) : undefined,
            original_name: originalName,
            format: format as CharDbModelMetadata['format'],
            file_size: fileSize,
            checksum,
            status: 'AVAILABLE',
            downloadUrl
        };
    }

    private downloadCharDbModel(model: CharDbModelMetadata, requestId: number): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            this.assertNfcRequestActive(requestId);
            const form = new FormData();
            form.append('baseUrl', this.nfcApiUrl);
            form.append('downloadUrl', model.downloadUrl);
            form.append('expectedSize', String(model.file_size));
            form.append('checksum', model.checksum);
            form.append('fileName', model.original_name);
            const request = new XMLHttpRequest();
            this.nfcDownloadRequest?.abort();
            this.nfcDownloadRequest = request;
            request.open('POST', wiz.url('chardb_download'), true);
            request.responseType = 'arraybuffer';
            request.timeout = 190000;
            request.onload = () => {
                if (this.nfcDownloadRequest === request) this.nfcDownloadRequest = undefined;
                if (requestId !== this.nfcSessionSequence) {
                    reject(this.createCharDbError('NFC 요청이 새 태그로 교체되었습니다.', undefined, 'NFC_CANCELLED'));
                    return;
                }
                const data = request.response as ArrayBuffer | null;
                if (request.status >= 200 && request.status < 300 && data) {
                    resolve(data);
                    return;
                }
                reject(this.createCharDbError(this.readCharDbDownloadError(data, request.status), request.status));
            };
            request.onerror = () => reject(this.createCharDbError('CharDB 모델 다운로드 중계 서버에 연결하지 못했습니다.', 0));
            request.ontimeout = () => reject(this.createCharDbError('CharDB 모델 다운로드 시간이 초과되었습니다.', 504));
            request.onabort = () => reject(this.createCharDbError('NFC 요청이 취소되었습니다.', undefined, 'NFC_CANCELLED'));
            request.send(form);
        });
    }

    private readCharDbDownloadError(response: ArrayBuffer | null, status: number): string {
        if (response && response.byteLength > 0) {
            try {
                const payload = JSON.parse(new TextDecoder().decode(response));
                const message = payload?.data?.error || payload?.data?.message || payload?.error || payload?.message;
                if (message) return String(message);
            } catch {
                // 바이너리 오류 응답이 아니면 상태 코드 메시지를 사용한다.
            }
        }
        return `CharDB 모델 다운로드에 실패했습니다. (HTTP ${status || 0})`;
    }

    private async downloadCharDbModelWithRetry(model: CharDbModelMetadata, requestId: number): Promise<ArrayBuffer> {
        let lastError: CharDbRequestError | undefined;
        for (let attempt = 0; attempt < 3; attempt += 1) {
            try {
                return await this.downloadCharDbModel(model, requestId);
            } catch (error: any) {
                const current = error as CharDbRequestError;
                if (current.code === 'NFC_CANCELLED' || current.status === 403 || current.status === 404) throw current;
                lastError = current;
                const retryable = !current.status || current.status >= 500;
                if (!retryable || attempt === 2) throw current;
                await this.waitForNfcRetry(750 * Math.pow(2, attempt), requestId);
            }
        }
        throw lastError || this.createCharDbError('CharDB 모델을 다운로드하지 못했습니다.');
    }

    private async sha256Hex(data: ArrayBuffer): Promise<string> {
        if (!window.crypto?.subtle) throw this.createCharDbError('이 브라우저는 SHA-256 파일 검증을 지원하지 않습니다.');
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    private nfcAssetName(model: CharDbModelMetadata): string {
        const baseName = model.original_name.replace(/\.(glb|gltf|obj)$/i, '');
        return String(model.name || baseName || 'CharDB 캐릭터').trim().slice(0, 30) || 'CharDB 캐릭터';
    }

    private createNfcCharacterAsset(
        model: CharDbModelMetadata,
        participantId: string,
        data: ArrayBuffer,
        fromCache: boolean
    ): NfcCharacterAsset {
        return {
            name: this.nfcAssetName(model),
            fileName: model.original_name,
            format: model.format.toLowerCase() as SkinFormat,
            data,
            participantId,
            assetId: model.asset_id,
            checksum: model.checksum,
            fileSize: model.file_size,
            baseUrl: this.nfcApiUrl,
            fromCache
        };
    }

    private async readVerifiedNfcCache(model: CharDbModelMetadata, participantId: string): Promise<NfcCharacterAsset | null> {
        const cached = this.localSkins.find((skin) => skin.nfcParticipantId === participantId
            && skin.nfcAssetId === model.asset_id
            && skin.nfcChecksum === model.checksum
            && skin.nfcBaseUrl === this.nfcApiUrl);
        if (!cached) return null;
        const validSize = cached.data.byteLength === model.file_size && cached.nfcFileSize === model.file_size;
        const validChecksum = validSize && await this.sha256Hex(cached.data) === model.checksum;
        if (!validSize || !validChecksum) {
            await this.skinStorage.deleteLocal(cached.id).catch(() => undefined);
            this.localSkins = this.localSkins.filter((skin) => skin.id !== cached.id);
            return null;
        }
        return this.createNfcCharacterAsset(model, participantId, cached.data.slice(0), true);
    }

    private async resolveNfcCharacterAsset(
        initialModel: CharDbModelMetadata,
        participantId: string,
        token: string,
        requestId: number
    ): Promise<NfcCharacterAsset> {
        let model = initialModel;
        let signedUrlRetried = false;
        while (true) {
            const cached = await this.readVerifiedNfcCache(model, participantId);
            this.assertNfcRequestActive(requestId);
            if (cached) return cached;
            try {
                const data = await this.downloadCharDbModelWithRetry(model, requestId);
                if (data.byteLength !== model.file_size) throw this.createCharDbError('CharDB 모델 파일 크기가 메타데이터와 일치하지 않습니다.', 422, 'FILE_SIZE_MISMATCH');
                const checksum = await this.sha256Hex(data);
                if (checksum !== model.checksum) throw this.createCharDbError('CharDB 모델 SHA-256 체크섬이 일치하지 않습니다.', 422, 'CHECKSUM_MISMATCH');
                this.assertNfcRequestActive(requestId);
                return this.createNfcCharacterAsset(model, participantId, data, false);
            } catch (error: any) {
                const current = error as CharDbRequestError;
                if (current.status === 403 && !signedUrlRetried) {
                    signedUrlRetried = true;
                    this.nfcRegistrationStatus = '모델 다운로드 주소가 만료되어 새 서명 URL을 요청합니다.';
                    this.requestUiRender();
                    model = await this.waitForCharDbModel(token, requestId);
                    continue;
                }
                if (current.status === 404) {
                    const occurredAt = new Date().toISOString();
                    throw this.createCharDbError(`CharDB 모델 파일을 찾지 못했습니다. 자산 ${model.asset_id} · ${occurredAt}`, 404);
                }
                throw current;
            }
        }
    }

    private buildNfcCacheId(asset: NfcCharacterAsset): string {
        const assetId = asset.assetId.replace(/[^0-9a-z-]/gi, '').slice(0, 80) || 'model';
        return `chardb-${assetId}-${asset.checksum}`;
    }

    private async loadNfcCharacter(uid: string, supersede: boolean) {
        if (!this.nfcApiUrl) return;
        if (this.skinBusy && !this.nfcRegistrationBusy) {
            this.nfcRegistrationStatus = '다른 캐릭터 작업이 끝난 뒤 NFC 태그를 다시 인식해주세요.';
            this.requestUiRender();
            return;
        }
        if (this.nfcRegistrationBusy && !supersede) return;

        const previousUid = this.nfcCurrentUid;
        const resetPreviousCharacter = Boolean(this.activeNfcCharacterMetadata && previousUid !== uid);
        await this.endNfcCharacterSession(false, resetPreviousCharacter, true);
        if (this.destroyed) return;
        this.clearNfcRegistrationDraft();
        const requestId = ++this.nfcSessionSequence;
        this.nfcCurrentUid = uid;
        this.nfcRegistrationBusy = true;
        this.nfcWaitingForModel = false;
        this.skinBusy = true;
        this.nfcRegistrationStatus = 'NFC 태그 인증 중...';
        this.requestUiRender();

        try {
            const auth = await this.authenticateNfcUid(uid, requestId);
            this.nfcUserToken = auth.token;
            this.nfcParticipantId = auth.participantId;
            this.nfcRegistrationStatus = 'NFC 인증 완료 · 최신 3D 캐릭터를 조회합니다.';
            this.requestUiRender();
            const model = await this.waitForCharDbModel(auth.token, requestId);
            this.nfcRegistrationStatus = '3D 캐릭터 파일을 내려받고 무결성을 확인합니다.';
            this.requestUiRender();
            const asset = await this.resolveNfcCharacterAsset(model, auth.participantId, auth.token, requestId);
            this.validateSkinData(asset.data, asset.format);
            const existing = this.localSkins.find((skin) => skin.id === this.buildNfcCacheId(asset));
            const rotationY = this.normalizeSkinRotation(existing?.rotationY);
            const prepared = await this.prepareCustomSkinVisual(asset.data, asset.format, rotationY);
            try {
                this.assertNfcRequestActive(requestId);
            } catch (error) {
                this.disposeObjectTree(prepared.visual);
                throw error;
            }
            this.nfcRegistrationDraft = {
                asset,
                recordId: existing?.id || this.buildNfcCacheId(asset),
                stats: this.normalizeCharacterStats(existing?.stats)
            };
            this.nfcCharacterNameDraft = String(existing?.name || asset.name).trim().slice(0, 30) || 'CharDB 캐릭터';
            this.nfcPreviewRotationY = rotationY;
            this.nfcPreviewInitialRotationY = rotationY;
            this.setNfcPreviewModel(prepared.visual);
            this.nfcPreviewReady = true;
            this.nfcDraftSaved = false;
            const cacheStatus = asset.fromCache ? '검증된 로컬 캐시 사용' : '다운로드·무결성 검증 완료';
            this.nfcRegistrationStatus = `${this.nfcCharacterNameDraft} 미리보기 준비 완료 · 좌우로 정면을 맞추고 이름을 입력해 저장하세요. (${cacheStatus})`;
            this.localSkinStatus = `CharDB ${asset.format.toUpperCase()} · 메시 ${prepared.summary.meshCount}개 · 저장 대기`;
            window.setTimeout(() => this.initializeNfcPreview(), 0);
        } catch (error: any) {
            const current = error as CharDbRequestError;
            if (current.code === 'NFC_CANCELLED' || requestId !== this.nfcSessionSequence) return;
            if (current.status === 401) {
                await this.endNfcCharacterSession(true, true, true);
                this.nfcRegistrationStatus = 'NFC 인증이 만료되었거나 무효화되었습니다. 태그를 다시 인식해주세요.';
                this.skinStatus = 'CharDB 인증이 무효화되어 기본 캐릭터로 전환했습니다.';
            } else {
                this.nfcUserToken = '';
                this.nfcRegistrationStatus = current.message || 'NFC 캐릭터를 불러오지 못했습니다.';
            }
        } finally {
            if (requestId === this.nfcSessionSequence) {
                this.nfcRegistrationBusy = false;
                this.nfcWaitingForModel = false;
                this.skinBusy = false;
                this.nfcDownloadRequest = undefined;
            }
            this.requestUiRender();
        }
    }

    private async removeObsoleteNfcCache(active: StoredSkin) {
        const obsolete = this.localSkins.filter((skin) => skin.id !== active.id
            && skin.nfcParticipantId === active.nfcParticipantId
            && skin.nfcBaseUrl === active.nfcBaseUrl);
        await Promise.all(obsolete.map((skin) => this.skinStorage.deleteLocal(skin.id).catch(() => undefined)));
        if (obsolete.length > 0) {
            const ids = new Set(obsolete.map((skin) => skin.id));
            this.localSkins = this.localSkins.filter((skin) => !ids.has(skin.id));
        }
    }

    private async endNfcCharacterSession(evictCache: boolean, resetCharacter: boolean, cancelRequests: boolean) {
        const participantId = this.nfcParticipantId || this.activeNfcCharacterMetadata?.participantId || '';
        if (cancelRequests) {
            this.nfcSessionSequence += 1;
            this.nfcDownloadRequest?.abort();
            this.nfcDownloadRequest = undefined;
        }
        this.nfcUserToken = '';
        this.nfcParticipantId = '';
        this.nfcCurrentUid = '';
        if (evictCache && participantId) {
            const stale = this.localSkins.filter((skin) => skin.nfcParticipantId === participantId);
            await Promise.all(stale.map((skin) => this.skinStorage.deleteLocal(skin.id).catch(() => undefined)));
            const ids = new Set(stale.map((skin) => skin.id));
            this.localSkins = this.localSkins.filter((skin) => !ids.has(skin.id));
        }
        if (resetCharacter && this.activeNfcCharacterMetadata) await this.restoreDefaultCharacterAfterNfcSession();
        if (cancelRequests) {
            this.nfcRegistrationBusy = false;
            this.nfcWaitingForModel = false;
            this.skinBusy = false;
        }
    }

    private async restoreDefaultCharacterAfterNfcSession() {
        this.activeNfcCharacterMetadata = null;
        this.customSkinData = null;
        this.customSkinFormat = 'glb';
        this.customSkinRotationY = 180;
        this.activeLocalSkinId = '';
        this.hasCustomSkin = false;
        this.selectedBuiltInSkinId = 'joseon-king-2';
        const fallback = this.builtInSkins.find((item) => item.id === 'joseon-king-2')!;
        this.skinName = fallback.name;
        this.characterNameDraft = fallback.name;
        this.skinFileName = '';
        this.characterCustomizationEnabled = false;
        this.customCharacterStats = { ...fallback.stats };
        this.persistBuiltInSkinSettings('joseon-king-2');
        await this.ensureKing2AssetLoaded().catch(() => undefined);
        this.replacePlayerVisual(this.createBuiltInPlayerVisual('joseon-king-2'));
        this.refreshCustomizationPreviewModel();
        await this.skinStorage.clear().catch(() => undefined);
    }

    private cleanNfcFileName(value: string): string {
        return String(value || '').split(/[\\/]/).pop()?.replace(/[^\p{L}\p{N}._ -]/gu, '').slice(0, 120) || '';
    }

    private async prepareCustomSkinVisual(
        data: ArrayBuffer,
        format: SkinFormat,
        rotationY: number
    ): Promise<PreparedSkinVisual> {
        const model = await this.parseSkinModel(data, format);
        const summary = this.inspectImportedModel(model);
        if (summary.meshCount === 0) {
            this.disposeObjectTree(model);
            throw new Error('캐릭터로 사용할 메시가 없는 3D 파일입니다.');
        }
        model.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            if (mesh.isMesh) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
            }
        });

        const initialBox = new THREE.Box3().setFromObject(model);
        const size = initialBox.getSize(new THREE.Vector3());
        if (!Number.isFinite(size.y) || size.y <= 0.001) {
            this.disposeObjectTree(model);
            throw new Error('캐릭터 크기를 계산할 수 없습니다.');
        }

        const wrapper = new THREE.Group();
        const orientation = new THREE.Group();
        orientation.name = 'uploaded-skin-orientation';
        orientation.add(model);
        wrapper.add(orientation);
        wrapper.animations = model.animations || [];
        const baseScale = 2.8 / size.y;
        model.scale.setScalar(baseScale);
        model.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.x -= center.x;
        model.position.z -= center.z;
        model.position.y += -box.min.y;
        orientation.rotation.y = THREE.MathUtils.degToRad(this.normalizeSkinRotation(rotationY));
        return { visual: wrapper, summary };
    }

    private async applyCustomSkin(
        data: ArrayBuffer,
        format: SkinFormat,
        rotationY: number = this.customSkinRotationY,
        nfcRequestId?: number
    ): Promise<ImportedModelSummary> {
        const prepared = await this.prepareCustomSkinVisual(data, format, rotationY);
        if (nfcRequestId !== undefined) {
            try {
                this.assertNfcRequestActive(nfcRequestId);
            } catch (error) {
                this.disposeObjectTree(prepared.visual);
                throw error;
            }
        }
        this.replacePlayerVisual(prepared.visual);
        return prepared.summary;
    }

    private readNfcCharacterMetadata(skin: StoredSkin): ActiveNfcCharacterMetadata | null {
        if (!skin.nfcParticipantId || !skin.nfcAssetId || !skin.nfcChecksum
            || !skin.nfcFileSize || !skin.nfcBaseUrl) return null;
        return {
            participantId: skin.nfcParticipantId,
            assetId: skin.nfcAssetId,
            checksum: skin.nfcChecksum,
            fileSize: skin.nfcFileSize,
            baseUrl: skin.nfcBaseUrl
        };
    }

    private activeNfcStorageFields(): Partial<StoredSkin> {
        const metadata = this.activeNfcCharacterMetadata;
        if (!metadata) return {};
        return {
            nfcParticipantId: metadata.participantId,
            nfcAssetId: metadata.assetId,
            nfcChecksum: metadata.checksum,
            nfcFileSize: metadata.fileSize,
            nfcBaseUrl: metadata.baseUrl
        };
    }

    private async persistSkin() {
        if (!this.customSkinData) return;
        const record: StoredSkin = {
            id: 'active',
            name: this.skinName,
            fileName: this.skinFileName,
            format: this.customSkinFormat,
            data: this.customSkinData.slice(0),
            scale: 1,
            yOffset: 0,
            rotationY: this.customSkinRotationY,
            libraryId: this.activeLocalSkinId || undefined,
            ...this.activeNfcStorageFields(),
            customizationEnabled: this.characterCustomizationEnabled,
            stats: { ...this.customCharacterStats },
            updatedAt: new Date().toISOString()
        };
        await this.skinStorage.put(record);
    }

    private requestUiRender() {
        if (this.renderPending || this.destroyed) return;
        this.renderPending = true;
        Promise.resolve(this.service.render())
            .catch(() => undefined)
            .finally(() => {
                this.renderPending = false;
                if (this.editorMap && this.editorHasCustomTerrain) this.scheduleTerrainCanvasRender();
            });
    }
}

export default PageQuizRunner3dComponent;